import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import SERVICE_PAGES from './service-pages';
import makeClientCare from './ClientCare';
import makeTrainerTools from './TrainerTools';
import makeMealPlanner from './MealPlanner';
import makeTraining from './Training';
import makeEasyLog from './EasyLog';
import makeBuild3 from './Build3';
import makeAppStore, { askAiConsent } from './AppStore';
import makeNativeFeatures from './NativeFeatures';
import makeBillingFlex from './BillingFlex';
import makeTrainerProgress from './TrainerProgress';
import makeRecipeSubmissions from './RecipeSubmissions';
import makeTrainerGaps from './TrainerGaps';
import makeExerciseSlotVideo from './ExerciseSlotVideo';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams, Link } from 'react-router-dom';

// ── THEME ─────────────────────────────────────────────────────────────────────
const T = {
  bg: '#F4F6F9',
  bg2: '#EBEDF2',
  card: '#FFFFFF',
  cardHover: '#F9FAFB',
  border: 'rgba(0,0,0,0.10)',
  orange: '#FF4500',
  orangeDark: '#CC3700',
  orangeLight: '#FF6A3D',
  gold: '#D97706',
  white: '#111111',
  gray: '#6B7280',
  grayLight: '#374151',
  success: '#16A34A',
  danger: '#DC2626',
  glass: 'rgba(0,0,0,0.02)',
  glassHover: 'rgba(0,0,0,0.05)',
};

const gradText = {
  background: `linear-gradient(135deg, ${T.orange} 0%, ${T.gold} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

const glassBg = {
  background: T.card,
  border: `1px solid ${T.border}`,
  boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
};

// The My pay unlock (trainer-pay.js, 2026-11-01). Held only in this page's memory, apart from the login token: a reload or sign-out loses it.
let payToken = null, payUntil = 0;
const payStore = {
  get: () => (payToken && payUntil > Date.now() ? payToken : null),
  expiresAt: () => payUntil,
  set: (t, until) => { payToken = t || null; payUntil = until ? Date.parse(until) : 0; },
  clearOnSignOut: () => {
    const tok = localStorage.getItem('db_token');
    if (payToken && tok) fetch('/api/coach/pay/lock', { method: 'POST', headers: { Authorization: `Bearer ${tok}` } }).catch(() => {});
    payToken = null; payUntil = 0;
  },
};

// ── AUTH CONTEXT ──────────────────────────────────────────────────────────────
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('db_token');
    if (!token) { setLoading(false); return; }
    api('/api/auth/me', 'GET', null, token)
      .then(d => { setUser(d.user); setOnboardingComplete(d.onboardingComplete); })
      .catch(() => localStorage.removeItem('db_token'))
      .finally(() => setLoading(false));
  }, []);

  const login = (userData, token, onboarded) => {
    localStorage.setItem('db_token', token);
    setUser(userData);
    setOnboardingComplete(onboarded);
  };

  const logout = () => {
    payStore.clearOnSignOut(); // My pay closes with the session (trainer-pay.js, 2026-11-01)
    localStorage.removeItem('db_token');
    setUser(null);
    setOnboardingComplete(false);
  };

  const completeOnboarding = () => setOnboardingComplete(true);

  return (
    <AuthCtx.Provider value={{ user, onboardingComplete, loading, login, logout, completeOnboarding }}>
      {children}
    </AuthCtx.Provider>
  );
}

// A trainer's login page is the trainer page (trainer-site, 2026-11-01), so signing out of the portal goes back there.
// Only on the main address or a local copy: not in the phone apps, not on the program sites. If that page is not being
// served there yet (it needs a sysadmin nginx change on live), this does what it always did.
async function landAfterSignOut(role, nav) {
  if (role === 'coach' && !window.Capacitor && /^((www\.)?healthybody\.fitness|localhost|127\.0\.0\.1)$/.test(window.location.hostname)) {
    try {
      const r = await fetch('/for-trainers/', { method: 'HEAD' });
      if (r.ok && /html/.test(r.headers.get('content-type') || '')) { window.location.href = '/for-trainers/#signin'; return; }
    } catch (e) { /* fall through */ }
  }
  nav('/');
}

// A trainer's session ended on its own (expired, or an admin turned the login off): back to the trainer page, the way signing out does (2026-11-01).
// Everyone else keeps what happened before: the next screen asks them to log in.
let sessionEnding = false;
function trainerSessionEnded(tok) {
  let role = null;
  try { role = JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).role; } catch (e) { return; }
  if (role !== 'coach' || sessionEnding) return;
  sessionEnding = true;
  localStorage.removeItem('db_token');
  landAfterSignOut('coach', () => { window.location.href = `${process.env.PUBLIC_URL || ''}/login`; });
}

// ── API HELPER ────────────────────────────────────────────────────────────────
async function api(path, method = 'GET', body = null, token = null, consentAsked = false) {
  const tok = token || localStorage.getItem('db_token');
  const res = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (res.status === 401 && tok && data && (data.login_disabled || /^Session expired/.test(data.error || ''))) trainerSessionEnded(tok);
  // App Store 5.1.2(i) (2026-10-30): an AI feature the member has not said yes to. Show the consent
  // screen (AppStore.jsx), save the answer, and try once more. "Not now" on Say it / Help still
  // retries (the server then uses the built-in reader / a person); on Snap a Meal / AI coach it stops.
  if (res.status === 403 && data && data.needsAiConsent && !consentAsked) {
    const allow = await askAiConsent();
    if (allow !== null) {
      await api('/api/ai-consent', 'POST', { allow }, token, true);
      if (allow || data.fallbackWhenDeclined) return api(path, method, body, token, true);
      const err = new Error(data.declinedMessage || data.error); err.status = 403; err.info = { ...data, declined: true };
      throw err;
    }
  }
  if (!res.ok) {
    const err = new Error(data.message || data.error || 'Something went wrong');
    err.status = res.status; err.info = data;
    throw err;
  }
  return data;
}

// ── PUSH NOTIFICATION HELPER ─────────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
}

// ── ROUTE GUARDS ──────────────────────────────────────────────────────────────
function PrivateRoute({ children }) {
  const { user, onboardingComplete, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'gym_admin') return <Navigate to="/gym-admin" replace />;
  if (user.role === 'cook') return <Navigate to="/cook-dashboard" replace />;
  if (user.role === 'trainer_applicant') return <Navigate to="/trainer-application" replace />;
  if (user.role === 'admin') return children;
  if (!onboardingComplete) return <Navigate to="/onboarding" replace />;
  return children;
}

// A trainer's alerts are in their portal (2026-11-01). Before this a trainer landing here was sent to the Rebuilder health questions.
function NotificationsRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user && user.role === 'coach') return <Navigate to="/coach?tool=alerts" replace />;
  return <PrivateRoute><TT.NotificationSettingsPage /></PrivateRoute>;
}

// Any signed-in role: the phone alerts screen is for trainers too (2026-11-02).
function SignedInRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user || user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function CoachRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user || (user.role !== 'admin' && user.role !== 'coach')) return <Navigate to="/login" replace />;
  return children;
}

function GymAdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user || (user.role !== 'gym_admin' && user.role !== 'admin')) return <Navigate to="/login" replace />;
  return children;
}

function CookRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user || (user.role !== 'admin' && user.role !== 'cook')) return <Navigate to="/login" replace />;
  return children;
}

// ── SHARED COMPONENTS ─────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: `3px solid ${T.border}`, borderTopColor: T.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: T.gray, fontSize: 14, fontFamily: 'Poppins' }}>Loading…</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Btn({ children, onClick, type = 'button', variant = 'primary', size = 'md', disabled, fullWidth, style }) {
  const sizes = { sm: { padding: '8px 20px', fontSize: 13 }, md: { padding: '14px 32px', fontSize: 15 }, lg: { padding: '18px 48px', fontSize: 17 } };
  const variants = {
    primary: { background: `linear-gradient(135deg, var(--accent, ${T.orange}), var(--accent-light, ${T.orangeLight}))`, color: '#fff', border: 'none' },
    outline: { background: 'transparent', color: `var(--accent, ${T.orange})`, border: `2px solid var(--accent, ${T.orange})` },
    ghost: { background: 'transparent', color: T.grayLight, border: `1px solid ${T.border}` },
    danger: { background: T.danger, color: '#fff', border: 'none' },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        ...sizes[size],
        ...variants[variant],
        borderRadius: 8,
        fontFamily: 'Poppins',
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        width: fullWidth ? '100%' : 'auto',
        transition: 'all 0.2s',
        letterSpacing: 0.5,
        boxShadow: variant === 'primary' ? `0 4px 24px rgba(255,69,0,0.3)` : 'none',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function Input({ label, type = 'text', value, onChange, placeholder, required, hint, error }) {
  return (
    <div style={{ marginBottom: 20 }}>
      {label && <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>{label}{required && <span style={{ color: T.orange }}> *</span>}</label>}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        style={{
          width: '100%', padding: '13px 16px', background: '#F5F7FA', border: `1px solid ${error ? T.danger : T.border}`,
          borderRadius: 8, color: T.white, fontSize: 15, fontFamily: 'Poppins', outline: 'none',
          transition: 'border-color 0.2s',
        }}
        onFocus={e => e.target.style.borderColor = T.orange}
        onBlur={e => e.target.style.borderColor = error ? T.danger : T.border}
      />
      {hint && !error && <p style={{ fontSize: 12, color: T.gray, marginTop: 4 }}>{hint}</p>}
      {error && <p style={{ fontSize: 12, color: T.danger, marginTop: 4 }}>{error}</p>}
    </div>
  );
}

function Select({ label, value, onChange, options, required }) {
  return (
    <div style={{ marginBottom: 20 }}>
      {label && <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>{label}{required && <span style={{ color: T.orange }}> *</span>}</label>}
      <select
        value={value}
        onChange={onChange}
        required={required}
        style={{
          width: '100%', padding: '13px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`,
          borderRadius: 8, color: value ? T.white : T.gray, fontSize: 15, fontFamily: 'Poppins', outline: 'none', cursor: 'pointer',
        }}
        onFocus={e => e.target.style.borderColor = T.orange}
        onBlur={e => e.target.style.borderColor = T.border}
      >
        {options.map(o => <option key={o.value} value={o.value} style={{ background: '#FFFFFF' }}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Card({ children, style, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderRadius: 16,
        padding: 24,
        transition: 'all 0.2s',
        background: hov && onClick ? T.cardHover : T.card,
        border: `1px solid ${T.border}`,
        boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Badge({ children, color = T.orange }) {
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: 0.5, background: `${color}22`, color, border: `1px solid ${color}44` }}>
      {children}
    </span>
  );
}

// Turn on phone alerts on this device, the same way the member Portal does. Returns the words to show (2026-11-01).
async function turnOnPushHere() {
  // In the phone app: the reason screen first, then the phone's own question (NativeFeatures.jsx, 2026-11-02).
  if (window.HB_NATIVE && window.HB_NATIVE.push) { window.history.pushState({}, '', '/settings/phone-alerts'); window.dispatchEvent(new PopStateEvent('popstate', { state: {} })); return ''; }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'This browser does not support phone alerts. On an iPhone, add HealthyBody to your Home Screen first.';
  if (Notification.permission === 'denied') return 'Alerts are blocked for this site in your browser settings.';
  await navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/sw.js`);
  const reg = await navigator.serviceWorker.ready;
  const { publicKey } = await api('/api/push/vapid-public-key', 'GET');
  if (!publicKey) return 'Phone alerts are not set up on the server yet. Email alerts still work.';
  let sub;
  try { sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }); }
  catch (e) { return 'This browser did not allow phone alerts. Check the notification permission for this site, then try again.'; }
  const j = sub.toJSON();
  await api('/api/push/subscribe', 'POST', { endpoint: j.endpoint, keys: j.keys });
  return 'Phone alerts are on for this device.';
}

function Navbar() {
  const service = useService();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const links = user
    ? user.role === 'gym_admin'
      ? [{ to: '/gym-admin', label: '🏛 Gym Admin' }]
      : user.role === 'cook'
      ? [{ to: '/cook-dashboard', label: '👨‍🍳 My Kitchen' }]
      : user.role === 'coach'
      ? [{ to: '/coach', label: '🏆 My Rebuilders' }]
      : user.role === 'trainer_applicant'
      ? [{ to: '/trainer-application', label: '📝 My application' }]
      : [
          { to: '/dashboard', label: 'Dashboard' },
          { to: '/program', label: 'My Program' },
          { to: '/workouts', label: '📒 Workout Log' },
          { to: '/mind', label: '🧠 Mind' },
          { to: '/mobility', label: '🦴 Mobility' },
          { to: '/conditions', label: '❤️ Living Well' },
          { to: '/longevity', label: '🌳 Strong at 65+' },
          { to: '/community', label: '👥 Community' },
          { to: '/get-outside', label: '🌲 Get Outside' },
          { to: '/messages', label: '💬 Messages' },
          ...(user.role === 'user' ? [{ to: '/help', label: '\u2753 Help' }] : []),
          { to: '/insights', label: '📈 Progress' },
          { to: '/calendar', label: '📅 Calendar' },
          { to: '/goals', label: '🎯 My Goal' },
          { to: '/photos', label: '📸 Photos' },
          { to: '/ai-coach', label: '🤖 AI Coach' },
          { to: '/portal', label: '👤 My Portal' },
          { to: '/profile', label: '📝 My Profile' },
          { to: '/billing', label: '💳 Plan & Billing' },
          ...(user.role === 'admin' || user.role === 'coach' ? [{ to: '/coach', label: '🏆 Coach' }] : []),
          ...(user.role === 'admin' ? [{ to: '/admin', label: '⚡ Admin' }] : []),
          // Cooking & Eating comes with every plan, so it always sits at the end.
          { to: '/food-plan', label: '🍽️ Food Plan' },
          { to: '/recipes', label: '🥗 Recipes' },
          { to: '/shopping', label: '🛒 Shopping' },
          { to: '/food-diary', label: '🍱 Food Diary' },
          { to: '/cooking-shows', label: '🎥 Cooking Shows' },
        ]
    : [];

  return (
    <nav className="hb-nav" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
      background: '#fff',
      borderBottom: `1px solid rgba(0,0,0,0.08)`,
      boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
      height: 64,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 32px',
    }}>
      <Link className="hb-nav-logo" to={user ? '/dashboard' : '/'} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {!service || service.key !== 'yoga' ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 26 }}>{service ? service.icon : '\u{1F33F}'}</span>
            <span style={{ fontFamily: 'Oswald', fontWeight: 600, fontSize: 24, letterSpacing: 0.5, color: '#101418' }}>{service ? service.name : 'HealthyBody'}</span>
          </span>
        ) : (
          <img src="/images/logo-dark.svg" alt="Yoga Studio" style={{ height: 44, width: 'auto' }} />
        )}
      </Link>

      {/* The member menu is long: it scrolls sideways instead of running off the screen. */}
      <div className="hb-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto', minWidth: 0, flex: '0 1 auto', scrollbarWidth: 'thin', marginLeft: 16 }}>
        {links.map(l => {
          const parts = l.label.match(/^(\S+)\s+(.+)$/);
          const emoji = parts ? parts[1] : null;
          const text = parts ? parts[2] : l.label;
          const isActive = loc.pathname === l.to;
          return (
            <Link key={l.to} to={l.to} style={{
              padding: '8px 12px', borderRadius: 8, textDecoration: 'none', fontSize: 15, fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0,
              color: isActive ? T.orange : T.gray,
              background: isActive ? `${T.orange}12` : 'transparent',
              transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 4,
            }}>
              {emoji && <span style={{ fontSize: 18, lineHeight: 1 }}>{emoji}</span>}
              <span>{text}</span>
            </Link>
          );
        })}
        {user
          ? <span className="hb-nav-signout"><Btn variant="ghost" size="sm" onClick={() => { const n = window.HB_NATIVE && window.HB_NATIVE.offline ? window.HB_NATIVE.offline.pending() : 0; if (n && !window.confirm(`${n} workout${n === 1 ? ' is' : 's are'} saved on this phone and not synced yet. Signing out deletes ${n === 1 ? 'it' : 'them'}. Sign out anyway?`)) return; const role = user.role; logout(); landAfterSignOut(role, nav); }}>Sign Out</Btn></span>
          : <>
              <Link to="/login" style={{ padding: '8px 16px', color: T.grayLight, fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>Log In</Link>
              <Btn size="sm" onClick={() => nav('/signup')}>Get Started</Btn>
            </>
        }
      </div>
    </nav>
  );
}

// ── EXERCISE PREVIEW (public tease section) ───────────────────────────────────
const POSE_CATS = ['Standing', 'Balance', 'Hip Openers', 'Backbends', 'Forward Folds', 'Twists', 'Core', 'Arm Balances', 'Inversions', 'Restorative', 'Breathwork'];
const POSE_COLORS = { Standing: T.orange, Balance: T.gold, 'Hip Openers': T.success, Backbends: T.orangeLight, 'Forward Folds': '#4A90E2', Twists: T.gold, Core: T.orange, 'Arm Balances': T.orangeLight, Inversions: '#4A90E2', Restorative: T.success, Breathwork: '#4A90E2' };
const POSE_ICONS = { Standing: '🌲', Balance: '⚖️', 'Hip Openers': '🦋', Backbends: '🌙', 'Forward Folds': '🍃', Twists: '🌀', Core: '🔥', 'Arm Balances': '🪶', Inversions: '🙃', Restorative: '🌿', Breathwork: '🌬️' };

function ExercisePreview({ nav }) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/exercises/preview', 'GET').then(d => setExercises(d.exercises || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const catColors = POSE_COLORS;
  const catIcon = POSE_ICONS;

  return (
    <section style={{ padding: '100px 32px', background: '#f5f5f5' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <Badge color={T.orange}>POSE LIBRARY</Badge>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(40px, 5vw, 64px)', marginTop: 16, letterSpacing: 2 }}>SEE WHAT YOU'LL PRACTICE</h2>
          <p style={{ color: '#555555', fontSize: 17, marginTop: 12, maxWidth: 520, margin: '12px auto 0' }}>A taste of the pose library. Members get step-by-step guidance, modifications, and safety notes for every pose in their program.</p>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading…</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 20, marginBottom: 40 }}>
              {exercises.map(ex => (
                <Card key={ex.id} style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ aspectRatio: '16/9', background: `linear-gradient(135deg, ${(catColors[ex.category] || T.orange)}22, ${(catColors[ex.category] || T.orange)}55)`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <span style={{ fontSize: 52 }}>{catIcon[ex.category] || '🧘'}</span>
                    <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.6)', borderRadius: 20, padding: '3px 10px', fontSize: 11, color: '#fff', fontWeight: 600 }}>FREE PREVIEW</div>
                  </div>
                  <div style={{ padding: '16px 20px 20px' }}>
                    <Badge color={catColors[ex.category] || T.orange}>{ex.category}</Badge>
                    <h4 style={{ fontFamily: 'Oswald', fontSize: 17, fontWeight: 600, color: '#111111', marginTop: 10, marginBottom: 6 }}>{ex.name}</h4>
                    <p style={{ fontSize: 12, color: '#666666', lineHeight: 1.5 }}>{ex.description}</p>
                  </div>
                </Card>
              ))}
            </div>
            <div style={{ textAlign: 'center', padding: '48px 40px', background: 'rgba(255,255,255,0.9)', borderRadius: 20, border: '2px dashed rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>🔒</div>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 26, fontWeight: 600, color: '#111111', marginBottom: 10 }}>Members Get the Full Pose Library</h3>
              <p style={{ color: '#666666', fontSize: 15, marginBottom: 28, maxWidth: 500, margin: '0 auto 28px' }}>Standing poses, balances, hip openers, backbends, twists, inversions, restorative poses, and breathwork, with modifications for your body.</p>
              <Btn onClick={() => nav('/signup')}>Unlock the Full Library →</Btn>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ── RECIPE PREVIEW (public tease section) ─────────────────────────────────────
function RecipePreview({ nav }) {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/recipes/preview', 'GET').then(d => setRecipes(d.recipes || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const catIcons = { Breakfast: '🍳', Lunch: '🥗', Dinner: '🍽️', Snack: '🍎' };

  return (
    <section style={{ padding: '100px 32px', background: '#ffffff' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <Badge color={T.gold}>COOKING CLASSES</Badge>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(40px, 5vw, 64px)', marginTop: 16, letterSpacing: 2 }}>HEALTHY EATING MADE EASY</h2>
          <p style={{ color: '#555555', fontSize: 17, marginTop: 12, maxWidth: 560, margin: '12px auto 0' }}>Quick, real-food meals for people on the go. High protein, great taste, no complicated prep.</p>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Loading…</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 20, marginBottom: 40 }}>
              {recipes.map(r => (
                <Card key={r.id} style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ aspectRatio: '16/9', background: `linear-gradient(135deg, ${T.gold}22, ${T.orange}22)`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <span style={{ fontSize: 52 }}>{catIcons[r.category] || '🍽️'}</span>
                    <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.6)', borderRadius: 20, padding: '3px 10px', fontSize: 11, color: '#fff', fontWeight: 600 }}>FREE PREVIEW</div>
                  </div>
                  <div style={{ padding: '16px 20px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Badge color={T.gold}>{r.category}</Badge>
                      {(r.prep_time || r.cook_time) ? <span style={{ fontSize: 11, color: '#888' }}>{(r.prep_time || 0) + (r.cook_time || 0)} min</span> : null}
                    </div>
                    <h4 style={{ fontFamily: 'Oswald', fontSize: 17, fontWeight: 600, color: '#111111', marginBottom: 6 }}>{r.title}</h4>
                    <p style={{ fontSize: 12, color: '#666666', lineHeight: 1.5, marginBottom: 10 }}>{r.description}</p>
                    {r.calories_per_serving && (
                      <div style={{ display: 'flex', gap: 12 }}>
                        <span style={{ fontSize: 11, color: T.orange, fontWeight: 600 }}>{r.calories_per_serving} cal</span>
                        {r.protein_g && <span style={{ fontSize: 11, color: T.success, fontWeight: 600 }}>{r.protein_g}g protein</span>}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
            <div style={{ textAlign: 'center', padding: '48px 40px', background: `linear-gradient(135deg, ${T.gold}08, ${T.orange}08)`, borderRadius: 20, border: '2px dashed rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: 52, marginBottom: 16 }}>🎬</div>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 26, fontWeight: 600, color: '#111111', marginBottom: 10 }}>Members Get Full Cooking Class Videos</h3>
              <p style={{ color: '#666666', fontSize: 15, marginBottom: 28, maxWidth: 500, margin: '0 auto 28px' }}>Step-by-step video cooking classes. Fast, healthy meals with real ingredients — built for busy people who want to eat better.</p>
              <Btn onClick={() => nav('/signup')}>Unlock All Recipes →</Btn>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ── LANDING PAGE ──────────────────────────────────────────────────────────────
// ── SERVICE AWARENESS ─────────────────────────────────────────────────────────
// The hostname decides which product this is. One build, many front doors.
// Remembered so the phone apps keep the right name and logo with no connection (2026-11-02).
function savedService() { try { return JSON.parse(localStorage.getItem('hb_service')) || null; } catch (e) { return null; } }
function useService() {
  const [service, setService] = useState(savedService);
  useEffect(() => {
    fetch('/api/service').then(r => r.json()).then(d => {
      const s = d.service || null;
      setService(s);
      try { if (s) localStorage.setItem('hb_service', JSON.stringify(s)); else localStorage.removeItem('hb_service'); } catch (e) { /* not kept */ }
      if (s) {
        document.title = s.name;
        const root = document.documentElement;
        root.style.setProperty('--accent', s.accent);
        root.style.setProperty('--accent-light', s.accent);
        const meta = document.querySelector('meta[name="description"]');
        if (meta && s.tagline) meta.setAttribute('content', s.name + ' — ' + s.tagline);
      }
    }).catch(() => {});
  }, []);
  return service;
}

// ── WELLSPRING — the master site ─────────────────────────────────────────────
// No price has been set (Mark, 2026-10-23). Every price on the site reads $xxx until he sets one.
const PRICE_TBD = '$xxx';
const priceText = p => (p && typeof p.monthly === 'number') ? `$${p.monthly}` : PRICE_TBD;

// Exercise programs feed the Cooking & Eating program; Mind and Sleep stand beside them.
const EXERCISE_KEYS = ['yoga', 'workout', 'calisthenics', 'hiking', 'mobility', 'longevity', 'conditions'];
const COOKING_KEY = 'food';

// Faint dotted lines from every exercise card to the Cooking card. Positions are
// measured from the real cards, so the lines follow the layout at any width.
function ConnectorLines({ boxRef, fromRefs, toRef, color }) {
  const [paths, setPaths] = useState({ w: 0, h: 0, d: [] });
  const last = useRef('');
  useLayoutEffect(() => {
    const draw = () => {
      const box = boxRef.current, to = toRef.current;
      if (!box || !to) return;
      const b = box.getBoundingClientRect(), t = to.getBoundingClientRect();
      const right = t.left - b.left > (b.width * 0.5);          // cooking card sits to the right
      const d = [];
      const tx = right ? t.left - b.left : t.left - b.left + t.width / 2;
      const ty = right ? t.top - b.top + t.height / 2 : t.top - b.top;
      fromRefs.current.filter(Boolean).forEach(el => {
        const r = el.getBoundingClientRect();
        const x = right ? r.right - b.left : r.left - b.left + r.width / 2;
        const y = right ? r.top - b.top + r.height / 2 : r.bottom - b.top;
        if (right) {
          const mx = x + (tx - x) * 0.55;
          d.push(`M${x},${y} C${mx},${y} ${mx},${ty} ${tx},${ty}`);
        } else if (y <= ty) {
          d.push(`M${x},${y} C${x},${(y + ty) / 2} ${tx},${(y + ty) / 2} ${tx},${ty}`);
        }
      });
      // Only update when the geometry really changed, or this effect re-renders forever.
      const key = `${Math.round(b.width)}x${Math.round(b.height)}|${d.join(' ')}`;
      if (key === last.current) return;
      last.current = key;
      setPaths({ w: b.width, h: b.height, d });
    };
    draw();
    const ro = new ResizeObserver(draw);
    if (boxRef.current) ro.observe(boxRef.current);
    window.addEventListener('resize', draw);
    const t = setTimeout(draw, 300);   // fonts and the services fetch settle after first paint
    return () => { ro.disconnect(); window.removeEventListener('resize', draw); clearTimeout(t); };
  });
  return (
    <svg width={paths.w} height={paths.h} aria-hidden="true"
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible', zIndex: 0 }}>
      {paths.d.map((d, i) => (
        <path key={i} d={d} fill="none" stroke={color} strokeOpacity="0.38" strokeWidth="2"
          strokeDasharray="1 7" strokeLinecap="round" />
      ))}
    </svg>
  );
}

// ── HEALTHYBODY — the master site ────────────────────────────────────────────
function MasterLanding({ service }) {
  const nav = useNavigate();
  const [data, setData] = useState({ services: [], bundle: null });
  const [picked, setPicked] = useState([]);
  const flowRef = useRef(null);
  const cookRef = useRef(null);
  const exRefs = useRef([]);

  useEffect(() => {
    fetch('/api/services').then(r => r.json()).then(d => setData(d)).catch(() => {});
  }, []);

  const A = service?.accent || '#0F6B6B';
  const modules = data.services.filter(s => s.kind === 'module');
  const exercise = EXERCISE_KEYS.map(k => modules.find(m => m.key === k)).filter(Boolean);
  const cooking = modules.find(m => m.key === COOKING_KEY);
  const others = modules.filter(m => !EXERCISE_KEYS.includes(m.key) && m.key !== COOKING_KEY);
  const chosen = modules.filter(m => picked.includes(m.key));
  const chosenExercise = exercise.filter(m => picked.includes(m.key));
  // Cooking & Eating is included with ANY other program (exercise, Mind or Sleep).
  const chosenOther = modules.filter(m => picked.includes(m.key) && m.key !== COOKING_KEY);
  const toggle = k => setPicked(p => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);

  const sectionPad = { padding: 'clamp(64px, 9vw, 108px) 24px' };
  const wrap = { maxWidth: 1180, margin: '0 auto' };
  const stepLabel = (n, text, color) => (
    <p style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2.2, textTransform: 'uppercase', color, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 26, height: 26, borderRadius: '50%', background: color, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, letterSpacing: 0 }}>{n}</span>
      {text}
    </p>
  );

  const card = (m, opts = {}) => {
    const on = picked.includes(m.key);
    const soon = m.status === 'building';
    return (
      <div key={m.key} ref={opts.innerRef} className="module-card"
        style={{
          position: 'relative', zIndex: 1,
          background: on ? `${m.accent}0F` : '#fff',
          border: `2px solid ${on ? m.accent : 'rgba(0,0,0,0.09)'}`,
          borderRadius: 16, padding: opts.big ? '30px 26px' : '20px 18px', opacity: soon ? 0.62 : 1,
          boxShadow: opts.big ? `0 10px 34px ${m.accent}22` : 'none',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <span style={{ fontSize: opts.big ? 52 : 32 }}>{m.icon}</span>
          {soon
            ? <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.4, color: '#8A5A00', background: '#FFF4DC', padding: '6px 12px', borderRadius: 20 }}>SOON</span>
            : <a href={`https://${m.host}`} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: m.accent, color: '#fff', textDecoration: 'none',
                padding: '10px 16px', borderRadius: 10, fontSize: 14, fontWeight: 700,
                boxShadow: `0 3px 10px ${m.accent}44`,
              }}>Visit site →</a>}
        </div>
        <h3 style={{ fontFamily: 'Oswald', fontSize: opts.big ? 24 : 18, fontWeight: 600, color: '#101418' }}>{opts.title || m.name}</h3>
        <p className="module-blurb" style={{ fontSize: 13.5, color: '#5B6470', lineHeight: 1.55, margin: '8px 0 12px' }}>{m.blurb}</p>
        {opts.children}
        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          {opts.included
            ? <p style={{ fontFamily: 'Bebas Neue', fontSize: 27, color: m.accent, letterSpacing: 0.5 }}>Included</p>
            : <p style={{ fontFamily: 'Bebas Neue', fontSize: 27, color: m.accent, letterSpacing: 0.5 }}>
                {priceText(m.price)}<span style={{ fontSize: 13, color: '#6B7280', fontFamily: 'Poppins' }}>/mo{opts.alone ? ' on its own' : ''}</span>
              </p>}
          {!soon && !opts.included && (
            <button type="button" onClick={() => toggle(m.key)}
              aria-label={on ? `Remove ${m.name}` : `Add ${m.name}`}
              style={{
                cursor: 'pointer', font: 'inherit', padding: '11px 20px', borderRadius: 10,
                border: `2px solid ${m.accent}`,
                background: on ? m.accent : '#fff', color: on ? '#fff' : m.accent,
                fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap',
              }}>{on ? '✓ Added' : '+ Add'}</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh' }}>
      {/* Hero */}
      <section style={{ ...sectionPad, background: `linear-gradient(170deg, ${A}0D 0%, #ffffff 62%)`, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <div style={wrap}>
          <p style={{ fontFamily: 'Oswald', fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', color: A, marginBottom: 20 }}>
            HealthyBody · Whole-person health
          </p>
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(46px, 8vw, 92px)', lineHeight: 0.98, letterSpacing: 1, color: '#101418', maxWidth: 16 + 'ch' }}>
            The whole of your health,<br />
            <span style={{ color: A }}>in one place.</span>
          </h1>
          <p style={{ fontSize: 'clamp(17px, 2vw, 20px)', color: '#4A5158', lineHeight: 1.65, maxWidth: '58ch', marginTop: 24 }}>
            Movement, food, rest and mind are one system. HealthyBody treats them that way —
            and builds every plan around your real medical history, not a template.
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 34 }}>
            <Btn size="lg" onClick={() => nav('/signup')}>Start free →</Btn>
            <Btn size="lg" variant="outline" onClick={() => document.getElementById('pick').scrollIntoView({ behavior: 'smooth' })}>
              Build your plan
            </Btn>
          </div>
          <p style={{ fontSize: 13, color: '#6B7280', marginTop: 18 }}>
            Free health profile · No card to start · Cancel any time
          </p>
        </div>
      </section>

      {/* The one thing that makes this different */}
      <section style={{ ...sectionPad, background: '#F7F9F9' }}>
        <div style={wrap}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(30px, 4.4vw, 50px)', letterSpacing: 1, color: '#101418', textAlign: 'center' }}>
            You answer your health questions once
          </h2>
          <p style={{ textAlign: 'center', color: '#4A5158', fontSize: 17, lineHeight: 1.65, maxWidth: '62ch', margin: '16px auto 0' }}>
            Your conditions, injuries, medications and age shape every plan you own — instantly, everywhere.
            Change one thing and every program you subscribe to updates with it.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginTop: 44 }}>
            {[
              { t: 'Built around your conditions', d: 'Diabetes, blood pressure, heart conditions, cancer recovery, osteoporosis and more — each one changes what we prescribe, not just what we warn you about.' },
              { t: 'Your medications count', d: 'On a beta blocker? Heart-rate zones are meaningless for you, so we stop using them. Most apps never ask.' },
              { t: 'We stop when we should', d: 'Some histories need a doctor before a program, not after. When that is you, we say so and hold the plan.' },
              { t: 'Right for your age', d: 'A plan at 19 and a plan at 71 are not the same plan. Balance and bone loading move to the front when they should.' },
            ].map(c => (
              <div key={c.t} style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '26px 24px' }}>
                <div style={{ width: 34, height: 3, background: A, borderRadius: 2, marginBottom: 16 }} />
                <h3 style={{ fontFamily: 'Oswald', fontSize: 17, fontWeight: 600, color: '#101418', marginBottom: 10 }}>{c.t}</h3>
                <p style={{ fontSize: 14, color: '#5B6470', lineHeight: 1.6 }}>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs: exercise first, cooking connected to all of them */}
      <section id="pick" style={sectionPad}>
        <div style={wrap}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(30px, 4.4vw, 50px)', letterSpacing: 1, color: '#101418', textAlign: 'center' }}>
            Start with how you want to move
          </h2>
          <p style={{ textAlign: 'center', color: '#4A5158', fontSize: 17, margin: '14px auto 0', maxWidth: '60ch' }}>
            Sign up for one or more exercise programs. Every one of them connects to Cooking &amp; Eating,
            so your meals are built for the training you chose. Or take Cooking &amp; Eating on its own.
          </p>

          <div ref={flowRef} className="hb-flow" style={{ position: 'relative', marginTop: 44 }}>
            {cooking && <ConnectorLines boxRef={flowRef} fromRefs={exRefs} toRef={cookRef} color={cooking.accent} />}
            <div className="hb-flow-ex">
              {stepLabel(1, 'Choose your exercise programs', A)}
              <div className="hb-ex-grid">
                {exercise.map((m, i) => card(m, { innerRef: el => { exRefs.current[i] = el; } }))}
              </div>
            </div>
            {cooking && (
              <div className="hb-flow-cook">
                {stepLabel(2, 'Then cooking & eating', cooking.accent)}
                {card(cooking, {
                  big: true, innerRef: cookRef, title: 'Cooking & Eating', included: chosenOther.length > 0, alone: true,
                  children: (
                    <div style={{ background: `${cooking.accent}0D`, border: `1px dashed ${cooking.accent}55`, borderRadius: 10, padding: '10px 12px', margin: '0 0 14px' }}>
                      <p style={{ fontSize: 12.5, color: '#3F4A44', lineHeight: 1.5 }}>
                        {chosenOther.length
                          ? <><strong>Included</strong> with {chosenOther.map(m => m.name).join(', ')}. Nothing extra to pay.</>
                          : <><strong>Included with every program.</strong> Or take it on its own.</>}
                      </p>
                    </div>
                  ),
                })}
              </div>
            )}
          </div>

          {others.length > 0 && (
            <div style={{ marginTop: 52 }}>
              <p style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2.2, textTransform: 'uppercase', color: '#5B6470', marginBottom: 14 }}>
                Also available: rest and mind
              </p>
              <div className="hb-ex-grid">{others.map(m => card(m))}</div>
            </div>
          )}

          {/* Selection summary: no price has been set yet */}
          <div style={{
            marginTop: 30, background: '#101418', color: '#fff', borderRadius: 18,
            padding: 'clamp(24px, 4vw, 38px)', display: 'flex', gap: 26, flexWrap: 'wrap',
            alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ flex: '1 1 300px' }}>
              <p style={{ fontFamily: 'Oswald', fontSize: 12, letterSpacing: 2.4, textTransform: 'uppercase', color: '#8C949C' }}>
                {chosen.length === 0 ? 'Nothing picked yet' : `${chosen.length} selected`}
              </p>
              <p style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(34px, 5vw, 50px)', lineHeight: 1.05, marginTop: 6 }}>
                {chosen.length === 0 && chosenOther.length === 0
                  ? 'Pick what fits your life'
                  : <>{PRICE_TBD}<span style={{ fontSize: 18, fontFamily: 'Poppins', color: '#8C949C' }}>/mo</span></>}
              </p>
              <p style={{ fontSize: 14, color: '#AEB6BE', marginTop: 10, lineHeight: 1.6 }}>
                {chosen.length === 0
                  ? 'Choose one or more exercise programs, then Cooking & Eating. Pricing is being finalized.'
                  : chosenOther.length
                    ? `${chosenOther.map(c => c.name).join(' · ')} · Cooking & Eating included. Pricing is being finalized.`
                    : 'Cooking & Eating on its own. Pricing is being finalized.'}
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Btn size="lg" onClick={() => nav('/signup')}>Start free →</Btn>
              <span style={{ fontSize: 12, color: '#8C949C', textAlign: 'center' }}>Health profile first. No card.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Honest boundary */}
      <section style={{ ...sectionPad, background: '#F7F9F9', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <div style={{ ...wrap, maxWidth: 760 }}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(26px, 3.6vw, 40px)', letterSpacing: 1, color: '#101418' }}>
            What we are, and what we are not
          </h2>
          <p style={{ fontSize: 16, color: '#4A5158', lineHeight: 1.75, marginTop: 16 }}>
            We are a fitness, nutrition and wellbeing service. We are not doctors, dietitians or therapists,
            and nothing here is medical advice. Before you start any plan, check with your own doctor that
            you are well enough for it — especially if you live with a heart condition, high blood pressure,
            diabetes, cancer, or anything else that changes what is safe for you.
          </p>
          <p style={{ fontSize: 16, color: '#4A5158', lineHeight: 1.75, marginTop: 14 }}>
            If something in your history means a plan could hurt you, we will hold it and tell you to talk to
            your doctor first. That is on purpose. And if you are ever in crisis, please call or text{' '}
            <strong style={{ color: '#101418' }}>988</strong> in the US, any time.
          </p>
        </div>
      </section>
    </div>
  );
}

function Landing() {
  const service = useService();
  if (!service) return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.gray }}>Loading…</div>;
  return service.kind === 'master' ? <MasterLanding service={service} /> : <ModuleLanding service={service} />;
}


// Hero content per front door. A new service without an entry still works —
// it falls back to the registry's own name, tagline and blurb.
const SERVICE_CONTENT = {
  yoga: {
    words: ['MOVE.', 'BREATHE.', 'RESTORE.', 'BELONG.'],
    lead: "No shame. No judgment. Just a yoga practice built specifically for <em>you</em> — wherever you're starting from.",
    sub: 'Personalized yoga sessions, breathwork, nutrition guidance, and the support you need to feel strong, flexible, and calm.',
    photos: [`${process.env.PUBLIC_URL}/images/instructor-meditation.jpg`, `${process.env.PUBLIC_URL}/images/pose-arm-balance.jpg`],
    captions: ['Seated Meditation', 'Breath and stillness', 'ARM BALANCE'],
  },
  workout: {
    words: ['LIFT.', 'PUSH.', 'RECOVER.', 'REPEAT.'],
    lead: "No shame. No judgment. Strength training built around <em>your</em> body, your equipment and your health history.",
    sub: 'A four-week plan that adjusts to the gear you actually have, the time you actually get, and the conditions you actually live with.',
    photos: [`${process.env.PUBLIC_URL}/images/workout-hero-row.jpg`, `${process.env.PUBLIC_URL}/images/workout-deadlift.jpg`],
    captions: ['Strength Training', 'Built for your body', 'PROGRESSIVE'],
  },
  food: {
    words: ['COOK.', 'EAT.', 'ENJOY.', 'REPEAT.'],
    lead: "Real food, real meals. A plan built around how <em>you</em> actually cook and what your body actually needs.",
    sub: 'Meal plans matched to your calories and macros, a recipe library, a food diary, an automatic shopping list, and cooking classes.',
    photos: [`${process.env.PUBLIC_URL}/images/cook-modern-kitchen.jpg`, `${process.env.PUBLIC_URL}/images/cook-chopping.jpg`],
    captions: ['Real Food', 'Cooked by real people', 'MEAL PLANS'],
  },
  mind: {
    words: ['BREATHE.', 'NOTICE.', 'STEADY.', 'CARRY ON.'],
    lead: "The other half of being healthy. Practical daily tools for stress, mood and focus — built for <em>your</em> week.",
    sub: 'Breathing practices, thought records, mood tracking and a weekly structure. Plus a clear, honest path to a professional when that is what you need.',
    photos: [`${process.env.PUBLIC_URL}/images/mind-meditation.jpg`, `${process.env.PUBLIC_URL}/images/mind-stretch.jpg`],
    captions: ['Daily Practice', 'Five minutes counts', 'GROUNDING'],
  },
  mobility: {
    words: ['ASSESS.', 'LOAD.', 'PROGRESS.', 'MOVE ON.'],
    lead: "Work on the thing that hurts. Staged eight-week tracks for the joint that is actually bothering <em>you</em>.",
    sub: 'Knee, shoulder, back, hip, neck and ankle tracks — with clear pain rules and a red-flag list so you know when to see a professional instead.',
    photos: [`${process.env.PUBLIC_URL}/images/pose-forward-fold.jpg`, `${process.env.PUBLIC_URL}/images/workout-squat-rack.jpg`],
    captions: ['Rehab Tracks', 'Staged, not random', '8 WEEKS'],
  },
  conditions: {
    words: ['LIVE.', 'MOVE.', 'MANAGE.', 'THRIVE.'],
    lead: "Built for your condition, not despite it. Programs written around the limits <em>your</em> doctor set.",
    sub: 'Type 2 diabetes, heart conditions, high blood pressure, arthritis, osteoporosis and cancer — movement, eating and monitoring for each.',
    photos: [`${process.env.PUBLIC_URL}/images/conditions-bp.jpg`, `${process.env.PUBLIC_URL}/images/workout-treadmill.jpg`],
    captions: ['Living Well', 'Inside your limits', 'DOCTOR-LED'],
  },
  sleep: {
    words: ['WIND DOWN.', 'SLEEP.', 'RECOVER.', 'REPEAT.'],
    lead: "Fix the thing that fixes everything else. A wind-down that <em>you</em> will actually keep.",
    sub: 'Nightly logging, a routine that holds, caffeine and light timing, and training load matched to what you actually slept.',
    photos: [`${process.env.PUBLIC_URL}/images/mind-meditation.jpg`, `${process.env.PUBLIC_URL}/images/pose-forward-fold.jpg`],
    captions: ['Sleep', 'The highest-leverage habit', 'NIGHTLY'],
  },
  longevity: {
    words: ['STEADY.', 'STRONG.', 'STANDING.', 'INDEPENDENT.'],
    lead: "Falls, not workouts, are what end independence. This is built to keep <em>you</em> on your feet.",
    sub: 'Balance and fall prevention first, then leg and grip strength and safe bone loading. Every movement has a seated option.',
    photos: [`${process.env.PUBLIC_URL}/images/workout-home-dumbbell.jpg`, `${process.env.PUBLIC_URL}/images/pose-forward-fold.jpg`],
    captions: ['Strong at 65+', 'Independence is the goal', 'BALANCE'],
  },
  // These two had no entry and fell back to the yoga headline (found 2026-10-23).
  calisthenics: {
    words: ['PUSH.', 'PULL.', 'HOLD.', 'LEVEL UP.'],
    lead: "Your own bodyweight, earned one rung at a time. Progressions built around <em>your</em> joints and your starting point.",
    sub: 'Pull-ups, dips, pistol squats and levers, reached through five skill ladders you earn with clean repetitions, not skip past.',
    photos: [`${process.env.PUBLIC_URL}/images/workout-pushups.jpg`, `${process.env.PUBLIC_URL}/images/workout-home-dumbbell.jpg`],
    captions: ['Skill Ladders', 'Earned, not skipped', '5 LADDERS'],
  },
  hiking: {
    words: ['WALK.', 'CLIMB.', 'EXPLORE.', 'REPEAT.'],
    lead: "Ten minutes on a pavement is where this starts. One ladder built around <em>your</em> health, all the way to a long day on a trail.",
    sub: 'Six stages from a short daily walk to big trail days, with foot care, warning signs, and almost all of the health benefit at the bottom of the ladder.',
    photos: [`${process.env.PUBLIC_URL}/images/workout-treadmill.jpg`, `${process.env.PUBLIC_URL}/images/mind-stretch.jpg`],
    captions: ['Walking & Hiking', 'Start with ten minutes', '6 STAGES'],
  },
};

// ── PER-SERVICE SALES PAGE ───────────────────────────────────────────────────
// Shown below the hero on every module front door. Content comes from
// service-pages.js so a new service is a data entry, not a new component.
function ServiceSell({ service }) {
  const nav = useNavigate();
  const P = SERVICE_PAGES[service.key];
  if (!P) return null;
  const A = service.accent;
  const pad = { padding: 'clamp(60px, 8vw, 96px) 24px' };
  const wrap = { maxWidth: 1060, margin: '0 auto' };

  return (
    <>
      {/* The problem */}
      <section style={{ ...pad, background: '#101418' }}>
        <div style={{ ...wrap, maxWidth: 780 }}>
          <p style={{ fontFamily: 'Oswald', fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', color: A, marginBottom: 18 }}>
            Why this exists
          </p>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(32px, 5.2vw, 58px)', lineHeight: 1.05, letterSpacing: 1, color: '#fff' }}>
            {P.hook}
          </h2>
          <p style={{ fontSize: 'clamp(16px, 1.9vw, 19px)', color: '#AEB6BE', lineHeight: 1.7, marginTop: 20 }}>{P.hookBody}</p>
          <div style={{ borderLeft: `3px solid ${A}`, paddingLeft: 20, marginTop: 30 }}>
            <p style={{ fontSize: 'clamp(16px, 1.9vw, 19px)', color: '#fff', lineHeight: 1.65, fontWeight: 500 }}>{P.promise}</p>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section style={pad}>
        <div style={wrap}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(30px, 4.4vw, 50px)', letterSpacing: 1, color: '#101418', textAlign: 'center' }}>
            What you get
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 20, marginTop: 40 }}>
            {P.youGet.map(g => (
              <div key={g.t} style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.09)', borderRadius: 14, padding: '24px 22px' }}>
                <div style={{ width: 30, height: 3, background: A, borderRadius: 2, marginBottom: 14 }} />
                <h3 style={{ fontFamily: 'Oswald', fontSize: 17, fontWeight: 600, color: '#101418', marginBottom: 8 }}>{g.t}</h3>
                <p style={{ fontSize: 14, color: '#5B6470', lineHeight: 1.6 }}>{g.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      <section style={{ padding: '46px 24px', background: `${A}0E`, borderTop: `1px solid ${A}22`, borderBottom: `1px solid ${A}22` }}>
        <div style={{ ...wrap, display: 'grid', gridTemplateColumns: `repeat(${P.proof.length}, 1fr)`, gap: 18, textAlign: 'center' }}>
          {P.proof.map(p => (
            <div key={p.l}>
              <p style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(30px, 4.6vw, 46px)', color: A, lineHeight: 1 }}>{p.n}</p>
              <p style={{ fontSize: 13, color: '#5B6470', marginTop: 6, lineHeight: 1.4 }}>{p.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section style={{ ...pad, background: '#F7F9F9' }}>
        <div style={wrap}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(30px, 4.4vw, 50px)', letterSpacing: 1, color: '#101418', textAlign: 'center' }}>
            How it works
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 20, marginTop: 40 }}>
            {P.how.map(h => (
              <div key={h.n}>
                <p style={{ fontFamily: 'Bebas Neue', fontSize: 34, color: `${A}66`, lineHeight: 1 }}>{h.n}</p>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, color: '#101418', margin: '8px 0 8px' }}>{h.t}</h3>
                <p style={{ fontSize: 14, color: '#5B6470', lineHeight: 1.6 }}>{h.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Honest fit */}
      <section style={pad}>
        <div style={{ ...wrap, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 26 }}>
          <div style={{ background: '#fff', border: `2px solid ${A}33`, borderRadius: 16, padding: '28px 26px' }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600, color: '#101418', marginBottom: 16 }}>This is for you if…</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
              {P.forYou.map(f => (
                <li key={f} style={{ display: 'flex', gap: 10, fontSize: 14.5, color: '#3F4750', lineHeight: 1.55 }}>
                  <span style={{ color: A, flexShrink: 0, fontWeight: 700 }}>✓</span>{f}
                </li>
              ))}
            </ul>
          </div>
          <div style={{ background: '#FAFAFA', border: '2px solid rgba(0,0,0,0.08)', borderRadius: 16, padding: '28px 26px' }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600, color: '#101418', marginBottom: 16 }}>This is not for you if…</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 11 }}>
              {P.notForYou.map(f => (
                <li key={f} style={{ display: 'flex', gap: 10, fontSize: 14.5, color: '#6B7280', lineHeight: 1.55 }}>
                  <span style={{ color: '#9AA4AE', flexShrink: 0, fontWeight: 700 }}>—</span>{f}
                </li>
              ))}
            </ul>
            <p style={{ fontSize: 13, color: '#8A929A', marginTop: 16, lineHeight: 1.55, fontStyle: 'italic' }}>
              We would rather tell you now than take your money.
            </p>
          </div>
        </div>
      </section>

      {/* Crisis / boundary notes where a service needs one */}
      {(P.crisis || P.boundary) && (
        <section style={{ padding: '10px 24px 30px' }}>
          <div style={{ ...wrap, maxWidth: 780, background: '#FBEEED', border: '1px solid #E4B9B5', borderLeft: '4px solid #B3261E', borderRadius: '0 10px 10px 0', padding: '18px 22px' }}>
            <p style={{ fontSize: 14.5, color: '#5A2320', lineHeight: 1.65 }}>{P.crisis || P.boundary}</p>
          </div>
        </section>
      )}

      {/* Add this service */}
      <section style={{ ...pad, background: '#101418' }}>
        <div style={{ ...wrap, maxWidth: 720, textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(30px, 4.6vw, 52px)', letterSpacing: 1, color: '#fff' }}>
            Add {service.name} to your plan
          </h2>
          <p style={{ fontSize: 17, color: '#AEB6BE', lineHeight: 1.65, marginTop: 14 }}>
            {priceText(service.price)} a month on its own, or included in HealthyBody with every other module.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginTop: 28 }}>
            <Btn size="lg" onClick={() => nav('/signup')}>Add {service.name} →</Btn>
            <a href={`${process.env.PUBLIC_URL}/`} style={{
              display: 'inline-flex', alignItems: 'center', padding: '14px 26px', borderRadius: 8,
              border: '2px solid rgba(255,255,255,0.28)', color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: 15,
            }}>See all modules</a>
          </div>
          <p style={{ fontSize: 13, color: '#6B7280', marginTop: 18 }}>
            Health profile first · No card to start · Cancel any time
          </p>
        </div>
      </section>
    </>
  );
}

function GlobalBackBar() {
  const service = useService();
  // Only on the module front doors — the master site is already home.
  if (!service || service.kind === 'master') return null;
  return (
    <a href={`${process.env.PUBLIC_URL}/`} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
      background: '#101418', color: '#fff', textDecoration: 'none',
      padding: '11px 16px', fontSize: 14, fontWeight: 600, position: 'relative', zIndex: 60,
    }}>
      <span style={{ fontSize: 17, lineHeight: 1 }}>←</span>
      Back to HealthyBody
      <span style={{ color: '#8C949C', fontWeight: 400 }}>· all modules</span>
    </a>
  );
}

function BackToWellspring({ service }) {
  if (!service || service.kind === 'master') return null;
  return (
    <a href={`${process.env.PUBLIC_URL}/`} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
      background: '#101418', color: '#fff', textDecoration: 'none',
      padding: '11px 16px', fontSize: 14, fontWeight: 600,
    }}>
      <span style={{ fontSize: 17 }}>&#8592;</span> Back to HealthyBody &middot; all modules
    </a>
  );
}

function CookingClasses({ service }) {
  const A = service.accent;
  const cooks = [
    { img: `${process.env.PUBLIC_URL}/images/cook-modern-kitchen.jpg`, dish: 'Sheet Pan Salmon + Veg', mins: 30, label: 'Weeknight dinners' },
    { img: `${process.env.PUBLIC_URL}/images/cook-chopping.jpg`, dish: 'High-Protein Greek Bowl', mins: 15, label: 'Fast lunches' },
    { img: `${process.env.PUBLIC_URL}/images/cook-tasting.jpg`, dish: 'Overnight Oats Power Jar', mins: 5, label: 'Breakfasts' },
    // Home kitchens only (Mark + Brody 2026-10-26): no restaurant or chef imagery.
    { img: `${process.env.PUBLIC_URL}/images/cook-chopping.jpg`, dish: 'Batch Cook Sunday', mins: 60, label: 'Meal prep' },
    { img: `${process.env.PUBLIC_URL}/images/cook-tasting.jpg`, dish: 'Egg White Veggie Scramble', mins: 13, label: 'Breakfasts' },
    { img: `${process.env.PUBLIC_URL}/images/cook-modern-kitchen.jpg`, dish: 'Apple Almond Protein Snack', mins: 2, label: 'Snacks' },
  ];
  return (
    <section style={{ padding: 'clamp(60px, 8vw, 96px) 24px', background: '#F7F9F9' }}>
      <div style={{ maxWidth: 1060, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 42 }}>
          <p style={{ fontFamily: 'Oswald', fontSize: 12, letterSpacing: 3, textTransform: 'uppercase', color: A, marginBottom: 14 }}>
            Cooking classes
          </p>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(30px, 4.4vw, 50px)', letterSpacing: 1, color: '#101418' }}>
            Watch someone cook it first
          </h2>
          <p style={{ color: '#4A5158', fontSize: 17, lineHeight: 1.65, maxWidth: '58ch', margin: '14px auto 0' }}>
            Every meal in your plan has a video, made by real women cooking in their own home kitchens. No chefs, no restaurant equipment, and no assumption that you already know what a term means.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {cooks.map((c, i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.09)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ position: 'relative', aspectRatio: '16/10', background: '#eee' }}>
                <img src={c.img} alt={'Cooking ' + c.dish} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent 55%)' }} />
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                  width: 54, height: 54, borderRadius: '50%', background: 'rgba(255,255,255,0.92)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: A,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.28)',
                }}>&#9654;</div>
                <span style={{
                  position: 'absolute', bottom: 10, right: 10, background: 'rgba(0,0,0,0.72)', color: '#fff',
                  fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20,
                }}>{c.mins} min</span>
              </div>
              <div style={{ padding: '15px 17px 18px' }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: A, marginBottom: 6 }}>{c.label}</p>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, color: '#101418' }}>{c.dish}</h3>
              </div>
            </div>
          ))}
        </div>
        <p style={{ textAlign: 'center', color: '#6B7280', fontSize: 13.5, marginTop: 26, fontStyle: 'italic' }}>
          Videos unlock when you join. Your plan decides which classes show up first.
        </p>
      </div>
    </section>
  );
}

function ModuleLanding({ service }) {
  const nav = useNavigate();
  const C = SERVICE_CONTENT[service?.key] || SERVICE_CONTENT.yoga;
  const heroStat = (SERVICE_PAGES[service?.key]?.proof || [])[0] || { n: '4', l: 'Week Program' };
  const heroPhotos = C.photos;
  const { user } = useAuth();
  const [bioSettings, setBioSettings] = useState({
    instructor_bio_text: "This studio was built for real people: stiff hips, busy schedules, old injuries, and all. Every program starts with your health history, so the poses, the pace, and the breathwork fit your body instead of forcing your body to fit the class.\nYou don't need to be flexible to begin. You need to show up and breathe. We'll build the rest together, one practice at a time.",
    instructor_bio_video_url: '',
  });

  useEffect(() => {
    fetch('/api/public/about').then(r => r.json()).then(d => {
      if (d.settings) setBioSettings(prev => ({ ...prev, ...d.settings }));
    }).catch(() => {});
  }, []);

  // No auto-redirect — let visitors browse the landing page freely

  const pillars = [
    { icon: '🧘', title: 'Practice with Purpose', desc: 'A personalized yoga program built around your body, your health, and your life. Every session has a reason.' },
    { icon: '🥗', title: 'Eat Right', desc: 'Real food, real macros. Meal guidance that fits your lifestyle without starvation or fad diets.' },
    { icon: '🌙', title: 'Rest & Restore', desc: 'Rest is part of the practice. Restorative poses, breathwork, and good sleep help your body absorb every session.' },
    { icon: '🌬️', title: 'Find Stillness', desc: 'Breath and meditation build a calmer, clearer mind that you carry with you off the mat.' },
  ];

  const stats = [
    { value: '4-Week', label: 'Custom Programs' },
    { value: '100%', label: 'Personalized' },
    { value: 'Zero', label: 'Judgment Zone' },
    { value: '∞', label: 'Encouragement' },
  ];

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh' }}>
      <BackToWellspring service={service} />
      {/* ── HERO ── */}
      <section style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        {/* Background gradient layers */}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 70% 50%, rgba(255,69,0,0.07) 0%, transparent 60%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 20% 80%, rgba(255,184,0,0.05) 0%, transparent 50%)' }} />

        {/* Grid lines decoration */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />

        <div className="resp-2col" style={{ position: 'relative', zIndex: 2, maxWidth: 1200, margin: '0 auto', padding: '120px 32px 80px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
          {/* Left — text */}
          <div className="animate-fadeInUp">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 20, background: `${T.orange}15`, border: `1px solid ${T.orange}33`, marginBottom: 24 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.orange, animation: 'pulse 2s infinite' }} />
              <span style={{ fontSize: 12, color: T.orange, fontWeight: 600, letterSpacing: 1 }}>ACCEPTING NEW REBUILDERS</span>
            </div>

            <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(64px, 8vw, 110px)', lineHeight: 0.95, letterSpacing: 2, marginBottom: 16 }}>
              <span style={{ display: 'block', color: '#111111' }}>{C.words[0]}</span>
              <span style={{ display: 'block', color: '#111111' }}>{C.words[1]}</span>
              <span style={{ display: 'block', color: service.accent }}>{C.words[2]}</span>
              <span style={{ display: 'block', color: service.accent }}>{C.words[3]}</span>
            </h1>

            <p style={{ fontSize: 18, color: '#333333', lineHeight: 1.7, marginBottom: 12, maxWidth: 480 }}>
              <span dangerouslySetInnerHTML={{ __html: C.lead }} />
            </p>
            <p style={{ fontSize: 16, color: '#555555', lineHeight: 1.6, marginBottom: 40, maxWidth: 460 }}>
              {C.sub}
            </p>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Btn size="lg" onClick={() => nav('/signup')}>Start My Free Program →</Btn>
              <Btn variant="outline" size="lg" onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}>Our Story</Btn>
            </div>

            <p style={{ marginTop: 20, fontSize: 13, color: '#666666' }}>✓ Free personalized program &nbsp;&nbsp; ✓ No credit card needed &nbsp;&nbsp; ✓ Start today</p>
          </div>

          {/* Right — photos */}
          <div className="animate-fadeInUp animate-delay-2" style={{ position: 'relative' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Photo slot 1 */}
              <div style={{
                aspectRatio: '3/5', borderRadius: 20, overflow: 'hidden', position: 'relative',
                border: '1px solid rgba(0,0,0,0.1)', marginTop: 60,
                background: '#eeeeee',
              }}>
                <img
                  src={heroPhotos[0]}
                  alt="Male yoga instructor in seated meditation"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
                <div style={{ display: 'none', position: 'absolute', inset: 0, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#eeeeee', gap: 8 }}>
                  <div style={{ fontSize: 48 }}>🧘</div>
                  <p style={{ fontSize: 12, color: T.gray, textAlign: 'center', padding: '0 16px' }}>Add photo</p>
                </div>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', padding: '24px 16px 16px' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#ffffff' }}>{C.captions[0]}</p>
                  <p style={{ fontSize: 11, color: T.orange }}>{C.captions[1]}</p>
                </div>
              </div>

              {/* Photo slot 2 */}
              <div style={{
                aspectRatio: '3/5', borderRadius: 20, overflow: 'hidden', position: 'relative',
                border: '1px solid rgba(0,0,0,0.1)',
                background: '#eeeeee',
              }}>
                <img
                  src={heroPhotos[1]}
                  alt="Male yoga instructor in an arm balance"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
                <div style={{ display: 'none', position: 'absolute', inset: 0, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#eeeeee', gap: 8 }}>
                  <div style={{ fontSize: 48 }}>🧘</div>
                  <p style={{ fontSize: 12, color: T.gray, textAlign: 'center', padding: '0 16px' }}>Add photo</p>
                </div>
                <div style={{ position: 'absolute', bottom: 12, right: 12 }}>
                  <Badge color={T.gold}>{C.captions[2]}</Badge>
                </div>
              </div>
            </div>

            {/* Floating stat cards */}
            <div className="hero-stat" style={{ position: 'absolute', top: 20, left: -20, background: 'rgba(255,255,255,0.95)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', backdropFilter: 'blur(12px)', borderRadius: 12, padding: '12px 16px', minWidth: 120 }}>
              <p style={{ fontSize: 24, fontFamily: 'Bebas Neue', color: service.accent }}>{heroStat.n}</p>
              <p style={{ fontSize: 11, color: '#666666' }}>{heroStat.l}</p>
            </div>
            <div className="hero-stat" style={{ position: 'absolute', bottom: 40, right: -20, background: 'rgba(255,255,255,0.95)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', backdropFilter: 'blur(12px)', borderRadius: 12, padding: '12px 16px', minWidth: 140 }}>
              <p style={{ fontSize: 24, fontFamily: 'Bebas Neue', color: T.success }}>4-Week</p>
              <p style={{ fontSize: 11, color: '#666666' }}>Custom Program</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section style={{ background: `linear-gradient(90deg, ${T.orange}11, ${T.gold}11)`, borderTop: '1px solid rgba(0,0,0,0.1)', borderBottom: '1px solid rgba(0,0,0,0.1)', padding: '24px 32px' }}>
        <div className="resp-stats" style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, textAlign: 'center' }}>
          {stats.map(s => (
            <div key={s.label}>
              <p style={{ fontFamily: 'Bebas Neue', fontSize: 36, ...gradText, lineHeight: 1 }}>{s.value}</p>
              <p style={{ fontSize: 13, color: '#666666', marginTop: 4 }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Per-service sales page */}
      <ServiceSell service={service} />
      {service.key === 'food' && <CookingClasses service={service} />}

      {/* ── The original yoga-specific page below this point ── */}
      {service.key === 'yoga' && (<>
      {/* ── 4 PILLARS ── */}
      <section style={{ padding: '100px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <Badge color={T.orange}>THE PROGRAM</Badge>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(40px, 5vw, 64px)', marginTop: 16, letterSpacing: 2 }}>4 PILLARS OF WHOLE-BODY WELLNESS</h2>
          <p style={{ color: '#555555', fontSize: 17, marginTop: 12, maxWidth: 500, margin: '12px auto 0' }}>Everything you need. Nothing you don't. A complete approach built on what actually works.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
          {pillars.map((p, i) => (
            <Card key={p.title} style={{ textAlign: 'center', padding: 36 }}>
              <div style={{ fontSize: 52, marginBottom: 20 }}>{p.icon}</div>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 12, color: '#111111' }}>{p.title}</h3>
              <p style={{ color: '#555555', fontSize: 15, lineHeight: 1.7 }}>{p.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* ── GLP-1 & INCLUSION ── */}
      <section style={{ padding: '100px 32px', background: '#f0f7f0', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 60 }}>
            <Badge color={T.success}>ALL ARE WELCOME HERE</Badge>
            <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(40px, 5vw, 60px)', marginTop: 16, letterSpacing: 2 }}>NO SHAME. NO JUDGMENT. JUST PROGRESS.</h2>
            <p style={{ color: '#444444', fontSize: 17, marginTop: 16, maxWidth: 640, margin: '16px auto 0', lineHeight: 1.7 }}>
              {service?.name || 'HealthyBody'} was built for real people — at every size, every age, and every level of flexibility. You belong here from day one.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginBottom: 40 }}>
            <Card style={{ padding: 32, borderLeft: `4px solid ${T.success}` }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>🤝</div>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, marginBottom: 12, color: '#111111' }}>No Body Shaming — Period</h3>
              <p style={{ color: '#555555', fontSize: 15, lineHeight: 1.7 }}>Every body is a starting point, not a judgment. We celebrate where you are and cheer where you're going. This is a shame-free zone, always.</p>
            </Card>
            <Card style={{ padding: 32, borderLeft: `4px solid ${T.orange}` }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>💊</div>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, marginBottom: 12, color: '#111111' }}>On a GLP-1? We're Perfect Together</h3>
              <p style={{ color: '#555555', fontSize: 15, lineHeight: 1.7 }}>Taking Ozempic, Wegovy, Mounjaro, or another GLP-1? Yoga works alongside it: build strength, balance, and mobility while your body changes.</p>
            </Card>
            <Card style={{ padding: 32, borderLeft: `4px solid ${T.gold}` }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✨</div>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, marginBottom: 12, color: '#111111' }}>Every Starting Point Is Valid</h3>
              <p style={{ color: '#555555', fontSize: 15, lineHeight: 1.7 }}>Never tried yoga, haven't moved much in years, recovering from an injury — your program adjusts to meet you exactly where you are right now.</p>
            </Card>
          </div>
          <div style={{ padding: '32px 40px', background: 'rgba(255,255,255,0.9)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 20, display: 'grid', gridTemplateColumns: '1fr auto', gap: 32, alignItems: 'center' }}>
            <div>
              <p style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, color: '#111111', marginBottom: 8 }}>GLP-1 Users: This Program Was Made for You</p>
              <p style={{ color: '#555555', fontSize: 15, lineHeight: 1.7 }}>
                GLP-1 medications are powerful for weight loss — but without regular strength work, a meaningful share of the weight lost can be muscle, not fat.
                Our yoga program is specifically designed to build functional strength, flexibility, and balance while supporting healthy weight and energy levels — a transformation that is real, lasting, and whole-body.
              </p>
            </div>
            <div style={{ flexShrink: 0 }}>
              <Btn onClick={() => nav('/signup')}>Start Free →</Btn>
            </div>
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" style={{ padding: '100px 32px', background: '#f5f5f5' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="resp-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <div style={{ aspectRatio: '4/5', borderRadius: 24, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.1)', background: '#eeeeee' }}>
                <img src="/images/pose-kraunchasana.jpg" alt="Male yoga instructor in Heron Pose" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              </div>
            </div>
            <div>
              <Badge color={T.gold}>YOUR INSTRUCTOR</Badge>
              <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 4vw, 56px)', marginTop: 16, letterSpacing: 2, lineHeight: 1.1 }}>
                PRACTICE WITH<br /><span style={gradText}>INTENTION</span>
              </h2>
              {bioSettings.instructor_bio_text.split('\n').filter(Boolean).map((para, i) => (
                <p key={i} style={{ color: i === 0 ? '#333333' : '#555555', fontSize: i === 0 ? 17 : 16, lineHeight: 1.85, marginTop: i === 0 ? 20 : 12 }}>
                  {para}
                </p>
              ))}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '28px 0' }}>
                {['Hatha & Vinyasa', 'Breathwork', 'Restorative & Yin'].map(t => <Badge key={t} color={T.orange}>{t}</Badge>)}
              </div>
              <Btn size="lg" onClick={() => nav('/signup')}>Begin Your Practice →</Btn>
            </div>
          </div>

          {/* Welcome video — shown if admin has set a video URL */}
          {bioSettings.instructor_bio_video_url && (
            <div style={{ marginTop: 64 }}>
              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <Badge color={T.orange}>VIDEO MESSAGE</Badge>
                <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(28px, 3vw, 40px)', letterSpacing: 2, marginTop: 12 }}>A WORD FROM YOUR INSTRUCTOR</h3>
              </div>
              <div style={{ maxWidth: 760, margin: '0 auto', borderRadius: 20, overflow: 'hidden', boxShadow: '0 16px 48px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.08)' }}>
                {/youtu/.test(bioSettings.instructor_bio_video_url) ? (
                  <iframe
                    src={bioSettings.instructor_bio_video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block' }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Welcome message"
                  />
                ) : (
                  <video src={bioSettings.instructor_bio_video_url} controls style={{ width: '100%', display: 'block' }} />
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── POSE GALLERY (yoga only) ── */}
      {service.key === 'yoga' && (
      <section style={{ padding: '100px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <Badge color={T.success}>WHOLE-BODY PRACTICE</Badge>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(40px, 5vw, 64px)', marginTop: 16, letterSpacing: 2 }}>STRENGTH, FLEXIBILITY, AND CALM</h2>
          <p style={{ color: '#555555', fontSize: 17, marginTop: 12 }}>Every practice is unique. Your program starts where your body is today.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          {[
            { label: 'Flexibility', img: `${process.env.PUBLIC_URL}/images/pose-split-backbend.jpg`, alt: 'Male yoga instructor in a split with a backbend' },
            { label: 'Release', img: `${process.env.PUBLIC_URL}/images/pose-forward-fold.jpg`, alt: 'Male yoga instructor folding forward over his legs' },
            { label: 'Strength', img: `${process.env.PUBLIC_URL}/images/pose-leg-behind-head.jpg`, alt: 'Male yoga instructor in a leg-behind-the-head pose' },
          ].map(({ label, img, alt }) => (
            <div key={label} style={{ borderRadius: 20, overflow: 'hidden', aspectRatio: '4/3', position: 'relative', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', background: '#eeeeee' }}>
              <img src={img} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              <div style={{ position: 'absolute', top: 14, left: 14 }}>
                <span style={{ background: T.success, color: '#fff', fontFamily: 'Oswald', fontWeight: 700, fontSize: 13, letterSpacing: 2, padding: '5px 14px', borderRadius: 30, textTransform: 'uppercase' }}>{label}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      )}

      {/* ── LIBRARY PREVIEWS — only where they belong ── */}
      {service.key === 'yoga' && <ExercisePreview nav={nav} />}
      {(service.key === 'food' || service.key === 'yoga') && <RecipePreview nav={nav} />}

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding: '100px 32px', background: '#f5f5f5' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
          <Badge color={T.orange}>SIMPLE PROCESS</Badge>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(40px, 5vw, 64px)', marginTop: 16, letterSpacing: 2, marginBottom: 60 }}>HOW IT WORKS</h2>
          {[
            { step: '01', title: 'Create Your Free Account', desc: 'Sign up in under 2 minutes. No credit card, no commitment.' },
            { step: '02', title: 'Tell Us About You', desc: 'Your age, goals, health history, and current fitness level. Everything is confidential.' },
            { step: '03', title: 'Get Your Custom Program', desc: 'Instantly receive a 4-week personalized yoga + nutrition plan built for your specific body and goals.' },
            { step: '04', title: 'Practice, Eat, Rest, Repeat', desc: 'Follow the plan, log your practice, and feel the change week by week.' },
          ].map((s, i) => (
            <div key={s.step} style={{ display: 'flex', gap: 32, alignItems: 'flex-start', textAlign: 'left', marginBottom: 48, padding: '0 20px' }}>
              <div style={{ minWidth: 64, height: 64, borderRadius: '50%', background: `linear-gradient(135deg, ${T.orange}, ${T.gold})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Bebas Neue', fontSize: 24, flexShrink: 0 }}>{s.step}</div>
              <div style={{ paddingTop: 8 }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 8, color: '#111111' }}>{s.title}</h3>
                <p style={{ color: '#555555', fontSize: 16, lineHeight: 1.7 }}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PRICING ── */}
      <section style={{ padding: '100px 32px', background: '#ffffff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <Badge color={T.orange}>MEMBERSHIP</Badge>
            <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(40px, 5vw, 64px)', marginTop: 16, letterSpacing: 2 }}>SIMPLE, HONEST PRICING</h2>
            <p style={{ color: '#555555', fontSize: 17, marginTop: 12 }}>No contracts. Cancel any time.</p>
          </div>

          {/* Launch activation fee banner */}
          <div style={{ background: 'rgba(255,69,0,0.05)', border: '1px solid rgba(255,69,0,0.25)', borderRadius: 12, padding: '14px 28px', textAlign: 'center', marginBottom: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 18 }}>🎉</span>
            <span style={{ fontSize: 15, color: '#333333' }}>
              <strong>Launch Special:</strong>{' '}
              <span style={{ textDecoration: 'line-through', color: '#aaaaaa', fontWeight: 600 }}>$99 one-time activation fee</span>{' '}
              <strong style={{ color: T.orange }}>FREE</strong>
              {' '}— no activation fee during our launch period. Offer ends soon.
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {[
              {
                name: 'Foundation', price: PRICE_TBD, period: '/month', annual: `${PRICE_TBD}/year`,
                color: T.orange, featured: false,
                desc: 'Everything you need to start your transformation.',
                features: ['Full 4-week personalized yoga plan', 'Pose library with step-by-step guidance', 'Nutrition plan + macro targets', 'Healthy recipe library', 'Practice logging & streaks', 'Progress photo tracking', 'AI Coach — 3 preview sessions/month', 'Email support'],
              },
              {
                name: 'Coached', price: PRICE_TBD, period: '/month', annual: `${PRICE_TBD}/year`,
                color: T.orange, featured: true,
                desc: 'The full experience — yoga, nutrition, and AI coaching.',
                features: ['Everything in Foundation', 'AI Coach — 25 sessions/month (ask about form, food, recovery, anything)', 'Personalized program updates', 'Full cooking class video library', 'GLP-1 optimization guidance', 'Priority support'],
              },
              {
                name: 'Elite', price: PRICE_TBD, period: '/month', annual: `${PRICE_TBD}/year`,
                color: T.gold, featured: false,
                desc: 'Personal attention from your trainer.',
                features: ['Everything in Coached', 'AI Coach — 75 sessions/month', 'Personal video review from your trainer each month', 'Your trainer reviews your progress and records specific pointers just for you', 'Monthly program customization', 'Direct message access to your trainer', 'Limited to 50 members total'],
              },
            ].map(plan => (
              <div key={plan.name} style={{ borderRadius: 20, overflow: 'hidden', border: `2px solid ${plan.featured ? plan.color : 'rgba(0,0,0,0.1)'}`, boxShadow: plan.featured ? `0 12px 40px rgba(255,69,0,0.15)` : '0 4px 16px rgba(0,0,0,0.06)', background: '#ffffff' }}>
                {plan.featured && (
                  <div style={{ background: `linear-gradient(90deg, ${T.orange}, ${T.gold})`, padding: '8px', textAlign: 'center' }}>
                    <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: 2, color: '#fff' }}>MOST POPULAR</p>
                  </div>
                )}
                <div style={{ padding: 36 }}>
                  <p style={{ fontFamily: 'Bebas Neue', fontSize: 14, letterSpacing: 3, color: plan.color, marginBottom: 8 }}>{plan.name.toUpperCase()}</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'Bebas Neue', fontSize: 56, color: '#111111', lineHeight: 1 }}>{plan.price}</span>
                    <span style={{ fontSize: 16, color: '#777777' }}>{plan.period}</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#888888', marginBottom: 8 }}>or {plan.annual}</p>
                  <p style={{ fontSize: 14, color: '#555555', lineHeight: 1.6, marginBottom: 24, minHeight: 42 }}>{plan.desc}</p>
                  <Btn fullWidth variant={plan.featured ? 'primary' : 'outline'} onClick={() => nav('/signup')}>
                    {plan.featured ? 'Start Free Trial →' : 'Get Started →'}
                  </Btn>
                  <div style={{ marginTop: 28 }}>
                    {plan.features.map(f => (
                      <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                        <span style={{ color: plan.color, fontSize: 14, flexShrink: 0, marginTop: 1 }}>✓</span>
                        <span style={{ fontSize: 13, color: '#444444', lineHeight: 1.5 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── BLACK LABEL — EXCLUSIVE TIER ── */}
          <div style={{ marginTop: 32, borderRadius: 20, overflow: 'hidden', background: 'linear-gradient(135deg, #0c0c0c 0%, #111111 100%)', border: '2px solid rgba(255,184,0,0.5)', boxShadow: '0 16px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,184,0,0.1)' }}>
            <div style={{ background: 'linear-gradient(90deg, #c8960c, #f5c518, #c8960c)', padding: '10px', textAlign: 'center' }}>
              <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 4, color: '#0c0c0c' }}>⚡ EXCLUSIVE — LIMITED TO 10 FOUNDING MEMBERS</p>
            </div>
            <div style={{ padding: '44px 48px', display: 'flex', gap: 56, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 280px' }}>
                <p style={{ fontFamily: 'Bebas Neue', fontSize: 14, letterSpacing: 5, color: T.gold, marginBottom: 12 }}>BLACK LABEL</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontFamily: 'Bebas Neue', fontSize: 72, color: '#ffffff', lineHeight: 1 }}>$199</span>
                  <span style={{ fontSize: 18, color: '#666666' }}>/month</span>
                </div>
                <p style={{ fontSize: 13, color: '#555555', marginBottom: 20 }}>or $1,499/year — save $889</p>
                <p style={{ fontSize: 15, color: '#b0b0b0', lineHeight: 1.75, marginBottom: 32 }}>
                  The most personal coaching relationship possible. Your trainer is in your corner — not as a platform, but as your dedicated yoga guide.
                </p>
                <Btn variant="primary" onClick={() => nav('/signup')}>Apply for Black Label →</Btn>
                <p style={{ fontSize: 12, color: '#444444', marginTop: 12 }}>Acceptance is not guaranteed. Your trainer personally reviews each application.</p>
              </div>
              <div style={{ flex: '1 1 280px' }}>
                {[
                  'Everything in Elite',
                  'AI Coach — 150 sessions/month (highest tier)',
                  'Weekly 30-min 1:1 video call with your trainer',
                  'Custom nutrition plan built by your trainer personally',
                  'Weekly review of your practice logs and daily progress',
                  'Direct text line — 24-hour guaranteed response',
                  'Founding Member rate locked in for life',
                  'First access to all new features and content',
                  'In-person assessment session (Knoxville area)',
                ].map(f => (
                  <div key={f} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 14 }}>
                    <span style={{ color: T.gold, fontSize: 15, flexShrink: 0, marginTop: 2 }}>✦</span>
                    <span style={{ fontSize: 14, color: '#cccccc', lineHeight: 1.55 }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p style={{ textAlign: 'center', color: '#888888', fontSize: 13, marginTop: 32 }}>All plans include a 7-day free trial. Cancel any time — no questions asked.</p>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ padding: '120px 32px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(255,69,0,0.06) 0%, transparent 70%)' }} />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(48px, 7vw, 90px)', letterSpacing: 3, lineHeight: 0.95, marginBottom: 24 }}>
            <span style={{ color: '#111111' }}>YOUR BEST SELF</span><br />
            <span style={gradText}>STARTS TODAY</span>
          </h2>
          <p style={{ fontSize: 18, color: '#444444', marginBottom: 40, lineHeight: 1.7 }}>
            You don't have to figure it out alone. Join {service?.name || 'HealthyBody'} and get a program that meets you exactly where you are — and takes you where you want to go.
          </p>
          <Btn size="lg" onClick={() => nav('/signup')}>Build My Free Program →</Btn>
          <p style={{ color: '#666666', fontSize: 14, marginTop: 20 }}>Takes 3 minutes. Completely free. No judgment — ever.</p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      </>)}

      <footer style={{ borderTop: '1px solid rgba(0,0,0,0.1)', padding: '32px', textAlign: 'center' }}>
        <p style={{ fontFamily: 'Oswald', fontWeight: 600, fontSize: 18, letterSpacing: 0.5, marginBottom: 8 }}>{service.name}</p>
        <p style={{ color: '#666666', fontSize: 14 }}>{service.tagline}</p>
        <p style={{ color: '#666666', fontSize: 12, marginTop: 16 }}>© {new Date().getFullYear()} Mark Siedlecki. All rights reserved.</p>
        <p style={{ marginTop: 10 }}>
          <a href={`${process.env.PUBLIC_URL}/credits`} style={{ color: '#666666', fontSize: 12, textDecoration: 'underline' }}>Photo credits</a>
        </p>
        <LegalLinks />
        <p style={{ color: '#888888', fontSize: 11, marginTop: 8, maxWidth: 600, margin: '8px auto 0' }}>
          This program is for informational purposes only. Always consult a physician before beginning any exercise or nutrition program, especially if you have existing health conditions.
        </p>
      </footer>
    </div>
  );
}

// -- DATE OF BIRTH + PRIVACY POLICY / TERMS (2026-10-31) -------------------------
// Sign-up asks for the date of birth (13 and older; the profile age is worked out from it on the server).
// The Privacy Policy and Terms pages, footer links and the sign-up tick appear only once an admin publishes them
// (Admin > Privacy & Terms, switch legal_pages_published; backend/legal-pages.js). Until then the pages say Coming soon.
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
function ageFromDob(dob) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dob || ''));
  if (!m) return null;
  const now = new Date(), mm = now.getMonth() + 1, dd = now.getDate();
  return now.getFullYear() - +m[1] - (mm < +m[2] || (mm === +m[2] && dd < +m[3]) ? 1 : 0);
}
const UNDER_13_TEXT = 'Thank you for wanting to join HealthyBody. You need to be 13 or older to have an account, so we cannot sign you up yet. We would love to see you back when you turn 13.';
function DobInput({ value, onChange, hint }) {
  const age = ageFromDob(value);
  return (
    <>
      <Input label="Date of Birth" type="date" value={value} onChange={onChange} required
        hint={hint || 'HealthyBody is for people 13 and older. Members under 18 need a parent or guardian to agree.'} />
      {age !== null && age >= 0 && age < 13 && (
        <div role="alert" style={{ background: '#FFF8E8', border: '1px solid #E8D5A8', borderRadius: 8, padding: '12px 16px', marginTop: -8, marginBottom: 16, fontSize: 14, color: '#4A3A15' }}>{UNDER_13_TEXT}</div>
      )}
    </>
  );
}
let legalStatus = null;
function useLegal() {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!legalStatus) legalStatus = api('/api/public/legal').catch(() => ({ published: false }));
    let live = true; legalStatus.then(r => { if (live) setD(r); });
    return () => { live = false; };
  }, []);
  return d || { published: false };
}
// The sign-up tick. Shown and required only when the pages are published.
function LegalAgree({ checked, onChange }) {
  const legal = useLegal();
  if (!legal.published) return null;
  return (
    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, marginBottom: 16, color: T.white }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} required style={{ width: 18, height: 18, marginTop: 2 }} />
      <span>I agree to the <Link to="/terms" target="_blank" style={{ color: T.orange }}>Terms of Service</Link> and the <Link to="/privacy" target="_blank" style={{ color: T.orange }}>Privacy Policy</Link>.</span>
    </label>
  );
}
function LegalLinks({ style }) {
  const legal = useLegal();
  if (!legal.published) return null;
  return (
    <p style={{ marginTop: 10, fontSize: 12, ...style }}>
      <Link to="/privacy" style={{ color: '#666666', textDecoration: 'underline' }}>Privacy Policy</Link>
      <span style={{ color: '#999999' }}> &middot; </span>
      <Link to="/terms" style={{ color: '#666666', textDecoration: 'underline' }}>Terms of Service</Link>
    </p>
  );
}
// /privacy and /terms. The server sends the text with every bracketed note already taken out.
function LegalPage({ doc }) {
  const [d, setD] = useState(null), [err, setErr] = useState('');
  useEffect(() => { setD(null); api(`/api/public/legal/${doc}`).then(setD).catch(e => setErr(e.message)); }, [doc]);
  const blocks = [];
  if (d && d.published) {
    let list = null;
    String(d.body || '').split('\n').forEach((line, i) => {
      if (line.startsWith('- ')) { if (!list) { list = []; blocks.push(<ul key={'u' + i} style={{ paddingLeft: 20, margin: '0 0 14px' }}>{list}</ul>); } list.push(<li key={i} style={{ marginBottom: 6, lineHeight: 1.6 }}>{line.slice(2)}</li>); return; }
      list = null;
      if (line.startsWith('## ')) blocks.push(<h3 key={i} style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600, margin: '20px 0 8px' }}>{line.slice(3)}</h3>);
      else if (line.startsWith('# ')) blocks.push(<h2 key={i} style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, margin: '28px 0 10px' }}>{line.slice(2)}</h2>);
      else blocks.push(<p key={i} style={{ margin: '0 0 12px', lineHeight: 1.65 }}>{line}</p>);
    });
  }
  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '90px 20px 60px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(30px, 5vw, 44px)', letterSpacing: 1.5, marginBottom: 12 }}>{d ? d.title : doc === 'terms' ? 'Terms of Service' : 'Privacy Policy'}</h1>
        {err && <p style={{ color: T.danger }}>{err}</p>}
        {!d && !err && <p style={{ color: T.gray }}>Loading...</p>}
        {d && !d.published && <Card style={{ padding: 24 }}><p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Coming soon</p><p style={{ color: T.gray, fontSize: 14, marginBottom: 0 }}>This page will be here before HealthyBody opens.</p></Card>}
        {d && d.published && <Card style={{ padding: '24px 26px', fontSize: 15, color: T.white }}>{blocks}</Card>}
      </div>
    </div>
  );
}

// ── AUTH PAGES ────────────────────────────────────────────────────────────────
// "Forgot password?" under the sign-in form. Always shows the same answer, whether or not the address exists.
function ForgotPassword({ defaultEmail }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const send = async e => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      const r = await api('/api/auth/forgot', 'POST', { email: email || defaultEmail, base: window.location.origin + (process.env.PUBLIC_URL || '') });
      setSent(r.message);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  if (!open) return (
    <p style={{ textAlign: 'center', marginTop: 14, fontSize: 13 }}>
      <button type="button" onClick={() => { setOpen(true); setEmail(defaultEmail || ''); }} style={{ background: 'none', border: 'none', color: T.orange, cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'Poppins' }}>Forgot password?</button>
    </p>
  );
  return (
    <form onSubmit={send} style={{ marginTop: 18, padding: 16, borderRadius: 12, background: T.glass, border: `1px solid ${T.border}` }}>
      {sent ? <p style={{ fontSize: 14, color: T.white }}>{sent}</p> : <>
        <Input label="Email for the reset link" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required />
        {err && <p style={{ color: T.danger, fontSize: 13, marginBottom: 10 }}>{err}</p>}
        <Btn type="submit" fullWidth disabled={busy}>{busy ? 'Sending…' : 'Email me a reset link'}</Btn>
      </>}
    </form>
  );
}

function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState(''); const [pw2, setPw2] = useState('');
  const [err, setErr] = useState(''); const [done, setDone] = useState(false); const [busy, setBusy] = useState(false);
  const submit = async e => {
    e.preventDefault(); setErr('');
    if (pw.length < 8) return setErr('Choose a password of at least 8 characters.');
    if (pw !== pw2) return setErr('The two passwords do not match.');
    setBusy(true);
    try { await api('/api/auth/reset', 'POST', { token, password: pw }); setDone(true); }
    catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px' }}>
      <Card style={{ width: '100%', maxWidth: 440, padding: 32 }}>
        <h2 style={{ fontFamily: 'Oswald', fontSize: 26, fontWeight: 600, marginBottom: 8 }}>Choose a new password</h2>
        {done ? <>
          <p style={{ color: T.grayLight, marginBottom: 20 }}>Your password is changed. Sign in with the new one.</p>
          <Btn fullWidth onClick={() => nav('/login')}>Go to sign in →</Btn>
        </> : <form onSubmit={submit}>
          <Input label="New password" type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Min 8 characters" required />
          <Input label="Type it again" type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Same password" required />
          {err && <p style={{ color: T.danger, fontSize: 13, marginBottom: 12 }}>{err}</p>}
          <Btn type="submit" fullWidth disabled={busy}>{busy ? 'Saving…' : 'Save new password'}</Btn>
        </form>}
      </Card>
    </div>
  );
}

function AuthPage({ mode }) {
  const svc = useService();
  const [tab, setTab] = useState(mode);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '', referral_code: '', offer_code: '', date_of_birth: '' });
  const [agree, setAgree] = useState(false);
  const legal = useLegal();
  const [offer, setOffer] = useState(null);
  const [offerErr, setOfferErr] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const { login } = useAuth();
  const nav = useNavigate();

  useEffect(() => { setTab(mode); }, [mode]);

  // Pre-fill referral code from ?ref= URL param
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setForm(f => ({ ...f, referral_code: ref.toUpperCase() }));
    const special = searchParams.get('offer');
    if (special) setForm(f => ({ ...f, offer_code: special.toUpperCase() }));
  }, [searchParams]);

  // What this sign-up gets: the code's special, or the standard sign-up when there is no code.
  useEffect(() => {
    if (tab !== 'signup') return;
    const code = form.offer_code.trim();
    const t = setTimeout(() => {
      api('/api/public/offer' + (code ? '?code=' + encodeURIComponent(code) : ''))
        .then(r => { setOffer(r.offer); setOfferErr(''); })
        .catch(e => { setOffer(null); setOfferErr(code ? e.message : ''); });
    }, code ? 400 : 0);
    return () => clearTimeout(t);
  }, [form.offer_code, tab]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setError('');
    if (tab === 'signup' && form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (tab === 'signup' && !form.date_of_birth) { setError('Please enter your date of birth.'); return; }
    if (tab === 'signup' && ageFromDob(form.date_of_birth) < 13) { setError(UNDER_13_TEXT); return; }
    if (tab === 'signup' && legal.published && !agree) { setError('Please tick the box to agree to the Terms of Service and the Privacy Policy.'); return; }
    setLoading(true);
    try {
      const endpoint = tab === 'signup' ? '/api/auth/signup' : '/api/auth/login';
      const body = tab === 'signup'
        ? { name: form.name, email: form.email, password: form.password, date_of_birth: form.date_of_birth, agree_legal: legal.published ? agree : undefined, referral_code: form.referral_code.trim() || undefined, offer_code: form.offer_code.trim() || undefined }
        : { email: form.email, password: form.password };
      const data = await api(endpoint, 'POST', body);
      login(data.user, data.token, data.onboardingComplete);
      if (data.user.role === 'admin') {
        nav('/admin');
      } else if (data.user.role === 'gym_admin') {
        nav('/gym-admin');
      } else if (data.user.role === 'coach') {
        nav('/coach');
      } else if (data.user.role === 'trainer_applicant') {
        nav('/trainer-application');
      } else {
        nav(data.onboardingComplete ? '/dashboard' : '/onboarding');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg, padding: '80px 24px 40px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 30%, rgba(255,69,0,0.08) 0%, transparent 60%)' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.01) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.01) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: 460 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ width: 52, height: 52, background: `linear-gradient(135deg, ${T.orange}, ${T.gold})`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, margin: '0 auto 12px' }}>🏋️</div>
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 28, letterSpacing: 3 }}>{(svc?.name || 'HealthyBody').toUpperCase()}</h1>
        </div>

        {/* Tab switcher */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', ...glassBg, borderRadius: 12, padding: 4, marginBottom: 32 }}>
          {['signup', 'login'].map(t => (
            <button key={t} onClick={() => { setTab(t); setError(''); }} style={{
              padding: '12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: 'Oswald', fontWeight: 600, fontSize: 16, letterSpacing: 1, transition: 'all 0.2s',
              background: tab === t ? `linear-gradient(135deg, ${T.orange}, ${T.orangeLight})` : 'transparent',
              color: tab === t ? T.white : T.gray,
              boxShadow: tab === t ? `0 4px 16px rgba(255,69,0,0.3)` : 'none',
            }}>
              {t === 'signup' ? 'GET STARTED' : 'SIGN IN'}
            </button>
          ))}
        </div>

        {/* Form */}
        <Card style={{ padding: 36 }}>
          <h2 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 8 }}>
            {tab === 'signup' ? 'Create Your Account' : 'Welcome Back'}
          </h2>
          <p style={{ color: T.gray, fontSize: 14, marginBottom: 28 }}>
            {tab === 'signup' ? 'Your own trainer, a plan built around your health, and food you will actually eat.' : 'Good to see you again.'}
          </p>

          <form onSubmit={submit}>
            {tab === 'signup' && <Input label="Full Name" value={form.name} onChange={set('name')} placeholder="Your name" required />}
            <Input label="Email Address" type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" required />
            <Input label="Password" type="password" value={form.password} onChange={set('password')} placeholder={tab === 'signup' ? 'Min 8 characters' : 'Your password'} required hint={tab === 'signup' ? 'Choose a strong password you\'ll remember' : ''} />
            {tab === 'signup' && <Input label="Confirm Password" type="password" value={form.confirm} onChange={set('confirm')} placeholder="Confirm your password" required />}
            {tab === 'signup' && <DobInput value={form.date_of_birth} onChange={set('date_of_birth')} />}
            {tab === 'signup' && (
              <Input
                label="Referral Code (optional)"
                value={form.referral_code}
                onChange={set('referral_code')}
                placeholder="Friend's code — e.g. ABC123"
                hint="Enter a referral code to give your friend a free month"
              />
            )}
            {tab === 'signup' && (
              <Input label="Special Offer Code (optional)" value={form.offer_code} onChange={set('offer_code')} placeholder="e.g. NEWYEAR" error={offerErr} hint={offerErr} />
            )}
            {tab === 'signup' && offer && (offer.name || offer.trial_days > 0 || offer.waive_initiation) && (
              <div style={{ background: `${T.success}12`, border: `1px solid ${T.success}44`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: T.white }}>
                {offer.name && <strong style={{ display: 'block', marginBottom: 4 }}>✓ {offer.name}</strong>}
                {offer.trial_days > 0 && <span style={{ display: 'block' }}>{offer.trial_days} days free · no card needed to start</span>}
                {offer.waive_initiation && <span style={{ display: 'block' }}>Initiation fee: <s style={{ color: T.gray }}>{offer.initiation_fee}</s> <strong style={{ color: T.success }}>$0</strong></span>}
                {offer.ends_on && <span style={{ display: 'block', fontSize: 12, color: T.gray, marginTop: 4 }}>Offer ends {offer.ends_on}</span>}
              </div>
            )}
            {tab === 'signup' && <LegalAgree checked={agree} onChange={setAgree} />}
            {error && <div style={{ background: `${T.danger}15`, border: `1px solid ${T.danger}44`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: T.danger }}>{error}</div>}
            <Btn type="submit" fullWidth disabled={loading || (tab === 'signup' && ageFromDob(form.date_of_birth) !== null && ageFromDob(form.date_of_birth) < 13)}>{loading ? 'Please wait…' : tab === 'signup' ? 'Create My Account →' : 'Sign In →'}</Btn>
          </form>
          {tab === 'login' && <ForgotPassword defaultEmail={form.email} />}

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: T.gray }}>
            {tab === 'signup' ? 'Already have an account? ' : 'New here? '}
            <button onClick={() => { setTab(tab === 'signup' ? 'login' : 'signup'); setError(''); }} style={{ background: 'none', border: 'none', color: T.orange, cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'Poppins' }}>
              {tab === 'signup' ? 'Sign In' : 'Create Account'}
            </button>
          </p>
          <p style={{ textAlign: 'center', marginTop: 10, fontSize: 13, color: T.gray }}>Are you a personal trainer? <Link to="/trainer-apply" style={{ color: T.orange, fontWeight: 600 }}>Apply to coach with HealthyBody</Link></p>
          {tab === 'signup' && (
            <p style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: T.gray, lineHeight: 1.5 }}>By creating an account you agree that this program is for informational purposes only and not a substitute for medical advice.</p>
          )}
          <LegalLinks style={{ textAlign: 'center' }} />
        </Card>


      </div>
    </div>
  );
}

// ── ONBOARDING WIZARD ─────────────────────────────────────────────────────────
function Onboarding({ editMode = false }) {
  const { user, completeOnboarding } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    date_of_birth: '', age: '', sex: '', height_feet: '', height_inches: '0', weight: '', goal_weight: '',
    body_fat_pct: '', goal: '', main_program: '', activity_level: '', fitness_experience: '',
    health_issues: [], injuries: [], equipment: [], dietary_preferences: [],
    medications: '', sleep_hours: '', stress_level: '', doctor_clearance: '',
      guardian_name: '', guardian_consent: false,
    food_allergies: [], food_allergies_other: '',
    zip_code: '', preferred_store: '', household_size: '', cooking_skill: '', minutes_per_meal: '',
    kitchen_equipment: [], grocery_budget: '', units: 'us',
    emergency_contact_name: '', emergency_contact_relationship: '', emergency_contact_phone: '',
    photo_consent: false,
    health_notes: '', disclaimer_accepted: false,
  });
  const [loadingProfile, setLoadingProfile] = useState(editMode);
  // Date of birth (2026-10-31): given at sign-up; members from before that are asked for it here, once.
  const [dobMissing, setDobMissing] = useState(false);
  const setDob = v => { const dob = typeof v === 'object' && v.target ? v.target.value : v; const a = ageFromDob(dob); setData(d => ({ ...d, date_of_birth: dob, age: a !== null && a >= 0 ? String(a) : '' })); };
  useEffect(() => {
    if (editMode) return;
    api('/api/profile').then(r => { if (r.date_of_birth) setDob(r.date_of_birth); else setDobMissing(true); }).catch(() => {});
  }, [editMode]);

  // Editing later: start from what the member already told us. Only fields the form knows are copied.
  useEffect(() => {
    if (!editMode) return;
    api('/api/profile').then(r => {
      const pr = r.profile;
      setDobMissing(!r.date_of_birth);
      if (pr) setData(d => {
        const next = { ...d };
        Object.keys(d).forEach(k => { if (pr[k] !== undefined && pr[k] !== null) next[k] = pr[k]; });
        if (r.date_of_birth) { next.date_of_birth = r.date_of_birth; next.age = String(ageFromDob(r.date_of_birth)); }
        next.guardian_consent = String(pr.guardian_consent || '') === 'yes';
        next.disclaimer_accepted = false;   // re-accepted on every save, so the waiver record stays current
        next.household_size = pr.household_size ? String(pr.household_size) : '';
        return next;
      });
    }).catch(() => {}).finally(() => setLoadingProfile(false));
  }, [editMode]);

  const set = k => v => setData(d => ({ ...d, [k]: typeof v === 'object' && v.target ? v.target.value : v }));
  const toggle = key => val => setData(d => ({
    ...d, [key]: d[key].includes(val) ? d[key].filter(i => i !== val) : [...d[key], val],
  }));
  const toggleIssue = toggle('health_issues');

  const TOTAL = 7;
  const pct = ((step - 1) / TOTAL) * 100;

  // Grouped so it is answerable, not a wall of 51 checkboxes.
  // Every id here matches a condition the safety engine knows about.
  const healthGroups = [
    { group: 'Heart & circulation', items: [
      { id: 'high_blood_pressure', label: 'High blood pressure' },
      { id: 'uncontrolled_hbp', label: 'Blood pressure over 180/110, not controlled' },
      { id: 'heart_condition', label: 'Heart condition' },
      { id: 'heart_attack_history', label: 'Heart attack (more than 6 weeks ago)' },
      { id: 'heart_attack_recent', label: 'Heart attack in the last 6 weeks' },
      { id: 'unstable_angina', label: 'Chest pain at rest, or getting worse' },
      { id: 'heart_failure', label: 'Heart failure' },
      { id: 'arrhythmia', label: 'Irregular heartbeat' },
      { id: 'pacemaker_icd', label: 'Pacemaker or defibrillator' },
      { id: 'stroke_history', label: 'Stroke or mini-stroke (TIA)' },
      { id: 'blood_clot_active', label: 'Active blood clot (DVT or lung clot)' },
      { id: 'aortic_aneurysm', label: 'Aortic aneurysm' },
    ]},
    { group: 'Blood sugar & hormones', items: [
      { id: 'diabetes_type_1', label: 'Type 1 diabetes' },
      { id: 'diabetes_type_2', label: 'Type 2 diabetes' },
      { id: 'thyroid_hypo', label: 'Under-active thyroid' },
      { id: 'thyroid_hyper', label: 'Over-active thyroid' },
    ]},
    { group: 'Lungs & breathing', items: [
      { id: 'asthma', label: 'Asthma' },
      { id: 'copd', label: 'COPD or emphysema' },
      { id: 'pulmonary_hypertension', label: 'Pulmonary hypertension' },
    ]},
    { group: 'Bones, joints & back', items: [
      { id: 'arthritis', label: 'Arthritis' },
      { id: 'osteoporosis', label: 'Osteoporosis' },
      { id: 'osteopenia', label: 'Osteopenia (low bone density)' },
      { id: 'back_pain', label: 'Ongoing back pain' },
      { id: 'herniated_disc', label: 'Herniated or bulging disc' },
      { id: 'bad_knees', label: 'Knee problems' },
      { id: 'joint_replacement', label: 'Joint replacement' },
      { id: 'hypermobility', label: 'Hypermobility / Ehlers-Danlos' },
      { id: 'hernia', label: 'Hernia (untreated)' },
      { id: 'recent_surgery', label: 'Surgery in the last 6 weeks' },
    ]},
    { group: 'Brain & nerves', items: [
      { id: 'seizure_disorder', label: 'Seizure disorder / epilepsy' },
      { id: 'multiple_sclerosis', label: 'Multiple sclerosis' },
      { id: 'parkinsons', label: "Parkinson's disease" },
      { id: 'vertigo', label: 'Vertigo or balance problems' },
      { id: 'neuropathy', label: 'Nerve damage / numbness in feet' },
      { id: 'concussion_recent', label: 'Concussion in the last 4 weeks' },
    ]},
    { group: 'Cancer', items: [
      { id: 'cancer_active', label: 'Cancer, currently in treatment' },
      { id: 'cancer_remission', label: 'Cancer, in remission' },
      { id: 'bone_metastases', label: 'Cancer that has spread to bone' },
      { id: 'lymphedema_risk', label: 'Lymph nodes removed' },
      { id: 'immunocompromised', label: 'Immune system suppressed' },
    ]},
    { group: 'Organs & blood', items: [
      { id: 'kidney_disease', label: 'Kidney disease' },
      { id: 'liver_condition', label: 'Liver condition' },
      { id: 'anemia', label: 'Anemia' },
      { id: 'bariatric_surgery', label: 'Weight-loss (bariatric) surgery' },
      { id: 'autoimmune', label: 'Autoimmune condition' },
      { id: 'glaucoma', label: 'Glaucoma or detached retina' },
    ]},
    { group: 'Pregnancy', items: [
      { id: 'pregnancy', label: 'Currently pregnant' },
      { id: 'postpartum', label: 'Gave birth in the last 12 weeks' },
      { id: 'preeclampsia_history', label: 'Preeclampsia in a past pregnancy' },
    ]},
    { group: 'Mental health', note: 'This changes what we show you. If you have ever struggled with eating or food, telling us here means we switch calorie and weight numbers off entirely.', items: [
      { id: 'eating_disorder', label: 'Eating disorder, now or in the past' },
      { id: 'severe_depression', label: 'Severe depression' },
      { id: 'bipolar', label: 'Bipolar disorder' },
      { id: 'psychosis', label: 'Psychotic disorder' },
      { id: 'substance_recovery', label: 'In recovery from substance use' },
      { id: 'suicidal_ideation', label: 'Thoughts of harming yourself' },
    ]},
  ];
  const healthOptions = healthGroups.flatMap(g => g.items);

  const injuryOptions = [
    { id: 'shoulder', label: '🙋 Shoulder' },
    { id: 'elbow', label: '💪 Elbow' },
    { id: 'wrist', label: '🤙 Wrist' },
    { id: 'hip', label: '🦯 Hip' },
    { id: 'knee', label: '🦵 Knee' },
    { id: 'ankle', label: '🦶 Ankle' },
    { id: 'lower_back', label: '🔧 Lower back' },
    { id: 'upper_back', label: '🔧 Upper back / neck' },
  ];

  const equipmentOptions = [
    { id: 'gym', label: '🏋️ Full gym' },
    { id: 'home_gym', label: '🏠 Home gym (weights and a bench)' },
    { id: 'dumbbells', label: '💪 Dumbbells' },
    { id: 'kettlebell', label: '🔔 Kettlebell' },
    { id: 'bands', label: '🎗️ Resistance bands' },
    { id: 'pullup_bar', label: '🪜 Pull-up bar' },
    { id: 'cardio_machines', label: '🚴 Treadmill, bike or rower' },
    { id: 'pool', label: '🏊 Pool' },
    { id: 'bodyweight_only', label: '🙌 No equipment (body weight only)' },
    { id: 'mat', label: '🧘 Yoga mat' },
    { id: 'blocks', label: '🧱 Yoga blocks' },
    { id: 'strap', label: '🎗️ Strap' },
    { id: 'bolster', label: '🛏️ Bolster or firm pillows' },
    { id: 'blanket', label: '🧣 Blanket' },
    { id: 'wall', label: '🧱 Clear wall space' },
    { id: 'chair', label: '🪑 Sturdy chair' },
  ];

  const dietOptions = [
    { id: 'vegan', label: '🌱 Vegan' },
    { id: 'vegetarian', label: '🥗 Vegetarian' },
    { id: 'keto', label: '🥩 Keto / Low-carb' },
    { id: 'gluten_free', label: '🌾 Gluten-free' },
    { id: 'dairy_free', label: '🥛 Dairy-free' },
    { id: 'diabetic_diet', label: '🩸 Diabetic-friendly meals' },
    { id: 'kidney_diet', label: '🫘 Kidney-friendly (low potassium)' },
    { id: 'none', label: '✅ No restrictions' },
  ];

  // Same nine ids as backend/allergens.js. The server enforces them; this only collects them.
  const allergyOptions = [
    { id: 'milk', label: '🥛 Milk / dairy' }, { id: 'eggs', label: '🥚 Eggs' },
    { id: 'fish', label: '🐟 Fish' }, { id: 'shellfish', label: '🦐 Shellfish' },
    { id: 'tree_nuts', label: '🌰 Tree nuts' }, { id: 'peanuts', label: '🥜 Peanuts' },
    { id: 'wheat', label: '🌾 Wheat / gluten' }, { id: 'soy', label: '🫘 Soy' },
    { id: 'sesame', label: '⚪ Sesame' },
  ];

  const canNext = () => {
    if (step === 1) return data.date_of_birth && ageFromDob(data.date_of_birth) >= 13 && data.sex;
    if (step === 2) return data.height_feet && data.weight && data.goal_weight;
    if (step === 3) return data.goal && data.main_program;
    if (step === 4) return data.activity_level && data.fitness_experience;
    if (step === 5) return true;
    if (step === 6) return true;
    if (step === 7) {
      const minor = Number(data.age) > 0 && Number(data.age) < 18;
      if (minor && (!data.guardian_consent || !data.guardian_name.trim())) return false;
      return data.disclaimer_accepted;
    }
    return false;
  };

  const finish = async () => {
    setSaving(true); setError('');
    try {
      await api('/api/profile', 'POST', data);
      completeOnboarding();
      nav(editMode ? '/program' : '/getting-started');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const selStyle = v => ({ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: v ? T.white : T.gray, fontSize: 14, fontFamily: 'Poppins', outline: 'none' });

  const StepWrap = ({ title, subtitle, children }) => (
    <div className="animate-fadeInUp">
      <h2 style={{ fontFamily: 'Oswald', fontSize: 28, fontWeight: 600, marginBottom: 8 }}>{title}</h2>
      <p style={{ color: T.gray, fontSize: 15, marginBottom: 32 }}>{subtitle}</p>
      {children}
    </div>
  );

  const steps = [
    // Step 1 — Basics
    <StepWrap key={1} title="Let's start with the basics" subtitle="This helps us build the right program for your body.">
      <DobInput value={data.date_of_birth} onChange={setDob}
        hint={dobMissing ? 'Please add your date of birth. We now ask every member for it; your age is worked out from it.'
          : data.age ? `You are ${data.age}. Your age affects your metabolic rate and recovery time.` : 'Your age affects your metabolic rate and recovery time.'} />
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 10, fontWeight: 500 }}>Biological Sex <span style={{ color: T.orange }}>*</span></label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[{ v: 'male', label: '♂ Male' }, { v: 'female', label: '♀ Female' }].map(o => (
            <button key={o.v} type="button" onClick={() => set('sex')(o.v)} style={{
              padding: '16px', borderRadius: 10, border: `2px solid ${data.sex === o.v ? T.orange : T.border}`,
              background: data.sex === o.v ? `${T.orange}15` : T.glass, color: data.sex === o.v ? T.orange : T.grayLight,
              cursor: 'pointer', fontFamily: 'Oswald', fontSize: 18, fontWeight: 600, transition: 'all 0.2s',
            }}>{o.label}</button>
          ))}
        </div>
        <p style={{ fontSize: 12, color: T.gray, marginTop: 6 }}>Used only for accurate calorie and hormone calculations.</p>
      </div>
    </StepWrap>,

    // Step 2 — Body stats
    <StepWrap key={2} title="Your current stats" subtitle="Used to calculate your calorie targets. All data stays private.">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Input label="Height — Feet" type="number" value={data.height_feet} onChange={set('height_feet')} placeholder="5" required />
        <Input label="Inches" type="number" value={data.height_inches} onChange={set('height_inches')} placeholder="8" />
      </div>
      <Input label="Current Weight (lbs)" type="number" value={data.weight} onChange={set('weight')} placeholder="e.g. 195" required />
      <Input label="Goal Weight (lbs)" type="number" value={data.goal_weight} onChange={set('goal_weight')} placeholder="e.g. 170" required hint="This helps set the pace of your program. A realistic goal = lasting results." />
      <Input label="Estimated Body Fat % (optional)" type="number" value={data.body_fat_pct} onChange={set('body_fat_pct')} placeholder="e.g. 22" hint="Don't know? Leave blank — a rough estimate is fine. Used to personalize your plan." />
    </StepWrap>,

    // Step 3 — Goal
    <StepWrap key={3} title="Your program and your goal" subtitle="Choose the exercise program you want, or eating and cooking only. You can change it later.">
      <p style={{ fontFamily: 'Oswald', fontSize: 15, letterSpacing: 1, color: T.grayLight, margin: '0 0 8px' }}>YOUR PROGRAM *</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 22 }}>
        {[
          { v: 'workout', label: '🏋️ Workout', desc: 'Strength and conditioning, built to the equipment you have.' },
          { v: 'yoga', label: '🧘 Yoga', desc: 'Strength, balance and flexibility through yoga.' },
          { v: 'calisthenics', label: '🤸 Calisthenics', desc: 'Body-weight strength, step by step.' },
          { v: 'hiking', label: '🥾 Walking & Hiking', desc: 'From a 10-minute walk to trail days.' },
          { v: 'mobility', label: '🦴 Mobility & Rehab', desc: 'Move better and ease stiff or sore joints.' },
          { v: 'longevity', label: '🌳 Strong at 65+', desc: 'Strength, balance and bone health for later life.' },
          { v: 'food', label: '🍽️ Eating & cooking only', desc: 'Meal plans, recipes and your trainer, no exercise program.' },
        ].map(g => (
          <button key={g.v} type="button" onClick={() => set('main_program')(g.v)} aria-pressed={data.main_program === g.v} style={{
            padding: '14px 16px', borderRadius: 12, border: `2px solid ${data.main_program === g.v ? T.orange : T.border}`,
            background: data.main_program === g.v ? `${T.orange}15` : T.glass, cursor: 'pointer', textAlign: 'left',
          }}>
            <p style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, color: data.main_program === g.v ? T.orange : T.white, marginBottom: 2 }}>{g.label}</p>
            <p style={{ fontSize: 12.5, color: T.gray }}>{g.desc}</p>
          </button>
        ))}
      </div>
      <p style={{ fontFamily: 'Oswald', fontSize: 15, letterSpacing: 1, color: T.grayLight, margin: '0 0 8px' }}>YOUR MAIN GOAL *</p>
      <div style={{ display: 'grid', gap: 12 }}>
        {[
          { v: 'lose_weight', label: '🔥 Lose Weight', desc: 'Burn fat, feel lighter, and build sustainable healthy habits.' },
          { v: 'build_muscle', label: '💪 Build Strength', desc: 'Get stronger and more stable, step by step.' },
          { v: 'both', label: '⚡ Both — Lose Weight & Build Strength', desc: 'A balanced plan for a lighter, stronger body.' },
          { v: 'general_fitness', label: '🌱 General Health & Wellness', desc: 'Feel better, move better, and build a foundation of lasting health.' },
        ].map(g => (
          <button key={g.v} type="button" onClick={() => set('goal')(g.v)} style={{
            padding: '18px 20px', borderRadius: 12, border: `2px solid ${data.goal === g.v ? T.orange : T.border}`,
            background: data.goal === g.v ? `${T.orange}15` : T.glass, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
          }}>
            <p style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600, color: data.goal === g.v ? T.orange : T.white, marginBottom: 4 }}>{g.label}</p>
            <p style={{ fontSize: 13, color: T.gray }}>{g.desc}</p>
          </button>
        ))}
      </div>
    </StepWrap>,

    // Step 4 — Activity level + experience
    <StepWrap key={4} title="Your activity & experience" subtitle="Be honest — your program should meet you where you actually are.">
      <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600, marginBottom: 10, marginTop: 4 }}>How active are you right now?</p>
      <div style={{ display: 'grid', gap: 10, marginBottom: 28 }}>
        {[
          { v: 'sedentary', label: '🛋️ Sedentary', desc: 'Mostly sitting. Little to no exercise.' },
          { v: 'lightly_active', label: '🚶 Lightly Active', desc: 'Light exercise 1–3 days/week, or a walking job.' },
          { v: 'moderately_active', label: '🏃 Moderately Active', desc: 'Moderate exercise 3–5 days/week.' },
          { v: 'very_active', label: '⚡ Very Active', desc: 'Hard exercise 6–7 days/week.' },
          { v: 'extremely_active', label: '🔥 Extremely Active', desc: 'Physical job + hard daily training.' },
        ].map(a => (
          <button key={a.v} type="button" onClick={() => set('activity_level')(a.v)} style={{
            padding: '14px 18px', borderRadius: 10, border: `2px solid ${data.activity_level === a.v ? T.orange : T.border}`,
            background: data.activity_level === a.v ? `${T.orange}15` : T.glass, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
          }}>
            <p style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, color: data.activity_level === a.v ? T.orange : T.white, marginBottom: 2 }}>{a.label}</p>
            <p style={{ fontSize: 12, color: T.gray }}>{a.desc}</p>
          </button>
        ))}
      </div>
      <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600, marginBottom: 10 }}>Fitness experience level?</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {[
          { v: 'beginner', label: '🌱 Beginner', desc: 'New to structured training (< 6 months)' },
          { v: 'intermediate', label: '🔥 Intermediate', desc: '6 months – 2 years consistent training' },
          { v: 'advanced', label: '⚡ Advanced', desc: '2+ years of consistent training' },
          { v: 'athlete', label: '🏆 Athlete', desc: 'Competitive sports or high-performance background' },
        ].map(e => (
          <button key={e.v} type="button" onClick={() => set('fitness_experience')(e.v)} style={{
            padding: '14px 16px', borderRadius: 10, border: `2px solid ${data.fitness_experience === e.v ? T.orange : T.border}`,
            background: data.fitness_experience === e.v ? `${T.orange}15` : T.glass, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
          }}>
            <p style={{ fontFamily: 'Oswald', fontSize: 15, fontWeight: 600, color: data.fitness_experience === e.v ? T.orange : T.white, marginBottom: 3 }}>{e.label}</p>
            <p style={{ fontSize: 11, color: T.gray }}>{e.desc}</p>
          </button>
        ))}
      </div>
    </StepWrap>,

    // Step 5 — Health conditions + injuries + equipment
    <StepWrap key={5} title="Health & Physical Limits" subtitle="Select everything that applies. Your plan will be built around your specific situation.">
      <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600, marginBottom: 4 }}>Medical conditions</p>
      <p style={{ fontSize: 12.5, color: T.gray, marginBottom: 14, lineHeight: 1.55 }}>
        Tick anything that applies. Some of these change your plan; a few mean we will ask you to see your doctor first.
        If none apply, leave it all blank and carry on.
      </p>
      {healthGroups.map(g => (
        <div key={g.group} style={{ marginBottom: 18 }}>
          <p style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 1.1, textTransform: 'uppercase', color: T.gold, marginBottom: 8 }}>{g.group}</p>
          {g.note && <p style={{ fontSize: 12, color: T.gray, marginBottom: 8, lineHeight: 1.5, fontStyle: 'italic' }}>{g.note}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 8 }}>
            {g.items.map(o => (
              <button key={o.id} type="button" onClick={() => toggleIssue(o.id)} style={{
                padding: '10px 13px', borderRadius: 10, border: `2px solid ${data.health_issues.includes(o.id) ? T.gold : T.border}`,
                background: data.health_issues.includes(o.id) ? `${T.gold}15` : T.glass, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
                color: data.health_issues.includes(o.id) ? T.gold : T.grayLight, fontSize: 12.5, fontWeight: 500, lineHeight: 1.35,
              }}>{o.label}</button>
            ))}
          </div>
        </div>
      ))}
      <div style={{ marginBottom: 24 }} />
      <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600, marginBottom: 10 }}>Current injuries or physical limitations</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 24 }}>
        {injuryOptions.map(o => (
          <button key={o.id} type="button" onClick={() => toggle('injuries')(o.id)} style={{
            padding: '10px 14px', borderRadius: 10, border: `2px solid ${data.injuries.includes(o.id) ? T.danger : T.border}`,
            background: data.injuries.includes(o.id) ? `${T.danger}15` : T.glass, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
            color: data.injuries.includes(o.id) ? T.danger : T.grayLight, fontSize: 13, fontWeight: 500,
          }}>{o.label}</button>
        ))}
      </div>
      <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600, marginBottom: 10 }}>Equipment available to you</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {equipmentOptions.map(o => (
          <button key={o.id} type="button" onClick={() => toggle('equipment')(o.id)} style={{
            padding: '10px 14px', borderRadius: 10, border: `2px solid ${data.equipment.includes(o.id) ? T.success : T.border}`,
            background: data.equipment.includes(o.id) ? `${T.success}15` : T.glass, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
            color: data.equipment.includes(o.id) ? T.success : T.grayLight, fontSize: 13, fontWeight: 500,
          }}>{o.label}</button>
        ))}
      </div>
    </StepWrap>,

    // Step 6 — Kitchen & household (drives meal plans, shopping lists and store prices)
    <StepWrap key={6} title="Your kitchen & household" subtitle="So meal plans fit how you actually shop and cook.">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Input label="ZIP code" value={data.zip_code} onChange={set('zip_code')} placeholder="e.g. 37919" hint="Finds your nearest stores and their prices." />
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Where you usually shop</label>
          <select value={data.preferred_store} onChange={set('preferred_store')} style={selStyle(data.preferred_store)}>
            <option value="">Select...</option>
            {[['Kroger', 'Kroger'], ['Walmart', 'Walmart'], ['Publix', 'Publix'], ['Aldi', 'Aldi'], ['Food City', 'Food City'], ['Target', 'Target'], ['Costco', 'Costco'], ["Sam's Club", "Sam's Club"], ['Whole Foods', 'Whole Foods'], ["Trader Joe's", "Trader Joe's"], ['Ingles', 'Ingles'], ['Other', 'Other']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>People you cook for (including you)</label>
          <select value={data.household_size} onChange={set('household_size')} style={selStyle(data.household_size)}>
            <option value="">Select...</option>
            {[['1', '1'], ['2', '2'], ['3', '3'], ['4', '4'], ['5', '5'], ['6', '6'], ['7', '7'], ['8', '8']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Weekly grocery budget</label>
          <select value={data.grocery_budget} onChange={set('grocery_budget')} style={selStyle(data.grocery_budget)}>
            <option value="">Select...</option>
            {[['Under $50', 'Under $50'], ['$50 to $100', '$50 to $100'], ['$100 to $150', '$100 to $150'], ['$150 to $200', '$150 to $200'], ['$200 to $300', '$200 to $300'], ['Over $300', 'Over $300']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>How comfortable are you cooking?</label>
          <select value={data.cooking_skill} onChange={set('cooking_skill')} style={selStyle(data.cooking_skill)}>
            <option value="">Select...</option>
            {[['beginner', 'Beginner: simple recipes please'], ['comfortable', 'Comfortable'], ['confident', 'Confident: bring it on']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Time you have for a weeknight meal</label>
          <select value={data.minutes_per_meal} onChange={set('minutes_per_meal')} style={selStyle(data.minutes_per_meal)}>
            <option value="">Select...</option>
            {[['15 minutes', '15 minutes'], ['30 minutes', '30 minutes'], ['45 minutes', '45 minutes'], ['An hour or more', 'An hour or more']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600, marginBottom: 10 }}>Kitchen equipment you have</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
        {[['oven', 'Oven'], ['stovetop', 'Stovetop'], ['microwave', 'Microwave'], ['air_fryer', 'Air fryer'], ['slow_cooker', 'Slow cooker / Instant Pot'], ['blender', 'Blender'], ['grill', 'Grill'], ['sheet_pan', 'Sheet pans']].map(([id, label]) => (
          <button key={id} type="button" onClick={() => toggle('kitchen_equipment')(id)} style={{
            padding: '10px 14px', borderRadius: 10, border: `2px solid ${data.kitchen_equipment.includes(id) ? T.success : T.border}`,
            background: data.kitchen_equipment.includes(id) ? `${T.success}15` : T.glass, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
            color: data.kitchen_equipment.includes(id) ? T.success : T.grayLight, fontSize: 13, fontWeight: 500,
          }}>{label}</button>
        ))}
      </div>
      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Units</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[['us', 'Pounds, feet, cups'], ['metric', 'Kilograms, meters, grams']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => set('units')(v)} style={{
              padding: '10px 12px', borderRadius: 8, border: `2px solid ${data.units === v ? T.orange : T.border}`,
              background: data.units === v ? `${T.orange}15` : T.glass, cursor: 'pointer', color: data.units === v ? T.orange : T.grayLight, fontSize: 12.5, fontWeight: 500,
            }}>{l}</button>
          ))}
        </div>
      </div>
    </StepWrap>,

    // Step 7 — Diet, lifestyle, & disclaimer
    <StepWrap key={7} title="Nutrition & Lifestyle" subtitle="Last step — these details help your trainer build your meal plan and recovery program.">
      <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600, marginBottom: 4 }}>Food allergies</p>
      <p style={{ fontSize: 12, color: T.gray, marginBottom: 10 }}>Any recipe containing these is removed from your recipes, meal plans and shopping lists.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        {allergyOptions.map(o => (
          <button key={o.id} type="button" onClick={() => toggle('food_allergies')(o.id)} style={{
            padding: '10px 14px', borderRadius: 10, border: `2px solid ${data.food_allergies.includes(o.id) ? T.error || '#D64545' : T.border}`,
            background: data.food_allergies.includes(o.id) ? '#D6454515' : T.glass, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
            color: data.food_allergies.includes(o.id) ? '#B23333' : T.grayLight, fontSize: 13, fontWeight: 500,
          }}>{o.label}</button>
        ))}
      </div>
      <Input label="Any other food allergy?" value={data.food_allergies_other} onChange={set('food_allergies_other')} placeholder="e.g. kiwi, mustard, celery" hint="We pass this to your coach. We cannot filter recipes on it automatically, so check every label." />
      <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600, marginBottom: 10 }}>Dietary preferences / restrictions</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
        {dietOptions.map(o => (
          <button key={o.id} type="button" onClick={() => toggle('dietary_preferences')(o.id)} style={{
            padding: '10px 14px', borderRadius: 10, border: `2px solid ${data.dietary_preferences.includes(o.id) ? T.orange : T.border}`,
            background: data.dietary_preferences.includes(o.id) ? `${T.orange}15` : T.glass, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s',
            color: data.dietary_preferences.includes(o.id) ? T.orange : T.grayLight, fontSize: 13, fontWeight: 500,
          }}>{o.label}</button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 4 }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Hours of sleep per night</label>
          <select value={data.sleep_hours} onChange={set('sleep_hours')} style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: data.sleep_hours ? T.white : T.gray, fontSize: 14, fontFamily: 'Poppins', outline: 'none' }}>
            <option value="">Select...</option>
            {['Less than 5', '5–6', '6–7', '7–8', '8–9', '9+'].map(h => <option key={h} value={h}>{h} hours</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Daily stress level</label>
          <select value={data.stress_level} onChange={set('stress_level')} style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: data.stress_level ? T.white : T.gray, fontSize: 14, fontFamily: 'Poppins', outline: 'none' }}>
            <option value="">Select...</option>
            {['Low', 'Moderate', 'High', 'Very high'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 20, marginTop: 16 }}>
        <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Current medications (optional — affects safe exercise intensity)</label>
        <textarea value={data.medications} onChange={set('medications')} placeholder="e.g. Metformin, beta-blockers, blood thinners, insulin… or type 'none'" rows={2} style={{ width: '100%', padding: '12px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical' }} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Doctor clearance for exercise? (optional)</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {[{ v: 'yes', label: '✅ Yes, cleared' }, { v: 'no', label: '⚠️ Not yet' }, { v: 'not_needed', label: '👍 Not needed' }].map(dc => (
            <button key={dc.v} type="button" onClick={() => set('doctor_clearance')(dc.v)} style={{
              padding: '10px 12px', borderRadius: 8, border: `2px solid ${data.doctor_clearance === dc.v ? T.orange : T.border}`,
              background: data.doctor_clearance === dc.v ? `${T.orange}15` : T.glass, cursor: 'pointer', color: data.doctor_clearance === dc.v ? T.orange : T.grayLight, fontSize: 12, fontWeight: 500, transition: 'all 0.2s',
            }}>{dc.label}</button>
          ))}
        </div>
        {data.doctor_clearance === 'yes' && <ClearanceNote />}
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Anything else your trainer should know?</label>
        <textarea value={data.health_notes} onChange={set('health_notes')} placeholder="Recent surgeries, specific restrictions, other context…" rows={2} style={{ width: '100%', padding: '12px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical' }} />
      </div>
      {Number(data.age) > 0 && Number(data.age) < 18 && (
        <div style={{ background: `${T.danger}0D`, border: `1px solid ${T.danger}44`, borderLeft: `4px solid ${T.danger}`, borderRadius: '0 12px 12px 0', padding: '18px 20px', marginBottom: 20 }}>
          <p style={{ fontSize: 13.5, fontWeight: 700, color: T.danger, marginBottom: 8 }}>A parent or guardian has to agree to this</p>
          <p style={{ fontSize: 12.5, color: T.grayLight, lineHeight: 1.6, marginBottom: 14 }}>
            You told us you are {data.age}. We do not issue a plan to anyone under 18 without a parent or guardian.
            We also do not set calorie limits or weight-loss targets for under-18s, because growth comes first.
          </p>
          <label style={{ display: 'block', fontSize: 12.5, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Parent or guardian full name</label>
          <input value={data.guardian_name} onChange={e => set('guardian_name')(e.target.value)}
            placeholder="Their full name"
            style={{ width: '100%', padding: '11px 14px', borderRadius: 8, border: `1px solid ${T.border}`, fontFamily: 'Poppins', fontSize: 14, boxSizing: 'border-box', marginBottom: 12 }} />
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={data.guardian_consent} onChange={e => set('guardian_consent')(e.target.checked)}
              style={{ marginTop: 2, accentColor: T.danger, width: 18, height: 18, cursor: 'pointer' }} />
            <span style={{ fontSize: 12.5, color: T.white, lineHeight: 1.6 }}>
              I confirm a parent or guardian has read this and agrees to it, and that they know to check with a doctor first.
            </span>
          </label>
        </div>
      )}

      <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600, marginBottom: 4 }}>Emergency contact (optional)</p>
      <p style={{ fontSize: 12, color: T.gray, marginBottom: 10 }}>Someone your coach can call if something goes wrong during a session.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Name" value={data.emergency_contact_name} onChange={set('emergency_contact_name')} placeholder="Full name" />
        <Input label="Relationship" value={data.emergency_contact_relationship} onChange={set('emergency_contact_relationship')} placeholder="e.g. spouse, parent" />
      </div>
      <Input label="Phone" value={data.emergency_contact_phone} onChange={set('emergency_contact_phone')} placeholder="e.g. 865-555-0100" />

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', background: T.glass, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
        <input type="checkbox" checked={!!data.photo_consent} onChange={e => set('photo_consent')(e.target.checked)} style={{ marginTop: 2, accentColor: T.orange, width: 18, height: 18, cursor: 'pointer' }} />
        <span style={{ fontSize: 12.5, color: T.grayLight, lineHeight: 1.6 }}>
          <strong style={{ color: T.white }}>Optional:</strong> HealthyBody may use my progress photos in its advertising. Leave this unticked and your photos stay private to you and your coach. You can change this any time in My Profile.
        </span>
      </label>

      {/* Disclaimer */}
      <div style={{ background: `${T.gold}08`, border: `1px solid ${T.gold}33`, borderRadius: 12, padding: '18px', marginBottom: 20 }}>
        <p style={{ fontSize: 12, color: T.grayLight, lineHeight: 1.7, marginBottom: 14 }}>
          <strong style={{ color: T.gold }}>⚕️ Medical Disclaimer:</strong> This program is for general informational and wellness purposes only. It is not a substitute for professional medical advice, diagnosis, or treatment. Always consult your physician before beginning any exercise or nutrition program, especially if you have existing health conditions. HealthyBody assumes no liability for any injury or health issue resulting from participation in this program.
        </p>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={data.disclaimer_accepted} onChange={e => set('disclaimer_accepted')(e.target.checked)} style={{ marginTop: 2, accentColor: T.orange, width: 18, height: 18, cursor: 'pointer' }} />
          <span style={{ fontSize: 13, color: T.white, lineHeight: 1.6 }}>
            I understand this program is not medical advice. I will consult my doctor before beginning if I have any conditions listed above. I accept full responsibility for my participation. <strong style={{ color: T.orange }}>*</strong>
          </span>
        </label>
      </div>
      {error && <div style={{ background: `${T.danger}15`, border: `1px solid ${T.danger}44`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: T.danger }}>{error}</div>}
    </StepWrap>,
  ];

  if (loadingProfile) return <Spinner />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px 40px' }}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        {editMode && (
          <div style={{ marginBottom: 24, padding: '14px 16px', borderRadius: 12, background: `${T.orange}10`, border: `1px solid ${T.orange}33` }}>
            <p style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600 }}>My Profile</p>
            <p style={{ fontSize: 13, color: T.grayLight }}>Change anything here and every program you have is rebuilt around it when you save.</p>
          </div>
        )}
        {/* Progress */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: T.gray }}>Step {step} of {TOTAL}</span>
            <span style={{ fontSize: 13, color: T.orange, fontWeight: 600 }}>{Math.round((step / TOTAL) * 100)}% complete</span>
          </div>
          <div style={{ height: 4, background: T.border, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(step / TOTAL) * 100}%`, background: `linear-gradient(90deg, ${T.orange}, ${T.gold})`, borderRadius: 2, transition: 'width 0.4s ease' }} />
          </div>
        </div>

        <Card style={{ padding: '40px 36px' }}>
          {steps[step - 1]}
          <div style={{ display: 'flex', gap: 12, marginTop: 32, justifyContent: 'space-between' }}>
            {step > 1 ? <Btn variant="ghost" onClick={() => setStep(s => s - 1)}>← Back</Btn> : <div />}
            {step < TOTAL
              ? <Btn disabled={!canNext()} onClick={() => setStep(s => s + 1)}>Continue →</Btn>
              : <Btn disabled={!canNext() || saving} onClick={finish}>{saving ? '⏳ Building your program…' : '🚀 Build My Program →'}</Btn>
            }
          </div>
        </Card>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: T.gray }}>
          Hi {user?.name?.split(' ')[0]} 👋 — your data is private and never shared.
        </p>
      </div>
    </div>
  );
}

// ── DASHBOARD ─────────────────────────────────────────────────────────────────
// -- MEMBER DASHBOARD (simplified 2026-10-30, build 3) -------------------------
// Mark, 10-30: about 34 tiles was too much. Now: Today in 10 seconds on top, the next getting-started
// step, 6 main tiles chosen by the member's program, and a collapsed "More" with every other link
// grouped by area. Nothing was removed. Eating-only members see no workout tiles; yoga wording
// ("practice", "poses") only for Yoga members; calorie numbers stay hidden when the safety plan says so.
const DASH_TILES = {
  workout: { emoji: '\u{25B6}\u{FE0F}', label: "Today's Workout", to: '/workout/today' },
  practice: { emoji: '\u{25B6}\u{FE0F}', label: "Today's Practice", to: '/workout/today' },
  mealplan: { emoji: '\u{1F37D}\u{FE0F}', label: 'Meal Plan', to: '/meal-plan' },
  messages: { emoji: '\u{1F4AC}', label: 'Messages', to: '/messages' },
  progress: { emoji: '\u{1F4C8}', label: 'My Progress', to: '/insights' },
  photos: { emoji: '\u{1F4F8}', label: 'Photo Check-in', to: '/photos' },
  trainer: { emoji: '\u{1F64B}', label: 'My Trainer', to: '/my-trainer' },
  diary: { emoji: '\u{1F371}', label: 'Food Diary', to: '/food-diary' },
};
const DASH_GROUPS = [
  { title: 'Training', workout: true, items: [
    { emoji: '\u{25B6}\u{FE0F}', label: "Today's Workout", to: '/workout/today' },
    { emoji: '\u{23F1}\u{FE0F}', label: 'Workout Timer', to: '/timer' },
    { emoji: '\u{2705}', label: "Log Today's Workout", to: '/workouts' },
    { emoji: '\u{1F4CB}', label: 'Full Program', to: '/program' },
    { emoji: '\u{1F4C5}', label: 'Calendar', to: '/calendar' },
    { emoji: '\u{1F4C8}', label: 'My Lifts', to: '/my-lifts' },
  ] },
  { title: 'Food', items: [
    { emoji: '\u{1F37D}\u{FE0F}', label: 'Meal Plan', to: '/meal-plan' },
    { emoji: '\u{1F4F7}', label: 'Snap a Meal', to: '/meal-photo' },
    { emoji: '\u{270B}', label: 'Hand Portions', to: '/hand-portions' },
    { emoji: '\u{1F371}', label: 'Food Diary', to: '/food-diary' },
    { emoji: '\u{1F50E}', label: 'Food Library', to: '/food-library' },
    { emoji: '\u{1F966}', label: 'Nutrition Center', to: '/nutrition-center' },
    { emoji: '\u{1F957}', label: 'Recipe Library', to: '/recipes' },
    { emoji: '\u{1F6D2}', label: 'Shopping List', to: '/shopping' },
    { emoji: '\u{1F3A5}', label: 'Cooking Shows', to: '/cooking-shows' },
    { emoji: '\u{1F48A}', label: 'Supplements', to: '/supplements' },
  ] },
  { title: 'Progress', items: [
    { emoji: '\u{1F4C8}', label: 'My Progress', to: '/insights' },
    { emoji: '\u{1F4CB}', label: 'My Weekly Reports', to: '/my-weekly-reports' },
    { emoji: '\u{1F4F8}', label: 'Photo Check-in', to: '/photos' },
    { emoji: '\u{1F5BC}\u{FE0F}', label: 'Progress Photos', to: '/progress' },
    { emoji: '\u{1F4CF}', label: 'Measurements', to: '/measurements' },
    { emoji: '\u{1F4DD}', label: 'Weekly Check-in', to: '/check-in' },
    { emoji: '\u{1F3AF}', label: 'My 90-Day Goal', to: '/goals' },
    { emoji: '\u{1F3C5}', label: 'Badges', to: '/badges' },
  ] },
  { title: 'Coaching & help', items: [
    { emoji: '\u{1F4AC}', label: 'Messages', to: '/messages' },
    { emoji: '\u{1F64B}', label: 'My Trainer', to: '/my-trainer' },
    { emoji: '\u{2705}', label: 'Getting Started', to: '/getting-started' },
    { emoji: '\u{1F4C5}', label: 'Book Training', to: '/book' },
    { emoji: '\u{1F916}', label: 'AI Coach', to: '/ai-coach' },
    { emoji: '\u{2753}', label: 'Help', to: '/help' },
  ] },
  { title: 'Community', items: [
    { emoji: '\u{1F4AC}', label: 'Groups', to: '/groups' },
    { emoji: '\u{1F465}', label: 'Community', to: '/community' },
    { emoji: '\u{1F3C1}', label: 'Challenges', to: '/challenges' },
    { emoji: '\u{1F3C6}', label: 'Contests', to: '/contests' },
    { emoji: '\u{1F91D}', label: 'Referral', to: '/referral' },
    { emoji: '\u{1F332}', label: 'Get Outside', to: '/get-outside' },
  ] },
  { title: 'Wellbeing & account', items: [
    { emoji: '\u{1F634}', label: 'Sleep', to: '/sleep' },
    { emoji: '\u{1F514}', label: 'Notifications', to: '/settings/notifications' },
    { emoji: '\u{1F464}', label: 'My Portal', to: '/portal' },
  ] },
];
const PROGRAM_PAGE_LABEL = { calisthenics: 'My Calisthenics', hiking: 'My Walking Plan', mobility: 'My Mobility Plan', longevity: 'My Strong at 65+ Plan' };
// Which 6 tiles lead the dashboard, by the member's main program.
function dashTilesFor(pd) {
  const p = pd || {};
  if (p.nutritionOnly || p.service === 'food') return ['mealplan', 'diary', 'messages', 'progress', 'photos', 'trainer'].map(k => DASH_TILES[k]);
  const first = p.guidePage ? { emoji: '\u{25B6}\u{FE0F}', label: PROGRAM_PAGE_LABEL[p.mainProgram] || 'My Program', to: p.guidePage }
    : (p.mainProgram || p.service) === 'yoga' ? DASH_TILES.practice : DASH_TILES.workout;
  return [first, ...['mealplan', 'messages', 'progress', 'photos', 'trainer'].map(k => DASH_TILES[k])];
}
function DashTile({ q, big }) {
  const nav = useNavigate();
  return (
    <button onClick={() => nav(q.to)}
      style={{ ...glassBg, padding: big ? '20px 10px' : '14px 8px', borderRadius: 12, cursor: 'pointer', textAlign: 'center', border: `1px solid ${T.border}`, transition: 'all 0.2s', fontFamily: 'Poppins', width: '100%' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = T.orange}
      onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
      <div style={{ fontSize: big ? 30 : 22, marginBottom: 6 }}>{q.emoji}</div>
      <p style={{ fontSize: big ? 14 : 12, color: T.grayLight, fontWeight: big ? 600 : 500 }}>{q.label}</p>
    </button>
  );
}
function Dashboard() {
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [hint, setHint] = useState({}); // before the trainer approves a plan: the member's chosen program and their active programs
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(() => { try { return localStorage.getItem('dash_more') === '1'; } catch (e) { return false; } });
  useEffect(() => {
    Promise.all([api('/api/program').catch(() => ({})), api('/api/profile').catch(() => ({})), api('/api/billing/status').catch(() => ({}))])
      .then(([prog, prof, bill]) => {
        setProgram(prog.program?.program_data || null);
        const active = (bill.active || []).map(a => a.service_key);
        setHint({ main: prof.profile?.main_program || null, foodOnlyModules: active.length > 0 && active.every(k => k === 'food') });
      }).finally(() => setLoading(false));
  }, []);
  if (loading) return <Spinner />;
  const foodOnly = program ? !!(program.nutritionOnly || program.service === 'food') : (hint.main === 'food' || (!hint.main && hint.foodOnlyModules));
  const isYoga = program ? (program.mainProgram || program.service) === 'yoga' : hint.main === 'yoga';
  const sup = (program && program.safety && program.safety.suppress) || [];
  const numbersHidden = sup.includes('calories') || sup.includes('macros');
  const main = dashTilesFor(program || (foodOnly ? { nutritionOnly: true } : { mainProgram: hint.main, guidePage: { calisthenics: '/calisthenics', hiking: '/hiking', mobility: '/mobility', longevity: '/longevity' }[hint.main] }));
  const mainTo = new Set(main.map(t => t.to));
  const groups = DASH_GROUPS.filter(g => !(foodOnly && g.workout)).map(g => ({ ...g, items: g.items.filter(i => !mainTo.has(i.to)) }))
    .map(g => g.title === 'Food' && foodOnly ? { ...g, items: [...g.items, { emoji: '\u{1F4CB}', label: 'My Eating Plan', to: '/program' }] } : g).filter(g => g.items.length);
  const flip = () => { setMore(!more); try { localStorage.setItem('dash_more', more ? '0' : '1'); } catch (e) {} };
  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 16px 60px', maxWidth: 1100, margin: '0 auto' }}>
      <p style={{ fontSize: 14, color: T.gray, margin: '0 0 12px' }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} - Welcome back, <b style={{ color: T.white }}>{user?.name?.split(' ')[0]}</b></p>
      <BroadcastBanner />
      <CC.ClientCareBanner hideSteps />
      <TT.CheckInBanner />
      <NF.PhoneAlertsNudge />
      <TT.TodayCard />
      <B3.NextStepCard />
      <div className="hb-dash-main" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        {main.map(q => <DashTile key={q.to + q.label} q={q} big />)}
      </div>
      <Card style={{ padding: 0, marginBottom: 24 }}>
        <button type="button" onClick={flip} aria-expanded={more} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Oswald', fontSize: 18, letterSpacing: 1, color: T.grayLight }}>
          <span>MORE</span><span style={{ fontFamily: 'Poppins', fontSize: 13, color: T.orange }}>{more ? 'Hide' : 'Show everything'} {more ? '\u25B2' : '\u25BC'}</span>
        </button>
        {more && <div style={{ padding: '0 20px 20px' }}>
          {groups.map(g => <div key={g.title} style={{ marginBottom: 18 }}>
            <p style={{ fontFamily: 'Oswald', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: T.gray, margin: '0 0 8px' }}>{g.title}</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 8 }}>{g.items.map(q => <DashTile key={q.to + q.label} q={q} />)}</div>
          </div>)}
          <p style={{ fontFamily: 'Oswald', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', color: T.gray, margin: '8px 0 8px' }}>My plan at a glance</p>
          <ProgramGlance program={program} numbersHidden={numbersHidden} isYoga={isYoga} foodOnly={foodOnly} />
        </div>}
      </Card>
      <CoachMessages />
    </div>
  );
}

function ProgramGlance({ program, numbersHidden, isYoga, foodOnly }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const currentWeekData = program?.weeks?.[0];
  const todayWorkout = currentWeekData?.days?.find(d => d.day === today);
  const goalLabels = { lose_weight: 'Lose Weight 🔥', build_muscle: 'Build Muscle 💪', both: 'Recomposition ⚡', general_fitness: 'General Health 🌱' };

  return (
    <div>
      <StatsBar />
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 15, color: T.gray, marginBottom: 4 }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, marginBottom: 8 }}>
          WELCOME BACK, <span style={gradText}>{user?.name?.split(' ')[0]?.toUpperCase()}</span>
        </h1>
        <p style={{ color: T.grayLight, fontSize: 16 }}>Your {goalLabels[program?.goal] || 'fitness'} journey continues. Let's make today count.</p>
      </div>

      {/* Key stats row */}
      <div className="animate-fadeInUp animate-delay-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 40 }}>
        {[
          { label: 'Program Level', value: program?.level || '—', icon: '🏆', color: T.orange },
          ...(numbersHidden ? [] : [
          { label: 'Daily Calories', value: program?.targetCalories?.toLocaleString() || '—', icon: '🔥', color: T.gold },
          { label: 'Daily Protein', value: program ? `${program.macros?.protein}g` : '—', icon: '💪', color: T.success }]),
          { label: 'Program Length', value: '4 Weeks', icon: '📅', color: T.orangeLight },
        ].map(s => (
          <Card key={s.label} style={{ padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 32 }}>{s.icon}</div>
            <div>
              <p style={{ fontFamily: 'Bebas Neue', fontSize: 28, color: s.color, lineHeight: 1 }}>{s.value}</p>
              <p style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>{s.label}</p>
            </div>
          </Card>
        ))}
      </div>

      <div className="hb-glance-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
        {/* Today's workout */}
        <div className="animate-fadeInUp animate-delay-2">
          {!foodOnly && <h2 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, marginBottom: 16, color: T.grayLight, letterSpacing: 1 }}>{isYoga ? "TODAY'S PRACTICE" : "TODAY'S WORKOUT"}</h2>}
          {!foodOnly && <Card style={{ padding: 32 }}>
            {todayWorkout && !todayWorkout.rest ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                  <div>
                    <Badge color={T.orange}>Week 1 — {today}</Badge>
                    <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 32, letterSpacing: 1, marginTop: 8 }}>{todayWorkout.type}</h3>
                  </div>
                  <Btn size="sm" onClick={() => nav('/program')}>Full Plan →</Btn>
                </div>
                <div style={{ display: 'grid', gap: 10 }}>
                  {todayWorkout.exercises?.slice(0, 4).map((ex, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#F5F7FA', borderRadius: 10 }}>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 15 }}>{ex.group ? `Superset ${ex.group}: ` : ''}{ex.name}</p>
                        {(ex.targetWeight || ex.effort) && <p style={{ fontSize: 11, fontWeight: 600, color: T.gray, marginTop: 2 }}>Target: {[ex.targetWeight, ex.effort].filter(Boolean).join(', ')}</p>}
                        {ex.note && <p style={{ fontSize: 11, color: T.gray, marginTop: 2 }}>{ex.note}</p>}
                      </div>
                      <span style={{ fontFamily: 'Oswald', fontSize: 15, color: T.orange, whiteSpace: 'nowrap' }}>{ex.sets} × {ex.reps}</span>
                    </div>
                  ))}
                  {(todayWorkout.exercises?.length || 0) > 4 && (
                    <button onClick={() => nav('/program')} style={{ background: 'none', border: 'none', color: T.orange, cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: '8px 0' }}>
                      + {todayWorkout.exercises.length - 4} more {isYoga ? 'poses' : 'exercises'} — view full program →
                    </button>
                  )}
                </div>
                {todayWorkout.cardio && (
                  <div style={{ marginTop: 16, padding: '16px', background: `${T.gold}10`, border: `1px solid ${T.gold}33`, borderRadius: 10 }}>
                    <p style={{ fontWeight: 600, color: T.gold, marginBottom: 4 }}>{isYoga ? '🌬️ Breathwork' : 'Cardio'} — {todayWorkout.cardio.duration}</p>
                    <p style={{ fontSize: 13, color: T.grayLight }}>{todayWorkout.cardio.type}</p>
                  </div>
                )}
              </>
            ) : todayWorkout?.rest ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 64, marginBottom: 16 }}>😴</div>
                <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 36, letterSpacing: 2, marginBottom: 8 }}>{todayWorkout.type}</h3>
                <p style={{ color: T.gray, fontSize: 16 }}>Recovery is where your body gets stronger. Rest today — earn tomorrow.</p>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <p style={{ color: T.gray, fontSize: 16, marginBottom: 20 }}>Your personalized program is ready.</p>
                <Btn onClick={() => nav('/program')}>View My Full Program →</Btn>
              </div>
            )}
          </Card>}
        </div>

        {/* Quick links + macros */}
        <div className="animate-fadeInUp animate-delay-3" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div>
            {!numbersHidden && <h2 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, marginBottom: 16, color: T.grayLight, letterSpacing: 1 }}>TODAY'S MACROS</h2>}
            {!numbersHidden && <Card style={{ padding: 24 }}>
              {program?.macros && (
                <div style={{ display: 'grid', gap: 14 }}>
                  {[
                    { label: 'Calories', value: `${program.targetCalories} kcal`, pct: 100, color: T.orange },
                    { label: 'Protein', value: `${program.macros.protein}g`, pct: 30, color: T.success },
                    { label: 'Carbs', value: `${program.macros.carbs}g`, pct: 45, color: T.gold },
                    { label: 'Fat', value: `${program.macros.fat}g`, pct: 25, color: T.orangeLight },
                  ].map(m => (
                    <div key={m.label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: T.grayLight }}>{m.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: m.color }}>{m.value}</span>
                      </div>
                      <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
                        <div style={{ height: '100%', width: `${m.pct}%`, background: m.color, borderRadius: 2 }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>}
          </div>

        </div>
      </div>
    </div>
  );
}

// ── STATS BAR (dashboard top) ─────────────────────────────────────────────────
function StatsBar() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api('/api/stats').then(d => setStats(d)).catch(() => {}); }, []);
  if (!stats) return null;
  const items = [
    { icon: '🔥', label: 'Current Streak', value: stats.current_streak != null ? stats.current_streak + 'd' : '—' },
    { icon: '💪', label: 'Longest Streak', value: stats.longest_streak != null ? stats.longest_streak + 'd' : '—' },
    { icon: '⚡', label: 'Total Workouts', value: stats.total_workouts != null ? stats.total_workouts : '—' },
    { icon: '🎁', label: 'Free Months', value: stats.free_months != null ? stats.free_months : '0' },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
      {items.map(item => (
        <div key={item.label} style={{ background: T.card, borderRadius: 12, padding: '16px 20px', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 24 }}>{item.icon}</span>
          <div>
            <p style={{ fontFamily: 'Bebas Neue', fontSize: 26, color: T.orange, lineHeight: 1 }}>{item.value}</p>
            <p style={{ fontSize: 11, color: T.gray, marginTop: 2 }}>{item.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── BROADCAST BANNER (dashboard top) ─────────────────────────────────────────
function BroadcastBanner() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed_broadcasts') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    api('/api/broadcasts').then(d => setBroadcasts(d.broadcasts || [])).catch(() => {});
  }, []);

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem('dismissed_broadcasts', JSON.stringify(next));
  };

  const visible = broadcasts.filter(b => !dismissed.includes(b.id));
  if (!visible.length) return null;

  return (
    <div style={{ marginBottom: 28 }}>
      {visible.map(b => (
        <div key={b.id} style={{ padding: '16px 20px', borderRadius: 12, background: b.is_pinned ? `${T.orange}18` : T.glass, border: `1px solid ${b.is_pinned ? T.orange : T.border}`, marginBottom: 10, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>{b.is_pinned ? '📣' : '📢'}</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: 700, fontSize: 15, color: b.is_pinned ? T.orange : T.white, marginBottom: 4 }}>{b.title}</p>
            <p style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.6 }}>{b.message}</p>
            {b.video_url && (
              <a href={b.video_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 13, color: T.orange, fontWeight: 600 }}>▶ Watch Video</a>
            )}
          </div>
          <button onClick={() => dismiss(b.id)} style={{ background: 'none', border: 'none', color: T.gray, cursor: 'pointer', fontSize: 18, padding: 4, flexShrink: 0, lineHeight: 1 }}>✕</button>
        </div>
      ))}
    </div>
  );
}

// ── COACH MESSAGES (dashboard widget) ────────────────────────────────────────
function CoachMessages() {
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api('/api/messages').then(d => { setMessages(d.messages || []); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  if (!loaded || messages.length === 0) return null;

  return (
    <div style={{ marginTop: 40 }}>
      <h2 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, marginBottom: 16, color: T.grayLight, letterSpacing: 1 }}>MESSAGES FROM YOUR COACH</h2>
      <div style={{ display: 'grid', gap: 12 }}>
        {messages.map(m => (
          <Card key={m.id} style={{ padding: '18px 22px', borderLeft: `3px solid ${T.orange}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <p style={{ fontWeight: 600, fontSize: 14, color: T.orange }}>{m.coach_name || 'Your Coach'}</p>
              <p style={{ fontSize: 12, color: T.gray }}>{new Date(m.created_at).toLocaleDateString()}</p>
            </div>
            <p style={{ fontSize: 15, color: T.white, lineHeight: 1.7 }}>{m.message}</p>
            {m.video_url && (
              <a href={m.video_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 10, fontSize: 13, color: T.orange, fontWeight: 600 }}>▶ Watch Video Message</a>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── PROGRAM PAGE ──────────────────────────────────────────────────────────────
function ProgramPage() {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeWeek, setActiveWeek] = useState(0);
  const [activeTab, setActiveTab] = useState('workout');
  const [expandedDay, setExpandedDay] = useState(null);

  const [awaiting, setAwaiting] = useState(false);
  useEffect(() => {
    api('/api/program').then(d => { setProgram(d.program?.program_data); setAwaiting(!!d.awaitingReview); }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  // The trainer reviews every system-built plan before the member sees it (client-care.js).
  if (!program && awaiting) return <CC.AwaitingReview what="exercise plan" />;
  if (!program) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg }}>
      <p style={{ color: T.gray }}>No program found. Please complete onboarding.</p>
    </div>
  );
  // A safety hold has no weeks: say so here too, the same as the dashboard and Today's Workout (2026-10-31).
  if (program.blocked) return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1100, margin: '0 auto' }}>
      <SafetyPanel data={program} accent={T.orange} />
      {!(program.safety && program.safety.minorNoConsent) && <div style={{ marginBottom: 18 }}><ClearanceNote /></div>}
      <Link to="/profile" style={{ display: 'inline-block', padding: '12px 24px', borderRadius: 8, background: T.orange, color: '#fff', fontWeight: 600, textDecoration: 'none' }}>Update my health answers</Link>
    </div>
  );

  const week = program.weeks?.[activeWeek];
  const nutritionOnly = !!program.nutritionOnly;
  const tab = nutritionOnly && activeTab === 'workout' ? 'nutrition' : activeTab;
  const tabStyle = active => ({
    padding: '12px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, letterSpacing: 1, transition: 'all 0.2s',
    background: active ? `linear-gradient(135deg, ${T.orange}, ${T.orangeLight})` : 'transparent',
    color: active ? T.white : T.gray,
  });

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1100, margin: '0 auto' }}>
      <div className="animate-fadeInUp" style={{ marginBottom: 40 }}>
        <Badge color={T.orange}>{program.level} Program</Badge>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(40px, 5vw, 64px)', letterSpacing: 2, marginTop: 12, marginBottom: 8 }}>
          {nutritionOnly ? <>YOUR <span style={gradText}>EATING</span> PLAN</> : <>YOUR <span style={gradText}>{(program.weeks || []).length || 4}-WEEK</span> PROGRAM</>}
        </h1>
        <p style={{ color: T.gray, fontSize: 16 }}>{nutritionOnly
          ? 'Cooking & Eating, built around your health. Add any exercise program and it comes included.'
          : 'Built specifically for you. Follow the plan. Trust the process.'}</p>
        {!nutritionOnly && <Link to="/workout/today" style={{ display: 'inline-block', marginTop: 14, padding: '12px 24px', borderRadius: 8, background: T.orange, color: '#fff', fontWeight: 600, textDecoration: 'none' }}>▶ Start today's workout</Link>}
      </div>

      {/* Week selector */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 32, flexWrap: 'wrap' }}>
        {program.weeks?.map((w, i) => (
          <button key={i} onClick={() => { setActiveWeek(i); setExpandedDay(null); }} style={{
            padding: '10px 24px', borderRadius: 8, border: `2px solid ${activeWeek === i ? T.orange : T.border}`,
            background: activeWeek === i ? `${T.orange}15` : 'transparent', color: activeWeek === i ? T.orange : T.gray,
            cursor: 'pointer', fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, transition: 'all 0.2s',
          }}>Week {w.week}</button>
        ))}
      </div>

      {week && (
        <Card style={{ padding: '20px 24px', marginBottom: 28, background: `${T.orange}08`, borderColor: `${T.orange}33` }}>
          <p style={{ fontSize: 13, color: T.orange, fontWeight: 600, marginBottom: 4 }}>📌 WEEK {week.week} FOCUS</p>
          <p style={{ color: T.grayLight, fontSize: 15, lineHeight: 1.6 }}>{week.progressionNote}</p>
        </Card>
      )}

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: 4, ...glassBg, borderRadius: 12, padding: 4, marginBottom: 32, width: 'fit-content' }}>
        {[{ k: 'workout', label: '💪 Workout' }, { k: 'nutrition', label: '🥗 Nutrition' }, { k: 'recovery', label: '😴 Recovery' }]
          .filter(t => !(nutritionOnly && t.k === 'workout')).map(t => (
          <button key={t.k} onClick={() => setActiveTab(t.k)} style={tabStyle(tab === t.k)}>{t.label}</button>
        ))}
      </div>

      {/* WORKOUT TAB */}
      {tab === 'workout' && week && (
        <div style={{ display: 'grid', gap: 12 }}>
          {week.days?.map((day, i) => (
            <Card key={i} style={{ padding: 0, overflow: 'hidden', cursor: day.rest ? 'default' : 'pointer' }}
              onClick={() => !day.rest && setExpandedDay(expandedDay === i ? null : i)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: day.rest ? T.border : `${T.orange}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                    {day.rest ? '🌙' : '🧘'}
                  </div>
                  <div>
                    <p style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 700, color: day.rest ? T.gray : T.white }}>{day.day}</p>
                    <p style={{ fontSize: 13, color: day.rest ? T.gray : T.orange, marginTop: 2 }}>{day.type}</p>
                  </div>
                </div>
                {!day.rest && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Badge color={T.orange}>{day.exercises?.length || 0} exercises</Badge>
                    <span style={{ color: T.gray, fontSize: 18 }}>{expandedDay === i ? '▲' : '▼'}</span>
                  </div>
                )}
              </div>

              {expandedDay === i && !day.rest && (
                <div style={{ borderTop: `1px solid ${T.border}`, padding: '20px 24px' }}>
                  <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
                    {day.exercises?.map((ex, j) => (
                      <div key={j} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 16px', background: '#F5F7FA', borderRadius: 10, ...(ex.group ? { borderLeft: `4px solid ${T.orange}` } : {}) }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {ex.group && <p style={{ fontSize: 11, fontWeight: 700, color: T.orange, letterSpacing: 1, marginBottom: 2 }}>{ex.group !== (day.exercises[j - 1] || {}).group ? `SUPERSET ${ex.group}: DO THESE BACK TO BACK` : `SUPERSET ${ex.group}`}</p>}
                          <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{ex.name}</p>
                          {(ex.targetWeight || ex.effort) && <p style={{ fontSize: 13, fontWeight: 600, color: T.white, marginBottom: 2 }}>Target: {[ex.targetWeight, ex.effort].filter(Boolean).join(', ')}</p>}
                          {ex.note && <p style={{ fontSize: 12, color: T.gold }}>{ex.note}</p>}
                          <p style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>Rest: {ex.rest}</p>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                          <p style={{ fontFamily: 'Oswald', fontSize: 20, color: T.orange, lineHeight: 1 }}>{ex.sets} × {ex.reps}</p>
                          <p style={{ fontSize: 11, color: T.gray }}>sets × reps</p>
                        </div>
                        {/* The full width of the row (2026-11-02): beside the sets and reps, a video was about 80 wide on a phone. */}
                        <div style={{ flexBasis: '100%', minWidth: 0 }}><CC.ExerciseActions ex={ex} day={day.day} week={activeWeek + 1} /></div>
                      </div>
                    ))}
                  </div>
                  {day.cardio && (
                    <div style={{ padding: '16px', background: `${T.gold}10`, border: `1px solid ${T.gold}33`, borderRadius: 10 }}>
                      <p style={{ fontWeight: 600, color: T.gold, marginBottom: 6 }}>🏃 Finish With Cardio — {day.cardio.duration}</p>
                      <p style={{ fontSize: 14, color: T.grayLight, marginBottom: 4 }}><strong>Type:</strong> {day.cardio.type}</p>
                      <p style={{ fontSize: 13, color: T.gray }}>{day.cardio.intensity}</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* NUTRITION TAB */}
      {tab === 'nutrition' && program.nutrition && (
        <div style={{ display: 'grid', gap: 24 }}>
          <Card style={{ padding: 32 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 20 }}>Daily Targets</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 24 }}>
              {[
                { label: 'Calories', value: `${program.nutrition.dailyCalories}`, unit: 'kcal', color: T.orange },
                { label: 'Protein', value: `${program.nutrition.macros.protein}`, unit: 'grams', color: T.success },
                { label: 'Carbs', value: `${program.nutrition.macros.carbs}`, unit: 'grams', color: T.gold },
                { label: 'Fat', value: `${program.nutrition.macros.fat}`, unit: 'grams', color: T.orangeLight },
              ].map(m => (
                <div key={m.label} style={{ textAlign: 'center', padding: 20, background: `${m.color}10`, border: `1px solid ${m.color}33`, borderRadius: 12 }}>
                  <p style={{ fontFamily: 'Bebas Neue', fontSize: 40, color: m.color, lineHeight: 1 }}>{m.value}</p>
                  <p style={{ fontSize: 11, color: T.gray, marginTop: 2 }}>{m.unit}</p>
                  <p style={{ fontSize: 13, color: T.grayLight, fontWeight: 600 }}>{m.label}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card style={{ padding: 32 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 20 }}>Daily Meal Plan</h3>
            {program.nutrition.allergyWarning && (
              <AllergyNotice screening={{ allergies: program.nutrition.allergies, warning: program.nutrition.allergyWarning }} />
            )}
            <div style={{ display: 'grid', gap: 16 }}>
              {program.nutrition.meals?.map(meal => (
                <div key={meal.name} style={{ padding: '16px 20px', background: '#F5F7FA', borderRadius: 12, borderLeft: `3px solid ${T.orange}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600 }}>{meal.name}</span>
                    <span style={{ fontSize: 13, color: T.gray }}>{meal.time}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {meal.ideas?.map((idea, i) => (
                      <span key={i} style={{ fontSize: 13, color: T.grayLight, background: T.glass, padding: '4px 10px', borderRadius: 20, border: `1px solid ${T.border}` }}>{idea}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card style={{ padding: 32 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 20 }}>Nutrition Tips</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              {program.nutrition.tips?.map((tip, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 16px', background: '#F5F7FA', borderRadius: 10 }}>
                  <span style={{ color: T.orange, fontWeight: 800, marginTop: 1 }}>✓</span>
                  <p style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.6 }}>{tip}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* RECOVERY TAB */}
      {tab === 'recovery' && program.recovery && (
        <div style={{ display: 'grid', gap: 24 }}>
          <Card style={{ padding: 32 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 8 }}>😴 Sleep</h3>
            <p style={{ fontFamily: 'Bebas Neue', fontSize: 40, ...gradText, marginBottom: 16 }}>{program.recovery.sleep?.hours}</p>
            <div style={{ display: 'grid', gap: 8 }}>
              {program.recovery.sleep?.tips?.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 14px', background: '#F5F7FA', borderRadius: 8 }}>
                  <span style={{ color: T.gold }}>★</span>
                  <p style={{ fontSize: 14, color: T.grayLight }}>{t}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card style={{ padding: 32 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 20 }}>🌿 Recovery Poses</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              {program.recovery.stretches?.map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#F5F7FA', borderRadius: 10 }}>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 15 }}>{s.name}</p>
                    <p style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>{s.when}</p>
                  </div>
                  <Badge color={T.orange}>{s.duration}</Badge>
                </div>
              ))}
            </div>
          </Card>

          <Card style={{ padding: 32 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 20 }}>💧 Hydration</h3>
            <div style={{ padding: '16px 20px', background: `${T.success}10`, border: `1px solid ${T.success}33`, borderRadius: 12, marginBottom: 20 }}>
              <p style={{ fontSize: 15, color: T.grayLight, lineHeight: 1.7 }}>{program.recovery.hydration}</p>
            </div>
            <h4 style={{ fontFamily: 'Oswald', fontSize: 18, marginBottom: 12 }}>Recovery Tips</h4>
            <div style={{ display: 'grid', gap: 8 }}>
              {program.recovery.tips?.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 14px', background: '#F5F7FA', borderRadius: 8 }}>
                  <span style={{ color: T.success, marginTop: 1 }}>✓</span>
                  <p style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.6 }}>{t}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── ADMIN PANEL ───────────────────────────────────────────────────────────────
function Admin() {
  const [tab, setTab] = useState(() => { try { return localStorage.getItem('admin_tab') || 'clients'; } catch (e) { return 'clients'; } });
  const pick = id => { setTab(id); try { localStorage.setItem('admin_tab', id); } catch (e) {} };
  // Grouped by job. Cooking & Eating is last because it comes with every plan rather than being one of them.
  const GROUPS = [
    { title: 'Members', items: [
      { id: 'clients', label: '👥 Members' }, { id: 'activity', label: '📊 Member activity' },
      { id: 'appointments', label: '📅 Appointments' }, { id: 'credits', label: '🎁 Free months' },
      { id: 'support', label: '\u{1F4E8} Support' },
    ] },
    { title: 'Team & partners', items: [
      { id: 'coaches', label: '🏆 Coaches & trainers' }, { id: 'client_care', label: '🧭 Rebuilder care & assignment' }, { id: 'trainer_perf', label: '📈 Trainer performance' }, { id: 'weekly_reports', label: '\u{1F4CB} Weekly progress reports' }, { id: 'trainer_apps', label: '🙋 Trainer applications' }, { id: 'trainer_interest', label: '\u{1F4E3} Trainer sign-ups' }, { id: 'lead_statement', label: '💵 Lead statements' }, { id: 'gyms', label: '🏛 Gyms' },
    ] },
    { title: 'Exercise programs', items: [
      { id: 'exercises', label: '🧘 Exercise library' }, { id: 'ai_coach', label: '🤖 AI coach' },
    ] },
    { title: 'Community & marketing', items: [
      { id: 'waitlist', label: '\u{1F4CB} Waitlist & ad pixel' }, { id: 'broadcasts', label: '📣 Announcements' }, { id: 'contests', label: '🥇 Contests' },
      { id: 'community_mod', label: '💬 Community board' }, { id: 'testimonials', label: '⭐ Testimonials' },
      { id: 'referrals', label: '🤝 Referrals' },
    ] },
    { title: 'Settings', items: [ { id: 'specials', label: '🏷 Specials & free trial' }, { id: 'plans_billing', label: '\u{1F4B3} Plans & billing options' }, { id: 'new_features', label: '🔌 Message series, good-morning & food library' }, { id: 'legal', label: '\u{1F4DC} Privacy & Terms' }, { id: 'settings', label: '⚙️ Site settings & keys' } ] },
    { title: 'Cooking & Eating', note: 'Included with every plan', accent: '#2D6A3E', items: [
      { id: 'recipes', label: '🥗 Recipes' }, { id: 'recipe_review', label: '\u{1F4DD} Recipes to review' }, { id: 'shows', label: '🎥 Cooking shows' }, { id: 'cooks', label: '🍳 Home cooks' },
    ] },
  ];
  const current = GROUPS.flatMap(g => g.items).find(i => i.id === tab) || GROUPS[0].items[0];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 1360, margin: '0 auto' }}>
      <div className="animate-fadeInUp" style={{ marginBottom: 24 }}>
        <Badge color={T.gold}>ADMIN</Badge>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(32px, 4vw, 48px)', letterSpacing: 2, marginTop: 10 }}>
          HEALTHYBODY <span style={gradText}>ADMIN</span>
        </h1>
      </div>
      <div className="hb-admin">
        <nav className="hb-admin-nav" aria-label="Admin sections">
          {GROUPS.map(g => (
            <div key={g.title} className="hb-admin-group" style={g.accent ? { borderTop: `2px dashed ${g.accent}55`, paddingTop: 12 } : null}>
              <p style={{ fontFamily: 'Oswald', fontSize: 11.5, letterSpacing: 2, textTransform: 'uppercase', color: g.accent || T.gray, margin: '0 0 6px 8px' }}>{g.title}</p>
              {g.note && <p style={{ fontSize: 11.5, color: g.accent, margin: '-4px 0 6px 8px' }}>{g.note}</p>}
              {g.items.map(i => (
                <button key={i.id} onClick={() => pick(i.id)} aria-current={tab === i.id ? 'page' : undefined} style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', marginBottom: 3, borderRadius: 8, cursor: 'pointer',
                  border: 'none', fontFamily: 'Poppins', fontSize: 14, fontWeight: tab === i.id ? 600 : 500,
                  background: tab === i.id ? (g.accent ? `${g.accent}18` : `${T.orange}15`) : 'transparent',
                  color: tab === i.id ? (g.accent || T.orange) : T.grayLight,
                }}>{i.label}</button>
              ))}
            </div>
          ))}
        </nav>
        <main style={{ minWidth: 0 }}>
          <h2 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 18 }}>{current.label.replace(/^\S+\s/, '')}</h2>
      {tab === 'clients'    && <AdminClients />}
      {tab === 'exercises'  && <AdminExercises />}
      {tab === 'recipes'    && <AdminRecipes />}
      {tab === 'recipe_review' && <RS.AdminReview />}
      {tab === 'coaches'    && <AdminCoaches />}
      {tab === 'cooks'      && <AdminCooks />}
      {tab === 'shows'      && <AdminShows />}
      {tab === 'broadcasts' && <AdminBroadcasts />}
      {tab === 'settings'   && <AdminSettings />}
      {tab === 'specials'   && <AdminSpecials />}
      {tab === 'plans_billing' && <BF.AdminPlans />}
      {tab === 'activity'   && <AdminActivity />}
      {tab === 'contests' && <AdminContests />}
      {tab === 'community_mod' && <AdminCommunityMod />}
      {tab === 'testimonials' && <AdminTestimonials />}
      {tab === 'referrals' && <AdminReferrals />}
      {tab === 'appointments' && <AdminAppointments />}
      {tab === 'credits' && <AdminCredits />}
      {tab === 'ai_coach' && <AdminAICoach />}
      {tab === 'gyms'     && <AdminGyms />}
      {tab === 'client_care' && <CC.AdminClientCare />}
      {tab === 'trainer_perf' && <CC.AdminTrainerPerformance />}
      {tab === 'weekly_reports' && <TP.AdminWeeklyReports />}
      {tab === 'trainer_apps' && <TT.AdminTrainerApps />}
      {tab === 'trainer_interest' && <TT.AdminTrainerInterest />}
      {tab === 'waitlist' && <TT.AdminWaitlist />}
      {tab === 'legal' && <TT.AdminLegal />}
      {tab === 'lead_statement' && <TT.AdminLeadStatement />}
      {tab === 'new_features' && <TT.AdminNewFeatures />}
      {tab === 'support' && <B3.AdminSupport />}
        </main>
      </div>
    </div>
  );
}

// Specials, free trial and initiation fee (2026-10-26). Blank trial days = no end date (today's behavior).
function AdminSpecials() {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const [fee, setFee] = useState('');
  const blank = { id: null, name: '', code: '', trial_days: '', waive_initiation: true, starts_on: '', ends_on: '', max_uses: '', note: '', active: true };
  const [form, setForm] = useState(null);
  const load = () => api('/api/admin/offers').then(r => { setD(r); setFee(r.initiation_fee_amount || ''); }).catch(e => setMsg(e.message));
  useEffect(() => { load(); }, []);
  if (!d) return msg ? <p style={{ color: T.danger }}>{msg}</p> : <Spinner />;
  const std = d.offers.find(o => o.is_standard);
  const specials = d.offers.filter(o => !o.is_standard);
  const feeShown = d.initiation_fee_amount ? `$${d.initiation_fee_amount}` : PRICE_TBD;
  const todayStr = new Date().toISOString().slice(0, 10);
  const liveState = o => !o.active ? 'OFF' : o.ends_on && todayStr > o.ends_on ? 'ENDED' : o.starts_on && todayStr < o.starts_on ? 'NOT STARTED' : o.max_uses != null && o.uses >= o.max_uses ? 'USED UP' : 'ON';
  const trialText = o => o.trial_days == null || o.trial_days === '' ? 'Not set (no end date)' : Number(o.trial_days) === 0 ? 'No free trial' : `${o.trial_days} days free`;
  const save = async (path, method, body, done) => {
    setMsg('');
    try { await api(path, method, body); await load(); setMsg('✓ Saved'); if (done) done(); }
    catch (e) { setMsg(e.message); }
  };
  const fieldStyle = { width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', boxSizing: 'border-box' };
  const F = ({ label, k, type = 'text', placeholder, hint }) => (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={{ display: 'block', fontSize: 12.5, color: T.grayLight, marginBottom: 5 }}>{label}</span>
      <input type={type} value={form[k] ?? ''} placeholder={placeholder} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={fieldStyle} />
      {hint && <span style={{ fontSize: 11.5, color: T.gray }}>{hint}</span>}
    </label>
  );
  const h3 = { fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, margin: '0 0 12px', textTransform: 'uppercase' };
  return (
    <div style={{ maxWidth: 900 }}>
      {!d.payments_on && <Card style={{ padding: 14, marginBottom: 16, border: `1px solid ${T.gold}55` }}><p style={{ fontSize: 13.5, color: T.grayLight }}>Payments are not switched on yet. You can set everything up now. Free trials only start counting down once payments are on, so nobody gets locked out with no way to pay.</p></Card>}
      {msg && <p style={{ color: msg.startsWith('✓') ? T.success : T.danger, marginBottom: 12 }}>{msg}</p>}

      <h3 style={h3}>Initiation fee</h3>
      <Card style={{ padding: 20, marginBottom: 22 }}>
        <p style={{ fontSize: 13.5, color: T.grayLight, marginBottom: 12 }}>A one-time fee on a member's first payment. Any special (or the standard sign-up) can waive it, so it shows to the member as {feeShown} with a $0 price.</p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ flex: '0 1 200px' }}><span style={{ display: 'block', fontSize: 12.5, color: T.grayLight, marginBottom: 5 }}>Amount shown on the site ($)</span>
            <input value={fee} placeholder="xxx (not set)" onChange={e => setFee(e.target.value)} style={fieldStyle} /></label>
          <Btn variant="secondary" onClick={() => save('/api/admin/initiation-fee', 'PUT', { amount: fee })}>Save amount</Btn>
        </div>
        <p style={{ fontSize: 12, color: d.initiation_price_set ? T.success : T.gray, marginTop: 10 }}>
          {d.initiation_price_set ? '✓ Stripe charge is set up.' : 'Stripe charge not set up yet: the owner adds a one-time Stripe Price ID as "stripe_price_initiation" under Site settings & keys. Until then no fee is charged.'} The amount here must match that Stripe price.
        </p>
      </Card>

      <h3 style={h3}>Standard sign-up (no code)</h3>
      <Card style={{ padding: 20, marginBottom: 22 }}>
        <p style={{ fontSize: 13.5, color: T.grayLight, marginBottom: 6 }}>Free trial: <strong style={{ color: T.white }}>{trialText(std)}</strong> · Initiation fee: <strong style={{ color: T.white }}>{std.waive_initiation ? 'waived ($0)' : feeShown}</strong> · Members on it: {std.members}</p>
        {form && form.id === std.id ? (
          <div style={{ marginTop: 12 }}>
            <p style={{ fontSize: 12.5, color: T.gray, marginBottom: 12 }}>A free trial is only offered through a special. While payments are off, sign-ups get their program with no end date; once payments are on, the standard sign-up gives no free time.</p>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 14 }}><input type="checkbox" checked={!!form.waive_initiation} onChange={e => setForm(f => ({ ...f, waive_initiation: e.target.checked }))} /> Waive the initiation fee</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn onClick={() => save(`/api/admin/offers/${std.id}`, 'PUT', form, () => setForm(null))}>Save</Btn>
              <Btn variant="secondary" onClick={() => setForm(null)}>Cancel</Btn>
            </div>
          </div>
        ) : <div style={{ marginTop: 10 }}><Btn variant="secondary" onClick={() => setForm({ ...std, trial_days: std.trial_days ?? '', waive_initiation: !!std.waive_initiation })}>Change</Btn></div>}
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ ...h3, margin: 0 }}>Specials ({specials.length})</h3>
        {!(form && !form.id) && <Btn size="sm" onClick={() => setForm(blank)}>+ New special</Btn>}
      </div>
      {form && form.id !== std.id && (
        <Card style={{ padding: 20, marginBottom: 16, border: `1px solid ${T.orange}55` }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 16px' }}>
            {F({ label: "Name (members see this)", k: "name", placeholder: "e.g. New Year special" })}
            {F({ label: "Code", k: "code", placeholder: "e.g. NEWYEAR", hint: "Letters, numbers, dashes. Link: …/signup?offer=CODE" })}
            {F({ label: "Free trial days", k: "trial_days", type: "number", placeholder: "blank = no end date" })}
            {F({ label: "Uses allowed", k: "max_uses", type: "number", placeholder: "blank = unlimited" })}
            {F({ label: "Starts on", k: "starts_on", type: "date" })}
            {F({ label: "Ends on", k: "ends_on", type: "date" })}
          </div>
          {F({ label: "Note (staff only)", k: "note", placeholder: "Where this code is advertised" })}
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 8 }}><input type="checkbox" checked={!!form.waive_initiation} onChange={e => setForm(f => ({ ...f, waive_initiation: e.target.checked }))} /> Waive the initiation fee</label>
          {form.id && <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginBottom: 14 }}><input type="checkbox" checked={!!form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Switched on</label>}
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <Btn onClick={() => form.id ? save(`/api/admin/offers/${form.id}`, 'PUT', form, () => setForm(null)) : save('/api/admin/offers', 'POST', form, () => setForm(null))}>{form.id ? 'Save special' : 'Create special'}</Btn>
            <Btn variant="secondary" onClick={() => setForm(null)}>Cancel</Btn>
          </div>
        </Card>
      )}
      {!specials.length && <p style={{ color: T.gray, fontSize: 14 }}>No specials yet.</p>}
      <div style={{ display: 'grid', gap: 10 }}>
        {specials.map(o => (
          <Card key={o.id} style={{ padding: 16, opacity: o.active ? 1 : 0.6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <div>
                <p style={{ fontWeight: 600 }}>{o.name} <Badge color={liveState(o) === 'ON' ? T.success : T.gray}>{liveState(o)}</Badge></p>
                <p style={{ fontSize: 13, color: T.grayLight, marginTop: 4 }}>Code <strong style={{ fontFamily: 'monospace' }}>{o.code}</strong> · {trialText(o)} · Initiation {o.waive_initiation ? 'waived' : feeShown}{o.starts_on || o.ends_on ? ` · ${o.starts_on || 'now'} to ${o.ends_on || 'no end'}` : ''} · Used {o.uses}{o.max_uses != null ? ` of ${o.max_uses}` : ''}</p>
                {o.note && <p style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>{o.note}</p>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn size="sm" variant="secondary" onClick={() => setForm({ ...o, trial_days: o.trial_days ?? '', max_uses: o.max_uses ?? '', starts_on: o.starts_on || '', ends_on: o.ends_on || '', waive_initiation: !!o.waive_initiation, active: !!o.active })}>Edit</Btn>
                <Btn size="sm" variant="secondary" onClick={() => save(`/api/admin/offers/${o.id}`, 'PUT', { active: !o.active })}>{o.active ? 'Switch off' : 'Switch on'}</Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AdminGyms() {
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(null); // gym object
  const [createForm, setCreateForm] = useState({ name: '', display_name: '', slug: '', primary_color: 'FF4500', secondary_color: 'FFB800', tier: 'starter', seat_count: '25', content_licensing: false });
  const [adminForm, setAdminForm] = useState({ name: '', email: '', password: '' });
  const [createError, setCreateError] = useState('');
  const [adminError, setAdminError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api('/api/admin/gyms').then(d => setGyms(d.gyms || [])).catch(e => console.error(e)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setC = k => e => setCreateForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const setA = k => e => setAdminForm(f => ({ ...f, [k]: e.target.value }));

  const slugify = name => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const createGym = async e => {
    e.preventDefault();
    setSaving(true); setCreateError('');
    try {
      await api('/api/admin/gyms', 'POST', { ...createForm, seat_count: parseInt(createForm.seat_count) || 25, content_licensing: createForm.content_licensing ? 1 : 0 });
      setShowCreate(false);
      setCreateForm({ name: '', display_name: '', slug: '', primary_color: 'FF4500', secondary_color: 'FFB800', tier: 'starter', seat_count: '25', content_licensing: false });
      load();
    } catch (err) { setCreateError(err.message); }
    finally { setSaving(false); }
  };

  const createAdmin = async e => {
    e.preventDefault();
    if (!showAdminModal) return;
    setSaving(true); setAdminError('');
    try {
      await api(`/api/admin/gyms/${showAdminModal.id}/admin`, 'POST', adminForm);
      setShowAdminModal(null);
      setAdminForm({ name: '', email: '', password: '' });
      alert(`Admin account created for ${showAdminModal.display_name}. Credentials will be delivered in the Word document.`);
    } catch (err) { setAdminError(err.message); }
    finally { setSaving(false); }
  };

  const TIERS = ['starter', 'growth', 'scale', 'enterprise'];

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 28, letterSpacing: 2, margin: 0 }}>WHITE-LABEL GYMS</h2>
          <p style={{ color: T.gray, fontSize: 13, marginTop: 4 }}>{gyms.length} gym{gyms.length !== 1 ? 's' : ''} on platform</p>
        </div>
        <Btn onClick={() => setShowCreate(true)} size="sm">+ New Gym</Btn>
      </div>

      {/* Create modal */}
      {showCreate && (
        <Card style={{ marginBottom: 32, padding: 28, border: `1px solid ${T.orange}44` }}>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 20, letterSpacing: 1, marginBottom: 20 }}>Create New Gym</h3>
          <form onSubmit={createGym}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Input label="Internal Name (legal)" value={createForm.name} onChange={setC('name')} placeholder="CrossFit Eastside LLC" required />
              <Input label="Display Name (members see)" value={createForm.display_name} onChange={e => { setC('display_name')(e); setCreateForm(f => ({ ...f, slug: f.slug || slugify(e.target.value) })); }} placeholder="CrossFit Eastside" required />
              <Input label="Slug (URL identifier)" value={createForm.slug} onChange={setC('slug')} placeholder="crossfit-eastside" required />
              <div>
                <label style={{ fontSize: 12, color: T.gray, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Tier</label>
                <select value={createForm.tier} onChange={setC('tier')} style={{ width: '100%', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', fontSize: 14, color: T.white, fontFamily: 'Poppins' }}>
                  {TIERS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <Input label="Seat Count" type="number" value={createForm.seat_count} onChange={setC('seat_count')} placeholder="25" />
              <Input label="Primary Color (hex, no #)" value={createForm.primary_color} onChange={setC('primary_color')} placeholder="FF4500" />
            </div>
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="cl" checked={createForm.content_licensing} onChange={setC('content_licensing')} style={{ width: 18, height: 18, accentColor: T.orange }} />
              <label htmlFor="cl" style={{ fontSize: 14, color: T.grayLight }}>Include content library (+$2/member/mo)</label>
            </div>
            {createError && <div style={{ marginTop: 16, color: T.danger, fontSize: 14 }}>{createError}</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <Btn type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create Gym'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowCreate(false); setCreateError(''); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      {/* Gym admin account modal */}
      {showAdminModal && (
        <Card style={{ marginBottom: 32, padding: 28, border: `1px solid ${T.gold}44` }}>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 20, letterSpacing: 1, marginBottom: 8 }}>Create Admin for {showAdminModal.display_name}</h3>
          <p style={{ color: T.gray, fontSize: 13, marginBottom: 20 }}>This creates a gym_admin account linked to this gym. The credentials will be saved in a Word document only — they will not appear on screen.</p>
          <form onSubmit={createAdmin}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <Input label="Admin Full Name" value={adminForm.name} onChange={setA('name')} placeholder="Jane Smith" required />
              <Input label="Admin Email" type="email" value={adminForm.email} onChange={setA('email')} placeholder="jane@theirgym.com" required />
              <Input label="Temporary Password" type="password" value={adminForm.password} onChange={setA('password')} placeholder="Min 12 characters" required />
            </div>
            {adminError && <div style={{ marginTop: 16, color: T.danger, fontSize: 14 }}>{adminError}</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <Btn type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create Admin Account'}</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdminModal(null); setAdminError(''); }}>Cancel</Btn>
            </div>
          </form>
        </Card>
      )}

      {/* Gym list */}
      {gyms.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '48px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🏛</div>
          <p style={{ fontSize: 16, color: T.grayLight, marginBottom: 8 }}>No gyms yet</p>
          <p style={{ fontSize: 13, color: T.gray }}>Click "+ New Gym" to onboard your first white-label gym partner.</p>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {gyms.map(g => (
            <Card key={g.id} style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: `#${g.primary_color || 'FF4500'}` }} />
                    <h3 style={{ fontFamily: 'Oswald', fontSize: 20, letterSpacing: 1, margin: 0 }}>{g.display_name}</h3>
                    <Badge color={g.active ? T.success : T.danger}>{g.active ? 'Active' : 'Inactive'}</Badge>
                    <Badge color={T.gold}>{g.tier}</Badge>
                    {g.content_licensing ? <Badge color={T.orange}>+Content</Badge> : null}
                  </div>
                  <p style={{ color: T.gray, fontSize: 13, margin: 0 }}>
                    {g.name} &nbsp;·&nbsp; slug: <code style={{ fontSize: 12, color: T.grayLight }}>{g.slug}</code> &nbsp;·&nbsp; {g.member_count} of {g.seat_count} seats used
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <Btn size="sm" variant="ghost" onClick={() => setShowAdminModal(g)}>+ Admin</Btn>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Old tier names kept for existing records; their prices were invented and are not set (2026-10-27).
const TIERS = {
  foundation:  { label: 'Foundation',  price: null, color: '#4A90D9', desc: 'Core workouts + nutrition' },
  coached:     { label: 'Coached',     price: null, color: T.success, desc: 'All features + coach access' },
  elite:       { label: 'Elite',       price: null, color: T.gold,    desc: 'Elite programming + priority support' },
  black_label: { label: 'Black Label', price: null, color: '#222',    desc: 'Concierge-level 1-on-1 coaching' },
};

const STATUS_META = {
  none:      { label: 'No subscription', color: T.gray },
  active:    { label: 'Active',          color: T.success },
  past_due:  { label: 'Past Due',        color: T.gold },
  suspended: { label: 'Suspended',       color: T.danger },
  canceled:  { label: 'Canceled',        color: T.gray },
};

function AdminClients() {
  const [stats, setStats]       = useState(null);
  const [users, setUsers]       = useState([]);
  const [billing, setBilling]   = useState({});
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [billingDetail, setBillingDetail] = useState(null);
  const [events, setEvents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [detailTab, setDetailTab] = useState('profile');
  const [billingForm, setBillingForm] = useState({ tier: '', method: '', next_date: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [adminMsgText, setAdminMsgText] = useState('');
  const [adminMsgVideo, setAdminMsgVideo] = useState('');
  const [adminMsgSending, setAdminMsgSending] = useState(false);
  const [clientMessages, setClientMessages] = useState([]);
  const [suspendReason, setSuspendReason] = useState('');

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api('/api/admin/stats'),
      api('/api/admin/users'),
      api('/api/admin/billing'),
    ]).then(([s, u, b]) => {
      setStats(s);
      setUsers(u.users);
      const map = {};
      (b.users || []).forEach(x => { map[x.id] = x; });
      setBilling(map);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  const viewUser = async id => {
    setSelected(id);
    setDetailTab('profile');
    setEvents([]);
    const [d, evts] = await Promise.all([
      api(`/api/admin/user/${id}`),
      api(`/api/admin/billing/${id}/events`).catch(() => ({ events: [] })),
    ]);
    setDetail(d);
    setBillingDetail(billing[id] || null);
    setEvents(evts.events || []);
    const b = billing[id];
    setBillingForm({ tier: b?.subscription_tier || '', method: b?.billing_method || '', next_date: b?.next_billing_date || '' });
  };

  const deleteUser = async id => {
    if (!window.confirm('Delete this Rebuilder permanently?')) return;
    await api(`/api/admin/user/${id}`, 'DELETE');
    setUsers(u => u.filter(x => x.id !== id));
    setSelected(null); setDetail(null);
  };

  const saveBilling = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await api(`/api/admin/billing/${selected}`, 'PUT', {
        subscription_tier: billingForm.tier || null,
        billing_method: billingForm.method || null,
        next_billing_date: billingForm.next_date || null,
      });
      const fresh = await api('/api/admin/billing');
      const map = {};
      (fresh.users || []).forEach(x => { map[x.id] = x; });
      setBilling(map);
      setBillingDetail(map[selected] || null);
    } catch(e) { alert(e.message); }
    finally { setActionLoading(false); }
  };

  const billingAction = async (endpoint, body = {}, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActionLoading(true);
    try {
      await api(`/api/admin/billing/${selected}/${endpoint}`, 'POST', body);
      const [fresh, evts] = await Promise.all([
        api('/api/admin/billing'),
        api(`/api/admin/billing/${selected}/events`).catch(() => ({ events: [] })),
      ]);
      const map = {};
      (fresh.users || []).forEach(x => { map[x.id] = x; });
      setBilling(map);
      setBillingDetail(map[selected] || null);
      setEvents(evts.events || []);
    } catch(e) { alert(e.message); }
    finally { setActionLoading(false); }
  };

  useEffect(() => {
    if (detailTab !== 'messages' || !selected) return;
    api(`/api/admin/clients/${selected}/messages`).then(d => setClientMessages(d.messages || [])).catch(() => {});
  }, [detailTab, selected]);

  const sendAdminMsg = async () => {
    if (!adminMsgText.trim() || !selected) return;
    setAdminMsgSending(true);
    try {
      await api('/api/coach/message', 'POST', { clientId: selected, message: adminMsgText, videoUrl: adminMsgVideo || undefined });
      setAdminMsgText('');
      setAdminMsgVideo('');
      const d = await api(`/api/admin/clients/${selected}/messages`);
      setClientMessages(d.messages || []);
    } catch(e) { alert(e.message); }
    finally { setAdminMsgSending(false); }
  };

  if (loading) return <Spinner />;

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const goalLabels = { lose_weight: '🔥 Lose Weight', build_muscle: '💪 Build Muscle', both: '⚡ Both', general_fitness: '🌱 General Health' };

  const pastDueCount   = Object.values(billing).filter(b => b.subscription_status === 'past_due').length;
  const suspendedCount = Object.values(billing).filter(b => b.subscription_status === 'suspended').length;
  const activeCount    = Object.values(billing).filter(b => b.subscription_status === 'active').length;
  const mrrEstimate    = Object.values(billing).filter(b => b.subscription_status === 'active').reduce((sum, b) => sum + (b.monthly_amount || 0), 0);

  const bd = billingDetail;
  const status = bd?.subscription_status || 'none';
  const statusM = STATUS_META[status] || STATUS_META.none;

  return (
    <>
      {/* The old four plan cards (Foundation, Coached, Elite, Black Label) are gone (2026-10-31): plans and prices live in Settings > Plans & billing options. */}
      <Card style={{ padding: '14px 18px', marginBottom: 24 }}>
        <p style={{ fontSize: 13, color: T.gray, margin: 0 }}>Plans, prices and payment schedules are set in <b>Settings &gt; Plans &amp; billing options</b>. The "Subscription tier" below is the old plan name, kept only for records made before October 30, 2026.</p>
      </Card>

      {/* Summary stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 28 }}>
          {[
            { label: 'Total Rebuilders',   value: stats.total,        color: T.orange },
            { label: 'Active Billing',  value: activeCount,         color: T.success },
            { label: 'Est. MRR',        value: `$${mrrEstimate}`,   color: T.gold },
            { label: 'Past Due',        value: pastDueCount,        color: pastDueCount > 0 ? T.gold : T.gray },
            { label: 'Suspended',       value: suspendedCount,      color: suspendedCount > 0 ? T.danger : T.gray },
          ].map(s => (
            <Card key={s.label} style={{ textAlign: 'center', padding: '18px 12px' }}>
              <p style={{ fontFamily: 'Bebas Neue', fontSize: 36, color: s.color, lineHeight: 1 }}>{s.value}</p>
              <p style={{ fontSize: 12, color: T.gray, marginTop: 4 }}>{s.label}</p>
            </Card>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '340px 1fr' : '1fr', gap: 24 }}>
        {/* Client list */}
        <div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search Rebuilders…"
            style={{ width: '100%', padding: '11px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 10, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', marginBottom: 12, boxSizing: 'border-box' }} />
          <div style={{ display: 'grid', gap: 8, maxHeight: 600, overflowY: 'auto' }}>
            {filtered.length === 0 && <p style={{ color: T.gray, textAlign: 'center', padding: 40 }}>No Rebuilders yet.</p>}
            {filtered.map(u => {
              const b = billing[u.id];
              const st = b?.subscription_status || 'none';
              const sm = STATUS_META[st] || STATUS_META.none;
              const isPastDue = st === 'past_due';
              const isSuspended = st === 'suspended';
              return (
                <Card key={u.id} onClick={() => viewUser(u.id)} style={{
                  padding: '14px 16px', cursor: 'pointer',
                  borderColor: selected === u.id ? T.orange : (isSuspended ? `${T.danger}66` : isPastDue ? `${T.gold}66` : T.border),
                  background: selected === u.id ? `${T.orange}08` : T.glass,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 1 }}>{u.name}</p>
                      <p style={{ fontSize: 12, color: T.gray, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</p>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 8 }}>
                      <Badge color={sm.color}>{sm.label}</Badge>
                      {b?.subscription_tier && (
                        <p style={{ fontSize: 11, color: T.gray, marginTop: 3 }}>{TIERS[b.subscription_tier]?.label || b.subscription_tier}</p>
                      )}
                    </div>
                  </div>
                  {isPastDue && b.grace_period_ends_at && (
                    <p style={{ fontSize: 11, color: T.gold, marginTop: 6 }}>
                      ⚠️ Grace ends {new Date(b.grace_period_ends_at).toLocaleDateString()}
                    </p>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        {selected && detail && (
          <Card style={{ padding: 28, overflowY: 'auto', maxHeight: 700 }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h2 style={{ fontFamily: 'Oswald', fontSize: 24, fontWeight: 700, margin: 0 }}>{detail.user.name}</h2>
                <p style={{ fontSize: 13, color: T.gray, marginTop: 2 }}>{detail.user.email}</p>
                <p style={{ fontSize: 11, color: T.gray, marginTop: 2 }}>Joined {new Date(detail.user.created_at).toLocaleDateString()}</p>
              </div>
              <Btn variant="danger" size="sm" onClick={() => deleteUser(detail.user.id)}>Delete</Btn>
            </div>

            {/* Tab nav */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: `1px solid ${T.border}`, paddingBottom: 12 }}>
              {[['profile', '👤 Profile'], ['billing', '💳 Billing'], ['history', '📋 History'], ['messages', '📬 Message']].map(([k, l]) => (
                <button key={k} onClick={() => setDetailTab(k)} style={{
                  padding: '7px 16px', borderRadius: 6, border: `1px solid ${detailTab === k ? T.orange : T.border}`,
                  background: detailTab === k ? `${T.orange}22` : 'transparent',
                  color: detailTab === k ? T.orange : T.gray, cursor: 'pointer', fontSize: 13, fontFamily: 'Poppins',
                }}>
                  {l}
                </button>
              ))}
            </div>

            {/* Profile tab */}
            {detailTab === 'profile' && (
              <div style={{ display: 'grid', gap: 16 }}>
                {detail.profile ? (
                  <div style={{ display: 'grid', gap: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      {[
                        { label: 'Age', value: detail.profile.age },
                        { label: 'Sex', value: detail.profile.sex },
                        { label: 'Height', value: `${detail.profile.height_feet}′${detail.profile.height_inches}″` },
                        { label: 'Current Weight', value: `${detail.profile.weight} lbs` },
                        { label: 'Goal Weight', value: `${detail.profile.goal_weight} lbs` },
                        { label: 'Body Fat Est.', value: detail.profile.body_fat_pct ? `${detail.profile.body_fat_pct}%` : 'Not provided' },
                        { label: 'Goal', value: goalLabels[detail.profile.goal] || detail.profile.goal },
                        { label: 'Activity Level', value: detail.profile.activity_level?.replace(/_/g, ' ') },
                        { label: 'Experience', value: detail.profile.fitness_experience?.replace(/_/g, ' ') },
                        { label: 'Sleep', value: detail.profile.sleep_hours ? `${detail.profile.sleep_hours} hrs/night` : 'Not provided' },
                        { label: 'Stress Level', value: detail.profile.stress_level || 'Not provided' },
                        { label: 'Doctor Clearance', value: detail.profile.doctor_clearance?.replace(/_/g, ' ') || 'Not provided' },
                      ].map(f => f.value ? (
                        <div key={f.label} style={{ padding: '10px 12px', background: '#F5F7FA', borderRadius: 8 }}>
                          <p style={{ fontSize: 10, color: T.gray, marginBottom: 2 }}>{f.label}</p>
                          <p style={{ fontSize: 14, fontWeight: 600, textTransform: 'capitalize' }}>{f.value}</p>
                        </div>
                      ) : null)}
                    </div>
                    {(() => {
                      let hi = []; try { hi = JSON.parse(detail.profile.health_issues || '[]'); } catch(e) {}
                      let inj = []; try { inj = JSON.parse(detail.profile.injuries || '[]'); } catch(e) {}
                      let equip = []; try { equip = JSON.parse(detail.profile.equipment || '[]'); } catch(e) {}
                      let diet = []; try { diet = JSON.parse(detail.profile.dietary_preferences || '[]'); } catch(e) {}
                      return (<>
                        {hi.length > 0 && (
                          <div style={{ padding: '12px 14px', background: `${T.gold}08`, border: `1px solid ${T.gold}33`, borderRadius: 10 }}>
                            <p style={{ fontSize: 11, color: T.gold, fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }}>Medical Conditions</p>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {hi.map(h => <Badge key={h} color={T.gold}>{h.replace(/_/g, ' ')}</Badge>)}
                            </div>
                          </div>
                        )}
                        {inj.length > 0 && (
                          <div style={{ padding: '12px 14px', background: `${T.danger}08`, border: `1px solid ${T.danger}33`, borderRadius: 10 }}>
                            <p style={{ fontSize: 11, color: T.danger, fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }}>Injuries / Physical Limits</p>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {inj.map(i => <Badge key={i} color={T.danger}>{i.replace(/_/g, ' ')}</Badge>)}
                            </div>
                          </div>
                        )}
                        {detail.profile.medications && (
                          <div style={{ padding: '12px 14px', background: `${T.danger}06`, border: `1px solid ${T.danger}22`, borderRadius: 10 }}>
                            <p style={{ fontSize: 11, color: T.danger, fontWeight: 700, letterSpacing: 1, marginBottom: 4, textTransform: 'uppercase' }}>Current Medications</p>
                            <p style={{ fontSize: 13, color: T.grayLight }}>{detail.profile.medications}</p>
                          </div>
                        )}
                        {equip.length > 0 && (
                          <div>
                            <p style={{ fontSize: 11, color: T.gray, fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }}>Equipment Available</p>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {equip.map(e => <Badge key={e} color={T.success}>{e.replace(/_/g, ' ')}</Badge>)}
                            </div>
                          </div>
                        )}
                        {diet.length > 0 && (
                          <div>
                            <p style={{ fontSize: 11, color: T.gray, fontWeight: 700, letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }}>Dietary Preferences</p>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {diet.map(d => <Badge key={d} color={T.orange}>{d.replace(/_/g, ' ')}</Badge>)}
                            </div>
                          </div>
                        )}
                        {detail.profile.health_notes && (
                          <div>
                            <p style={{ fontSize: 11, color: T.gray, fontWeight: 700, letterSpacing: 1, marginBottom: 4, textTransform: 'uppercase' }}>Additional Health Notes</p>
                            <p style={{ fontSize: 13, color: T.grayLight, padding: '10px', background: '#F5F7FA', borderRadius: 8 }}>{detail.profile.health_notes}</p>
                          </div>
                        )}
                      </>);
                    })()}
                    {detail.program && (
                      <div style={{ padding: '14px 16px', background: `${T.orange}08`, border: `1px solid ${T.orange}33`, borderRadius: 10 }}>
                        <p style={{ fontSize: 13, color: T.orange, fontWeight: 600, marginBottom: 6 }}>Current Program</p>
                        <p style={{ fontSize: 13, color: T.grayLight }}>Level: <strong>{detail.program.program_data?.level}</strong></p>
                        <p style={{ fontSize: 13, color: T.grayLight }}>Daily calories: <strong>{detail.program.program_data?.targetCalories?.toLocaleString()} kcal</strong></p>
                        <p style={{ fontSize: 13, color: T.grayLight }}>Generated: {new Date(detail.program.created_at).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ color: T.gray, textAlign: 'center', padding: '20px 0' }}>Rebuilder hasn't completed onboarding yet.</p>
                )}

                {/* Club Rules & Conduct */}
                <div style={{ padding: '16px 18px', background: `${T.danger}08`, border: `1px solid ${T.danger}44`, borderRadius: 10 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: T.danger, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Club Rules &amp; Conduct</p>
                  {status === 'suspended' ? (
                    <div>
                      <p style={{ fontSize: 13, color: T.danger, marginBottom: 4 }}>
                        This member is currently <strong>suspended</strong>
                        {bd?.suspended_at ? ` (since ${new Date(bd.suspended_at).toLocaleDateString()})` : ''}.
                      </p>
                      <p style={{ fontSize: 12, color: T.gray, marginBottom: 12 }}>See the Billing tab for full details and event history.</p>
                      <Btn size="sm" onClick={() => billingAction('reactivate', {}, `Reactivate ${detail?.user?.name || 'this member'}?`)} disabled={actionLoading} style={{ background: T.success }}>
                        Reactivate Member
                      </Btn>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 11, color: T.gray, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Reason for suspension</label>
                        <textarea
                          value={suspendReason}
                          onChange={e => setSuspendReason(e.target.value)}
                          placeholder="e.g. Repeated violation of gym conduct policy..."
                          rows={2}
                          style={{ width: '100%', resize: 'vertical', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 10px', fontSize: 13, color: T.white, fontFamily: 'Poppins' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Btn size="sm" variant="ghost" style={{ borderColor: T.danger, color: T.danger }}
                          onClick={() => {
                            if (!suspendReason.trim()) { alert('Please enter a reason for the suspension.'); return; }
                            billingAction('suspend', { reason: suspendReason }, `Suspend ${detail?.user?.name || 'this member'} for rule violation?`);
                          }}
                          disabled={actionLoading}>
                          Suspend for Rule Violation
                        </Btn>
                        <Btn size="sm"
                          onClick={() => deleteUser(detail.user.id)}
                          disabled={actionLoading}
                          style={{ background: '#7f1d1d', color: '#fff', border: 'none' }}>
                          Remove from Club
                        </Btn>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Billing tab */}
            {detailTab === 'billing' && (
              <div style={{ display: 'grid', gap: 20 }}>
                {/* Status banner */}
                <div style={{ padding: '14px 18px', background: `${statusM.color}15`, border: `1px solid ${statusM.color}44`, borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <p style={{ fontSize: 11, color: T.gray, marginBottom: 2 }}>BILLING STATUS</p>
                    <p style={{ fontSize: 18, fontWeight: 700, color: statusM.color }}>{statusM.label}</p>
                    {bd?.payment_failed_at && (
                      <p style={{ fontSize: 12, color: T.gold, marginTop: 4 }}>
                        Failed: {new Date(bd.payment_failed_at).toLocaleDateString()}
                        {bd.grace_period_ends_at && ` · Grace ends ${new Date(bd.grace_period_ends_at).toLocaleDateString()}`}
                      </p>
                    )}
                    {bd?.payment_failed_reason && (
                      <p style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>Reason: {bd.payment_failed_reason}</p>
                    )}
                    {bd?.suspended_at && (
                      <p style={{ fontSize: 12, color: T.danger, marginTop: 4 }}>Suspended {new Date(bd.suspended_at).toLocaleDateString()}</p>
                    )}
                  </div>
                  {/* Quick action buttons based on status */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(status === 'active' || status === 'none') && (
                      <Btn size="sm" variant="ghost" style={{ borderColor: T.gold, color: T.gold }}
                        onClick={() => {
                          const r = window.prompt('Reason for payment failure (e.g. "Card declined", "ACH returned"):');
                          if (r !== null) billingAction('mark-failed', { reason: r || 'Payment declined' }, null);
                        }}
                        disabled={actionLoading}>
                        Mark Payment Failed
                      </Btn>
                    )}
                    {status === 'past_due' && (
                      <>
                        <Btn size="sm" onClick={() => billingAction('clear-failed', {}, 'Mark payment as received and restore account?')} disabled={actionLoading} style={{ background: T.success }}>
                          Payment Received ✓
                        </Btn>
                        <Btn size="sm" variant="ghost" style={{ borderColor: T.danger, color: T.danger }}
                          onClick={() => billingAction('suspend', { reason: 'Suspended by admin' }, 'Suspend this account now?')}
                          disabled={actionLoading}>
                          Suspend Now
                        </Btn>
                      </>
                    )}
                    {status === 'suspended' && (
                      <Btn size="sm" onClick={() => billingAction('reactivate', {}, 'Reactivate this account?')} disabled={actionLoading} style={{ background: T.success }}>
                        Reactivate Account
                      </Btn>
                    )}
                    {status === 'active' && (
                      <Btn size="sm" variant="ghost" style={{ borderColor: T.danger, color: T.danger }}
                        onClick={() => billingAction('suspend', { reason: 'Suspended by admin' }, 'Suspend this account?')}
                        disabled={actionLoading}>
                        Suspend
                      </Btn>
                    )}
                  </div>
                </div>

                {/* Billing settings */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={{ fontSize: 11, color: T.gray, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Old plan name (before Oct 30, 2026)</label>
                    <select value={billingForm.tier} onChange={e => setBillingForm(f => ({ ...f, tier: e.target.value }))}
                      style={{ width: '100%', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: T.white, fontFamily: 'Poppins' }}>
                      <option value="">— No subscription —</option>
                      {Object.entries(TIERS).map(([k, t]) => (
                        <option key={k} value={k}>{t.label} ({t.price != null ? `$${t.price}` : PRICE_TBD}/mo)</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: T.gray, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Payment Method</label>
                    <select value={billingForm.method} onChange={e => setBillingForm(f => ({ ...f, method: e.target.value }))}
                      style={{ width: '100%', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: T.white, fontFamily: 'Poppins' }}>
                      <option value="">— Not set —</option>
                      <option value="card">Credit / Debit Card</option>
                      <option value="ach">ACH Bank Transfer</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: T.gray, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Next Billing Date</label>
                    <input type="date" value={billingForm.next_date} onChange={e => setBillingForm(f => ({ ...f, next_date: e.target.value }))}
                      style={{ width: '100%', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: T.white, fontFamily: 'Poppins', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 0 }}>
                    <Btn onClick={saveBilling} disabled={actionLoading} style={{ width: '100%' }}>
                      {actionLoading ? 'Saving…' : 'Save Billing Info'}
                    </Btn>
                  </div>
                </div>

                {/* Current values display */}
                {bd && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    {[
                      { label: 'Monthly Amount', value: bd.monthly_amount != null ? `$${bd.monthly_amount}/mo` : '—' },
                      { label: 'Method on File', value: bd.billing_method === 'card' ? '💳 Card' : bd.billing_method === 'ach' ? '🏦 ACH' : '—' },
                      { label: 'Next Billing', value: bd.next_billing_date ? new Date(bd.next_billing_date + 'T12:00:00').toLocaleDateString() : '—' },
                    ].map(f => (
                      <div key={f.label} style={{ padding: '10px 12px', background: '#F5F7FA', borderRadius: 8 }}>
                        <p style={{ fontSize: 10, color: T.gray, marginBottom: 2 }}>{f.label}</p>
                        <p style={{ fontSize: 14, fontWeight: 600 }}>{f.value}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* History tab */}
            {detailTab === 'history' && (
              <div>
                {events.length === 0 ? (
                  <p style={{ color: T.gray, textAlign: 'center', padding: '40px 0', fontSize: 14 }}>No billing events yet.</p>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {events.map(ev => {
                      const evColors = {
                        payment_failed: T.danger, payment_succeeded: T.success,
                        account_suspended: T.danger, account_reactivated: T.success,
                        tier_changed: T.gold, subscription_started: T.success, subscription_canceled: T.gray,
                      };
                      const c = evColors[ev.event_type] || T.gray;
                      return (
                        <div key={ev.id} style={{ padding: '12px 14px', background: '#F5F7FA', borderRadius: 8, borderLeft: `3px solid ${c}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: c }}>{ev.event_type.replace(/_/g, ' ').toUpperCase()}</span>
                            <span style={{ fontSize: 11, color: T.gray }}>{new Date(ev.created_at).toLocaleString()}</span>
                          </div>
                          {ev.description && <p style={{ fontSize: 13, color: T.grayLight }}>{ev.description}</p>}
                          {ev.amount != null && <p style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>${ev.amount}/mo</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── MESSAGES TAB ── */}
            {detailTab === 'messages' && (
              <div style={{ display: 'grid', gap: 20 }}>
                <div>
                  <p style={{ fontSize: 13, color: T.gray, marginBottom: 10 }}>Send a coaching message to {detail.user.name}</p>
                  <textarea
                    value={adminMsgText}
                    onChange={e => setAdminMsgText(e.target.value)}
                    placeholder="Type your message here…"
                    rows={4}
                    style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }}
                  />
                  <input
                    type="text"
                    value={adminMsgVideo}
                    onChange={e => setAdminMsgVideo(e.target.value)}
                    placeholder="Video URL (optional)"
                    style={{ width: '100%', padding: '10px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box', marginBottom: 12 }}
                  />
                  <Btn disabled={adminMsgSending || !adminMsgText.trim()} onClick={sendAdminMsg}>
                    {adminMsgSending ? 'Sending…' : 'Send Message →'}
                  </Btn>
                  {!process.env.RESEND_API_KEY && (
                    <p style={{ fontSize: 11, color: T.gray, marginTop: 8 }}>Note: RESEND_API_KEY not set — message is saved but email notification won't send.</p>
                  )}
                </div>
                <div>
                  <p style={{ fontSize: 12, color: T.gray, marginBottom: 10, fontWeight: 600, letterSpacing: 0.5 }}>PREVIOUS MESSAGES</p>
                  {clientMessages.length === 0
                    ? <p style={{ color: T.gray, fontSize: 13 }}>No messages sent to this Rebuilder yet.</p>
                    : clientMessages.map(m => (
                      <div key={m.id} style={{ padding: '12px 14px', background: '#F5F7FA', borderRadius: 8, border: `1px solid ${T.border}`, marginBottom: 8, borderLeft: `3px solid ${T.orange}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 12, color: T.orange, fontWeight: 600 }}>From: {m.coach_name || 'Coach'}</span>
                          <span style={{ fontSize: 11, color: T.gray }}>{new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        <p style={{ fontSize: 13, color: T.white, lineHeight: 1.6 }}>{m.message}</p>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </>
  );
}

function AdminExercises() {
  const DIFFS = ['beginner', 'intermediate', 'advanced'];
  const blank = { name: '', category: 'Legs', subcategory: '', description: '', instructions: '', muscle_groups: '', equipment: 'dumbbell', difficulty: 'beginner', is_preview: false, video_url: '', sort_order: 0 };

  const [exercises, setExercises] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('All');

  const CATS = [...new Set([...POSE_CATS, ...exercises.map(e => e.category).filter(Boolean)])];
  // A video that became ready while this exercise is open in the form: the form takes the new address, so Save cannot put the old one back.
  const lastList = useRef([]);
  const load = () => api('/api/exercises').then(d => {
    const list = d.exercises || [];
    setForm(f => { if (!f || !f.id) return f; const was = lastList.current.find(e => e.id === f.id), now = list.find(e => e.id === f.id); return was && now && was.video_url !== now.video_url ? { ...f, video_url: now.video_url || '' } : f; });
    lastList.current = list;
    setExercises(list);
  });
  useEffect(() => { load(); }, []);

  // Video upload on a slot (exercise-slots.js, 2026-11-02).
  const [slots, setSlots] = useState({ jobs: {}, shared: {}, maxBytes: 0, total: 0, withVideo: 0 });
  const loadSlots = () => api('/api/admin/exercise-slots').then(setSlots).catch(() => {});
  useEffect(() => { loadSlots(); }, []);
  const slotBusy = Object.values(slots.jobs).some(j => j.status === 'processing');
  useEffect(() => {
    if (!slotBusy) return undefined;
    const t = setInterval(() => { load(); loadSlots(); }, 3000);
    return () => { clearInterval(t); };
  }, [slotBusy]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, muscle_groups: form.muscle_groups.split(',').map(s => s.trim()).filter(Boolean) };
      if (form.id) await api(`/api/admin/exercises/${form.id}`, 'PUT', payload);
      else await api('/api/admin/exercises', 'POST', payload);
      await load();
      setForm(null);
    } catch(e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  // Removing (2026-10-29): owner only, type the exact name, and it can be put back from "Removed exercises".
  const [removed, setRemoved] = useState({ owner: false, removed: [] });
  const loadRemoved = () => api('/api/admin/exercises-removed').then(setRemoved).catch(() => {});
  useEffect(() => { loadRemoved(); }, []);
  const del = async ex => {
    const typed = window.prompt(`Remove "${ex.name}" from the exercise library?\n\nType the exercise name exactly to confirm. It is kept under "Removed exercises" and can be put back.`);
    if (typed == null) return;
    try {
      await api(`/api/admin/exercises/${ex.id}`, 'DELETE', { confirm: typed });
      setExercises(e => e.filter(x => x.id !== ex.id)); loadRemoved();
    } catch (e) { alert(e.message); }
  };
  const restore = async id => { try { await api(`/api/admin/exercises-removed/${id}/restore`, 'POST', {}); await load(); loadRemoved(); } catch (e) { alert(e.message); } };

  const edit = ex => setForm({ ...ex, muscle_groups: Array.isArray(ex.muscle_groups) ? ex.muscle_groups.join(', ') : ex.muscle_groups || '', is_preview: !!ex.is_preview });

  const filtered = filterCat === 'All' ? exercises : exercises.filter(e => e.category === filterCat);

  const iField = (label, key, type='text', opts={}) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 11, color: T.grayLight, display: 'block', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {type === 'select' ? (
        <select value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ width: '100%', padding: '9px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins' }}>
          {opts.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'checkbox' ? (
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))} />
          <span style={{ fontSize: 13, color: T.grayLight }}>Show as free preview on landing page</span>
        </label>
      ) : type === 'textarea' ? (
        <textarea value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} rows={3} style={{ width: '100%', padding: '9px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins', resize: 'vertical', boxSizing: 'border-box' }} />
      ) : (
        <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ width: '100%', padding: '9px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins', boxSizing: 'border-box' }} />
      )}
    </div>
  );

  return (
    <div>
    <TG.AdminTrainerExercises onPromoted={load} />
    <div style={{ display: 'grid', gridTemplateColumns: form ? '1fr 400px' : '1fr', gap: 24, alignItems: 'start' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['All', ...CATS].map(c => (
              <button key={c} onClick={() => setFilterCat(c)} style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${filterCat === c ? T.orange : T.border}`, background: filterCat === c ? `${T.orange}20` : 'transparent', color: filterCat === c ? T.orange : T.gray, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{c}</button>
            ))}
          </div>
          <Btn size="sm" onClick={() => setForm({ ...blank })}>+ Add Exercise</Btn>
        </div>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 12 }}>{slots.withVideo} of {slots.total} exercises have their own video. MP4 with H.264 video and AAC sound plays everywhere. MOV and WEBM are converted if the server has ffmpeg. Up to 2 GB.</p>
        <div style={{ display: 'grid', gap: 10 }}>
          {filtered.map(ex => (
            <Card key={ex.id} style={{ padding: '14px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                  <p style={{ fontWeight: 600, fontSize: 15 }}>{ex.name}</p>
                  <Badge color={T.orange}>{ex.category}</Badge>
                  <Badge color={ex.is_preview ? T.success : T.gray}>{ex.is_preview ? 'Preview' : 'Members'}</Badge>
                  {ex.video_url && <Badge color={T.gold}>📹 Video</Badge>}
                </div>
                <p style={{ fontSize: 12, color: T.gray }}>{ex.difficulty} · {ex.equipment}</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn size="sm" variant="outline" onClick={() => edit(ex)}>Edit</Btn>
                {removed.owner && <Btn size="sm" variant="danger" onClick={() => del(ex)}>Remove...</Btn>}
              </div>
              <ESV.Slot ex={ex} job={slots.jobs[ex.id]} shared={slots.shared[ex.id]} maxBytes={slots.maxBytes} onChanged={() => { load(); loadSlots(); }} />
            </Card>
          ))}
          {filtered.length === 0 && <p style={{ color: T.gray, textAlign: 'center', padding: 40 }}>No exercises in this category.</p>}
          {!removed.owner && <p style={{ fontSize: 12, color: T.gray, marginTop: 8 }}>Only the owner can remove exercises from the library. You can edit them.</p>}
          {removed.removed.length > 0 && (
            <Card style={{ padding: '14px 18px', marginTop: 12 }}>
              <p style={{ fontWeight: 600, marginBottom: 6 }}>Removed exercises ({removed.removed.length})</p>
              {removed.removed.map(r => (
                <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, padding: '4px 0' }}>
                  <span style={{ flex: 1 }}>{r.name} <span style={{ color: T.gray }}>· removed {String(r.removed_at || '').slice(0, 10)} by {r.removed_by}</span></span>
                  {removed.owner && <Btn size="sm" variant="outline" onClick={() => restore(r.id)}>Put back</Btn>}
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>

      {form && (
        <Card style={{ padding: 28, position: 'sticky', top: 84 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600 }}>{form.id ? 'Edit Exercise' : 'New Exercise'}</h3>
            <button onClick={() => setForm(null)} style={{ background: 'none', border: 'none', color: T.gray, cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
          </div>
          {iField('Exercise Name *', 'name')}
          {iField('Category *', 'category', 'select', { options: CATS })}
          {iField('Difficulty', 'difficulty', 'select', { options: DIFFS })}
          {iField('Equipment', 'equipment')}
          {iField('Muscle Groups (comma-separated)', 'muscle_groups')}
          {iField('Description', 'description', 'textarea')}
          {iField('Instructions', 'instructions', 'textarea')}
          {iField('Video link: a direct https link to an .mp4, .webm or .m3u8 file, not a YouTube or Vimeo page. Upload video on the list fills this in.', 'video_url')}
          {iField('Thumbnail URL', 'thumbnail_url')}
          {iField('Sort Order', 'sort_order', 'number')}
          {iField('Free Preview', 'is_preview', 'checkbox')}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Btn fullWidth disabled={saving || !form.name} onClick={save}>{saving ? 'Saving…' : 'Save Exercise'}</Btn>
          </div>
        </Card>
      )}
    </div>
    </div>
  );
}

function AdminRecipes() {
  const CATS = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
  const blank = { title: '', category: 'Lunch', description: '', prep_time: '', cook_time: '', servings: '', calories_per_serving: '', protein_g: '', carbs_g: '', fat_g: '', ingredients: '', instructions: '', tags: '', is_preview: false, video_url: '' };

  const [recipes, setRecipes] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('All');

  const load = () => api('/api/recipes').then(d => setRecipes(d.recipes || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        ingredients: form.ingredients.split('\n').map(s => s.trim()).filter(Boolean),
        instructions: form.instructions.split('\n').map(s => s.trim()).filter(Boolean),
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
      };
      if (form.id) await api(`/api/admin/recipes/${form.id}`, 'PUT', payload);
      else await api('/api/admin/recipes', 'POST', payload);
      await load();
      setForm(null);
    } catch(e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  const del = async id => {
    if (!window.confirm('Delete this recipe?')) return;
    await api(`/api/admin/recipes/${id}`, 'DELETE');
    setRecipes(r => r.filter(x => x.id !== id));
  };

  const edit = r => setForm({
    ...r,
    ingredients: Array.isArray(r.ingredients) ? r.ingredients.join('\n') : r.ingredients || '',
    instructions: Array.isArray(r.instructions) ? r.instructions.join('\n') : r.instructions || '',
    tags: Array.isArray(r.tags) ? r.tags.join(', ') : r.tags || '',
    is_preview: !!r.is_preview,
  });

  const filtered = filterCat === 'All' ? recipes : recipes.filter(r => r.category === filterCat);

  const iField = (label, key, type='text') => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, color: T.grayLight, display: 'block', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {type === 'select' ? (
        <select value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ width: '100%', padding: '8px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins' }}>
          {CATS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'checkbox' ? (
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))} />
          <span style={{ fontSize: 13, color: T.grayLight }}>Show as free preview on landing page</span>
        </label>
      ) : type === 'textarea' ? (
        <textarea value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} rows={4} style={{ width: '100%', padding: '8px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 12, fontFamily: 'Poppins', resize: 'vertical', boxSizing: 'border-box' }} />
      ) : (
        <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ width: '100%', padding: '8px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins', boxSizing: 'border-box' }} />
      )}
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: form ? '1fr 420px' : '1fr', gap: 24, alignItems: 'start' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {['All', ...CATS].map(c => (
              <button key={c} onClick={() => setFilterCat(c)} style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${filterCat === c ? T.gold : T.border}`, background: filterCat === c ? `${T.gold}20` : 'transparent', color: filterCat === c ? T.gold : T.gray, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{c}</button>
            ))}
          </div>
          <Btn size="sm" onClick={() => setForm({ ...blank })}>+ Add Recipe</Btn>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {filtered.map(r => (
            <Card key={r.id} style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <p style={{ fontWeight: 600, fontSize: 15 }}>{r.title}</p>
                  <Badge color={T.gold}>{r.category}</Badge>
                  <Badge color={r.is_preview ? T.success : T.gray}>{r.is_preview ? 'Preview' : 'Members'}</Badge>
                  {r.video_url && <Badge color={T.orange}>📹 Video</Badge>}
                </div>
                <p style={{ fontSize: 12, color: T.gray }}>{r.calories_per_serving ? `${r.calories_per_serving} cal` : ''}{r.protein_g ? ` · ${r.protein_g}g protein` : ''}</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn size="sm" variant="outline" onClick={() => edit(r)}>Edit</Btn>
                <Btn size="sm" variant="danger" onClick={() => del(r.id)}>Del</Btn>
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <p style={{ color: T.gray, textAlign: 'center', padding: 40 }}>No recipes in this category.</p>}
        </div>
      </div>

      {form && (
        <Card style={{ padding: 24, position: 'sticky', top: 84, maxHeight: '85vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600 }}>{form.id ? 'Edit Recipe' : 'New Recipe'}</h3>
            <button onClick={() => setForm(null)} style={{ background: 'none', border: 'none', color: T.gray, cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
          </div>
          {iField('Recipe Title *', 'title')}
          {iField('Category *', 'category', 'select')}
          {iField('Description', 'description')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[['Prep (min)', 'prep_time', 'number'], ['Cook (min)', 'cook_time', 'number'], ['Servings', 'servings', 'number'], ['Calories', 'calories_per_serving', 'number'], ['Protein (g)', 'protein_g', 'number'], ['Carbs (g)', 'carbs_g', 'number'], ['Fat (g)', 'fat_g', 'number']].map(([l, k, t]) => (
              <div key={k} style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 11, color: T.grayLight, display: 'block', marginBottom: 3, fontWeight: 500 }}>{l}</label>
                <input type={t || 'text'} value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={{ width: '100%', padding: '7px 10px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins', boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>
          {iField('Ingredients (one per line)', 'ingredients', 'textarea')}
          {iField('Instructions (one per line)', 'instructions', 'textarea')}
          {iField('Tags (comma-separated)', 'tags')}
          {iField('Cloudflare Stream Video URL', 'video_url')}
          {iField('Free Preview', 'is_preview', 'checkbox')}
          <Btn fullWidth disabled={saving || !form.title} onClick={save}>{saving ? 'Saving…' : 'Save Recipe'}</Btn>
        </Card>
      )}
    </div>
  );
}

function AdminActivity() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [logs, setLogs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const catColors = POSE_COLORS;
  const feelingLabels = ['', '😴 Rough', '😐 OK', '😊 Good', '💪 Great', '🔥 Crushed it'];

  useEffect(() => {
    api('/api/admin/users').then(u => setUsers(u.users || [])).finally(() => setLoading(false));
  }, []);

  const viewActivity = async user => {
    setSelected(user);
    setLoadingDetail(true);
    try {
      const [w, p] = await Promise.all([
        api(`/api/admin/workouts/${user.id}`),
        api(`/api/admin/photos/${user.id}`),
      ]);
      setLogs(w.logs || []);
      setPhotos(p.photos || []);
    } finally { setLoadingDetail(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, alignItems: 'start' }}>
      <div>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.gray, marginBottom: 12 }}>SELECT REBUILDER</h3>
        <div style={{ display: 'grid', gap: 8, maxHeight: 600, overflowY: 'auto' }}>
          {users.length === 0 && <p style={{ color: T.gray, fontSize: 13, padding: '20px 0' }}>No Rebuilders yet.</p>}
          {users.map(u => (
            <Card key={u.id} onClick={() => viewActivity(u)} style={{
              padding: '14px 18px', cursor: 'pointer',
              borderColor: selected?.id === u.id ? T.orange : T.border,
              background: selected?.id === u.id ? `${T.orange}08` : T.glass,
            }}>
              <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{u.name}</p>
              <p style={{ fontSize: 12, color: T.gray }}>{u.email}</p>
            </Card>
          ))}
        </div>
      </div>

      <div>
        {!selected && <Card style={{ padding: 60, textAlign: 'center' }}><p style={{ color: T.gray }}>Select a Rebuilder to view their workout history and progress photos.</p></Card>}
        {selected && loadingDetail && <Spinner />}
        {selected && !loadingDetail && (
          <>
            <h2 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, marginBottom: 20 }}>{selected.name}'s Activity</h2>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.gray, marginBottom: 12 }}>PRACTICE LOG</h3>
            {logs.length === 0 ? (
              <Card style={{ padding: 32, textAlign: 'center', marginBottom: 24 }}><p style={{ color: T.gray }}>No workouts logged yet.</p></Card>
            ) : (
              <div style={{ display: 'grid', gap: 12, marginBottom: 28 }}>
                {logs.map(log => (
                  <Card key={log.id} style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <p style={{ fontFamily: 'Oswald', fontSize: 15, fontWeight: 600, color: T.orange }}>{new Date(log.logged_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                        <p style={{ fontSize: 12, color: T.gray }}>{log.exercises.length} exercise{log.exercises.length !== 1 ? 's' : ''}{log.duration_minutes ? ` · ${log.duration_minutes} min` : ''}</p>
                      </div>
                      <span style={{ fontSize: 18 }}>{feelingLabels[log.feeling]?.split(' ')[0] || '💪'}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {log.exercises.map((ex, i) => <Badge key={i} color={catColors[ex.category] || T.orange}>{ex.name}</Badge>)}
                    </div>
                    {log.notes && <p style={{ fontSize: 12, color: T.grayLight, marginTop: 8, padding: '8px 12px', background: '#F5F7FA', borderRadius: 8 }}>{log.notes}</p>}
                  </Card>
                ))}
              </div>
            )}
            <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.gray, marginBottom: 12 }}>PROGRESS PHOTOS</h3>
            {photos.length === 0 ? (
              <Card style={{ padding: 32, textAlign: 'center' }}><p style={{ color: T.gray }}>No progress photos uploaded yet.</p></Card>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                {photos.map(p => (
                  <Card key={p.id} style={{ padding: 0, overflow: 'hidden' }}>
                    <img src={`/uploads/${p.filename}`} alt={p.label || p.type} style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover' }} />
                    <div style={{ padding: '8px 10px' }}>
                      <Badge color={p.type === 'before' ? T.danger : p.type === 'after' ? T.success : T.gold}>{p.type}</Badge>
                      <p style={{ fontSize: 11, color: T.gray, marginTop: 4 }}>{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── PROGRESS PHOTOS PAGE ──────────────────────────────────────────────────────
function ProgressPage() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photoType, setPhotoType] = useState('progress');
  const fileRef = useRef(null);

  const load = () => api('/api/photos').then(d => setPhotos(d.photos || []));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const token = localStorage.getItem('db_token');
      const res = await fetch(`/api/photos/${photoType}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) throw new Error('Upload failed');
      await load();
    } catch (e) { alert('Upload failed: ' + e.message); }
    finally { setUploading(false); }
  };

  const deletePhoto = async id => {
    if (!window.confirm('Delete this photo?')) return;
    await api(`/api/photos/${id}`, 'DELETE');
    setPhotos(p => p.filter(x => x.id !== id));
  };

  const typeColors = { before: T.danger, progress: T.gold, after: T.success };
  const typeLabels = { before: 'Before', progress: 'Progress', after: 'After' };

  const grouped = { before: [], progress: [], after: [] };
  photos.forEach(p => { if (grouped[p.type]) grouped[p.type].push(p); else grouped.progress.push(p); });

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1100, margin: '0 auto' }}>
      <div className="animate-fadeInUp" style={{ marginBottom: 40 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, marginBottom: 8 }}>
          PROGRESS <span style={gradText}>PHOTOS</span>
        </h1>
        <p style={{ color: T.gray, fontSize: 16 }}>Track your transformation. Photos are private — only you and your coach can see them.</p>
      </div>

      <Card style={{ padding: 28, marginBottom: 32 }}>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>UPLOAD A PHOTO</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {['before', 'progress', 'after'].map(t => (
              <button key={t} onClick={() => setPhotoType(t)} style={{
                padding: '8px 18px', borderRadius: 8, border: `2px solid ${photoType === t ? typeColors[t] : T.border}`, cursor: 'pointer',
                background: photoType === t ? `${typeColors[t]}15` : 'transparent', color: photoType === t ? typeColors[t] : T.gray,
                fontFamily: 'Oswald', fontWeight: 600, fontSize: 14, letterSpacing: 1,
              }}>{typeLabels[t]}</button>
            ))}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => upload(e.target.files[0])} />
          <Btn disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? 'Uploading…' : `📷 Upload ${typeLabels[photoType]} Photo`}
          </Btn>
        </div>
        <p style={{ fontSize: 12, color: T.gray, marginTop: 12 }}>JPG or PNG. Max 10MB. Your coach will be able to see these photos to track your progress.</p>
      </Card>

      {['before', 'progress', 'after'].map(type => grouped[type].length > 0 && (
        <div key={type} style={{ marginBottom: 36 }}>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, letterSpacing: 2, color: typeColors[type], marginBottom: 14, borderBottom: `1px solid ${T.border}`, paddingBottom: 8, textTransform: 'uppercase' }}>{typeLabels[type]} Photos ({grouped[type].length})</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
            {grouped[type].map(p => (
              <Card key={p.id} style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
                <img src={`/uploads/${p.filename}`} alt={p.type} style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block' }} />
                <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ fontSize: 11, color: T.gray }}>{new Date(p.created_at).toLocaleDateString()}</p>
                  <button onClick={() => deletePhoto(p.id)} style={{ background: 'none', border: 'none', color: T.danger, cursor: 'pointer', fontSize: 14, padding: '2px 4px' }}>🗑</button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
      {photos.length === 0 && (
        <Card style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>📸</div>
          <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 32, letterSpacing: 2, marginBottom: 12 }}>NO PHOTOS YET</h3>
          <p style={{ color: T.gray, marginBottom: 24 }}>Upload your first photo above. Your transformation story starts with a single picture.</p>
        </Card>
      )}
    </div>
  );
}

// ── WORKOUT LOGGER PAGE ───────────────────────────────────────────────────────
function WorkoutsPage() {
  const [exercises, setExercises] = useState([]);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState({});
  const [exDetails, setExDetails] = useState({}); // { [id]: { sets, reps, weight } }
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [feeling, setFeeling] = useState(3);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('log');
  const [customExercises, setCustomExercises] = useState([]);
  const [newCustom, setNewCustom] = useState('');
  const [customCat, setCustomCat] = useState('Custom');
  const [overload, setOverload] = useState({ suggestions: [], frequency: [] });

  const catColors = POSE_COLORS;
  const feelingLabels = ['', '😴 Rough', '😐 OK', '😊 Good', '💪 Great', '🔥 Crushed it'];

  const refresh = useCallback(async () => {
    const [ex, logs, ol] = await Promise.all([
      api('/api/exercises').catch(() => ({ exercises: [] })),
      api('/api/workouts').catch(() => ({ logs: [] })),
      api('/api/workouts/overload').catch(() => ({ suggestions: [], frequency: [] })),
    ]);
    setExercises(ex.exercises || []);
    setHistory(logs.logs || []);
    setOverload(ol);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addCustomExercise = () => {
    if (!newCustom.trim()) return;
    const id = `custom_${Date.now()}`;
    setCustomExercises(prev => [...prev, { id, name: newCustom.trim(), category: customCat }]);
    setSelected(s => ({ ...s, [id]: true }));
    setNewCustom('');
  };

  const allExercises = [...exercises, ...customExercises];
  const byCategory = allExercises.reduce((acc, ex) => { (acc[ex.category] = acc[ex.category] || []).push(ex); return acc; }, {});
  const selectedIds = Object.keys(selected).filter(id => selected[id]);
  const toggle = id => setSelected(s => ({ ...s, [id]: !s[id] }));
  const setDetail = (id, field, val) => setExDetails(d => ({ ...d, [id]: { ...(d[id] || {}), [field]: val } }));

  const logWorkout = async () => {
    if (!selectedIds.length) return;
    setSaving(true);
    try {
      const exList = selectedIds.map(id => {
        const ex = allExercises.find(e => e.id === id);
        const det = exDetails[id] || {};
        return {
          id, name: ex?.name, category: ex?.category,
          sets: parseInt(det.sets) || null,
          reps: parseInt(det.reps) || null,
          weight: parseFloat(det.weight) || null,
        };
      });
      await api('/api/workouts', 'POST', { exercises: exList, duration_minutes: parseInt(duration) || null, notes, feeling });
      await refresh();
      setSelected({}); setExDetails({}); setDuration(''); setNotes(''); setFeeling(3);
      setSaved(true); setTimeout(() => setSaved(false), 3500);
    } catch (e) { console.error(e); } finally { setSaving(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1200, margin: '0 auto' }}>
      <div className="animate-fadeInUp" style={{ marginBottom: 40 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, marginBottom: 8 }}>
          LOG YOUR <span style={gradText}>PRACTICE</span>
        </h1>
        <p style={{ color: T.gray, fontSize: 16 }}>Check off what you did today. Every session builds your streak.</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
        {[{ id: 'log', label: '✅ Log Today' }, { id: 'history', label: '📅 My History' }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '10px 24px', borderRadius: 8, border: `1px solid ${activeTab === t.id ? T.orange : T.border}`, cursor: 'pointer', fontFamily: 'Oswald', fontWeight: 600, fontSize: 15, letterSpacing: 1, transition: 'all 0.2s',
            background: activeTab === t.id ? `linear-gradient(135deg, ${T.orange}, ${T.orangeLight})` : T.glass,
            color: activeTab === t.id ? '#fff' : T.gray,
          }}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'log' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24, alignItems: 'start' }}>
          <div>
            {Object.entries(byCategory).map(([cat, exList]) => (
              <div key={cat} style={{ marginBottom: 28 }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, letterSpacing: 2, color: catColors[cat] || T.orange, marginBottom: 10, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}`, paddingBottom: 6 }}>{cat}</h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {exList.map(ex => {
                    const on = !!selected[ex.id];
                    const col = catColors[cat] || T.orange;
                    return (
                      <div key={ex.id} onClick={() => toggle(ex.id)} style={{
                        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s',
                        border: `2px solid ${on ? col : T.border}`, background: on ? `${col}12` : T.glass,
                      }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${on ? col : T.border}`, background: on ? col : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                          {on && <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>✓</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: 600, fontSize: 15, color: on ? T.white : T.grayLight }}>{ex.name}</p>
                          {ex.muscle_groups?.length > 0 && <p style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>{Array.isArray(ex.muscle_groups) ? ex.muscle_groups.join(', ') : ex.muscle_groups}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {exercises.length === 0 && <p style={{ color: T.gray, textAlign: 'center', padding: 40 }}>Loading exercises…</p>}

            {/* Custom exercise entry */}
            <div style={{ marginTop: 28, padding: '20px 24px', background: T.glass, border: `1px solid ${T.border}`, borderRadius: 12 }}>
              <h4 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.gray, marginBottom: 12, textTransform: 'uppercase' }}>Add Custom Pose</h4>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <input value={newCustom} onChange={e => setNewCustom(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCustomExercise()} placeholder="e.g. Box Jumps, Swimming, Yoga…"
                  style={{ flex: '1 1 200px', padding: '10px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none' }} />
                <select value={customCat} onChange={e => setCustomCat(e.target.value)} style={{ padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', cursor: 'pointer' }}>
                  {['Custom', ...POSE_CATS].map(c => <option key={c} value={c} style={{ background: '#F5F7FA' }}>{c}</option>)}
                </select>
                <Btn size="sm" onClick={addCustomExercise} disabled={!newCustom.trim()}>+ Add</Btn>
              </div>
            </div>
          </div>

          <div>
            <Card style={{ padding: 28, position: 'sticky', top: 84 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600, marginBottom: 20 }}>SESSION SUMMARY</h3>
              <p style={{ fontSize: 13, color: T.gray, marginBottom: selectedIds.length ? 12 : 20 }}>Poses: <strong style={{ color: T.orange }}>{selectedIds.length}</strong></p>
              {selectedIds.length > 0 && (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    {selectedIds.map(id => { const ex = allExercises.find(e => e.id === id); return ex ? <Badge key={id} color={catColors[ex.category] || T.orange}>{ex.name}</Badge> : null; })}
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 11, fontFamily: 'Oswald', letterSpacing: 1.5, color: T.gray, marginBottom: 10, textTransform: 'uppercase' }}>📊 Rounds / Breaths held (optional)</p>
                    {selectedIds.map(id => {
                      const ex = allExercises.find(e => e.id === id);
                      if (!ex) return null;
                      const det = exDetails[id] || {};
                      const inputSt = { padding: '7px 8px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 6, color: T.white, fontSize: 12, fontFamily: 'Poppins', outline: 'none', width: '100%', boxSizing: 'border-box' };
                      return (
                        <div key={id} style={{ marginBottom: 10, padding: '10px 12px', background: T.glass, borderRadius: 8, border: `1px solid ${T.border}` }}>
                          <p style={{ fontSize: 12, color: T.orange, fontWeight: 600, marginBottom: 8 }}>{ex.name}</p>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                            <div>
                              <p style={{ fontSize: 10, color: T.gray, marginBottom: 3 }}>Rounds</p>
                              <input type="number" min="1" max="20" placeholder="2" value={det.sets || ''} onChange={e => setDetail(id, 'sets', e.target.value)} style={inputSt} />
                            </div>
                            <div>
                              <p style={{ fontSize: 10, color: T.gray, marginBottom: 3 }}>Breaths held</p>
                              <input type="number" min="1" max="100" placeholder="5" value={det.reps || ''} onChange={e => setDetail(id, 'reps', e.target.value)} style={inputSt} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 6, fontWeight: 500 }}>Duration (min)</label>
                <input type="number" value={duration} onChange={e => setDuration(e.target.value)} placeholder="e.g. 45" style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 8, fontWeight: 500 }}>How'd it feel? ({feelingLabels[feeling]})</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
                  {[1,2,3,4,5].map(v => (
                    <button key={v} onClick={() => setFeeling(v)} style={{ padding: '8px 4px', borderRadius: 8, border: `2px solid ${feeling === v ? T.orange : T.border}`, background: feeling === v ? `${T.orange}20` : 'transparent', cursor: 'pointer', fontSize: 14, color: feeling === v ? T.orange : T.gray, fontWeight: 600 }}>{v}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 6, fontWeight: 500 }}>Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="PRs, form notes, how you felt…" rows={3} style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              {saved && <div style={{ background: `${T.success}15`, border: `1px solid ${T.success}44`, borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 14, color: T.success, textAlign: 'center' }}>✅ Workout logged! Keep it up!</div>}
              <Btn fullWidth disabled={!selectedIds.length || saving} onClick={logWorkout}>
                {saving ? 'Saving…' : selectedIds.length ? `Log ${selectedIds.length} Pose${selectedIds.length !== 1 ? 's' : ''} →` : 'Select poses above'}
              </Btn>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div>
          {overload.suggestions.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 16, letterSpacing: 2, color: T.orange, marginBottom: 12, textTransform: 'uppercase' }}>📈 Progressive Overload Suggestions</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                {overload.suggestions.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', background: T.glass, border: `1px solid ${T.gold}44`, borderRadius: 10 }}>
                    <div style={{ fontSize: 28 }}>{s.type === 'increase_weight' ? '⬆️' : '📊'}</div>
                    <div>
                      <p style={{ fontWeight: 600, fontSize: 15, color: T.white }}>{s.exercise}</p>
                      {s.type === 'increase_weight' && (
                        <p style={{ fontSize: 13, color: T.gray, marginTop: 2 }}>
                          You've done <strong style={{ color: T.orange }}>{s.currentWeight} lbs</strong> for {s.sessions} sessions in a row — try <strong style={{ color: T.success }}>{s.suggestedWeight} lbs</strong> next time.
                        </p>
                      )}
                      {s.type === 'start_tracking' && (
                        <p style={{ fontSize: 13, color: T.gray, marginTop: 2 }}>
                          You've logged this <strong style={{ color: T.orange }}>{s.sessions}×</strong>. Start entering your weight to track progress!
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {history.length === 0 ? (
            <Card style={{ padding: 60, textAlign: 'center' }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>📅</div>
              <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 32, letterSpacing: 2, marginBottom: 12 }}>NO PRACTICES LOGGED YET</h3>
              <p style={{ color: T.gray, marginBottom: 24 }}>Log your first workout to start building your history.</p>
              <Btn onClick={() => setActiveTab('log')}>Log a Workout →</Btn>
            </Card>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {history.map(log => (
                <Card key={log.id} style={{ padding: '20px 24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <p style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, color: T.orange }}>{new Date(log.logged_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                      <p style={{ fontSize: 13, color: T.gray, marginTop: 2 }}>{log.exercises.length} exercise{log.exercises.length !== 1 ? 's' : ''}{log.duration_minutes ? ` · ${log.duration_minutes} min` : ''}</p>
                    </div>
                    <span style={{ fontSize: 22 }}>{feelingLabels[log.feeling]?.split(' ')[0] || '💪'}</span>
                  </div>
                  {log.exercises.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: log.notes ? 12 : 0 }}>
                      {log.exercises.map((ex, i) => (
                        <Badge key={i} color={catColors[ex.category] || T.orange}>
                          {ex.name}{ex.sets && ex.reps ? ` ${ex.sets}×${ex.reps}` : ''}{ex.weight ? ` @ ${ex.weight}lb` : ''}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {log.notes && <p style={{ fontSize: 13, color: T.grayLight, padding: '10px 14px', background: '#F5F7FA', borderRadius: 8, marginTop: 8 }}>{log.notes}</p>}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── FOOD DIARY PAGE ───────────────────────────────────────────────────────────
function FoodDiaryPage() {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [filtered, setFiltered] = useState([]);
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedFood, setSelectedFood] = useState(null);
  const [amount, setAmount] = useState(100);
  const [mealType, setMealType] = useState('breakfast');
  const [customName, setCustomName] = useState('');
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState('search');

  const loadLogs = useCallback(async (d) => {
    const data = await api(`/api/food-log?date=${d}`).catch(() => ({ logs: [] }));
    setLogs(data.logs || []);
  }, []);

  useEffect(() => { loadLogs(date); }, [date, loadLogs]);

  useEffect(() => {
    if (search.length < 2) { setFiltered([]); return; }
    api(`/api/food-search?q=${encodeURIComponent(search)}`).then(d => setFiltered(d.foods || [])).catch(() => {});
  }, [search]);

  const totals = logs.reduce((acc, l) => ({
    cal: acc.cal + (l.calories || 0),
    pro: acc.pro + (l.protein_g || 0),
    carb: acc.carb + (l.carbs_g || 0),
    fat: acc.fat + (l.fat_g || 0),
  }), { cal: 0, pro: 0, carb: 0, fat: 0 });

  const addEntry = async () => {
    if (!selectedFood && !customName) return;
    setAdding(true);
    try {
      await api('/api/food-log', 'POST', { food_item_id: selectedFood?.id || null, custom_name: customName || null, amount_g: amount, meal_type: mealType, logged_date: date });
      await loadLogs(date);
      setSelectedFood(null); setCustomName(''); setSearch(''); setFiltered([]); setAmount(100);
    } catch(e) {} finally { setAdding(false); }
  };

  const removeEntry = async (id) => {
    await api(`/api/food-log/${id}`, 'DELETE').catch(() => {});
    setLogs(prev => prev.filter(l => l.id !== id));
  };

  const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
  const mealColors = { breakfast: T.gold, lunch: T.success, dinner: T.orange, snack: '#4A90E2' };
  const mealIcons = { breakfast: '🍳', lunch: '🥗', dinner: '🍽️', snack: '🍎' };
  const byMeal = mealTypes.reduce((acc, m) => { acc[m] = logs.filter(l => l.meal_type === m); return acc; }, {});

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1200, margin: '0 auto' }}>
      <div className="animate-fadeInUp" style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, marginBottom: 8 }}>FOOD <span style={gradText}>DIARY</span></h1>
        <p style={{ color: T.gray, fontSize: 16 }}>Log everything you eat. Knowledge is the first step to results.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 24, alignItems: 'start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              style={{ padding: '10px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', cursor: 'pointer' }} />
            <span style={{ color: T.gray, fontSize: 14 }}>{date === new Date().toISOString().split('T')[0] ? 'Today' : new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
          </div>

          <Card style={{ marginBottom: 24, padding: '20px 24px' }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.gray, marginBottom: 14, textTransform: 'uppercase' }}>Daily Totals</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              {[{ label: 'Calories', value: Math.round(totals.cal), unit: 'kcal', color: T.orange },
                { label: 'Protein', value: totals.pro.toFixed(1), unit: 'g', color: T.success },
                { label: 'Carbs', value: totals.carb.toFixed(1), unit: 'g', color: T.gold },
                { label: 'Fat', value: totals.fat.toFixed(1), unit: 'g', color: '#4A90E2' }].map(m => (
                <div key={m.label} style={{ textAlign: 'center' }}>
                  <p style={{ fontFamily: 'Bebas Neue', fontSize: 28, color: m.color, lineHeight: 1 }}>{m.value}</p>
                  <p style={{ fontSize: 11, color: T.gray, textTransform: 'uppercase', letterSpacing: 0.5 }}>{m.unit} {m.label}</p>
                </div>
              ))}
            </div>
          </Card>

          {mealTypes.map(meal => (
            <div key={meal} style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, fontWeight: 600, letterSpacing: 2, color: mealColors[meal], marginBottom: 8, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6, borderBottom: `1px solid ${T.border}`, paddingBottom: 6 }}>
                {mealIcons[meal]} {meal}
                {byMeal[meal].length > 0 && <span style={{ color: T.gray, fontWeight: 400, fontSize: 12, marginLeft: 'auto' }}>{byMeal[meal].reduce((s, l) => s + (l.calories || 0), 0)} kcal</span>}
              </h3>
              {byMeal[meal].length === 0 ? (
                <p style={{ color: T.gray, fontSize: 13, paddingLeft: 4 }}>Nothing logged yet</p>
              ) : byMeal[meal].map(entry => (
                <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: T.glass, border: `1px solid ${T.border}`, marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 14, color: T.white, fontWeight: 500 }}>{entry.food_name || entry.custom_name}</p>
                    <p style={{ fontSize: 12, color: T.gray }}>{entry.amount_g}g{entry.calories ? ` · ${entry.calories} kcal` : ''}{entry.protein_g ? ` · ${entry.protein_g}g protein` : ''}</p>
                  </div>
                  <button onClick={() => removeEntry(entry.id)} style={{ background: 'none', border: 'none', color: T.danger, cursor: 'pointer', fontSize: 16, opacity: 0.7, padding: 4 }}>✕</button>
                </div>
              ))}
            </div>
          ))}
        </div>

        <Card style={{ padding: 24, position: 'sticky', top: 84 }}>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>ADD FOOD</h3>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            {['search', 'custom'].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: '8px', borderRadius: 6, border: `1px solid ${tab === t ? T.orange : T.border}`, background: tab === t ? `${T.orange}20` : 'transparent', color: tab === t ? T.orange : T.gray, cursor: 'pointer', fontSize: 13, fontFamily: 'Poppins', fontWeight: 600 }}>
                {t === 'search' ? '🔍 Search' : '✏️ Custom'}
              </button>
            ))}
          </div>

          {tab === 'search' ? (
            <div>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search foods (e.g. chicken, rice)…"
                style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
              {filtered.length > 0 && (
                <div style={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: 8, marginBottom: 12 }}>
                  {filtered.map(item => (
                    <div key={item.id} onClick={() => { setSelectedFood(item); setFiltered([]); setSearch(item.name); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${T.border}`, background: selectedFood?.id === item.id ? `${T.orange}15` : 'transparent', transition: 'background 0.15s' }}>
                      <p style={{ fontSize: 13, color: T.white, fontWeight: 500 }}>{item.name}</p>
                      <p style={{ fontSize: 11, color: T.gray }}>{item.category} · {item.calories_per_100g} kcal/100g</p>
                    </div>
                  ))}
                </div>
              )}
              {selectedFood && <div style={{ padding: '10px 12px', background: `${T.orange}10`, borderRadius: 8, border: `1px solid ${T.orange}33`, marginBottom: 12 }}>
                <p style={{ fontSize: 13, color: T.orange, fontWeight: 600 }}>{selectedFood.name}</p>
                <p style={{ fontSize: 11, color: T.gray }}>{selectedFood.calories_per_100g} kcal · {selectedFood.protein_per_100g}g protein per 100g</p>
              </div>}
            </div>
          ) : (
            <input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="Food name (e.g. Grandma's soup)"
              style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }} />
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 5, fontWeight: 500 }}>Amount (grams)</label>
            <input type="number" value={amount} onChange={e => setAmount(parseInt(e.target.value) || 100)} min={1}
              style={{ width: '100%', padding: '10px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 5, fontWeight: 500 }}>Meal</label>
            <select value={mealType} onChange={e => setMealType(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', cursor: 'pointer' }}>
              {mealTypes.map(m => <option key={m} value={m} style={{ background: '#F5F7FA' }}>{mealIcons[m]} {m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
            </select>
          </div>

          {selectedFood && amount > 0 && (
            <div style={{ padding: '10px 12px', background: T.glass, borderRadius: 8, border: `1px solid ${T.border}`, marginBottom: 14 }}>
              <p style={{ fontSize: 12, color: T.gray }}>≈ {Math.round((selectedFood.calories_per_100g || 0) * amount / 100)} kcal · {((selectedFood.protein_per_100g || 0) * amount / 100).toFixed(1)}g protein</p>
            </div>
          )}

          <Btn fullWidth disabled={(!selectedFood && !customName) || adding} onClick={addEntry}>{adding ? 'Adding…' : 'Add Entry →'}</Btn>
        </Card>
      </div>
    </div>
  );
}

// ── SHOPPING LIST PAGE ────────────────────────────────────────────────────────
function ShoppingListPage() {
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [zipcode, setZipcode] = useState('');
  const [userCoords, setUserCoords] = useState(null); // { lat, lng }
  const [geoStatus, setGeoStatus] = useState('idle'); // idle | requesting | granted | denied
  const [list, setList] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  // Kroger API state
  const [krogerStores, setKrogerStores] = useState(null); // null = not loaded, [] = none found
  const [krogerStoresLoading, setKrogerStoresLoading] = useState(false);
  const [selectedKrogerStore, setSelectedKrogerStore] = useState(null);
  const [krogerProducts, setKrogerProducts] = useState(null); // array of product results
  const [krogerProductsLoading, setKrogerProductsLoading] = useState(false);

  useEffect(() => { api('/api/recipes').then(d => setRecipes(d.recipes || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  // Start from the ZIP code saved in My Profile, so store prices work without retyping it.
  useEffect(() => { api('/api/profile').then(r => { const z = r.profile?.zip_code; if (z) setZipcode(z); }).catch(() => {}); }, []);

  const toggleRecipe = id => setSelectedRecipes(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const getLocation = () => {
    if (!navigator.geolocation) return;
    setGeoStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoStatus('granted');
      },
      () => setGeoStatus('denied'),
      { timeout: 10000 }
    );
  };

  const generateList = async () => {
    if (!selectedRecipes.length) return;
    setGenerating(true);
    setKrogerStores(null); setSelectedKrogerStore(null); setKrogerProducts(null);
    try {
      const data = await api('/api/shopping-list', 'POST', {
        recipeIds: selectedRecipes,
        zipcode: zipcode || null,
        lat: userCoords?.lat || null,
        lng: userCoords?.lng || null,
      });
      setList(data);
      // Auto-check for Kroger stores if we have location info
      if (userCoords || zipcode) {
        setKrogerStoresLoading(true);
        const params = new URLSearchParams();
        if (userCoords) { params.set('lat', userCoords.lat); params.set('lng', userCoords.lng); }
        else if (zipcode) params.set('zipcode', zipcode);
        api(`/api/shopping/kroger-stores?${params}`).then(d => {
          if (d.apiAvailable && d.stores?.length) setKrogerStores(d.stores);
          else setKrogerStores([]);
        }).catch(() => setKrogerStores([])).finally(() => setKrogerStoresLoading(false));
      }
    } catch(e) {} finally { setGenerating(false); }
  };

  const loadKrogerProducts = async (store) => {
    setSelectedKrogerStore(store);
    setKrogerProducts(null);
    setKrogerProductsLoading(true);
    const allIngredients = (list?.shoppingList || []).flatMap(sec => sec.items.map(it => it.ingredient));
    try {
      const d = await api('/api/shopping/kroger-products', 'POST', { locationId: store.locationId, ingredients: allIngredients });
      setKrogerProducts(d.apiAvailable ? d.products : []);
    } catch(e) { setKrogerProducts([]); } finally { setKrogerProductsLoading(false); }
  };

  const sectionIcons = { 'Produce': '🥦', 'Meat & Seafood': '🥩', 'Dairy & Eggs': '🥚', 'Grains & Bread': '🍞', 'Canned & Dry Goods': '🥫', 'Frozen': '🧊', 'Condiments & Oils': '🫙', 'Spices & Seasonings': '🧂', 'Protein & Supplements': '💊', 'Snacks & Other': '🛒' };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1200, margin: '0 auto' }}>
      <div className="animate-fadeInUp" style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, marginBottom: 8 }}>SHOPPING <span style={gradText}>LIST</span></h1>
        <p style={{ color: T.gray, fontSize: 16 }}>Pick your recipes, get a list organized by store section so you can shop fast.</p>
      </div>

      {!list ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24, alignItems: 'start' }}>
          <div>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>Select Recipes for Your List</h3>
            {loading ? <p style={{ color: T.gray }}>Loading recipes…</p> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                {recipes.map(r => {
                  const on = selectedRecipes.includes(r.id);
                  return (
                    <div key={r.id} onClick={() => toggleRecipe(r.id)} style={{ padding: '14px 16px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s', border: `2px solid ${on ? T.gold : T.border}`, background: on ? `${T.gold}12` : T.glass }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${on ? T.gold : T.border}`, background: on ? T.gold : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {on && <span style={{ color: '#000', fontSize: 11, fontWeight: 700 }}>✓</span>}
                        </div>
                        <div>
                          <p style={{ fontWeight: 600, fontSize: 14, color: on ? T.white : T.grayLight }}>{r.title}</p>
                          <p style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>{r.category}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Card style={{ padding: 24, position: 'sticky', top: 84 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 15, fontWeight: 600, marginBottom: 14 }}>BUILD YOUR LIST</h3>
            <p style={{ fontSize: 13, color: T.gray, marginBottom: 16 }}>{selectedRecipes.length} recipe{selectedRecipes.length !== 1 ? 's' : ''} selected</p>

            {/* Location */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 6, fontWeight: 500 }}>Your Location</label>
              {geoStatus === 'granted' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: `${T.success}15`, border: `1px solid ${T.success}44`, borderRadius: 8 }}>
                  <span style={{ fontSize: 14 }}>📍</span>
                  <span style={{ fontSize: 13, color: T.success }}>Location detected</span>
                </div>
              ) : geoStatus === 'denied' ? (
                <div style={{ fontSize: 12, color: T.gray }}>Location blocked — enter zip code below instead.</div>
              ) : (
                <button onClick={getLocation} disabled={geoStatus === 'requesting'}
                  style={{ width: '100%', padding: '10px 12px', background: `${T.orange}15`, border: `1px solid ${T.orange}44`, borderRadius: 8, color: T.orange, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Poppins' }}>
                  {geoStatus === 'requesting' ? '📍 Detecting…' : '📍 Use My Location'}
                </button>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 6, fontWeight: 500 }}>Or Enter Zip Code</label>
              <input value={zipcode} onChange={e => setZipcode(e.target.value)} placeholder="e.g. 37402" maxLength={5}
                style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <Btn fullWidth disabled={!selectedRecipes.length || generating} onClick={generateList}>{generating ? 'Building…' : 'Generate Shopping List →'}</Btn>
          </Card>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <p style={{ color: T.gray, fontSize: 14 }}>{list.recipeCount} recipe{list.recipeCount !== 1 ? 's' : ''} · {list.ingredientCount} ingredients</p>
            <div style={{ display: 'flex', gap: 12 }}>
              <Btn variant="outline" size="sm" onClick={() => setList(null)}>← Change Recipes</Btn>
              <Btn variant="ghost" size="sm" onClick={() => window.print()}>🖨️ Print</Btn>
            </div>
          </div>

          {/* Price summary + store tier comparison */}
          {list.totalEstimate > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 24 }}>
              <Card style={{ padding: '18px 22px', borderLeft: `3px solid ${T.orange}` }}>
                <p style={{ fontSize: 12, color: T.gray, marginBottom: 4 }}>Estimated Total</p>
                <p style={{ fontFamily: 'Bebas Neue', fontSize: 32, color: T.orange, letterSpacing: 1 }}>${list.totalEstimate.toFixed(2)}</p>
                <p style={{ fontSize: 11, color: T.gray }}>Based on average US retail prices</p>
              </Card>
              {list.priceTiers && (
                <>
                  <Card style={{ padding: '18px 22px' }}>
                    <p style={{ fontSize: 12, color: T.gray, marginBottom: 4 }}>Budget Stores</p>
                    <p style={{ fontSize: 22, fontWeight: 700, color: '#4caf50' }}>${list.priceTiers.budget.toFixed(2)}</p>
                    <p style={{ fontSize: 11, color: T.gray }}>Walmart, Aldi, WinCo</p>
                  </Card>
                  <Card style={{ padding: '18px 22px' }}>
                    <p style={{ fontSize: 12, color: T.gray, marginBottom: 4 }}>Mid-Range Stores</p>
                    <p style={{ fontSize: 22, fontWeight: 700, color: T.white }}>${list.priceTiers.mid.toFixed(2)}</p>
                    <p style={{ fontSize: 11, color: T.gray }}>Kroger, Publix, Food City</p>
                  </Card>
                  <Card style={{ padding: '18px 22px' }}>
                    <p style={{ fontSize: 12, color: T.gray, marginBottom: 4 }}>Premium Stores</p>
                    <p style={{ fontSize: 22, fontWeight: 700, color: T.gold }}>${list.priceTiers.premium.toFixed(2)}</p>
                    <p style={{ fontSize: 11, color: T.gray }}>Whole Foods, Sprouts</p>
                  </Card>
                </>
              )}
            </div>
          )}

          {list.stores?.length > 0 && (
            <Card style={{ marginBottom: 24, padding: '16px 24px' }}>
              <h4 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 1, color: T.gold, marginBottom: 14 }}>
                GROCERY STORES {userCoords ? 'NEAR YOU' : zipcode ? `NEAR ${zipcode}` : ''}
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {list.stores.map((s, i) => {
                  const tierColor = s.tier === 'budget' ? '#4caf50' : s.tier === 'premium' ? T.gold : T.orange;
                  return (
                    <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20, background: `${tierColor}15`, border: `1px solid ${tierColor}55`, color: T.white, fontSize: 13, textDecoration: 'none', transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${tierColor}30`; e.currentTarget.style.borderColor = tierColor; }}
                      onMouseLeave={e => { e.currentTarget.style.background = `${tierColor}15`; e.currentTarget.style.borderColor = `${tierColor}55`; }}>
                      🗺️ {s.name}
                    </a>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: T.gray, marginTop: 12 }}>Click any store to open Google Maps for directions.</p>
            </Card>
          )}

          {/* Kroger API: real prices + aisle data */}
          {(krogerStoresLoading || krogerStores?.length > 0 || krogerStores?.length === 0) && (
            <Card style={{ marginBottom: 24, padding: '18px 24px', border: `1px solid ${T.gold}33` }}>
              <h4 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 1, color: T.gold, marginBottom: 12 }}>
                🏪 KROGER FAMILY STORES — REAL PRICES & AISLE LOCATIONS
              </h4>
              {krogerStoresLoading && <p style={{ fontSize: 13, color: T.gray }}>Looking up Kroger stores near you…</p>}
              {!krogerStoresLoading && krogerStores?.length === 0 && (
                <p style={{ fontSize: 13, color: T.gray }}>No Kroger, Ralphs, Fred Meyer, or King Soopers found nearby. These live prices are only available for Kroger-family stores.</p>
              )}
              {!krogerStoresLoading && krogerStores?.length > 0 && (
                <>
                  <p style={{ fontSize: 12, color: T.gray, marginBottom: 10 }}>Select a store to see real prices and exact aisle locations for every item on your list:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                    {krogerStores.map(s => {
                      const isSelected = selectedKrogerStore?.locationId === s.locationId;
                      return (
                        <button key={s.locationId} onClick={() => loadKrogerProducts(s)}
                          style={{ padding: '9px 16px', borderRadius: 8, border: `1px solid ${isSelected ? T.gold : T.border}`, background: isSelected ? `${T.gold}20` : T.glass, color: isSelected ? T.gold : T.grayLight, cursor: 'pointer', fontSize: 13, fontFamily: 'Poppins', fontWeight: isSelected ? 600 : 400, transition: 'all 0.2s' }}>
                          {s.name} {s.chain && s.chain !== s.name ? `(${s.chain})` : ''}
                        </button>
                      );
                    })}
                  </div>
                  {krogerProductsLoading && <p style={{ fontSize: 13, color: T.gray }}>Loading prices for {selectedKrogerStore?.name}…</p>}
                  {krogerProducts && !krogerProductsLoading && (
                    <div>
                      <p style={{ fontSize: 12, color: T.gray, marginBottom: 10 }}>
                        Found <strong style={{ color: T.white }}>{krogerProducts.length}</strong> of your items at {selectedKrogerStore?.name}:
                      </p>
                      <div style={{ display: 'grid', gap: 6 }}>
                        {krogerProducts.map((p, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', background: '#F5F7FA', borderRadius: 8, border: `1px solid ${T.border}` }}>
                            <div style={{ flex: 1 }}>
                              <p style={{ fontSize: 13, color: T.white, fontWeight: 500 }}>{p.ingredient}</p>
                              <p style={{ fontSize: 11, color: T.gray, marginTop: 2 }}>{p.productName}</p>
                              {p.aisle && <p style={{ fontSize: 11, color: T.gold, marginTop: 2 }}>📍 {p.aisle}</p>}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              {p.salePrice ? (
                                <>
                                  <p style={{ fontSize: 15, fontWeight: 700, color: '#4caf50' }}>${p.salePrice.toFixed(2)} <span style={{ fontSize: 10, color: T.gray, fontWeight: 400 }}>SALE</span></p>
                                  <p style={{ fontSize: 11, color: T.gray, textDecoration: 'line-through' }}>${p.price?.toFixed(2)}</p>
                                </>
                              ) : p.price != null ? (
                                <p style={{ fontSize: 15, fontWeight: 700, color: T.white }}>${p.price.toFixed(2)}</p>
                              ) : (
                                <p style={{ fontSize: 11, color: T.gray }}>Price N/A</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {krogerProducts.length > 0 && (
                        <p style={{ fontSize: 11, color: T.gray, marginTop: 10 }}>
                          Live prices from Kroger's API · {selectedKrogerStore?.address}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card>
          )}

          {/* Walking-order note */}
          <Card style={{ marginBottom: 20, padding: '12px 20px', background: `${T.gold}08`, border: `1px solid ${T.gold}30` }}>
            <p style={{ fontSize: 13, color: T.grayLight }}>
              <span style={{ fontWeight: 600, color: T.gold }}>Smart path: </span>
              Items are sorted in the order you'll naturally find them in most stores — Produce first, Frozen last. This avoids backtracking and keeps you away from the candy, chips, and snack aisles in the center.
            </p>
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
            {list.shoppingList.map(section => (
              <Card key={section.section} style={{ padding: '20px 24px' }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, color: T.orange, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, letterSpacing: 1 }}>
                  {sectionIcons[section.section] || '🛒'} {section.section}
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: T.gray, fontWeight: 400 }}>${(section.sectionTotal || 0).toFixed(2)}</span>
                </h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {section.items.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ width: 16, height: 16, borderRadius: 3, border: `2px solid ${T.border}`, flexShrink: 0, marginTop: 3 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <p style={{ fontSize: 13, color: T.white }}>{item.ingredient}</p>
                          {item.estimatedPrice && <span style={{ fontSize: 11, color: T.gray, fontVariantNumeric: 'tabular-nums', flexShrink: 0, marginLeft: 8 }}>~${item.estimatedPrice.toFixed(2)}</span>}
                        </div>
                        <p style={{ fontSize: 11, color: T.gray }}>for {item.recipe}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── COOKING SHOWS PAGE ────────────────────────────────────────────────────────
function CookingShowsPage() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api('/api/cooking-shows').then(d => setShows(d.shows || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1200, margin: '0 auto' }}>
      <div className="animate-fadeInUp" style={{ marginBottom: 40 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, marginBottom: 8 }}>COOKING <span style={gradText}>SHOWS</span></h1>
        <p style={{ color: T.gray, fontSize: 16 }}>Home cooks show you how to make clean, healthy meals in an ordinary kitchen.</p>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 60, color: T.gray }}>Loading shows…</div>
      : shows.length === 0 ? (
        <Card style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🎥</div>
          <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 32, letterSpacing: 2, marginBottom: 12 }}>COMING SOON</h3>
          <p style={{ color: T.gray }}>Guest cooking shows are on the way. Check back soon!</p>
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
          {shows.map(show => (
            <Card key={show.id} style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }} onClick={() => setSelected(show)}>
              <div style={{ aspectRatio: '16/9', background: `linear-gradient(135deg, ${T.orange}22, ${T.gold}22)`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                {show.thumbnail_url ? <img src={show.thumbnail_url} alt={show.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 56 }}>🍳</span>}
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${T.orange}cc`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>▶</div>
                </div>
              </div>
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Badge color={T.orange}>Ep. {show.episode_number}</Badge>
                  <p style={{ fontSize: 12, color: T.gray }}>with {show.host_name}</p>
                </div>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, marginBottom: 6 }}>{show.title}</h3>
                {show.description && <p style={{ fontSize: 13, color: T.gray, lineHeight: 1.6 }}>{show.description}</p>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setSelected(null)}>
          <div style={{ maxWidth: 800, width: '100%', background: T.card, borderRadius: 16, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            {selected.video_url
              ? <div style={{ aspectRatio: '16/9' }}><iframe src={selected.video_url} style={{ width: '100%', height: '100%', border: 'none' }} allow="autoplay; fullscreen" title={selected.title} /></div>
              : <div style={{ aspectRatio: '16/9', background: '#F5F7FA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p style={{ color: T.gray }}>Video coming soon</p></div>}
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 28, letterSpacing: 2 }}>{selected.title}</h3>
                  <p style={{ color: T.gray, fontSize: 14 }}>Ep. {selected.episode_number} with {selected.host_name}</p>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: T.gray, fontSize: 24, cursor: 'pointer', padding: 4 }}>✕</button>
              </div>
              {selected.description && <p style={{ fontSize: 14, color: T.grayLight, marginTop: 12, lineHeight: 1.7 }}>{selected.description}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── COACH PORTAL PAGE ──────────────────────────────────────────────────────────
function CoachPortalPage() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api('/api/coach/clients').then(d => setClients(d.clients || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  const viewClient = async id => {
    setSelected(id); setDetail(null); setMessage(''); setSent(false);
    const d = await api(`/api/coach/client/${id}`).catch(() => null);
    setDetail(d);
  };

  const sendMessage = async () => {
    if (!message.trim() || !selected) return;
    setSending(true);
    try { await api('/api/coach/message', 'POST', { clientId: selected, message }); setMessage(''); setSent(true); setTimeout(() => setSent(false), 3000); }
    catch(e) {} finally { setSending(false); }
  };

  const client = selected ? clients.find(c => c.id === selected) : null;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1300, margin: '0 auto' }}>
      <div className="animate-fadeInUp" style={{ marginBottom: 32 }}>
        <Badge color={T.gold}>COACH PORTAL</Badge>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(32px, 4vw, 48px)', letterSpacing: 2, marginTop: 10 }}>YOUR <span style={gradText}>REBUILDERS</span></h1>
        <p style={{ color: T.gray, fontSize: 15, marginTop: 4 }}>Review Rebuilder progress and send personalized updates.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 24, alignItems: 'start' }}>
        <div>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.gray, marginBottom: 12, textTransform: 'uppercase' }}>Rebuilders ({clients.length})</h3>
          {loading ? <p style={{ color: T.gray, fontSize: 13 }}>Loading…</p>
          : clients.length === 0 ? <p style={{ color: T.gray, fontSize: 13 }}>No Rebuilders assigned yet. Ask admin to assign Rebuilders to your account.</p>
          : clients.map(c => (
            <div key={c.id} onClick={() => viewClient(c.id)} style={{ padding: '12px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 8, transition: 'all 0.2s', border: `2px solid ${selected === c.id ? T.orange : T.border}`, background: selected === c.id ? `${T.orange}12` : T.glass }}>
              <p style={{ fontWeight: 600, fontSize: 14, color: selected === c.id ? T.white : T.grayLight }}>{c.name}</p>
              <p style={{ fontSize: 12, color: T.gray, marginTop: 2 }}>{c.email}</p>
              {c.goal && <p style={{ fontSize: 11, color: T.orange, marginTop: 4 }}>{c.goal}</p>}
            </div>
          ))}
        </div>

        <div>
          {!selected ? (
            <Card style={{ padding: 60, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>👆</div>
              <p style={{ color: T.gray }}>Select a Rebuilder to view their details and send a message.</p>
            </Card>
          ) : !detail ? (
            <Card style={{ padding: 40, textAlign: 'center' }}><p style={{ color: T.gray }}>Loading Rebuilder data…</p></Card>
          ) : (
            <div>
              <Card style={{ padding: 24, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
                  <div>
                    <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 32, letterSpacing: 2 }}>{detail.user?.name}</h2>
                    <p style={{ color: T.gray, fontSize: 14 }}>Member since {new Date(detail.user?.created_at).toLocaleDateString()}</p>
                  </div>
                  {detail.profile && (
                    <div style={{ display: 'flex', gap: 20 }}>
                      {detail.profile.age && <div style={{ textAlign: 'center' }}><p style={{ fontFamily: 'Bebas Neue', fontSize: 24, color: T.orange }}>{detail.profile.age}</p><p style={{ fontSize: 11, color: T.gray }}>Age</p></div>}
                      {detail.profile.weight && <div style={{ textAlign: 'center' }}><p style={{ fontFamily: 'Bebas Neue', fontSize: 24, color: T.gold }}>{detail.profile.weight}</p><p style={{ fontSize: 11, color: T.gray }}>lbs</p></div>}
                      {detail.profile.goal_weight && <div style={{ textAlign: 'center' }}><p style={{ fontFamily: 'Bebas Neue', fontSize: 24, color: T.success }}>{detail.profile.goal_weight}</p><p style={{ fontSize: 11, color: T.gray }}>goal</p></div>}
                    </div>
                  )}
                </div>
                {detail.profile?.goal && <div style={{ marginTop: 12, padding: '10px 14px', background: `${T.orange}10`, borderRadius: 8, border: `1px solid ${T.orange}33` }}><p style={{ fontSize: 13, color: T.orange, fontWeight: 600 }}>Goal: {detail.profile.goal}</p></div>}
              </Card>

              <Card style={{ padding: 20, marginBottom: 20 }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.gray, marginBottom: 12, textTransform: 'uppercase' }}>Recent Practices ({detail.workouts?.length || 0})</h3>
                {!detail.workouts?.length ? <p style={{ fontSize: 13, color: T.gray }}>No workouts logged yet.</p>
                : detail.workouts.slice(0, 5).map(w => (
                  <div key={w.id} style={{ padding: '10px 0', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: 14, color: T.white }}>{new Date(w.logged_at).toLocaleDateString()}</p>
                      <p style={{ fontSize: 12, color: T.gray }}>{w.exercises?.length || 0} exercises{w.duration_minutes ? ` · ${w.duration_minutes} min` : ''}</p>
                    </div>
                    <span style={{ fontSize: 20 }}>{['', '😴', '😐', '😊', '💪', '🔥'][w.feeling || 3]}</span>
                  </div>
                ))}
              </Card>

              {detail.photos?.length > 0 && (
                <Card style={{ padding: 20, marginBottom: 20 }}>
                  <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.gray, marginBottom: 12, textTransform: 'uppercase' }}>Progress Photos ({detail.photos.length})</h3>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {detail.photos.map(p => (
                      <div key={p.id} style={{ width: 80, height: 80, borderRadius: 8, overflow: 'hidden', background: '#F5F7FA', border: `1px solid ${T.border}` }}>
                        <img src={`https://yoga.novaredigital.com/uploads/${p.filename}`} alt={p.label || p.type} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              <Card style={{ padding: 24 }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.gray, marginBottom: 14, textTransform: 'uppercase' }}>Send Message to {detail.user?.name?.split(' ')[0]}</h3>
                <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Write your coaching update, feedback on their progress, or encouragement…" rows={4}
                  style={{ width: '100%', padding: '13px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 14 }} />
                {sent && <div style={{ background: `${T.success}15`, border: `1px solid ${T.success}44`, borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 14, color: T.success, textAlign: 'center' }}>✅ Message sent!</div>}
                <Btn disabled={!message.trim() || sending} onClick={sendMessage}>{sending ? 'Sending…' : `Send Message to ${client?.name?.split(' ')[0] || 'Rebuilder'} →`}</Btn>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ADMIN: COACHES ─────────────────────────────────────────────────────────────
function AdminCoaches() {
  const [coaches, setCoaches] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', bio: '', specialty: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assignModalId, setAssignModalId] = useState(null);

  const load = useCallback(async () => {
    const [c, u] = await Promise.all([api('/api/admin/coaches'), api('/api/admin/users')]);
    const loaded = c.coaches || [];
    setCoaches(loaded); setUsers(u.users || []);
    return loaded;
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const createCoach = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return;
    setSaving(true);
    try { await api('/api/admin/coaches', 'POST', form); setForm({ name: '', email: '', password: '', bio: '', specialty: '' }); await load(); }
    catch(e) { alert(e.message); } finally { setSaving(false); }
  };

  const assign = async (coachId, clientId) => {
    await api(`/api/admin/coaches/${coachId}/assign`, 'POST', { clientId }).catch(() => {});
    await load();
  };

  const unassign = async (coachId, clientId) => {
    await api(`/api/admin/coaches/${coachId}/assign/${clientId}`, 'DELETE').catch(() => {});
    await load();
  };

  const assignModal = coaches.find(c => c.id === assignModalId) || null;

  return (
    <div>
    <TG.AdminTrainerAccess onChange={load} />
    <TG.AdminCoverage />
    <div className="hb-coach-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 28, alignItems: 'start' }}>
      <div>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>Coach Accounts ({coaches.length})</h3>
        {loading ? <p style={{ color: T.gray }}>Loading…</p>
        : coaches.length === 0 ? <p style={{ color: T.gray, fontSize: 14 }}>No coaches yet. Create the first one →</p>
        : coaches.map(c => (
          <Card key={c.id} style={{ padding: '18px 22px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: 15, color: T.white }}>{c.name}</p>
                <p style={{ fontSize: 13, color: T.gray }}>{c.email}</p>
                {c.specialty && <p style={{ fontSize: 12, color: T.orange, marginTop: 4 }}>{c.specialty}</p>}
                <p style={{ fontSize: 12, color: T.grayLight, marginTop: 4 }}>{(c.assignedClients || []).length} Rebuilder{(c.assignedClients || []).length !== 1 ? 's' : ''} assigned</p>
              </div>
              <Btn size="sm" variant="outline" onClick={() => setAssignModalId(c.id)}>Manage Rebuilders</Btn>
            </div>
          </Card>
        ))}
      </div>

      <Card style={{ padding: 24, position: 'sticky', top: 84 }}>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>ADD COACH</h3>
        <form onSubmit={createCoach}>
          {[
            { label: 'Full Name', key: 'name', required: true, placeholder: 'Coach full name' },
            { label: 'Email', key: 'email', type: 'email', required: true, placeholder: 'coach@example.com' },
            { label: 'Password', key: 'password', type: 'password', required: true, placeholder: 'Set a password' },
            { label: 'Specialty', key: 'specialty', placeholder: 'e.g. Weight Loss, Strength' },
            { label: 'Bio', key: 'bio', placeholder: 'Short bio (optional)' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 4, fontWeight: 500 }}>{f.label}{f.required && <span style={{ color: T.orange }}> *</span>}</label>
              <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} required={f.required}
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 6, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
          <Btn type="submit" fullWidth disabled={saving} style={{ marginTop: 8 }}>{saving ? 'Creating…' : 'Create Coach Account →'}</Btn>
        </form>
      </Card>

      {assignModal && (() => {
        const assignedIds = new Set((assignModal.assignedClients || []).map(ac => ac.client_id));
        const assignedClients = users.filter(u => u.role === 'user' && assignedIds.has(u.id));
        const unassignedClients = users.filter(u => u.role === 'user' && !assignedIds.has(u.id));
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.87)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setAssignModalId(null)}>
            <div style={{ background: T.card, borderRadius: 16, padding: 28, maxWidth: 520, width: '100%', maxHeight: '75vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 24, letterSpacing: 2, marginBottom: 4 }}>Manage Rebuilders</h3>
              <p style={{ fontSize: 13, color: T.gray, marginBottom: 20 }}>Coach: {assignModal.name}</p>

              {assignedClients.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: T.grayLight, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Assigned ({assignedClients.length})</p>
                  {assignedClients.map(u => (
                    <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                      <div>
                        <p style={{ fontSize: 14, color: T.white }}>{u.name}</p>
                        <p style={{ fontSize: 12, color: T.gray }}>{u.email}</p>
                      </div>
                      <Btn size="sm" variant="danger" onClick={() => unassign(assignModal.id, u.id)}>Unassign</Btn>
                    </div>
                  ))}
                </div>
              )}

              {unassignedClients.length > 0 && (
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: T.grayLight, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Available Rebuilders ({unassignedClients.length})</p>
                  {unassignedClients.map(u => (
                    <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                      <div>
                        <p style={{ fontSize: 14, color: T.white }}>{u.name}</p>
                        <p style={{ fontSize: 12, color: T.gray }}>{u.email}</p>
                      </div>
                      <Btn size="sm" variant="outline" onClick={() => assign(assignModal.id, u.id)}>Assign</Btn>
                    </div>
                  ))}
                </div>
              )}

              {users.filter(u => u.role === 'user').length === 0 && (
                <p style={{ fontSize: 14, color: T.gray }}>No member accounts yet.</p>
              )}

              <Btn variant="ghost" fullWidth onClick={() => setAssignModalId(null)} style={{ marginTop: 20 }}>Close</Btn>
            </div>
          </div>
        );
      })()}
    </div>
    </div>
  );
}

// ── ADMIN: COOKING SHOWS ──────────────────────────────────────────────────────
function AdminShows() {
  const [shows, setShows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', host_name: '', description: '', video_url: '', thumbnail_url: '', episode_number: 1, is_published: false });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const d = await api('/api/admin/cooking-shows').catch(() => ({ shows: [] }));
    setShows(d.shows || []);
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await api(`/api/admin/cooking-shows/${editing}`, 'PUT', form); }
      else { await api('/api/admin/cooking-shows', 'POST', form); }
      setEditing(null); setForm({ title: '', host_name: '', description: '', video_url: '', thumbnail_url: '', episode_number: 1, is_published: false });
      await load();
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  };

  const del = async id => {
    if (!window.confirm('Delete this show?')) return;
    await api(`/api/admin/cooking-shows/${id}`, 'DELETE').catch(() => {});
    await load();
  };

  const startEdit = show => {
    setEditing(show.id);
    setForm({ title: show.title, host_name: show.host_name, description: show.description || '', video_url: show.video_url || '', thumbnail_url: show.thumbnail_url || '', episode_number: show.episode_number || 1, is_published: !!show.is_published });
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 28, alignItems: 'start' }}>
      <div>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>Cooking Shows ({shows.length})</h3>
        {loading ? <p style={{ color: T.gray }}>Loading…</p>
        : shows.length === 0 ? <p style={{ color: T.gray, fontSize: 14 }}>No shows yet. Add the first one →</p>
        : shows.map(s => (
          <Card key={s.id} style={{ padding: '16px 22px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Badge color={s.is_published ? T.success : T.gray}>{s.is_published ? 'Published' : 'Draft'}</Badge>
                  <span style={{ fontSize: 12, color: T.gray }}>Ep. {s.episode_number}</span>
                </div>
                <p style={{ fontWeight: 600, fontSize: 15, color: T.white }}>{s.title}</p>
                <p style={{ fontSize: 13, color: T.gray }}>{s.host_name}</p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn size="sm" variant="ghost" onClick={() => startEdit(s)}>Edit</Btn>
                <Btn size="sm" variant="danger" onClick={() => del(s.id)}>Del</Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card style={{ padding: 24, position: 'sticky', top: 84 }}>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, marginBottom: 14 }}>{editing ? 'EDIT SHOW' : 'ADD SHOW'}</h3>
        <form onSubmit={save}>
          {[
            { label: 'Title', key: 'title', required: true, placeholder: 'Show title' },
            { label: 'Host Name', key: 'host_name', required: true, placeholder: "Home cook's name" },
            { label: 'Episode #', key: 'episode_number', type: 'number', placeholder: '1' },
            { label: 'Video URL', key: 'video_url', placeholder: 'YouTube embed or direct URL' },
            { label: 'Thumbnail URL', key: 'thumbnail_url', placeholder: 'Image URL for preview' },
            { label: 'Description', key: 'description', placeholder: "What's this episode about?" },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 4 }}>{f.label}{f.required && <span style={{ color: T.orange }}> *</span>}</label>
              {f.key === 'description'
                ? <textarea value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} rows={2}
                    style={{ width: '100%', padding: '9px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 6, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                : <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 1 : e.target.value }))} placeholder={f.placeholder} required={f.required}
                    style={{ width: '100%', padding: '9px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 6, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
              }
            </div>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_published} onChange={e => setForm(p => ({ ...p, is_published: e.target.checked }))} style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 14, color: T.grayLight }}>Published (visible to members)</span>
          </label>
          <Btn type="submit" fullWidth disabled={saving}>{saving ? 'Saving…' : editing ? 'Save Changes →' : 'Add Show →'}</Btn>
          {editing && <Btn type="button" variant="ghost" fullWidth onClick={() => { setEditing(null); setForm({ title: '', host_name: '', description: '', video_url: '', thumbnail_url: '', episode_number: 1, is_published: false }); }} style={{ marginTop: 8 }}>Cancel</Btn>}
        </form>
      </Card>
    </div>
  );
}

// ── ADMIN: COOKS ──────────────────────────────────────────────────────────────
function AdminCooks() {
  const [cooks, setCooks] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', specialty: '', bio: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assignModalId, setAssignModalId] = useState(null);

  const load = useCallback(async () => {
    const [c, u] = await Promise.all([
      api('/api/admin/cooks').catch(() => ({ cooks: [] })),
      api('/api/admin/users').catch(() => ({ users: [] })),
    ]);
    setCooks(c.cooks || []); setUsers(u.users || []);
    return c.cooks || [];
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const createCook = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return;
    setSaving(true);
    try { await api('/api/admin/cooks', 'POST', form); setForm({ name: '', email: '', password: '', specialty: '', bio: '' }); await load(); }
    catch(e) { alert(e.message); } finally { setSaving(false); }
  };

  const assign = async (cookId, clientId) => {
    await api(`/api/admin/cooks/${cookId}/assign`, 'POST', { clientId }).catch(() => {});
    await load();
  };

  const unassign = async (cookId, clientId) => {
    await api(`/api/admin/cooks/${cookId}/assign/${clientId}`, 'DELETE').catch(() => {});
    await load();
  };

  const assignModal = cooks.find(c => c.id === assignModalId) || null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28, alignItems: 'start' }}>
      <div>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>Cook Accounts ({cooks.length})</h3>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 16 }}>Cooks post content — recipes, live sessions, tips. By default their content broadcasts to all Rebuilders. Assign specific Rebuilders to send individual content.</p>
        {loading ? <p style={{ color: T.gray }}>Loading…</p>
        : cooks.length === 0 ? <p style={{ color: T.gray, fontSize: 14 }}>No cooks yet. Create one →</p>
        : cooks.map(c => (
          <Card key={c.id} style={{ padding: '18px 22px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: 15, color: T.white }}>{c.name}</p>
                <p style={{ fontSize: 13, color: T.gray }}>{c.email}</p>
                {c.specialty && <p style={{ fontSize: 12, color: T.orange, marginTop: 4 }}>{c.specialty}</p>}
                <p style={{ fontSize: 12, color: T.grayLight, marginTop: 4 }}>
                  {(c.assignedClients || []).length === 0
                    ? 'Broadcasting to all Rebuilders'
                    : `${(c.assignedClients || []).length} individual Rebuilder${(c.assignedClients || []).length !== 1 ? 's' : ''} + broadcasts`}
                </p>
              </div>
              <Btn size="sm" variant="outline" onClick={() => setAssignModalId(c.id)}>Manage Rebuilders</Btn>
            </div>
          </Card>
        ))}
      </div>

      <Card style={{ padding: 24, position: 'sticky', top: 84 }}>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>ADD COOK</h3>
        <form onSubmit={createCook}>
          {[
            { label: 'Full Name', key: 'name', required: true, placeholder: 'Cook full name' },
            { label: 'Email', key: 'email', type: 'email', required: true, placeholder: 'cook@example.com' },
            { label: 'Password', key: 'password', type: 'password', required: true, placeholder: 'Set a password' },
            { label: 'Specialty', key: 'specialty', placeholder: 'e.g. Meal Prep, Vegan Cooking' },
            { label: 'Bio', key: 'bio', placeholder: 'Short bio (optional)' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 4, fontWeight: 500 }}>{f.label}{f.required && <span style={{ color: T.orange }}> *</span>}</label>
              <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} required={f.required}
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 6, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          ))}
          <Btn type="submit" fullWidth disabled={saving} style={{ marginTop: 8 }}>{saving ? 'Creating…' : 'Create Cook Account →'}</Btn>
        </form>
      </Card>

      {assignModal && (() => {
        const assignedIds = new Set((assignModal.assignedClients || []).map(ac => ac.client_id));
        const assignedClients = users.filter(u => u.role === 'user' && assignedIds.has(u.id));
        const unassignedClients = users.filter(u => u.role === 'user' && !assignedIds.has(u.id));
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.87)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={() => setAssignModalId(null)}>
            <div style={{ background: T.card, borderRadius: 16, padding: 28, maxWidth: 520, width: '100%', maxHeight: '75vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 24, letterSpacing: 2, marginBottom: 4 }}>Manage Rebuilders</h3>
              <p style={{ fontSize: 13, color: T.gray, marginBottom: 4 }}>Cook: {assignModal.name}</p>
              <p style={{ fontSize: 12, color: T.grayLight, marginBottom: 20 }}>This cook broadcasts to all Rebuilders by default. Assigning Rebuilders here also enables individual content targeted to them.</p>

              {assignedClients.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: T.grayLight, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Individual Rebuilders ({assignedClients.length})</p>
                  {assignedClients.map(u => (
                    <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                      <div><p style={{ fontSize: 14, color: T.white }}>{u.name}</p><p style={{ fontSize: 12, color: T.gray }}>{u.email}</p></div>
                      <Btn size="sm" variant="danger" onClick={() => unassign(assignModal.id, u.id)}>Remove</Btn>
                    </div>
                  ))}
                </div>
              )}

              {unassignedClients.length > 0 && (
                <div>
                  <p style={{ fontSize: 12, fontWeight: 600, color: T.grayLight, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>Other Rebuilders ({unassignedClients.length})</p>
                  {unassignedClients.map(u => (
                    <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                      <div><p style={{ fontSize: 14, color: T.white }}>{u.name}</p><p style={{ fontSize: 12, color: T.gray }}>{u.email}</p></div>
                      <Btn size="sm" variant="outline" onClick={() => assign(assignModal.id, u.id)}>Add</Btn>
                    </div>
                  ))}
                </div>
              )}

              <Btn variant="ghost" fullWidth onClick={() => setAssignModalId(null)} style={{ marginTop: 20 }}>Done</Btn>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── ADMIN: BROADCASTS ────────────────────────────────────────────────────────
function AdminBroadcasts() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [form, setForm] = useState({ title: '', message: '', video_url: '', is_pinned: false });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const d = await api('/api/admin/broadcasts').catch(() => ({ broadcasts: [] }));
    setBroadcasts(d.broadcasts || []);
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const send = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api('/api/admin/broadcasts', 'POST', form);
      setForm({ title: '', message: '', video_url: '', is_pinned: false });
      await load();
    } catch(err) { alert(err.message); } finally { setSaving(false); }
  };

  const toggle = async (b) => {
    await api(`/api/admin/broadcasts/${b.id}`, 'PUT', { ...b, is_active: !b.is_active }).catch(() => {});
    await load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this broadcast?')) return;
    await api(`/api/admin/broadcasts/${id}`, 'DELETE').catch(() => {});
    await load();
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 28, alignItems: 'start' }}>
      <div>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>All Broadcasts</h3>
        {loading ? <p style={{ color: T.gray }}>Loading…</p>
        : broadcasts.length === 0 ? <p style={{ color: T.gray, fontSize: 14 }}>No broadcasts yet. Write your first message to members →</p>
        : broadcasts.map(b => (
          <Card key={b.id} style={{ padding: '16px 22px', marginBottom: 10, borderLeft: `3px solid ${b.is_active ? T.orange : T.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Badge color={b.is_active ? T.success : T.gray}>{b.is_active ? 'Active' : 'Hidden'}</Badge>
                  {b.is_pinned ? <Badge color={T.orange}>📌 Pinned</Badge> : null}
                  <span style={{ fontSize: 12, color: T.gray }}>{new Date(b.created_at).toLocaleDateString()}</span>
                </div>
                <p style={{ fontWeight: 700, fontSize: 15, color: T.white, marginBottom: 4 }}>{b.title}</p>
                <p style={{ fontSize: 13, color: T.grayLight, lineHeight: 1.6 }}>{b.message}</p>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Btn size="sm" variant="ghost" onClick={() => toggle(b)}>{b.is_active ? 'Hide' : 'Show'}</Btn>
                <Btn size="sm" variant="danger" onClick={() => del(b.id)}>Del</Btn>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card style={{ padding: 24, position: 'sticky', top: 84 }}>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, marginBottom: 14 }}>NEW BROADCAST</h3>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 16, lineHeight: 1.6 }}>Sends to all members on their dashboard. Pin it to keep it at the top.</p>
        <form onSubmit={send}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 4 }}>Subject / Title <span style={{ color: T.orange }}>*</span></label>
            <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. New workout drop this week!" required
              style={{ width: '100%', padding: '9px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 6, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 4 }}>Message <span style={{ color: T.orange }}>*</span></label>
            <textarea value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Write your message to all members…" required rows={4}
              style={{ width: '100%', padding: '9px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 6, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontSize: 12, color: T.grayLight, display: 'block', marginBottom: 4 }}>Video URL (optional)</label>
            <input value={form.video_url} onChange={e => setForm(p => ({ ...p, video_url: e.target.value }))} placeholder="YouTube link, Loom, or direct video"
              style={{ width: '100%', padding: '9px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 6, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.is_pinned} onChange={e => setForm(p => ({ ...p, is_pinned: e.target.checked }))} style={{ width: 16, height: 16 }} />
            <span style={{ fontSize: 14, color: T.grayLight }}>Pin to top of member dashboard</span>
          </label>
          <Btn type="submit" fullWidth disabled={saving}>{saving ? 'Sending…' : 'Send to All Members →'}</Btn>
        </form>
      </Card>
    </div>
  );
}

// ── ADMIN: SITE SETTINGS ──────────────────────────────────────────────────────
function AdminSettings() {
  const [settings, setSettings] = useState({ instructor_bio_text: '', instructor_bio_video_url: '', anthropic_api_key: '', nps_api_key: '', resend_api_key: '', kroger_client_id: '', kroger_client_secret: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api('/api/settings').then(d => {
      if (d.settings) setSettings(prev => ({ ...prev, ...d.settings }));
    }).catch(() => {});
  }, []);

  const save = async (key) => {
    setSaving(true); setSaved(false);
    try {
      await api('/api/admin/settings', 'PUT', { key, value: settings[key] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch(err) { alert(err.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 24, textTransform: 'uppercase' }}>Homepage & Bio Settings</h3>

      <Card style={{ padding: 28, marginBottom: 20 }}>
        <h4 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Instructor Bio Text</h4>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>This story appears in the "Our Story" section on the public homepage. Write it in first person — visitors connect with the real journey.</p>
        <textarea value={settings.instructor_bio_text} onChange={e => setSettings(p => ({ ...p, instructor_bio_text: e.target.value }))} rows={8}
          style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', lineHeight: 1.7, outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 14 }} />
        <Btn onClick={() => save('instructor_bio_text')} disabled={saving}>Save Bio Text →</Btn>
      </Card>

      <Card style={{ padding: 28, marginBottom: 20 }}>
        <h4 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Homepage Video URL</h4>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>Paste a YouTube link or direct video URL. A video player will appear below the bio on the homepage. Leave blank to hide the video section.</p>
        <input value={settings.instructor_bio_video_url} onChange={e => setSettings(p => ({ ...p, instructor_bio_video_url: e.target.value }))} placeholder="https://www.youtube.com/watch?v=…"
          style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }} />
        <Btn onClick={() => save('instructor_bio_video_url')} disabled={saving}>Save Video URL →</Btn>
      </Card>

      <Card style={{ padding: 28, marginBottom: 20 }}>
        <h4 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>🤖 Anthropic API Key</h4>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>Turns on every AI feature: the member AI coach, meal photos, the help desk, "Say it", trainer "Write it for me" drafts and the good-morning message. Get a key at console.anthropic.com. The key is stored securely on the server and never exposed to clients. Leave blank to disable AI features.</p>
        <input type="password" value={settings.anthropic_api_key}
          onChange={e => setSettings(p => ({ ...p, anthropic_api_key: e.target.value }))}
          placeholder="sk-ant-api…"
          style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
          onFocus={e => e.target.style.borderColor = T.orange}
          onBlur={e => e.target.style.borderColor = T.border}
        />
        <Btn onClick={() => save('anthropic_api_key')} disabled={saving}>Save API Key →</Btn>
      </Card>

      <Card style={{ padding: 28, marginBottom: 20 }}>
        <h4 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>🌲 National Parks API Key</h4>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>Enables the "Get Outside" parks page for members. Free key from <strong>developer.nps.gov</strong> — sign up, click "Get API Key", paste it here. No cost, instant approval.</p>
        <input type="password" value={settings.nps_api_key}
          onChange={e => setSettings(p => ({ ...p, nps_api_key: e.target.value }))}
          placeholder="NPS API key…"
          style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
          onFocus={e => e.target.style.borderColor = T.orange}
          onBlur={e => e.target.style.borderColor = T.border}
        />
        <Btn onClick={() => save('nps_api_key')} disabled={saving}>Save NPS Key →</Btn>
      </Card>

      <Card style={{ padding: 28, marginBottom: 20 }}>
        <h4 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>📧 Resend API Key (Email Notifications)</h4>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>Enables referral notification emails and coach message alerts to be delivered. Get a free key at <strong>resend.com</strong>. Optional: emails go out through the server's own mail system when it is set up, and this key is only used when it is not.</p>
        <input type="password" value={settings.resend_api_key}
          onChange={e => setSettings(p => ({ ...p, resend_api_key: e.target.value }))}
          placeholder="re_…"
          style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
          onFocus={e => e.target.style.borderColor = T.orange}
          onBlur={e => e.target.style.borderColor = T.border}
        />
        <Btn onClick={() => save('resend_api_key')} disabled={saving}>Save Resend Key →</Btn>
      </Card>

      <Card style={{ padding: 28, marginBottom: 20 }}>
        <h4 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>🛒 Kroger Grocery Integration</h4>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>Enables members to find nearby Kroger stores and search for products from their shopping list. Get credentials at <strong>developer.kroger.com</strong>. Both fields are required.</p>
        <input type="password" value={settings.kroger_client_id}
          onChange={e => setSettings(p => ({ ...p, kroger_client_id: e.target.value }))}
          placeholder="Kroger Client ID…"
          style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box', marginBottom: 10 }}
          onFocus={e => e.target.style.borderColor = T.orange}
          onBlur={e => e.target.style.borderColor = T.border}
        />
        <input type="password" value={settings.kroger_client_secret}
          onChange={e => setSettings(p => ({ ...p, kroger_client_secret: e.target.value }))}
          placeholder="Kroger Client Secret…"
          style={{ width: '100%', padding: '12px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}
          onFocus={e => e.target.style.borderColor = T.orange}
          onBlur={e => e.target.style.borderColor = T.border}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn onClick={() => save('kroger_client_id')} disabled={saving} style={{ flex: 1 }}>Save Client ID →</Btn>
          <Btn onClick={() => save('kroger_client_secret')} disabled={saving} style={{ flex: 1 }}>Save Client Secret →</Btn>
        </div>
      </Card>

      {saved && <p style={{ color: T.success, fontSize: 14, fontWeight: 600 }}>✓ Saved successfully</p>}
    </div>
  );
}

// ── RECIPES PAGE (members only) ───────────────────────────────────────────────
// Shown wherever the server has removed recipes for this member.
function AllergyNotice({ screening }) {
  if (!screening) return null;
  const what = [...(screening.allergies || []), ...(screening.diet || []).map(d => d.replace('_', ' '))].join(', ');
  return (
    <div style={{ padding: '12px 16px', marginBottom: 20, borderRadius: 10, background: '#D6454510', border: '1px solid #D6454540' }}>
      <p style={{ fontSize: 13, color: T.white, marginBottom: 4 }}>
        <strong>Checked against your list: {what}.</strong>{screening.removed ? ` ${screening.removed} recipe${screening.removed === 1 ? ' is' : 's are'} hidden because of it.` : ''}
      </p>
      <p style={{ fontSize: 12, color: T.gray }}>{screening.warning}</p>
    </div>
  );
}

function RecipesPage() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [selected, setSelected] = useState(null);
  const [screening, setScreening] = useState(null);

  const catIcons = { Breakfast: '🍳', Lunch: '🥗', Dinner: '🍽️', Snack: '🍎' };
  const categories = ['All', 'Breakfast', 'Lunch', 'Dinner', 'Snack'];

  useEffect(() => {
    api('/api/recipes').then(d => { setRecipes(d.recipes || []); setScreening(d.screening || null); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'All' ? recipes : recipes.filter(r => r.category === filter);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1200, margin: '0 auto' }}>
      <div className="animate-fadeInUp" style={{ marginBottom: 40 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, marginBottom: 8 }}>
          HEALTHY <span style={gradText}>RECIPES</span>
        </h1>
        <p style={{ color: T.gray, fontSize: 16 }}>Quick, real-food meals built for results. No complicated prep, no mystery ingredients.</p>
      </div>
      <AllergyNotice screening={screening} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 32, flexWrap: 'wrap' }}>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)} style={{
            padding: '8px 20px', borderRadius: 20, border: `1px solid ${filter === c ? T.gold : T.border}`, cursor: 'pointer', fontFamily: 'Poppins', fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
            background: filter === c ? `${T.gold}20` : 'transparent', color: filter === c ? T.gold : T.gray,
          }}>{c === 'All' ? 'All Recipes' : `${catIcons[c] || ''} ${c}`}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.gray }}>Loading recipes…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 24 }}>
          {filtered.map(r => (
            <Card key={r.id} style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }} onClick={() => setSelected(r)}>
              <div style={{ aspectRatio: '16/9', background: `linear-gradient(135deg, ${T.gold}22, ${T.orange}22)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 60 }}>{catIcons[r.category] || '🍽️'}</span>
              </div>
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <Badge color={T.gold}>{r.category}</Badge>
                  {(r.prep_time || r.cook_time) && <span style={{ fontSize: 12, color: T.gray }}>{(r.prep_time || 0) + (r.cook_time || 0)} min</span>}
                </div>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, color: T.white, marginBottom: 8 }}>{r.title}</h3>
                <p style={{ fontSize: 13, color: T.gray, lineHeight: 1.6, marginBottom: 14 }}>{r.description}</p>
                {r.calories_per_serving && (
                  <div style={{ display: 'flex', gap: 16, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 12, color: T.orange, fontWeight: 600 }}>{r.calories_per_serving} cal</span>
                    {r.protein_g && <span style={{ fontSize: 12, color: T.success, fontWeight: 600 }}>{r.protein_g}g protein</span>}
                    {r.carbs_g && <span style={{ fontSize: 12, color: T.gold, fontWeight: 600 }}>{r.carbs_g}g carbs</span>}
                    {r.fat_g && <span style={{ fontSize: 12, color: T.gray, fontWeight: 600 }}>{r.fat_g}g fat</span>}
                  </div>
                )}
                {r.servings && <p style={{ fontSize: 11, color: T.gray, marginTop: 6 }}>Serves {r.servings} · Tap to view full recipe & print</p>}
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <p style={{ color: T.gray, gridColumn: '1/-1', textAlign: 'center', padding: 40 }}>No recipes in this category yet.</p>}
        </div>
      )}

      {/* Recipe detail modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 2000, overflowY: 'auto', padding: '40px 24px' }} onClick={() => setSelected(null)}>
          <div style={{ maxWidth: 680, margin: '0 auto', background: T.card, borderRadius: 20, overflow: 'hidden' }} onClick={e => e.stopPropagation()} id="recipe-print-area">
            <div style={{ padding: '32px 36px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
                <div>
                  <Badge color={T.gold}>{selected.category}</Badge>
                  <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 36, letterSpacing: 2, marginTop: 8, lineHeight: 1.1 }}>{selected.title}</h2>
                  <p style={{ color: T.gray, fontSize: 14, marginTop: 6 }}>{selected.description}</p>
                </div>
                <div style={{ display: 'flex', gap: 10, flexShrink: 0, marginLeft: 16 }}>
                  <button onClick={() => window.print()} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.glass, color: T.grayLight, cursor: 'pointer', fontSize: 13, fontFamily: 'Poppins', fontWeight: 600 }}>🖨️ Print</button>
                  <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: T.gray, fontSize: 22, cursor: 'pointer', padding: 4 }}>✕</button>
                </div>
              </div>

              {/* Nutrition */}
              {selected.calories_per_serving && (
                <div style={{ display: 'flex', gap: 20, padding: '16px 20px', background: `${T.orange}08`, borderRadius: 12, border: `1px solid ${T.orange}22`, marginBottom: 28 }}>
                  {[
                    { label: 'Calories', val: selected.calories_per_serving, unit: 'kcal', col: T.orange },
                    { label: 'Protein', val: selected.protein_g, unit: 'g', col: T.success },
                    { label: 'Carbs', val: selected.carbs_g, unit: 'g', col: T.gold },
                    { label: 'Fat', val: selected.fat_g, unit: 'g', col: '#4A90E2' },
                  ].filter(m => m.val).map(m => (
                    <div key={m.label} style={{ textAlign: 'center' }}>
                      <p style={{ fontFamily: 'Bebas Neue', fontSize: 26, color: m.col, lineHeight: 1 }}>{m.val}{m.unit}</p>
                      <p style={{ fontSize: 11, color: T.gray, textTransform: 'uppercase', letterSpacing: 0.5 }}>{m.label}</p>
                    </div>
                  ))}
                  {selected.servings && <div style={{ textAlign: 'center', marginLeft: 'auto' }}><p style={{ fontFamily: 'Bebas Neue', fontSize: 26, color: T.grayLight, lineHeight: 1 }}>{selected.servings}</p><p style={{ fontSize: 11, color: T.gray, textTransform: 'uppercase' }}>Servings</p></div>}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
                {/* Ingredients */}
                <div>
                  <h3 style={{ fontFamily: 'Oswald', fontSize: 16, letterSpacing: 2, color: T.gold, marginBottom: 14, textTransform: 'uppercase' }}>Ingredients</h3>
                  {(Array.isArray(selected.ingredients) ? selected.ingredients : (selected.ingredients ? JSON.parse(selected.ingredients) : [])).map((ing, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                      <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${T.border}`, flexShrink: 0, marginTop: 2 }} />
                      <p style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.5 }}>{ing}</p>
                    </div>
                  ))}
                </div>

                {/* Instructions */}
                <div>
                  <h3 style={{ fontFamily: 'Oswald', fontSize: 16, letterSpacing: 2, color: T.orange, marginBottom: 14, textTransform: 'uppercase' }}>Instructions</h3>
                  {(Array.isArray(selected.instructions) ? selected.instructions : (selected.instructions ? JSON.parse(selected.instructions) : [])).map((step, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: `${T.orange}22`, border: `1px solid ${T.orange}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 700, color: T.orange }}>{i + 1}</div>
                      <p style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.6 }}>{step}</p>
                    </div>
                  ))}
                </div>
              </div>

              {(selected.prep_time || selected.cook_time) && (
                <div style={{ display: 'flex', gap: 20, marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
                  {selected.prep_time && <p style={{ fontSize: 13, color: T.gray }}>⏱ Prep: <strong style={{ color: T.white }}>{selected.prep_time} min</strong></p>}
                  {selected.cook_time && <p style={{ fontSize: 13, color: T.gray }}>🔥 Cook: <strong style={{ color: T.white }}>{selected.cook_time} min</strong></p>}
                </div>
              )}
            </div>
          </div>
          <style>{`@media print { body > *:not(#recipe-print-area) { display: none !important; } #recipe-print-area { box-shadow: none; margin: 0; } }`}</style>
        </div>
      )}
    </div>
  );
}

// ── TIME AGO HELPER ───────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── GET OUTSIDE PAGE ──────────────────────────────────────────────────────────
function GetOutsidePage() {
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('TN');
  const [unavailable, setUnavailable] = useState(false);

  const loadParks = () => {
    setLoading(true);
    api('/api/parks?state=' + stateFilter + (search ? '&q=' + encodeURIComponent(search) : ''))
      .then(d => {
        if (d.available === false) { setUnavailable(true); setParks([]); }
        else { setUnavailable(false); setParks(d.parks || []); }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadParks(); }, [stateFilter]);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 60px)', letterSpacing: 2, marginBottom: 8 }}>
          GET <span style={gradText}>OUTSIDE</span>
        </h1>
        <p style={{ color: T.grayLight, fontSize: 16 }}>Find parks, trails & outdoor spots near you</p>
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap' }}>
        <input
          placeholder="Search parks..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && loadParks()}
          style={{ flex: 1, minWidth: 200, padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }}
        />
        <select
          value={stateFilter}
          onChange={e => setStateFilter(e.target.value)}
          style={{ padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', cursor: 'pointer' }}
        >
          {['TN','VA','KY','NC','GA'].map(s => <option key={s} value={s} style={{ background: '#F5F7FA' }}>{s}</option>)}
        </select>
        <Btn onClick={loadParks} size="sm">Search</Btn>
      </div>
      {unavailable && (
        <Card style={{ padding: 40, textAlign: 'center', border: `1px solid ${T.orange}` }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🌲</div>
          <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 28, letterSpacing: 1, marginBottom: 8, color: T.orange }}>NPS API key not configured yet — coming soon!</h3>
          <p style={{ color: T.gray, fontSize: 15 }}>Park data will be available once the NPS API integration is enabled.</p>
        </Card>
      )}
      {loading && !unavailable && <Spinner />}
      {error && <p style={{ color: T.danger, fontSize: 14 }}>{error}</p>}
      {!loading && !unavailable && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {parks.map((park, i) => (
            <Card key={park.id || park.parkCode || i} style={{ padding: 24 }}>
              {park.images?.length > 0 && (
                <img src={park.images[0].url} alt={park.images[0].altText || ''} style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, marginBottom: 16 }} />
              )}
              <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 22, letterSpacing: 1, marginBottom: 8 }}>{park.fullName || park.name}</h3>
              <p style={{ fontSize: 13, color: T.grayLight, lineHeight: 1.6, marginBottom: 12 }}>
                {park.description?.substring(0, 160)}{park.description?.length > 160 ? '…' : ''}
              </p>
              {park.activities?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {park.activities.slice(0, 5).map((a, ai) => (
                    <span key={ai} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${T.success}22`, color: T.success, border: `1px solid ${T.success}44` }}>{a.name || a}</span>
                  ))}
                </div>
              )}
              {park.url && (
                <a href={park.url} target="_blank" rel="noreferrer" style={{ color: T.orange, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>Visit Park Site →</a>
              )}
            </Card>
          ))}
          {parks.length === 0 && <p style={{ color: T.gray, fontSize: 15 }}>No parks found. Try a different search or state.</p>}
        </div>
      )}
    </div>
  );
}

// ── COMMUNITY PAGE ────────────────────────────────────────────────────────────
function CommunityPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [userReactions, setUserReactions] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadPosts = () => {
    api('/api/community')
      .then(d => { setPosts(d.posts || []); setUserReactions(d.userReactions || []); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadPosts(); }, []);

  const submit = async () => {
    if (!newPost.trim()) return;
    setSubmitting(true);
    try { await api('/api/community', 'POST', { content: newPost }); setNewPost(''); loadPosts(); }
    catch(err) { alert(err.message); } finally { setSubmitting(false); }
  };

  const react = async (id) => {
    await api('/api/community/' + id + '/react', 'POST');
    const reacted = userReactions.includes(id);
    setUserReactions(prev => reacted ? prev.filter(x => x !== id) : [...prev, id]);
    setPosts(prev => prev.map(p => p.id === id ? { ...p, fire_count: (p.fire_count || 0) + (reacted ? -1 : 1) } : p));
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await api('/api/community/' + id, 'DELETE');
    setPosts(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 60px)', letterSpacing: 2, marginBottom: 8 }}>
          COMMUNITY <span style={gradText}>BOARD</span>
        </h1>
        <p style={{ color: T.grayLight, fontSize: 16 }}>Share your wins, ask questions, and support each other.</p>
      </div>
      <Card style={{ padding: 24, marginBottom: 32 }}>
        <textarea
          rows={3}
          placeholder="Share a win, a tip, or some encouragement..."
          value={newPost}
          onChange={e => setNewPost(e.target.value)}
          style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 12 }}
        />
        <Btn onClick={submit} disabled={submitting || !newPost.trim()}>Post Your Win 🔥</Btn>
      </Card>
      {loading && <Spinner />}
      <div style={{ display: 'grid', gap: 16 }}>
        {posts.map(post => (
          <Card key={post.id} style={{ padding: 22, borderLeft: post.is_pinned ? `3px solid ${T.orange}` : undefined }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${T.orange}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: T.orange, flexShrink: 0 }}>
                  {post.author_name?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 14, color: T.white }}>{post.author_name}</p>
                  <p style={{ fontSize: 12, color: T.gray }}>{timeAgo(post.created_at)}</p>
                </div>
                {post.is_pinned && <Badge color={T.orange}>📌 PINNED</Badge>}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button onClick={() => react(post.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', gap: 4, color: userReactions.includes(post.id) ? T.orange : T.gray }}>
                  🔥 <span style={{ fontSize: 13, fontFamily: 'Poppins' }}>{post.fire_count || 0}</span>
                </button>
                {user?.id === post.user_id && (
                  <button onClick={() => deletePost(post.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: T.danger }}>✕</button>
                )}
              </div>
            </div>
            <p style={{ fontSize: 15, color: T.grayLight, lineHeight: 1.7 }}>{post.content}</p>
          </Card>
        ))}
        {!loading && posts.length === 0 && (
          <p style={{ color: T.gray, textAlign: 'center', padding: 40 }}>No posts yet. Be the first to share! 🔥</p>
        )}
      </div>
    </div>
  );
}

// ── SLEEP PAGE ────────────────────────────────────────────────────────────────
function SleepPage() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], hours_slept: '', recovery_score: '', resting_hr: '', hrv: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const A = '#3B4A6B';

  const load = () => api('/api/sleep')
    .then(setD)
    .catch(err => setD({ locked: /not active|not on your plan/i.test(err.message || '') || err.status === 402, lockInfo: err.info, error: true }));
  useEffect(() => { load(); }, []);

  const submit = async e => {
    e.preventDefault(); setSaving(true);
    try { await api('/api/sleep', 'POST', form); setSaved(true); setTimeout(() => setSaved(false), 3000); load(); }
    catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  if (!d) return <ModuleLoading />;
  if (d.locked) return <ModuleLocked info={d.lockInfo} />;

  const logs = d.logs || [];
  const hoursColor = h => h >= 7 ? T.success : h >= 6 ? T.gold : T.danger;
  const label = { display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 };
  const field = { width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 980, margin: '0 auto' }}>
      <ModuleHeader icon="🌙" title="SLEEP" accent={A}
        sub="Fix the thing that fixes everything else. Wake time first, caffeine second, everything else after that." />
      <SafetyPanel data={d} accent={A} />

      {d.blocked ? null : (
        <>
          {d.target && (
            <p style={{ fontSize: 14, color: T.gray, marginBottom: 22 }}>
              Your target: <strong style={{ color: A }}>{d.target}</strong>
              {d.focus && d.focus.length ? <> · working on <strong style={{ color: A }}>{d.focus.join(' and ')}</strong></> : null}
            </p>
          )}

          {(d.notes || []).length > 0 && (
            <Card style={{ padding: 20, marginBottom: 26, borderLeft: `5px solid ${A}` }}>
              {(d.notes || []).map((n, i) => (
                <p key={i} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6, marginTop: i ? 10 : 0 }}>{n}</p>
              ))}
            </Card>
          )}

          {/* The screener runs before the habits, because habits do not treat apnea. */}
          {(d.screeners || []).length > 0 && (
            <>
              <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 6 }}>Answer these first</h2>
              <p style={{ fontSize: 13.5, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>
                If any of these is a yes, it goes to a doctor before it goes to a routine. Nothing further down this page treats a sleep disorder.
              </p>
              <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 36 }}>
                {(d.screeners || []).map((q, i) => (
                  <div key={q.key} style={{ padding: '16px 20px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: T.white, marginBottom: 6, lineHeight: 1.5 }}>{q.question}</p>
                    <p style={{ fontSize: 13.5, color: '#B3261E', lineHeight: 1.6 }}><strong>If yes:</strong> {q.ifYes}</p>
                  </div>
                ))}
              </Card>
            </>
          )}

          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 14 }}>Log last night</h2>
          <Card style={{ padding: 26, marginBottom: 36 }}>
            <form onSubmit={submit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
                {[
                  { key: 'date', label: 'Date', type: 'date' },
                  { key: 'hours_slept', label: 'Hours slept', type: 'number', placeholder: '7.5', step: '0.5', min: '1', max: '14' },
                  { key: 'recovery_score', label: 'Recovery score (Oura / Fitbit)', type: 'number', placeholder: '85', min: '0', max: '100' },
                  { key: 'resting_hr', label: 'Resting HR (bpm)', type: 'number', placeholder: '60' },
                  { key: 'hrv', label: 'HRV (ms)', type: 'number', placeholder: '45' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={label}>{f.label}</label>
                    <input type={f.type} placeholder={f.placeholder} step={f.step} min={f.min} max={f.max}
                      value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={field} />
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={label}>Notes</label>
                <textarea rows={2} placeholder="What was different about last night?" value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={{ ...field, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Log sleep'}</Btn>
                {saved && <span style={{ color: T.success, fontSize: 13 }}>Saved</span>}
              </div>
            </form>
          </Card>

          {logs.length > 0 && (
            <>
              <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 14 }}>Your nights</h2>
              <Card style={{ padding: 24, marginBottom: 36 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 100, overflowX: 'auto' }}>
                  {logs.slice(0, 14).reverse().map((l, i) => {
                    const h = parseFloat(l.hours_slept) || 0;
                    return (
                      <div key={l.id || i} style={{ flex: '0 0 auto', width: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                        <div title={`${h}h`} style={{ width: '100%', height: `${Math.min((h / 9) * 100, 100)}%`, background: hoursColor(h), borderRadius: '4px 4px 0 0', minHeight: 4 }} />
                        <span style={{ fontSize: 9, color: T.gray, whiteSpace: 'nowrap' }}>{l.date?.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </>
          )}

          {(d.recovery || []).length > 0 && (
            <>
              <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 6 }}>What to train on, given what you slept</h2>
              <p style={{ fontSize: 13.5, color: T.gray, marginBottom: 14 }}>Match the session to the night you actually had, not to the plan you wrote on Sunday.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 36 }}>
                {(d.recovery || []).map(r => (
                  <Card key={r.when} style={{ padding: 20 }}>
                    <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, color: A, marginBottom: 10 }}>{r.when}</h3>
                    <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6, marginBottom: 10 }}>{r.training}</p>
                    <p style={{ fontSize: 12.5, color: T.gray, lineHeight: 1.55, fontStyle: 'italic' }}>{r.why}</p>
                  </Card>
                ))}
              </div>
            </>
          )}

          {(d.windDown || []).length > 0 && (
            <>
              <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 14 }}>The wind-down, counted back from lights out</h2>
              <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 36 }}>
                {(d.windDown || []).map((w, i) => (
                  <div key={w.at} style={{ display: 'flex', gap: 16, padding: '16px 20px', borderTop: i ? `1px solid ${T.border}` : 'none', alignItems: 'baseline' }}>
                    <span style={{ fontFamily: 'Oswald', fontSize: 13, fontWeight: 600, color: A, minWidth: 64 }}>{w.at}</span>
                    <span>
                      <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: T.white, marginBottom: 4 }}>{w.what}</span>
                      <span style={{ display: 'block', fontSize: 13, color: T.grayLight, lineHeight: 1.55 }}>{w.why}</span>
                    </span>
                  </div>
                ))}
              </Card>
            </>
          )}

          {(d.rules || []).length > 0 && (
            <Card style={{ padding: 22, marginBottom: 26, borderLeft: `5px solid ${A}` }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>The rules that do the work</h3>
              <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 7 }}>
                {(d.rules || []).map(r => <li key={r} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.55 }}>{r}</li>)}
              </ul>
            </Card>
          )}

          {d.escalate && (
            <Card style={{ padding: 22, borderLeft: '5px solid #B3261E' }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 10 }}>When this needs a doctor, not a routine</h3>
              <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6 }}>{d.escalate}</p>
              {d.disclaimer && <p style={{ fontSize: 12.5, color: T.gray, marginTop: 12, fontStyle: 'italic' }}>{d.disclaimer}</p>}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ── SUPPLEMENTS PAGE ──────────────────────────────────────────────────────────
function SupplementsPage() {
  const [saved, setSaved] = useState([]);
  const [logged, setLogged] = useState([]);
  const [newSupp, setNewSupp] = useState({ name: '', dose: '', default_time: 'morning' });
  const [adding, setAdding] = useState(false);
  const today = new Date().toISOString().split('T')[0];

  const load = () => {
    api('/api/supplements?date=' + today)
      .then(d => { setSaved(d.supplements || []); setLogged(d.logged || []); })
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleLog = async (s) => {
    const logEntry = logged.find(l => l.name === s.name);
    if (logEntry) {
      await api('/api/supplement-log/' + logEntry.id, 'DELETE');
    } else {
      await api('/api/supplement-log', 'POST', { supplement_id: s.id, name: s.name, dose: s.dose, time_of_day: s.default_time, logged_date: today });
    }
    load();
  };

  const deleteSupp = async id => {
    if (!window.confirm('Remove this supplement?')) return;
    await api('/api/supplements/' + id, 'DELETE');
    load();
  };

  const addSupp = async e => {
    e.preventDefault();
    if (!newSupp.name) return;
    setAdding(true);
    try { await api('/api/supplements', 'POST', newSupp); setNewSupp({ name: '', dose: '', default_time: 'morning' }); load(); }
    catch(err) { alert(err.message); } finally { setAdding(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 60px)', letterSpacing: 2, marginBottom: 8 }}>
          SUPPLEMENT<span style={gradText}>S</span>
        </h1>
        <p style={{ color: T.grayLight, fontSize: 16 }}>Track your daily supplement routine.</p>
      </div>
      <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>Today — {today}</h3>
      <div style={{ display: 'grid', gap: 12, marginBottom: 40 }}>
        {saved.length === 0 && <p style={{ color: T.gray, fontSize: 14 }}>No supplements added yet. Add some below.</p>}
        {saved.map(s => {
          const isLogged = logged.some(l => l.name === s.name);
          return (
            <Card key={s.id} style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 16 }}>
              <button onClick={() => toggleLog(s)} style={{
                width: 32, height: 32, borderRadius: '50%', border: `2px solid ${isLogged ? T.success : T.border}`,
                background: isLogged ? T.success : 'transparent', color: isLogged ? '#fff' : T.gray,
                cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>✓</button>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: 15, color: isLogged ? T.success : T.white, textDecoration: isLogged ? 'line-through' : 'none' }}>{s.name}</p>
                <p style={{ fontSize: 12, color: T.gray }}>{s.dose} · {s.default_time}</p>
              </div>
              <button onClick={() => deleteSupp(s.id)} style={{ background: 'none', border: 'none', color: T.danger, cursor: 'pointer', fontSize: 18 }}>✕</button>
            </Card>
          );
        })}
      </div>
      <Card style={{ padding: 28 }}>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 20, textTransform: 'uppercase' }}>Add Supplement</h3>
        <form onSubmit={addSupp}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Name</label>
              <input placeholder="e.g. Creatine" value={newSupp.name} onChange={e => setNewSupp(p => ({ ...p, name: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Dose</label>
              <input placeholder="e.g. 5g" value={newSupp.dose} onChange={e => setNewSupp(p => ({ ...p, dose: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Time</label>
              <select value={newSupp.default_time} onChange={e => setNewSupp(p => ({ ...p, default_time: e.target.value }))}
                style={{ padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', cursor: 'pointer' }}>
                <option value="morning">Morning</option>
                <option value="midday">Midday</option>
                <option value="evening">Evening</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <Btn type="submit" disabled={adding}>Add Supplement</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

// ── MEASUREMENTS PAGE ─────────────────────────────────────────────────────────
function MeasurementsPage() {
  const [measurements, setMeasurements] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], weight_lbs: '', waist_in: '', hips_in: '', chest_in: '', arms_in: '', thighs_in: '', neck_in: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = () => api('/api/measurements').then(d => setMeasurements(d.measurements || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async e => {
    e.preventDefault(); setSaving(true);
    try { await api('/api/measurements', 'POST', form); setSaved(true); setTimeout(() => setSaved(false), 3000); setShowForm(false); load(); }
    catch(err) { alert(err.message); } finally { setSaving(false); }
  };

  const fields = [
    { key: 'weight_lbs', label: 'Weight (lbs)' },
    { key: 'waist_in',   label: 'Waist (in)'   },
    { key: 'hips_in',    label: 'Hips (in)'    },
    { key: 'chest_in',   label: 'Chest (in)'   },
    { key: 'arms_in',    label: 'Arms (in)'    },
    { key: 'thighs_in',  label: 'Thighs (in)'  },
    { key: 'neck_in',    label: 'Neck (in)'    },
  ];

  const first = measurements[measurements.length - 1];
  const latest = measurements[0];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 60px)', letterSpacing: 2, marginBottom: 8 }}>
            BODY <span style={gradText}>MEASUREMENTS</span>
          </h1>
          <p style={{ color: T.grayLight, fontSize: 16 }}>Track your body composition over time.</p>
        </div>
        <Btn variant="outline" onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : '+ Add Measurements'}</Btn>
      </div>

      {measurements.length >= 2 && first && latest && first.weight_lbs && latest.weight_lbs && (
        <Card style={{ padding: 20, marginBottom: 24 }}>
          <p style={{ color: T.success, fontWeight: 700, fontSize: 16 }}>
            {parseFloat(latest.weight_lbs) < parseFloat(first.weight_lbs)
              ? `Down ${(parseFloat(first.weight_lbs) - parseFloat(latest.weight_lbs)).toFixed(1)} lbs since ${first.date}`
              : `Up ${(parseFloat(latest.weight_lbs) - parseFloat(first.weight_lbs)).toFixed(1)} lbs since ${first.date}`}
          </p>
        </Card>
      )}

      {showForm && (
        <Card style={{ padding: 28, marginBottom: 32 }}>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 20, textTransform: 'uppercase' }}>New Entry</h3>
          <form onSubmit={submit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Date</label>
              <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                style={{ padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
              {fields.map(f => (
                <div key={f.key}>
                  <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>{f.label}</label>
                  <input type="number" step="0.1" placeholder="—" value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Notes</label>
              <textarea rows={2} placeholder="Any notes..." value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Btn type="submit" disabled={saving}>Save Measurements</Btn>
              {saved && <span style={{ color: T.success, fontSize: 13 }}>✓ Saved</span>}
            </div>
          </form>
        </Card>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {measurements.map(m => (
          <Card key={m.id} style={{ padding: 22 }}>
            <p style={{ fontFamily: 'Bebas Neue', fontSize: 20, letterSpacing: 1, color: T.orange, marginBottom: 12 }}>{m.date}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {fields.filter(f => m[f.key]).map(f => (
                <span key={f.key} style={{ padding: '6px 14px', borderRadius: 20, background: T.glass, border: `1px solid ${T.border}`, fontSize: 13, color: T.grayLight }}>
                  <span style={{ color: T.gray }}>{f.label}: </span>
                  <strong style={{ color: T.white }}>{m[f.key]}</strong>
                </span>
              ))}
              {m.notes && <span style={{ fontSize: 13, color: T.gray, alignSelf: 'center' }}>{m.notes}</span>}
            </div>
          </Card>
        ))}
        {measurements.length === 0 && (
          <p style={{ color: T.gray, textAlign: 'center', padding: 40 }}>No measurements yet. Click "Add Measurements" to start tracking.</p>
        )}
      </div>
    </div>
  );
}

// ── MEAL PLAN PAGE ────────────────────────────────────────────────────────────
function MealPlanPage() {
  const getWeekStart = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d);
    monday.setDate(diff);
    return monday.toISOString().split('T')[0];
  };
  const weekStart = getWeekStart();

  // plan[day] = { breakfast, lunch, dinner, snack } where each slot is either a recipeId (string) or null/undefined
  const [plan, setPlan] = useState({});
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saved, setSaved] = useState(false);
  const [screening, setScreening] = useState(null);
  const [numbersHidden, setNumbersHidden] = useState(false);

  useEffect(() => {
    Promise.all([
      api('/api/meal-plan?week_start=' + weekStart),
      api('/api/recipes'),
    ]).then(([mp, rp]) => {
      // Plan slots may be full recipe objects (from auto-generate) or just IDs (from manual save) — normalise to IDs
      const raw = mp.plan || {};
      const normalised = {};
      ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].forEach(d => {
        const slot = raw[d] || {};
        let parsed = slot;
        if (typeof slot === 'string') { try { parsed = JSON.parse(slot); } catch(e) { parsed = {}; } }
        normalised[d] = {
          breakfast: parsed.breakfast?.recipeId || parsed.breakfast || null,
          lunch: parsed.lunch?.recipeId || parsed.lunch || null,
          dinner: parsed.dinner?.recipeId || parsed.dinner || null,
          snack: parsed.snack?.recipeId || parsed.snack || null,
        };
      });
      setPlan(normalised);
      setRecipes(rp.recipes || []);
      setScreening(rp.screening || null);
      setNumbersHidden(!!rp.numbersHidden);
    }).finally(() => setLoading(false));
  }, []);

  const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
  const DAY_LABELS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
  const MEALS = ['breakfast','lunch','dinner','snack'];
  const MEAL_LABELS = { breakfast: '🌅 Breakfast', lunch: '☀️ Lunch', dinner: '🌙 Dinner', snack: '🍎 Snack' };
  const MEAL_CAL_PCT = { breakfast: 0.28, lunch: 0.33, dinner: 0.33, snack: 0.06 };

  const recipeById = recipes.reduce((m, r) => { m[r.id] = r; return m; }, {});

  const updateSlot = (day, meal, val) => {
    setPlan(p => ({ ...p, [day]: { ...(p[day] || {}), [meal]: val || null } }));
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const body = { week_start: weekStart };
      DAYS.forEach(d => { body[d] = plan[d] || {}; });
      await api('/api/meal-plan', 'PUT', body);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch(err) { alert(err.message); } finally { setSaving(false); }
  };

  const autoGenerate = async () => {
    setGenerating(true);
    try {
      const result = await api('/api/meal-plan/generate', 'POST', { week_start: weekStart });
      // Generated plan has full recipe objects — normalise to IDs
      const normalised = {};
      DAYS.forEach(d => {
        const slot = result.plan?.[d] || {};
        normalised[d] = {
          breakfast: slot.breakfast?.recipeId || null,
          lunch: slot.lunch?.recipeId || null,
          dinner: slot.dinner?.recipeId || null,
          snack: slot.snack?.recipeId || null,
        };
      });
      setPlan(normalised);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch(err) { alert(err.message); } finally { setGenerating(false); }
  };

  // Day-level macro summary
  const dayTotals = (day) => {
    const slots = plan[day] || {};
    let cal = 0, pro = 0, carb = 0, fat = 0;
    Object.values(slots).forEach(rid => {
      if (!rid) return;
      const r = recipeById[rid];
      if (!r) return;
      cal += r.calories_per_serving || 0;
      pro += r.protein_g || 0;
      carb += r.carbs_g || 0;
      fat += r.fat_g || 0;
    });
    return { cal, pro, carb, fat };
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 60px)', letterSpacing: 2, marginBottom: 8 }}>
            MEAL <span style={gradText}>PLANNER</span>
          </h1>
          <p style={{ color: T.grayLight, fontSize: 16 }}>Week of {weekStart}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {saved && <span style={{ color: T.success, fontSize: 13 }}>✓ Saved</span>}
          <Btn variant="secondary" onClick={autoGenerate} disabled={generating || saving}>
            {generating ? 'Generating…' : '✨ Auto-Generate Week'}
          </Btn>
          <Btn onClick={savePlan} disabled={saving || generating}>{saving ? 'Saving…' : 'Save Plan'}</Btn>
        </div>
      </div>

      <AllergyNotice screening={screening} />
      {recipes.length === 0 && (
        <p style={{ fontSize: 14, color: T.grayLight, marginBottom: 16 }}>No recipe in the library fits your allergy and diet list yet, so there is nothing safe to plan with. We would rather leave the week empty than guess.</p>
      )}

      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(7, minmax(160px, 1fr))`, gap: 10, minWidth: 900 }}>
          {DAYS.map((day, di) => {
            const totals = dayTotals(day);
            return (
              <div key={day} style={{ background: T.glass, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 12px' }}>
                <p style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 1.5, color: T.orange, marginBottom: 4, textTransform: 'uppercase' }}>{DAY_LABELS[di]}</p>
                {!numbersHidden && totals.cal > 0 && (
                  <p style={{ fontSize: 10, color: T.gray, marginBottom: 10 }}>{totals.cal} cal · {totals.pro}g protein</p>
                )}
                {MEALS.map(meal => {
                  const rid = plan[day]?.[meal] || '';
                  const rec = rid ? recipeById[rid] : null;
                  return (
                    <div key={meal} style={{ marginBottom: 8 }}>
                      <p style={{ fontSize: 10, color: T.gray, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.8 }}>{MEAL_LABELS[meal]}</p>
                      <select
                        value={rid}
                        onChange={e => updateSlot(day, meal, e.target.value)}
                        style={{ width: '100%', padding: '7px 8px', background: rec ? `${T.orange}15` : '#F5F7FA', border: `1px solid ${rec ? T.orange + '44' : T.border}`, borderRadius: 6, color: rec ? T.orange : T.gray, fontSize: 11, fontFamily: 'Poppins', outline: 'none', cursor: 'pointer' }}
                      >
                        <option value="" style={{ background: '#F5F7FA', color: T.gray }}>— Select —</option>
                        {recipes.filter(r => meal === 'snack' ? r.category === 'Snack' : meal === 'breakfast' ? r.category === 'Breakfast' : meal === 'lunch' ? r.category === 'Lunch' : r.category === 'Dinner').map(r => (
                          <option key={r.id} value={r.id} style={{ background: '#F5F7FA', color: '#fff' }}>{r.title}</option>
                        ))}
                        {/* Show all recipes as a fallback if category is empty */}
                        {recipes.filter(r => meal === 'snack' ? r.category === 'Snack' : meal === 'breakfast' ? r.category === 'Breakfast' : meal === 'lunch' ? r.category === 'Lunch' : r.category === 'Dinner').length === 0 &&
                          recipes.map(r => <option key={r.id} value={r.id} style={{ background: '#F5F7FA', color: '#fff' }}>{r.title}</option>)
                        }
                      </select>
                      {rec && !numbersHidden && rec.calories_per_serving != null && (
                        <p style={{ fontSize: 9, color: T.gray, marginTop: 2 }}>{rec.calories_per_serving || 0} cal · {rec.protein_g || 0}g protein</p>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 20, padding: '14px 18px', background: T.glass, borderRadius: 10, border: `1px solid ${T.border}` }}>
        <p style={{ fontSize: 12, color: T.gray }}>💡 <strong style={{ color: T.white }}>Auto-Generate</strong> picks recipes from the database to fill your week. Adjust any slot manually, then Save Plan to lock it in.</p>
      </div>
    </div>
  );
}

// ── CONTESTS PAGE ─────────────────────────────────────────────────────────────
function ContestsPage() {
  const [contests, setContests] = useState([]);
  const [myEntries, setMyEntries] = useState([]);
  const [leaderboard, setLeaderboard] = useState({});
  const [loadingLb, setLoadingLb] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    api('/api/contests').then(d => { setContests(d.contests || []); setMyEntries(d.myEntries || []); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const join = async id => {
    try { await api('/api/contests/' + id + '/join', 'POST'); load(); }
    catch(err) { alert(err.message); }
  };

  const loadLeaderboard = async id => {
    setLoadingLb(p => ({ ...p, [id]: true }));
    try {
      const d = await api('/api/contests/' + id + '/leaderboard');
      setLeaderboard(p => ({ ...p, [id]: d.rows || [] }));
    } catch(err) { alert(err.message); } finally { setLoadingLb(p => ({ ...p, [id]: false })); }
  };

  const metricLabels = {
    workouts_completed: 'Most Practices Completed',
    streak_days: 'Longest Streak',
    weight_lost: 'Most Weight Lost',
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 60px)', letterSpacing: 2, marginBottom: 8 }}>
          CONTESTS & <span style={gradText}>CHALLENGES</span>
        </h1>
        <p style={{ color: T.grayLight, fontSize: 16 }}>Winners receive a free month and a medal badge! 🥇</p>
      </div>
      <div style={{ display: 'grid', gap: 24 }}>
        {contests.map(c => {
          const joined = myEntries.some(e => e.contest_id === c.id);
          const lb = leaderboard[c.id];
          return (
            <Card key={c.id} style={{ padding: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <div>
                  <h3 style={{ fontFamily: 'Bebas Neue', fontSize: 26, letterSpacing: 1, marginBottom: 6 }}>{c.title}</h3>
                  <Badge color={T.gold}>{metricLabels[c.metric_type] || c.metric_type}</Badge>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <Btn variant="outline" size="sm" onClick={() => lb ? setLeaderboard(p => { const n = {...p}; delete n[c.id]; return n; }) : loadLeaderboard(c.id)}>
                    {lb ? 'Hide Board' : 'View Leaderboard'}
                  </Btn>
                  <Btn size="sm" disabled={joined} onClick={() => join(c.id)}>{joined ? '✓ Joined' : 'Join Challenge'}</Btn>
                </div>
              </div>
              <p style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.6, marginBottom: 12 }}>{c.description}</p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: T.gray }}>
                <span>📅 {c.start_date} → {c.end_date}</span>
                {c.prize_description && <span>🎁 {c.prize_description}</span>}
                {c.free_months_prize > 0 && <span>🎟️ {c.free_months_prize} free month{c.free_months_prize !== 1 ? 's' : ''}</span>}
              </div>
              {lb && (
                <div style={{ marginTop: 20, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                  <p style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.grayLight, marginBottom: 12, textTransform: 'uppercase' }}>Leaderboard</p>
                  {lb.slice(0, 10).map((row, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                      <span style={{ fontFamily: 'Bebas Neue', fontSize: 18, color: i === 0 ? T.gold : T.gray, width: 32 }}>{i === 0 ? '🥇' : i + 1}</span>
                      <span style={{ flex: 1, fontSize: 14, color: T.white }}>{row.name}</span>
                      <span style={{ fontSize: 13, color: T.orange, fontWeight: 600 }}>{row.score}</span>
                    </div>
                  ))}
                  {lb.length === 0 && <p style={{ color: T.gray, fontSize: 13 }}>No entries yet. Be the first!</p>}
                </div>
              )}
            </Card>
          );
        })}
        {contests.length === 0 && (
          <p style={{ color: T.gray, textAlign: 'center', padding: 60, fontSize: 15 }}>No active challenges right now. Check back soon!</p>
        )}
      </div>
    </div>
  );
}

// ── REFERRAL PAGE ─────────────────────────────────────────────────────────────
function ReferralPage() {
  const [code, setCode]             = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [freeMonths, setFreeMonths] = useState(0);
  const [loading, setLoading]       = useState(true);
  const [copied, setCopied]         = useState(false);
  const [form, setForm]             = useState({ name: '', cell: '', email: '', address: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  const load = () => {
    api('/api/referral-code')
      .then(d => { setCode(d.code || ''); setSubmissions(d.submissions || []); setFreeMonths(d.free_months || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const copyLink = () => {
    const url = 'https://yoga.novaredigital.com/signup?ref=' + code;
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2200); });
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submitReferral = async e => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess('');
    if (!form.name.trim()) { setSubmitError('Name is required.'); return; }
    if (!form.cell.trim()) { setSubmitError('Cell number is required.'); return; }
    setSubmitting(true);
    try {
      await api('/api/referral/submit', 'POST', {
        referred_name: form.name,
        referred_cell: form.cell,
        referred_email: form.email || undefined,
        referred_address: form.address || undefined,
      });
      setSubmitSuccess(`${form.name} has been submitted! You'll earn a free month when they join.`);
      setForm({ name: '', cell: '', email: '', address: '' });
      load();
    } catch(err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 860, margin: '0 auto' }}>
      <div style={{ marginBottom: 36 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 58px)', letterSpacing: 2, marginBottom: 8 }}>
          REFERRAL <span style={gradText}>PROGRAM</span>
        </h1>
        <p style={{ color: T.grayLight, fontSize: 16 }}>Know someone who would benefit? Send them our way — earn a free month when they join.</p>
      </div>

      {/* Credits banner */}
      {freeMonths > 0 && (
        <Card style={{ padding: '18px 24px', marginBottom: 20, background: `${T.gold}18`, border: `1px solid ${T.gold}55`, display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 28 }}>🎉</span>
          <div>
            <p style={{ fontFamily: 'Bebas Neue', fontSize: 22, color: T.gold, letterSpacing: 1 }}>{freeMonths} Free Month{freeMonths !== 1 ? 's' : ''} Earned</p>
            <p style={{ fontSize: 13, color: T.gray }}>One comes off your plan the next time you check out.</p>
          </div>
        </Card>
      )}

      {/* Submit referral form — PRIMARY SECTION */}
      <Card style={{ padding: 32, marginBottom: 24 }}>
        <h2 style={{ fontFamily: 'Oswald', fontSize: 18, letterSpacing: 1, marginBottom: 6 }}>Refer a Friend</h2>
        <p style={{ fontSize: 14, color: T.gray, marginBottom: 24, lineHeight: 1.6 }}>
          Fill in their info below and we'll follow up with them. You earn a free month once they sign up.
        </p>

        {submitSuccess && (
          <div style={{ background: `${T.success}18`, border: `1px solid ${T.success}44`, borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: T.success }}>
            ✓ {submitSuccess}
          </div>
        )}
        {submitError && (
          <div style={{ background: `${T.danger}15`, border: `1px solid ${T.danger}44`, borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 14, color: T.danger }}>
            {submitError}
          </div>
        )}

        <form onSubmit={submitReferral}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: T.gray, fontFamily: 'Oswald', letterSpacing: 1, marginBottom: 6 }}>FRIEND'S NAME <span style={{ color: T.orange }}>*</span></label>
              <input value={form.name} onChange={set('name')} placeholder="Full name" required
                style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', boxSizing: 'border-box', outline: 'none' }}
                onFocus={e => e.target.style.borderColor = T.orange} onBlur={e => e.target.style.borderColor = T.border} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: T.gray, fontFamily: 'Oswald', letterSpacing: 1, marginBottom: 6 }}>CELL NUMBER <span style={{ color: T.orange }}>*</span></label>
              <input value={form.cell} onChange={set('cell')} placeholder="(555) 555-5555" type="tel" required
                style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', boxSizing: 'border-box', outline: 'none' }}
                onFocus={e => e.target.style.borderColor = T.orange} onBlur={e => e.target.style.borderColor = T.border} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, color: T.gray, fontFamily: 'Oswald', letterSpacing: 1, marginBottom: 6 }}>EMAIL ADDRESS <span style={{ color: T.gray, fontWeight: 400 }}>(optional)</span></label>
            <input value={form.email} onChange={set('email')} placeholder="friend@email.com" type="email"
              style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', boxSizing: 'border-box', outline: 'none' }}
              onFocus={e => e.target.style.borderColor = T.orange} onBlur={e => e.target.style.borderColor = T.border} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 12, color: T.gray, fontFamily: 'Oswald', letterSpacing: 1, marginBottom: 6 }}>ADDRESS <span style={{ color: T.gray, fontWeight: 400 }}>(optional)</span></label>
            <input value={form.address} onChange={set('address')} placeholder="Street, City, State"
              style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', boxSizing: 'border-box', outline: 'none' }}
              onFocus={e => e.target.style.borderColor = T.orange} onBlur={e => e.target.style.borderColor = T.border} />
          </div>
          <Btn type="submit" disabled={submitting}>{submitting ? 'Submitting…' : 'Submit Referral →'}</Btn>
        </form>
      </Card>

      {/* Referral link — secondary */}
      <Card style={{ padding: 24, marginBottom: 24 }}>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 14, textTransform: 'uppercase' }}>Or Share Your Link</h3>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>
          Send this link to a friend. If they sign up using your code, you'll earn a free month automatically.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <code style={{ flex: 1, padding: '10px 14px', background: '#111', borderRadius: 6, fontSize: 12, color: T.orange, border: `1px solid ${T.border}`, minWidth: 200 }}>
            yoga.novaredigital.com/signup?ref={code}
          </code>
          <Btn onClick={copyLink} variant="outline">{copied ? '✓ Copied!' : 'Copy'}</Btn>
        </div>
      </Card>

      {/* Disclaimer */}
      <Card style={{ padding: 20, marginBottom: 32, border: `1px solid ${T.orange}44` }}>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 12, letterSpacing: 2, color: T.orange, marginBottom: 8, textTransform: 'uppercase' }}>How Credits Work</h3>
        <p style={{ fontSize: 13, color: T.grayLight, lineHeight: 1.7 }}>
          You earn one free month for each person you refer who joins as a paying member. A free month comes off your plan the next time you check out. Each person can only be credited once — if two members refer the same person, the first submission on record earns the credit.
        </p>
      </Card>

      {/* Submitted referrals history */}
      {submissions.length > 0 && (
        <>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.grayLight, marginBottom: 14, textTransform: 'uppercase' }}>Your Referrals</h3>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    {['Name','Cell','Email','Status'].map(h => (
                      <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 11, color: T.gray, fontFamily: 'Oswald', letterSpacing: 1 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: T.white }}>{s.referred_name}</td>
                      <td style={{ padding: '10px 16px', fontSize: 13, color: T.grayLight }}>{s.referred_cell}</td>
                      <td style={{ padding: '10px 16px', fontSize: 12, color: T.grayLight }}>{s.referred_email || '—'}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <Badge color={s.credit_awarded ? T.success : T.gray}>
                          {s.credit_awarded ? '✓ Free month earned' : 'Pending'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ── BOOKING PAGE ──────────────────────────────────────────────────────────────
// A time in the Rebuilder's own clock, with the zone named. Older rows without an exact moment show as stored (2026-11-01).
function apptWhen(s) {
  const plain = () => { try { return `${new Date(s.slot_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at ${s.slot_time}`; } catch (e) { return `${s.slot_date} at ${s.slot_time}`; } };
  if (!s.starts_at) return plain();
  try { return new Date(s.starts_at).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }); } catch (e) { return plain(); }
}

function BookingPage() {
  const [slots, setSlots] = useState([]);
  const [myAppts, setMyAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingSlotId, setBookingSlotId] = useState(null);
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(false);

  const load = () => {
    Promise.all([api('/api/appointments/slots'), api('/api/appointments')])
      .then(([s, a]) => { setSlots(s.slots || []); setMyAppts(a.appointments || []); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const confirm = async () => {
    setBooking(true);
    try { await api('/api/appointments', 'POST', { slot_id: bookingSlotId, notes }); setBookingSlotId(null); setNotes(''); load(); }
    catch(err) { alert(err.message); } finally { setBooking(false); }
  };

  // Cancel an appointment (2026-11-01). The slot opens up again and your trainer is told.
  const cancelAppt = async a => {
    if (!window.confirm(`Cancel your appointment with ${a.coach_name} on ${a.slot_date} at ${a.slot_time}?`)) return;
    try { await api(`/api/appointments/${a.id}/cancel`, 'POST'); load(); }
    catch(err) { alert(err.message); }
  };
  const isUpcoming = a => a.status === 'confirmed' && a.slot_date >= new Date().toISOString().slice(0, 10);
  const statusColor = s => s === 'confirmed' ? T.success : s === 'canceled' ? T.danger : T.gold;

  if (loading) return <Spinner />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px clamp(16px, 4vw, 32px) 60px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 60px)', letterSpacing: 2, marginBottom: 8 }}>
          BOOK <span style={gradText}>TRAINING</span>
        </h1>
        <p style={{ color: T.grayLight, fontSize: 16 }}>Schedule a one-on-one session with your trainer.</p>
      </div>

      <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>Available Slots</h3>
      {slots.length === 0 ? (
        <Card style={{ padding: 40, textAlign: 'center', marginBottom: 40 }}>
          <p style={{ color: T.gray, fontSize: 15 }}>Your trainer has no open times right now. Check back soon, or ask them in Messages.</p>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 16, marginBottom: 40 }}>
          {slots.map(s => (
            <Card key={s.id} style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <p style={{ fontWeight: 700, fontSize: 16, color: T.white, marginBottom: 4 }}>
                    {apptWhen(s)}
                  </p>
                  <p style={{ fontSize: 13, color: T.gray }}>Trainer: {s.coach_name} · {s.duration_minutes} min</p>
                </div>
                <Btn size="sm" onClick={() => setBookingSlotId(bookingSlotId === s.id ? null : s.id)}>
                  {bookingSlotId === s.id ? 'Cancel' : 'Book This Slot'}
                </Btn>
              </div>
              {bookingSlotId === s.id && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
                  <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Notes for your coach (optional)</label>
                  <textarea rows={2} placeholder="Goals, questions, focus areas..." value={notes} onChange={e => setNotes(e.target.value)}
                    style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginBottom: 12 }} />
                  <Btn onClick={confirm} disabled={booking}>Confirm Booking</Btn>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {myAppts.length > 0 && (
        <>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>My Appointments</h3>
          <div style={{ display: 'grid', gap: 12 }}>
            {myAppts.map(a => (
              <Card key={a.id} style={{ padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <p style={{ fontWeight: 600, fontSize: 15, color: T.white }}>{a.coach_name}</p>
                  <p style={{ fontSize: 13, color: T.gray }}>{apptWhen(a)}</p>
                  {a.video_link && <p style={{ margin: '6px 0 0' }}><a href={a.video_link} target="_blank" rel="noopener noreferrer" style={{ color: T.orange, fontWeight: 600, fontSize: 14 }}>Join the video call</a></p>}
                  {!a.video_link && a.video_opens_at && <p style={{ fontSize: 12, color: T.gray, marginTop: 4 }}>Your video call link shows here 30 minutes before.</p>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Badge color={statusColor(a.status)}>{a.status}</Badge>
                  {isUpcoming(a) && <Btn size="sm" variant="ghost" onClick={() => cancelAppt(a)}>Cancel appointment</Btn>}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── ADMIN SUB-COMPONENTS ──────────────────────────────────────────────────────
function AdminContests() {
  const [contests, setContests] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', metric_type: 'workouts_completed', start_date: '', end_date: '', prize_description: '', free_months_prize: 0 });
  const [winnerId, setWinnerId] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => api('/api/admin/contests').then(d => setContests(d.contests || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async e => {
    e.preventDefault();
    try { await api('/api/admin/contests', 'POST', form); load(); } catch(err) { alert(err.message); }
  };

  const toggle = async c => {
    await api('/api/admin/contests/' + c.id, 'PUT', { is_active: !c.is_active });
    load();
  };

  const pickWinner = async id => {
    if (!winnerId[id]) return;
    try { await api('/api/admin/contests/' + id + '/winner', 'POST', { user_id: winnerId[id] }); alert('Winner set!'); }
    catch(err) { alert(err.message); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 20, textTransform: 'uppercase' }}>Contests & Challenges</h3>
      <Card style={{ padding: 28, marginBottom: 28 }}>
        <h4 style={{ fontFamily: 'Oswald', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>New Contest</h4>
        <form onSubmit={create}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Title</label>
              <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Contest Title" required
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Metric</label>
              <select value={form.metric_type} onChange={e => setForm(p => ({ ...p, metric_type: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', cursor: 'pointer', boxSizing: 'border-box' }}>
                <option value="workouts_completed">Practices Completed</option>
                <option value="streak_days">Streak Days</option>
                <option value="weight_lost">Weight Lost</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} required
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>End Date</label>
              <input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} required
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Prize Description</label>
              <input value={form.prize_description} onChange={e => setForm(p => ({ ...p, prize_description: e.target.value }))} placeholder="e.g. 1 free month + medal"
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Free Months Prize</label>
              <input type="number" min="0" value={form.free_months_prize} onChange={e => setForm(p => ({ ...p, free_months_prize: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Description</label>
            <textarea rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Contest description..."
              style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>
          <Btn type="submit">Create Contest</Btn>
        </form>
      </Card>
      <div style={{ display: 'grid', gap: 12 }}>
        {contests.map(c => (
          <Card key={c.id} style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <p style={{ fontWeight: 700, fontSize: 15, color: T.white, marginBottom: 4 }}>{c.title}</p>
                <p style={{ fontSize: 12, color: T.gray }}>{c.metric_type} · {c.start_date} → {c.end_date} · {c.entry_count || 0} entries</p>
              </div>
              <button onClick={() => toggle(c)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${c.is_active ? T.success : T.border}`, background: c.is_active ? `${T.success}22` : 'transparent', color: c.is_active ? T.success : T.gray, cursor: 'pointer', fontSize: 12, fontFamily: 'Poppins' }}>
                {c.is_active ? '✓ Active' : 'Inactive'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
              <input placeholder="User ID for winner" value={winnerId[c.id] || ''} onChange={e => setWinnerId(p => ({ ...p, [c.id]: e.target.value }))}
                style={{ padding: '8px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 6, color: T.white, fontSize: 13, fontFamily: 'Poppins', outline: 'none', width: 160 }} />
              <Btn size="sm" variant="outline" onClick={() => pickWinner(c.id)}>Pick Winner 🥇</Btn>
            </div>
          </Card>
        ))}
        {contests.length === 0 && <p style={{ color: T.gray, fontSize: 13 }}>No contests yet.</p>}
      </div>
    </div>
  );
}

function AdminCommunityMod() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api('/api/admin/community').then(d => setPosts(d.posts || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    await api('/api/admin/community/' + id, 'PUT', patch);
    setPosts(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 20, textTransform: 'uppercase' }}>Community Moderation</h3>
      <div style={{ display: 'grid', gap: 10 }}>
        {posts.map(p => (
          <Card key={p.id} style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: 14, color: T.white, marginBottom: 4 }}>{p.author_name}</p>
                <p style={{ fontSize: 13, color: T.grayLight }}>{(p.content || '').substring(0, 80)}{(p.content || '').length > 80 ? '…' : ''}</p>
                <p style={{ fontSize: 11, color: T.gray, marginTop: 4 }}>🔥 {p.fire_count || 0}</p>
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.grayLight, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!p.is_approved} onChange={e => update(p.id, { is_approved: e.target.checked, is_pinned: !!p.is_pinned })} />
                  Approved
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.grayLight, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!p.is_pinned} onChange={e => update(p.id, { is_approved: !!p.is_approved, is_pinned: e.target.checked })} />
                  Pinned
                </label>
              </div>
            </div>
          </Card>
        ))}
        {posts.length === 0 && <p style={{ color: T.gray, fontSize: 14 }}>No posts yet.</p>}
      </div>
    </div>
  );
}

function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api('/api/admin/testimonials').then(d => setTestimonials(d.testimonials || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const approve = async id => {
    await api('/api/admin/testimonials/' + id, 'PUT', { is_approved: 1 });
    setTestimonials(prev => prev.map(t => t.id === id ? { ...t, is_approved: 1 } : t));
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 20, textTransform: 'uppercase' }}>Testimonials</h3>
      <div style={{ display: 'grid', gap: 10 }}>
        {testimonials.map(t => (
          <Card key={t.id} style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, fontSize: 14, color: T.white, marginBottom: 4 }}>{t.name}</p>
                <p style={{ fontSize: 13, color: T.grayLight }}>{(t.content || '').substring(0, 80)}{(t.content || '').length > 80 ? '…' : ''}</p>
                {(t.before_weight || t.after_weight) && (
                  <p style={{ fontSize: 12, color: T.success, marginTop: 4 }}>{t.before_weight}lbs → {t.after_weight}lbs</p>
                )}
              </div>
              <button onClick={() => approve(t.id)} disabled={!!t.is_approved}
                style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${t.is_approved ? T.success : T.orange}`, background: t.is_approved ? `${T.success}22` : `${T.orange}22`, color: t.is_approved ? T.success : T.orange, cursor: t.is_approved ? 'default' : 'pointer', fontSize: 13, fontFamily: 'Poppins' }}>
                {t.is_approved ? '✓ Approved' : 'Approve'}
              </button>
            </div>
          </Card>
        ))}
        {testimonials.length === 0 && <p style={{ color: T.gray, fontSize: 14 }}>No testimonials yet.</p>}
      </div>
    </div>
  );
}

function AdminReferrals() {
  const [referrals, setReferrals]     = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [awarding, setAwarding]       = useState(null);
  const [activeTab, setActiveTab]     = useState('submissions');

  const load = () => {
    Promise.all([
      api('/api/admin/referrals'),
      api('/api/admin/referral-submissions'),
    ]).then(([r, s]) => { setReferrals(r.referrals || []); setSubmissions(s.submissions || []); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const awardCredit = async (id) => {
    setAwarding(id);
    try {
      await api(`/api/admin/referral-submissions/${id}/award`, 'PUT', {});
      load();
    } finally { setAwarding(null); }
  };

  if (loading) return <Spinner />;

  const tabStyle = (active) => ({
    padding: '8px 20px', borderRadius: 6, border: 'none', cursor: 'pointer',
    fontFamily: 'Oswald', fontSize: 13, letterSpacing: 1, fontWeight: 600,
    background: active ? T.orange : 'transparent',
    color: active ? T.white : T.gray,
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button style={tabStyle(activeTab === 'submissions')} onClick={() => setActiveTab('submissions')}>
          WARM REFERRALS {submissions.length > 0 && `(${submissions.length})`}
        </button>
        <button style={tabStyle(activeTab === 'code')} onClick={() => setActiveTab('code')}>
          CODE SIGNUPS {referrals.length > 0 && `(${referrals.length})`}
        </button>
      </div>

      {activeTab === 'submissions' && (
        <>
          <p style={{ fontSize: 13, color: T.gray, marginBottom: 16 }}>Members who submitted a friend's info directly. Award credit once the friend joins.</p>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                    {['Submitted By','Friend Name','Cell','Email','Address','Status','Credit'].map(h => (
                      <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, color: T.gray, fontFamily: 'Oswald', letterSpacing: 1, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: T.white, whiteSpace: 'nowrap' }}>{s.referrer_name}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: T.white }}>{s.referred_name}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: T.grayLight, whiteSpace: 'nowrap' }}>{s.referred_cell}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: T.grayLight }}>{s.referred_email || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: T.grayLight, maxWidth: 160 }}>{s.referred_address || '—'}</td>
                      <td style={{ padding: '10px 14px' }}><Badge color={s.credit_awarded ? T.success : T.gray}>{s.credit_awarded ? 'Credited' : s.status}</Badge></td>
                      <td style={{ padding: '10px 14px' }}>
                        {s.credit_awarded
                          ? <span style={{ fontSize: 13, color: T.success }}>✓ Done</span>
                          : <button onClick={() => awardCredit(s.id)} disabled={awarding === s.id}
                              style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${T.orange}`, background: `${T.orange}22`, color: T.orange, cursor: 'pointer', fontSize: 12, fontFamily: 'Poppins' }}>
                              {awarding === s.id ? '…' : 'Award Credit'}
                            </button>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {submissions.length === 0 && <p style={{ color: T.gray, fontSize: 14, padding: 24 }}>No warm referrals submitted yet.</p>}
            </div>
          </Card>
        </>
      )}

      {activeTab === 'code' && (
        <>
          <p style={{ fontSize: 13, color: T.gray, marginBottom: 16 }}>Members who signed up using a referral code.</p>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  {['Referrer','Referred','Status','Free Month'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: T.gray, fontFamily: 'Oswald', letterSpacing: 1 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {referrals.map((r, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: T.white }}>{r.referrer_name}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: T.grayLight }}>{r.referred_email}</td>
                    <td style={{ padding: '10px 16px' }}><Badge color={r.status === 'completed' ? T.success : T.gray}>{r.status}</Badge></td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: r.free_month_applied ? T.success : T.gray }}>{r.free_month_applied ? '✓ Yes' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {referrals.length === 0 && <p style={{ color: T.gray, fontSize: 14, padding: 24 }}>No code-based referrals yet.</p>}
          </Card>
        </>
      )}
    </div>
  );
}

function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [slotForm, setSlotForm] = useState({ coach_id: '', slot_date: '', slot_time: '', duration_minutes: 60 });
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([api('/api/admin/appointments'), api('/api/admin/coaches')])
      .then(([a, c]) => { setAppointments(a.appointments || []); setCoaches(c.coaches || []); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const cancelAppt = async a => {
    if (!window.confirm(`Cancel ${a.client_name}'s appointment with ${a.coach_name} on ${a.slot_date} at ${a.slot_time}? Both of them are told, and the slot opens up again.`)) return;
    try { await api(`/api/appointments/${a.id}/cancel`, 'POST'); load(); }
    catch(err) { alert(err.message); }
  };

  const addSlot = async e => {
    e.preventDefault(); setAdding(true);
    try { await api('/api/admin/appointment-slots', 'POST', slotForm); setSlotForm({ coach_id: '', slot_date: '', slot_time: '', duration_minutes: 60 }); alert('Slot added!'); }
    catch(err) { alert(err.message); } finally { setAdding(false); }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 20, textTransform: 'uppercase' }}>Appointments</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        <div>
          <h4 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, marginBottom: 12, color: T.grayLight }}>Upcoming Appointments</h4>
          <div style={{ display: 'grid', gap: 10 }}>
            {appointments.map(a => (
              <Card key={a.id} style={{ padding: '14px 18px' }}>
                <p style={{ fontWeight: 600, fontSize: 14, color: T.white, marginBottom: 4 }}>{a.client_name}</p>
                <p style={{ fontSize: 12, color: T.gray }}>Coach: {a.coach_name} · {a.slot_date} {a.slot_time}</p>
                <Badge color={a.status === 'confirmed' ? T.success : a.status === 'canceled' ? T.danger : T.gold}>{a.status}</Badge>
                {a.status === 'confirmed' && a.slot_date >= new Date().toISOString().slice(0, 10) && <Btn size="sm" variant="ghost" style={{ marginLeft: 8 }} onClick={() => cancelAppt(a)}>Cancel appointment</Btn>}
              </Card>
            ))}
            {appointments.length === 0 && <p style={{ color: T.gray, fontSize: 13 }}>No appointments scheduled.</p>}
          </div>
        </div>
        <Card style={{ padding: 24 }}>
          <h4 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, marginBottom: 16, color: T.grayLight }}>Add Slot</h4>
          <form onSubmit={addSlot}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Coach</label>
              <select value={slotForm.coach_id} onChange={e => setSlotForm(p => ({ ...p, coach_id: e.target.value }))} required
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', cursor: 'pointer', boxSizing: 'border-box' }}>
                <option value="">Select coach…</option>
                {/* Bookings join coach_id to users.id, so a slot stores the trainer's login id (2026-11-01). */}
                {coaches.filter(c => c.active !== 0).map(c => <option key={c.id} value={c.user_id} style={{ background: '#F5F7FA' }}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Date</label>
              <input type="date" value={slotForm.slot_date} onChange={e => setSlotForm(p => ({ ...p, slot_date: e.target.value }))} required
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Time</label>
              <input type="time" value={slotForm.slot_time} onChange={e => setSlotForm(p => ({ ...p, slot_time: e.target.value }))} required
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Duration (minutes)</label>
              <input type="number" value={slotForm.duration_minutes} onChange={e => setSlotForm(p => ({ ...p, duration_minutes: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <Btn type="submit" disabled={adding} fullWidth>Add Slot</Btn>
          </form>
        </Card>
      </div>
    </div>
  );
}

function AdminCredits() {
  const [form, setForm] = useState({ email: '', months: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async e => {
    e.preventDefault(); setSubmitting(true);
    try { const r = await api('/api/admin/credits', 'POST', form); setSuccess(`${r.name} now has ${r.free_months} free month${r.free_months === 1 ? '' : 's'}.`); setTimeout(() => setSuccess(false), 6000); setForm({ email: '', months: 1 }); }
    catch(err) { alert(err.message); } finally { setSubmitting(false); }
  };

  return (
    <div style={{ maxWidth: 500 }}>
      <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 20, textTransform: 'uppercase' }}>Award Free Months</h3>
      <Card style={{ padding: 28 }}>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Rebuilder's email</label>
            <input type="email" placeholder="member@email.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required
              style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6 }}>Free Months to Award</label>
            <input type="number" min="1" max="12" value={form.months} onChange={e => setForm(p => ({ ...p, months: parseInt(e.target.value) || 1 }))} required
              style={{ width: '100%', padding: '11px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <Btn type="submit" disabled={submitting} fullWidth>Award Free Month{form.months !== 1 ? 's' : ''}</Btn>
          {success && <p style={{ color: T.success, fontSize: 13, marginTop: 10, textAlign: 'center' }}>✓ {success}</p>}
        </form>
      </Card>
    </div>
  );
}

function AdminAICoach() {
  const [question, setQuestion] = useState('');
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [configured, setConfigured] = useState(null);
  const bottomRef = useRef(null);

  const QUICK = [
    "Who hasn't logged a workout this week?",
    "Which Rebuilders are making the most progress?",
    "Who should I check in on today?",
    "Who is closest to their goal weight?",
    "Who has been most consistent this month?",
    "Give me a full status on all my Rebuilders.",
  ];

  useEffect(() => {
    api('/api/admin/users').then(d => setClients(d.users || [])).catch(() => {});
    api('/api/ai/coach/status').then(d => setConfigured(d.configured)).catch(() => setConfigured(false));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const ask = async (q) => {
    const text = (q || question).trim();
    if (!text || loading) return;
    const entry = { role: 'user', content: text, client: clientId ? clients.find(c => c.id === clientId)?.name : 'All Rebuilders' };
    setHistory(h => [...h, entry]);
    setQuestion('');
    setLoading(true);
    try {
      const d = await api('/api/ai/coach', 'POST', { question: text, client_id: clientId || undefined });
      setHistory(h => [...h, { role: 'ai', content: d.answer, tokens: d.tokens_used }]);
    } catch(err) {
      setHistory(h => [...h, { role: 'error', content: err.message }]);
    } finally {
      setLoading(false);
    }
  };

  if (configured === false) {
    return (
      <div style={{ maxWidth: 600 }}>
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🤖</div>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 20, letterSpacing: 1, marginBottom: 12 }}>AI Coach Not Configured</h3>
          <p style={{ color: T.gray, fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>
            To enable the AI coach, add your Anthropic API key in the <strong style={{ color: T.orange }}>Settings</strong> tab.
            Get a key at console.anthropic.com — it uses Claude Opus 5 and costs roughly $0.01–0.05 per query.
          </p>
          <p style={{ color: T.gray, fontSize: 13 }}>Once the key is saved, this tab becomes your AI-powered coaching assistant.</p>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 920 }}>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 6, textTransform: 'uppercase' }}>🤖 AI Coach Assistant</h3>
        <p style={{ color: T.gray, fontSize: 13, lineHeight: 1.6 }}>Ask anything about your Rebuilders. The AI sees all workout logs, progress check-ins, sleep data, measurements, and appointments.</p>
      </div>

      <Card style={{ padding: 16, marginBottom: 14 }}>
        <label style={{ fontSize: 11, color: T.gray, fontFamily: 'Poppins', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Focus on a specific Rebuilder (optional)</label>
        <select value={clientId} onChange={e => setClientId(e.target.value)} style={{ width: '100%', padding: '10px 14px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: clientId ? T.white : T.gray, fontSize: 14, fontFamily: 'Poppins', cursor: 'pointer', outline: 'none' }}>
          <option value="">All Rebuilders — overview mode</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Card>

      <div style={{ marginBottom: 16 }}>
        <p style={{ fontSize: 11, color: T.gray, fontFamily: 'Poppins', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>Quick questions</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {QUICK.map((q, i) => (
            <button key={i} onClick={() => ask(q)} disabled={loading} style={{
              padding: '6px 14px', borderRadius: 20, border: `1px solid ${T.border}`, background: 'transparent',
              color: T.grayLight, fontSize: 12, fontFamily: 'Poppins', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
            }}
              onMouseOver={e => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.grayLight; }}
            >{q}</button>
          ))}
        </div>
      </div>

      {history.length > 0 && (
        <Card style={{ padding: 0, marginBottom: 14, maxHeight: 520, overflowY: 'auto' }}>
          {history.map((msg, i) => (
            <div key={i} style={{ padding: '18px 24px', borderBottom: i < history.length - 1 ? `1px solid ${T.border}` : 'none', background: msg.role === 'user' ? T.glass : 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <span style={{ fontSize: 22, flexShrink: 0 }}>{msg.role === 'user' ? '👨‍🏫' : msg.role === 'error' ? '⚠️' : '🤖'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <p style={{ fontSize: 11, color: msg.role === 'user' ? T.orange : msg.role === 'error' ? T.danger : T.gold, fontFamily: 'Oswald', letterSpacing: 1, textTransform: 'uppercase' }}>
                      {msg.role === 'user' ? `Instructor — ${msg.client}` : msg.role === 'error' ? 'Error' : 'AI Coach'}
                    </p>
                    {msg.tokens && <span style={{ fontSize: 11, color: T.gray }}>{msg.tokens.toLocaleString()} tokens</span>}
                  </div>
                  <p style={{ color: msg.role === 'error' ? T.danger : T.white, fontSize: 14, fontFamily: 'Poppins', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>{msg.content}</p>
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 22 }}>🤖</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: T.orange, opacity: 0.5, animation: `pulse 1.2s ${i*0.2}s ease-in-out infinite` }} />)}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </Card>
      )}

      <Card style={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <input value={question} onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
            placeholder={clientId ? `Ask about ${clients.find(c => c.id === clientId)?.name || 'this Rebuilder'}…` : 'Ask about all your Rebuilders…'}
            disabled={loading}
            style={{ flex: 1, padding: '12px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none' }}
            onFocus={e => e.target.style.borderColor = T.orange}
            onBlur={e => e.target.style.borderColor = T.border}
          />
          <Btn onClick={() => ask()} disabled={loading || !question.trim()}>
            {loading ? '…' : 'Ask →'}
          </Btn>
        </div>
        {history.length > 0 && (
          <button onClick={() => setHistory([])} style={{ background: 'none', border: 'none', color: T.gray, fontSize: 12, fontFamily: 'Poppins', cursor: 'pointer', marginTop: 8 }}>Clear conversation</button>
        )}
      </Card>
      <style>{`@keyframes pulse { 0%,100%{opacity:0.3;transform:scale(0.8)} 50%{opacity:1;transform:scale(1)} }`}</style>
    </div>
  );
}

// ── MEMBER AI COACH ───────────────────────────────────────────────────────────
function MemberAICoach() {
  const { user } = useAuth();
  const [question, setQuestion]   = useState('');
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [usage, setUsage]         = useState(null);
  const [error, setError]         = useState('');
  const [limitHit, setLimitHit]   = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api('/api/ai/member-coach/usage').then(d => setUsage(d)).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, loading]);

  async function ask() {
    if (!question.trim() || loading) return;
    const q = question.trim();
    setQuestion('');
    setError('');
    setHistory(h => [...h, { role: 'user', content: q }]);
    setLoading(true);
    try {
      const convHistory = history.slice(-6);
      const d = await api('/api/ai/member-coach', 'POST', { question: q, history: convHistory });
      setHistory(h => [...h, { role: 'assistant', content: d.answer }]);
      setUsage(prev => prev ? { ...prev, used: d.usage.used, remaining: d.usage.remaining } : prev);
    } catch(err) {
      if (err.message?.includes('monthly_limit_reached') || err.message?.includes('429')) {
        setLimitHit(true);
        setHistory(h => h.slice(0, -1));
      } else if (err.message?.includes('rate_limited')) {
        setHistory(h => [...h, { role: 'error', content: 'Slow down — max 5 questions per hour. Try again shortly.' }]);
      } else if (err.info?.needsAiConsent || err.info?.guardianConsentNeeded) {
        setHistory(h => [...h, { role: 'error', content: err.message }]);
      } else if (err.message?.includes('account_flagged')) {
        setHistory(h => [...h, { role: 'error', content: 'Your account has been flagged for unusual activity. Please contact support.' }]);
      } else {
        setHistory(h => [...h, { role: 'error', content: 'Something went wrong. Please try again.' }]);
      }
      setError(err.message || '');
    } finally {
      setLoading(false);
    }
  }

  const usagePct = usage ? Math.min(100, (usage.used / usage.limit) * 100) : 0;
  const usageColor = usagePct >= 100 ? T.error : usagePct >= 80 ? T.gold : T.success;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 20px 60px', maxWidth: 860, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(32px, 5vw, 48px)', letterSpacing: 2, marginBottom: 6 }}>
          🤖 AI <span style={gradText}>COACH</span>
        </h1>
        <p style={{ color: T.gray, fontSize: 15 }}>
          Ask anything about your training, nutrition, or recovery. Personalized to your program and data.
        </p>
      </div>

      {/* Usage bar */}
      {usage && (
        <Card style={{ padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 13, color: T.gray }}>
              {usage.used} of {usage.limit} AI coach questions used this month
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: usageColor }}>
              {usage.remaining} remaining
            </span>
          </div>
          <div style={{ height: 6, background: T.border, borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${usagePct}%`, background: usageColor, borderRadius: 3, transition: 'width 0.4s ease' }} />
          </div>
        </Card>
      )}

      {/* Limit wall */}
      {limitHit && (
        <Card style={{ padding: 32, textAlign: 'center', marginBottom: 20, border: `1px solid ${T.orange}` }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 22, marginBottom: 8 }}>Monthly Limit Reached</h3>
          <p style={{ color: T.gray, fontSize: 15, marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>
            You've used all {usage?.limit} AI coach questions for this month. They reset on the 1st. Your trainer can answer anything in Messages.
          </p>
          <Link to="/messages" style={{ display: 'inline-block', padding: '12px 24px', borderRadius: 8, background: T.orange, color: '#fff', fontWeight: 600, textDecoration: 'none' }}>Message my trainer →</Link>
        </Card>
      )}

      {/* Conversation */}
      {history.length > 0 && (
        <Card style={{ padding: 20, marginBottom: 16, maxHeight: 520, overflowY: 'auto' }}>
          {history.map((msg, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, marginBottom: 20,
              flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
            }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                background: msg.role === 'user' ? T.orange : msg.role === 'error' ? T.error : '#2a2a2a',
              }}>
                {msg.role === 'user' ? '👤' : msg.role === 'error' ? '⚠️' : '🤖'}
              </div>
              <div style={{
                maxWidth: '78%', padding: '12px 16px', borderRadius: 14, fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-wrap',
                background: msg.role === 'user' ? `${T.orange}22` : '#1e1e1e',
                color: msg.role === 'error' ? T.error : T.white,
                borderBottomRightRadius: msg.role === 'user' ? 4 : 14,
                borderBottomLeftRadius: msg.role === 'user' ? 14 : 4,
              }}>
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🤖</div>
              <div style={{ padding: '14px 18px', borderRadius: 14, background: '#1e1e1e', borderBottomLeftRadius: 4 }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: T.orange, opacity: 0.5, animation: `pulse 1.2s ${i*0.2}s ease-in-out infinite` }} />)}
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </Card>
      )}

      {/* Empty state */}
      {history.length === 0 && !limitHit && (
        <Card style={{ padding: 32, textAlign: 'center', marginBottom: 16, opacity: 0.85 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
          <p style={{ color: T.gray, fontSize: 15, lineHeight: 1.7, maxWidth: 420, margin: '0 auto' }}>
            Ask me anything about your training, nutrition, or recovery. I know your program, your recent workouts, and your goals.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 20 }}>
            {[
              'Should I work out today?',
              'What should I eat before training?',
              'Why are my legs still sore?',
              'How do I improve my squat?',
            ].map(s => (
              <button key={s} onClick={() => setQuestion(s)} style={{ padding: '8px 14px', borderRadius: 20, border: `1px solid ${T.border}`, background: 'transparent', color: T.gray, fontSize: 13, fontFamily: 'Poppins', cursor: 'pointer' }}>
                {s}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Input */}
      {!limitHit && (
        <Card style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <input
              value={question} onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder="Ask your AI coach anything…"
              disabled={loading}
              style={{ flex: 1, padding: '12px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none' }}
              onFocus={e => e.target.style.borderColor = T.orange}
              onBlur={e => e.target.style.borderColor = T.border}
            />
            <Btn onClick={ask} disabled={loading || !question.trim()}>
              {loading ? '…' : 'Ask →'}
            </Btn>
          </div>
          {history.length > 0 && (
            <button onClick={() => { setHistory([]); setLimitHit(false); }} style={{ background: 'none', border: 'none', color: T.gray, fontSize: 12, fontFamily: 'Poppins', cursor: 'pointer', marginTop: 8 }}>
              Clear conversation
            </button>
          )}
          <p style={{ fontSize: 11, color: T.gray, marginTop: 8, opacity: 0.6 }}>
            AI Coach uses your real workout data and program. Not a substitute for medical advice.
          </p>
        </Card>
      )}

      <style>{`@keyframes pulse { 0%,100%{opacity:0.3;transform:scale(0.8)} 50%{opacity:1;transform:scale(1)} }`}</style>
    </div>
  );
}

// ── MEMBER PORTAL ─────────────────────────────────────────────────────────────
function resizeAndEncode(file, maxW, maxH, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxW) { h = Math.round(h * maxW / w); w = maxW; }
        if (h > maxH) { w = Math.round(w * maxH / h); h = maxH; }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function MemberPortal() {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [tab, setTab]                 = useState('overview');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadingProgress, setUploadingProgress] = useState(false);
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoDate, setPhotoDate]     = useState(new Date().toISOString().slice(0, 10));
  const [viewPhoto, setViewPhoto]     = useState(null);
  const [photoError, setPhotoError]   = useState('');

  const [pushStatus, setPushStatus] = React.useState('loading');
  const [pushLoading, setPushLoading] = React.useState(false);

  const load = () => {
    api('/api/member/portal').then(d => {
      setData(d);
      if (d.user?.profilePhoto) setProfilePhoto(d.user.profilePhoto);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (window.HB_NATIVE && window.HB_NATIVE.push) { setPushStatus('native'); return; }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) { setPushStatus('unsupported'); return; }
    navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/sw.js`).then(reg => {
      if (Notification.permission === 'denied') { setPushStatus('denied'); return; }
      reg.pushManager.getSubscription().then(sub => setPushStatus(sub ? 'subscribed' : 'unsubscribed'));
    }).catch(() => setPushStatus('unsupported'));
  }, []);

  const togglePush = async () => {
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (pushStatus === 'subscribed') {
        const sub = await reg.pushManager.getSubscription();
        if (sub) { await sub.unsubscribe(); await api('/api/push/unsubscribe', 'POST', { endpoint: sub.endpoint }); }
        setPushStatus('unsubscribed');
      } else {
        const { publicKey } = await api('/api/push/vapid-public-key', 'GET');
        if (!publicKey) { alert('Push notifications are not configured yet. Check back later.'); return; }
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
        const j = sub.toJSON();
        await api('/api/push/subscribe', 'POST', { endpoint: j.endpoint, keys: j.keys });
        setPushStatus('subscribed');
      }
    } catch(e) {
      if (Notification.permission === 'denied') setPushStatus('denied');
    } finally { setPushLoading(false); }
  };

  const handleProfilePhoto = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingProfile(true);
    setPhotoError('');
    try {
      const encoded = await resizeAndEncode(file, 400, 400, 0.8);
      await api('/api/member/profile-photo', 'POST', { photoData: encoded });
      setProfilePhoto(encoded);
    } catch(err) { setPhotoError('Upload failed — try a smaller image.'); }
    finally { setUploadingProfile(false); }
  };

  const handleProgressPhoto = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingProgress(true);
    setPhotoError('');
    try {
      const encoded = await resizeAndEncode(file, 900, 900, 0.8);
      await api('/api/member/progress-photos', 'POST', { photoData: encoded, caption: photoCaption, photoDate });
      setPhotoCaption(''); load();
    } catch(err) { setPhotoError('Upload failed — try a smaller image.'); }
    finally { setUploadingProgress(false); }
  };

  const deleteProgressPhoto = async id => {
    if (!window.confirm('Delete this photo?')) return;
    await api(`/api/member/progress-photos/${id}`, 'DELETE');
    load();
  };

  const fmtDate = s => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  if (loading) return <Spinner />;
  if (!data) return <div style={{ padding: '100px 32px', color: T.gray }}>Could not load portal.</div>;

  const { user, workouts, measurements, aiCoach, wellness, nutrition, badges, credits, referrals, progressPhotos, coachMessages = [], cookContent = [] } = data;
  const unreadCount = coachMessages.filter(m => !m.read_at).length;

  const tabs = [
    { key: 'overview',  label: 'Overview' },
    { key: 'progress',  label: 'Progress & Photos' },
    { key: 'activity',  label: 'Activity' },
    { key: 'messages',  label: `Messages${unreadCount > 0 ? ` (${unreadCount})` : ''}` },
    { key: 'wellness',  label: 'Wellness' },
    { key: 'account',   label: 'Account' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, paddingTop: 72 }}>
      {/* Hero header */}
      <div style={{ background: 'linear-gradient(180deg, #111 0%, #0a0a0a 100%)', borderBottom: `1px solid ${T.border}`, padding: '32px 32px 0' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginBottom: 24 }}>
            {/* Profile photo */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: 100, height: 100, borderRadius: '50%', border: `3px solid ${T.orange}`, overflow: 'hidden', background: '#F5F7FA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {profilePhoto
                  ? <img src={profilePhoto} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: 40, color: T.gray }}>👤</span>
                }
              </div>
              <label style={{ position: 'absolute', bottom: 2, right: 2, width: 26, height: 26, borderRadius: '50%', background: T.orange, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, border: '2px solid #0a0a0a' }} title="Change profile photo">
                {uploadingProfile ? '⏳' : '📷'}
                <input type="file" accept="image/*" onChange={handleProfilePhoto} style={{ display: 'none' }} />
              </label>
            </div>
            {/* Name & tier */}
            <div style={{ flex: 1, paddingBottom: 8 }}>
              <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(28px, 4vw, 44px)', letterSpacing: 2, marginBottom: 4 }}>{user.name}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {user.planName && <Badge color={T.orange}>{user.planName}</Badge>}
                <span style={{ fontSize: 12, color: T.gray }}>Member since {fmtDate(user.memberSince)}</span>
                {user.goal && <span style={{ fontSize: 12, color: T.grayLight }}>· Goal: {user.goal}</span>}
              </div>
            </div>
            {/* Quick stats */}
            <div style={{ display: 'flex', gap: 20, paddingBottom: 8 }}>
              {[
                { label: 'Streak', val: `${workouts.streak} days` },
                { label: 'This Month', val: `${workouts.thisMonth} workouts` },
                { label: 'AI Sessions', val: `${aiCoach.used}/${aiCoach.limit}` },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center' }}>
                  <p style={{ fontFamily: 'Bebas Neue', fontSize: 22, color: T.orange, letterSpacing: 1, lineHeight: 1 }}>{s.val}</p>
                  <p style={{ fontSize: 11, color: T.gray, marginTop: 4 }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{ padding: '10px 20px', background: 'none', border: 'none', borderBottom: tab === t.key ? `2px solid ${T.orange}` : '2px solid transparent', color: tab === t.key ? T.white : T.gray, fontFamily: 'Oswald', fontSize: 13, letterSpacing: 1, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 32px 60px' }}>
        {photoError && <div style={{ background: `${T.danger}15`, border: `1px solid ${T.danger}44`, borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: T.danger }}>{photoError}</div>}

        {/* ── OVERVIEW ── */}
        {tab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18 }}>
            {/* Profile card */}
            <Card style={{ padding: 24, gridColumn: '1 / -1' }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>My Profile</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                {[
                  { label: 'Goal', val: user.goal || '—' },
                  { label: 'Activity Level', val: user.activity_level || '—' },
                  { label: 'Age', val: user.age || '—' },
                  { label: 'Starting Weight', val: user.weight ? `${user.weight} lbs` : '—' },
                  { label: 'Goal Weight', val: user.goal_weight ? `${user.goal_weight} lbs` : '—' },
                  { label: 'Height', val: user.height_feet ? `${user.height_feet}′${user.height_inches || 0}″` : '—' },
                  { label: 'Diet Preference', val: user.dietary_preferences || '—' },
                  { label: 'Email', val: user.email },
                ].map(f => (
                  <div key={f.label}>
                    <p style={{ fontSize: 11, color: T.gray, marginBottom: 2, fontFamily: 'Oswald', letterSpacing: 1 }}>{f.label.toUpperCase()}</p>
                    <p style={{ fontSize: 13, color: T.white, fontWeight: 500 }}>{f.val}</p>
                  </div>
                ))}
              </div>
              {user.health_notes && (
                <div style={{ marginTop: 16, padding: '12px 16px', background: '#111', borderRadius: 8, borderLeft: `3px solid ${T.orange}` }}>
                  <p style={{ fontSize: 11, color: T.gray, marginBottom: 4 }}>HEALTH NOTES</p>
                  <p style={{ fontSize: 13, color: T.grayLight }}>{user.health_notes}</p>
                </div>
              )}
            </Card>

            {/* Today snapshot */}
            <Card style={{ padding: 24 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Today</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 13, color: T.grayLight }}>💧 Water</span>
                  <span style={{ fontSize: 14, color: T.white, fontWeight: 600 }}>{Math.round((wellness.waterTodayMl || 0) / 237)} cups</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 13, color: T.grayLight }}>🔥 Calories</span>
                  <span style={{ fontSize: 14, color: T.white, fontWeight: 600 }}>{Math.round(nutrition.today?.cal || 0)} kcal</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: 13, color: T.grayLight }}>🥩 Protein</span>
                  <span style={{ fontSize: 14, color: T.white, fontWeight: 600 }}>{Math.round(nutrition.today?.prot || 0)}g</span>
                </div>
                {wellness.sleep[0] && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: T.grayLight }}>😴 Last Sleep</span>
                    <span style={{ fontSize: 14, color: T.white, fontWeight: 600 }}>{wellness.sleep[0].hours_slept}h</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Workout stats */}
            <Card style={{ padding: 24 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Practice Stats</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { label: 'This Week', val: workouts.thisWeek },
                  { label: 'This Month', val: workouts.thisMonth },
                  { label: 'Total Logged', val: workouts.total },
                  { label: 'Longest Streak', val: `${workouts.longestStreak}d` },
                ].map(s => (
                  <div key={s.label} style={{ background: '#111', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                    <p style={{ fontFamily: 'Bebas Neue', fontSize: 28, color: T.orange, letterSpacing: 1 }}>{s.val}</p>
                    <p style={{ fontSize: 11, color: T.gray }}>{s.label}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Current measurements */}
            {measurements.latest && (
              <Card style={{ padding: 24 }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Current Measurements</h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {[
                    ['Weight', measurements.latest.weight_lbs, 'lbs'],
                    ['Waist', measurements.latest.waist_in, '″'],
                    ['Hips', measurements.latest.hips_in, '″'],
                    ['Chest', measurements.latest.chest_in, '″'],
                    ['Arms', measurements.latest.arms_in, '″'],
                  ].filter(r => r[1]).map(([label, val, unit]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 13, color: T.grayLight }}>{label}</span>
                      <span style={{ fontSize: 13, color: T.white, fontWeight: 600 }}>{val}{unit}</span>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: T.gray, marginTop: 10 }}>Logged {fmtDate(measurements.latest.date)}</p>
              </Card>
            )}

            {/* Badges */}
            {badges.length > 0 && (
              <Card style={{ padding: 24 }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Badges Earned</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {badges.map(b => (
                    <div key={b.id} title={fmtDate(b.earned_at)} style={{ padding: '6px 14px', borderRadius: 20, background: `${T.gold}18`, border: `1px solid ${T.gold}44`, fontSize: 13, color: T.gold }}>
                      🏅 {b.badge_label}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Recent activity */}
            {workouts.recent?.length > 0 && (
              <Card style={{ padding: 24, gridColumn: badges.length > 0 ? '' : '1 / -1' }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Recent Practices</h3>
                <div style={{ display: 'grid', gap: 8 }}>
                  {workouts.recent.slice(0, 5).map((w, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                      <div>
                        <p style={{ fontSize: 13, color: T.white }}>{w.workout_type || 'Practice'}</p>
                        <p style={{ fontSize: 11, color: T.gray }}>{fmtDate(w.logged_at || w.date)}</p>
                      </div>
                      {w.duration_minutes && <span style={{ fontSize: 12, color: T.grayLight }}>{w.duration_minutes} min</span>}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ── PROGRESS & PHOTOS ── */}
        {tab === 'progress' && (
          <div>
            {/* Measurement history */}
            {measurements.history.length > 0 && (
              <Card style={{ padding: 24, marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Weight & Measurement History</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                        {['Date', 'Weight', 'Waist', 'Hips', 'Chest', 'Arms'].map(h => (
                          <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: T.gray, fontFamily: 'Oswald', letterSpacing: 1 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {measurements.history.map((m, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                          <td style={{ padding: '8px 12px', fontSize: 12, color: T.grayLight }}>{m.date}</td>
                          <td style={{ padding: '8px 12px', fontSize: 13, color: T.white, fontWeight: 600 }}>{m.weight_lbs ? `${m.weight_lbs} lbs` : '—'}</td>
                          {['waist_in','hips_in','chest_in','arms_in'].map(k => (
                            <td key={k} style={{ padding: '8px 12px', fontSize: 12, color: T.grayLight }}>{m[k] ? `${m[k]}″` : '—'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Upload progress photo */}
            <Card style={{ padding: 24, marginBottom: 24 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Add Progress Photo</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: T.gray, marginBottom: 6 }}>Date</label>
                  <input type="date" value={photoDate} onChange={e => setPhotoDate(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: T.gray, marginBottom: 6 }}>Caption (optional)</label>
                  <input value={photoCaption} onChange={e => setPhotoCaption(e.target.value)} placeholder="e.g. Week 4 check-in"
                    style={{ width: '100%', padding: '10px 12px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 13, fontFamily: 'Poppins', boxSizing: 'border-box' }} />
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: T.orange, borderRadius: 8, cursor: 'pointer', fontSize: 13, color: T.white, fontFamily: 'Poppins', whiteSpace: 'nowrap', marginBottom: 1 }}>
                  {uploadingProgress ? '⏳ Uploading…' : '📷 Choose Photo'}
                  <input type="file" accept="image/*" onChange={handleProgressPhoto} style={{ display: 'none' }} disabled={uploadingProgress} />
                </label>
              </div>
            </Card>

            {/* Progress photo gallery */}
            {progressPhotos.length === 0
              ? <Card style={{ padding: 32, textAlign: 'center' }}><p style={{ color: T.gray, fontSize: 14 }}>No progress photos yet. Add your first one above to track your transformation.</p></Card>
              : (
                <>
                  <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>Progress Photos ({progressPhotos.length})</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
                    {progressPhotos.map(p => (
                      <div key={p.id} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}`, background: '#111', cursor: 'pointer' }} onClick={() => setViewPhoto(p)}>
                        <div style={{ paddingTop: '100%', position: 'relative', background: '#222' }}>
                          <div style={{ position: 'absolute', inset: 0 }}>
                            {p.photo_data
                              ? <img src={p.photo_data} alt="Progress" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: T.gray }}>📷</div>
                            }
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(0deg,rgba(0,0,0,0.85) 0%,transparent 100%)', padding: '28px 10px 8px' }}>
                              <p style={{ fontSize: 11, color: T.white }}>{p.photo_date || fmtDate(p.created_at)}</p>
                              {p.caption && <p style={{ fontSize: 10, color: T.gray }}>{p.caption}</p>}
                            </div>
                          </div>
                        </div>
                        <button onClick={e => { e.stopPropagation(); deleteProgressPhoto(p.id); }}
                          style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', color: T.danger, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                      </div>
                    ))}
                  </div>
                </>
              )
            }
          </div>
        )}

        {/* ── ACTIVITY ── */}
        {tab === 'activity' && (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${T.border}` }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2 }}>Workout Log — All Time ({workouts.total})</h3>
            </div>
            {workouts.recent.length === 0
              ? <p style={{ padding: 32, color: T.gray, textAlign: 'center' }}>No workouts logged yet. Start tracking from the Workout Log page.</p>
              : workouts.recent.map((w, i) => (
                <div key={i} style={{ padding: '14px 24px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: 14, color: T.white, fontWeight: 500 }}>{w.workout_type || 'Practice'}</p>
                    <p style={{ fontSize: 12, color: T.gray, marginTop: 3 }}>{fmtDate(w.logged_at || w.date)}</p>
                    {w.notes && <p style={{ fontSize: 12, color: T.grayLight, marginTop: 4, maxWidth: 400 }}>{w.notes}</p>}
                  </div>
                  {w.duration_minutes && (
                    <span style={{ fontSize: 13, color: T.grayLight, flexShrink: 0, marginLeft: 16 }}>{w.duration_minutes} min</span>
                  )}
                </div>
              ))
            }
          </Card>
        )}

        {/* ── MESSAGES ── */}
        {tab === 'messages' && (
          <div>
            {/* Coach Messages */}
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>Messages from Your Coach</h3>
              {coachMessages.length === 0
                ? <Card style={{ padding: 24 }}><p style={{ color: T.gray, fontSize: 14 }}>No messages from your coach yet.</p></Card>
                : coachMessages.map(m => (
                  <Card key={m.id} style={{ padding: '18px 22px', marginBottom: 12, borderLeft: m.read_at ? `3px solid ${T.border}` : `3px solid ${T.orange}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: T.white }}>{m.coach_name || 'Your Coach'}</span>
                        {!m.read_at && <Badge color={T.orange}>New</Badge>}
                      </div>
                      <span style={{ fontSize: 11, color: T.gray }}>{new Date(m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    {m.subject && <p style={{ fontSize: 13, fontWeight: 600, color: T.grayLight, marginBottom: 6 }}>{m.subject}</p>}
                    <p style={{ fontSize: 14, color: T.white, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{m.message}</p>
                  </Card>
                ))
              }
            </div>

            {/* Cook Content */}
            <div>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 14, letterSpacing: 2, color: T.grayLight, marginBottom: 16, textTransform: 'uppercase' }}>Recipes & Content from Your Cook</h3>
              {cookContent.length === 0
                ? <Card style={{ padding: 24 }}><p style={{ color: T.gray, fontSize: 14 }}>No cook content yet — check back soon.</p></Card>
                : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                    {cookContent.map(c => (
                      <Card key={c.id} style={{ padding: '18px 22px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <Badge color={c.target_type === 'individual' ? T.gold : T.orange}>{c.target_type === 'individual' ? 'Just for you' : 'Broadcast'}</Badge>
                          <span style={{ fontSize: 11, color: T.gray }}>{new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                        <p style={{ fontWeight: 600, fontSize: 15, color: T.white, marginBottom: 6 }}>{c.title}</p>
                        <p style={{ fontSize: 12, color: T.gray, marginBottom: 10 }}>by {c.cook_name}</p>
                        {c.description && <p style={{ fontSize: 13, color: T.grayLight, lineHeight: 1.6, marginBottom: 10 }}>{c.description}</p>}
                        {c.video_url && (
                          <a href={c.video_url} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: T.orange, textDecoration: 'none', fontWeight: 500 }}>
                            ▶ Watch Video
                          </a>
                        )}
                      </Card>
                    ))}
                  </div>
                )
              }
            </div>
          </div>
        )}

        {/* ── WELLNESS ── */}
        {tab === 'wellness' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <Card style={{ padding: 24 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Sleep Log</h3>
              {wellness.sleep.length === 0
                ? <p style={{ color: T.gray, fontSize: 13 }}>No sleep logged yet.</p>
                : wellness.sleep.map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 13, color: T.grayLight }}>{s.date}</span>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontSize: 13, color: T.white }}>{s.hours_slept}h</span>
                      {s.recovery_score && <span style={{ fontSize: 11, color: T.gray, marginLeft: 8 }}>Recovery {s.recovery_score}%</span>}
                    </div>
                  </div>
                ))
              }
            </Card>
            <Card style={{ padding: 24 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Hydration</h3>
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <p style={{ fontFamily: 'Bebas Neue', fontSize: 56, color: T.orange, letterSpacing: 2 }}>{Math.round((wellness.waterTodayMl || 0) / 237)}</p>
                <p style={{ color: T.gray, fontSize: 14 }}>cups today</p>
                <p style={{ color: T.grayLight, fontSize: 12, marginTop: 8 }}>{wellness.waterTodayMl || 0} mL · goal 8 cups (1,893 mL)</p>
              </div>
            </Card>
          </div>
        )}

        {/* ── ACCOUNT ── */}
        {tab === 'account' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <Card style={{ padding: 24 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Membership</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ padding: '12px 16px', background: `${T.orange}15`, borderRadius: 10, border: `1px solid ${T.orange}44` }}>
                  <p style={{ fontFamily: 'Bebas Neue', fontSize: 20, color: T.orange, letterSpacing: 1 }}>{user.planName || 'No plan chosen yet'}</p>
                  <p style={{ fontSize: 12, color: T.gray }}>Current plan · <Link to="/billing" style={{ color: T.orange }}>Plan &amp; Billing</Link></p>
                </div>
                <div>
                  <p style={{ fontSize: 12, color: T.gray, marginBottom: 2 }}>AI coach questions</p>
                  <p style={{ fontSize: 14, color: T.white }}>{aiCoach.used} used of {aiCoach.limit} this month</p>
                  <div style={{ height: 4, background: T.border, borderRadius: 2, marginTop: 8 }}>
                    <div style={{ height: '100%', borderRadius: 2, width: `${Math.min(100, (aiCoach.used / aiCoach.limit) * 100)}%`, background: aiCoach.used >= aiCoach.limit ? T.danger : T.orange, transition: 'width 0.5s' }} />
                  </div>
                </div>
                <div>
                  <p style={{ fontSize: 12, color: T.gray, marginBottom: 2 }}>Free months earned</p>
                  <p style={{ fontFamily: 'Bebas Neue', fontSize: 22, color: T.gold }}>{credits} month{credits !== 1 ? 's' : ''}</p>
                </div>
                <div>
                  <p style={{ fontSize: 12, color: T.gray, marginBottom: 2 }}>Member since</p>
                  <p style={{ fontSize: 14, color: T.white }}>{fmtDate(user.memberSince)}</p>
                </div>
              </div>
            </Card>

            <Card style={{ padding: 24 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Referrals</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                {referrals.code && (
                  <div style={{ padding: '10px 14px', background: '#111', borderRadius: 8 }}>
                    <p style={{ fontSize: 11, color: T.gray, marginBottom: 4 }}>YOUR CODE</p>
                    <p style={{ fontFamily: 'Bebas Neue', fontSize: 28, color: T.orange, letterSpacing: 4 }}>{referrals.code}</p>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1, padding: '12px', background: '#111', borderRadius: 8, textAlign: 'center' }}>
                    <p style={{ fontFamily: 'Bebas Neue', fontSize: 24, color: T.white }}>{referrals.submitted}</p>
                    <p style={{ fontSize: 11, color: T.gray }}>Referred</p>
                  </div>
                  <div style={{ flex: 1, padding: '12px', background: '#111', borderRadius: 8, textAlign: 'center' }}>
                    <p style={{ fontFamily: 'Bebas Neue', fontSize: 24, color: T.gold }}>{referrals.converted}</p>
                    <p style={{ fontSize: 11, color: T.gray }}>Converted</p>
                  </div>
                </div>
                <Btn variant="outline" size="sm" onClick={() => window.location.href = `${process.env.PUBLIC_URL}/referral`}>Go to Referral Page →</Btn>
              </div>
            </Card>

            <Card style={{ padding: 24, gridColumn: '1 / -1' }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 13, letterSpacing: 2, color: T.orange, marginBottom: 16, textTransform: 'uppercase' }}>Push Notifications</h3>
              {pushStatus === 'native' ? (
                <Link to="/settings/phone-alerts" style={{ color: T.orange, fontWeight: 600, fontSize: 14 }}>Phone alerts for this phone</Link>
              ) : pushStatus === 'unsupported' ? (
                <p style={{ fontSize: 13, color: T.gray }}>Your browser does not support push notifications. Try Chrome or Edge on desktop or Android.</p>
              ) : pushStatus === 'denied' ? (
                <div>
                  <p style={{ fontSize: 13, color: T.danger, marginBottom: 8 }}>Notifications are blocked in your browser settings.</p>
                  <p style={{ fontSize: 12, color: T.gray }}>To enable: open your browser site settings, find HealthyBody, and set Notifications to "Allow." Then reload this page.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    {pushStatus === 'subscribed' ? (
                      <p style={{ fontSize: 13, color: T.white }}>Notifications are <strong style={{ color: T.success }}>on</strong> — you'll get alerts for new coach messages and announcements.</p>
                    ) : (
                      <p style={{ fontSize: 13, color: T.gray }}>Turn on notifications to get instant alerts for coach messages and announcements, even when the app is closed.</p>
                    )}
                  </div>
                  <Btn
                    size="sm"
                    disabled={pushLoading || pushStatus === 'loading'}
                    onClick={togglePush}
                    style={{ background: pushStatus === 'subscribed' ? '#333' : T.orange, color: '#fff', minWidth: 160, flexShrink: 0 }}>
                    {pushLoading ? 'Working…' : pushStatus === 'subscribed' ? 'Turn Off Notifications' : 'Enable Notifications'}
                  </Btn>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Full-size photo viewer */}
      {viewPhoto && (
        <div onClick={() => setViewPhoto(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative' }} onClick={e => e.stopPropagation()}>
            <img src={viewPhoto.photo_data} alt="Progress" style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: 10, objectFit: 'contain' }} />
            <div style={{ marginTop: 10, textAlign: 'center' }}>
              <p style={{ color: T.white, fontSize: 14 }}>{viewPhoto.photo_date} {viewPhoto.caption && `· ${viewPhoto.caption}`}</p>
            </div>
            <button onClick={() => setViewPhoto(null)} style={{ position: 'absolute', top: -12, right: -12, background: T.danger, border: 'none', borderRadius: '50%', width: 30, height: 30, color: T.white, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── GYM ADMIN PORTAL ─────────────────────────────────────────────────────────
function GymAdminPortal() {
  const [dashboard, setDashboard] = useState(null);
  const [members, setMembers] = useState([]);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '' });
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api('/api/gym-admin/dashboard'),
      api('/api/gym-admin/members'),
      api('/api/gym-admin/invite'),
    ]).then(([dash, mem, inv]) => {
      setDashboard(dash);
      setMembers(mem.members || []);
      setInvite(inv);
    }).catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const copyInvite = () => {
    if (!invite?.inviteUrl) return;
    navigator.clipboard.writeText(invite.inviteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerate invite link? The current link will stop working immediately.')) return;
    try {
      const data = await api('/api/gym-admin/invite/regenerate', 'POST');
      setInvite(data);
    } catch (e) { alert(e.message); }
  };

  const removeMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from your gym? Their account stays but they lose gym access.`)) return;
    setRemoving(memberId);
    try {
      await api(`/api/gym-admin/members/${memberId}`, 'DELETE');
      setMembers(m => m.filter(x => x.id !== memberId));
      if (dashboard) setDashboard(d => ({ ...d, memberCount: d.memberCount - 1, seatsRemaining: d.seatsRemaining + 1 }));
    } catch (e) { alert(e.message); }
    finally { setRemoving(null); }
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return null;
    return Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  };

  if (loading) return <Spinner />;

  const gym = dashboard?.gym;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '96px 32px 60px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 40 }}>
          <p style={{ fontFamily: 'Oswald', fontSize: 12, letterSpacing: 3, color: T.gray, textTransform: 'uppercase', marginBottom: 8 }}>GYM ADMIN PORTAL</p>
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, color: T.white, margin: 0 }}>
            {gym?.display_name || 'Your Gym'}
          </h1>
          {gym && (
            <p style={{ color: T.gray, fontSize: 14, marginTop: 6 }}>
              {gym.tier.charAt(0).toUpperCase() + gym.tier.slice(1)} tier &nbsp;·&nbsp; {gym.seat_count} seat license
              {gym.content_licensing ? ' &nbsp;·&nbsp; Content library included' : ''}
            </p>
          )}
        </div>

        {/* Stat cards */}
        {dashboard && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
            {[
              { label: 'Total Members', value: dashboard.memberCount, icon: '👥', color: T.orange },
              { label: 'Active This Week', value: dashboard.activeThisWeek, icon: '⚡', color: T.success },
              { label: 'Seats Remaining', value: dashboard.seatsRemaining, icon: '🪑', color: dashboard.seatsRemaining <= 5 ? T.danger : T.gold },
            ].map(s => (
              <Card key={s.label} style={{ textAlign: 'center', padding: '28px 20px' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>{s.icon}</div>
                <div style={{ fontFamily: 'Bebas Neue', fontSize: 48, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 13, color: T.gray, marginTop: 6 }}>{s.label}</div>
              </Card>
            ))}
          </div>
        )}

        {/* Invite Link */}
        <Card style={{ marginBottom: 24, padding: 28 }}>
          <h3 style={{ fontFamily: 'Oswald', fontSize: 18, letterSpacing: 1, color: T.white, marginBottom: 6 }}>🔗 Member Invite Link</h3>
          <p style={{ color: T.gray, fontSize: 13, marginBottom: 20 }}>Share this link with your gym members. Anyone with the link can create an account and join your gym automatically.</p>
          {invite && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 200, background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 16px', fontSize: 12, color: T.grayLight, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {invite.inviteUrl}
              </div>
              <Btn size="sm" onClick={copyInvite} variant={copied ? 'ghost' : 'primary'} style={{ flexShrink: 0 }}>
                {copied ? '✓ Copied!' : 'Copy Link'}
              </Btn>
              <Btn size="sm" variant="ghost" onClick={regenerate} style={{ flexShrink: 0 }}>Regenerate</Btn>
            </div>
          )}
        </Card>

        {/* Members */}
        <Card style={{ padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 18, letterSpacing: 1, color: T.white, margin: 0 }}>
              👥 Members ({members.length})
            </h3>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {dashboard?.seatsRemaining <= 5 && (
                <Badge color={T.danger}>{dashboard.seatsRemaining} seat{dashboard.seatsRemaining !== 1 ? 's' : ''} left</Badge>
              )}
            </div>
          </div>

          {members.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: T.gray }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
              <p style={{ fontSize: 16, marginBottom: 8, color: T.grayLight }}>No members yet</p>
              <p style={{ fontSize: 13 }}>Share your invite link above to start adding members.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Member', 'Goal', 'Joined', 'Last Practice', ''].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, color: T.gray, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => {
                    const ds = daysSince(m.last_workout);
                    return (
                      <tr key={m.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: '14px 12px' }}>
                          <div style={{ fontWeight: 600, fontSize: 14, color: T.white }}>{m.name}</div>
                          <div style={{ fontSize: 12, color: T.gray }}>{m.email}</div>
                        </td>
                        <td style={{ padding: '14px 12px', fontSize: 13, color: T.grayLight, whiteSpace: 'nowrap' }}>
                          {m.goal ? m.goal.replace(/_/g, ' ') : '—'}
                        </td>
                        <td style={{ padding: '14px 12px', fontSize: 13, color: T.gray, whiteSpace: 'nowrap' }}>
                          {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '14px 12px', fontSize: 13, whiteSpace: 'nowrap' }}>
                          {ds === null ? <span style={{ color: T.gray }}>Never</span>
                            : ds === 0 ? <span style={{ color: T.success }}>Today</span>
                            : ds <= 3 ? <span style={{ color: T.success }}>{ds}d ago</span>
                            : ds <= 7 ? <span style={{ color: T.gold }}>{ds}d ago</span>
                            : <span style={{ color: T.danger }}>{ds}d ago</span>
                          }
                        </td>
                        <td style={{ padding: '14px 12px', textAlign: 'right' }}>
                          <button
                            onClick={() => removeMember(m.id, m.name)}
                            disabled={removing === m.id}
                            style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 12px', fontSize: 12, color: T.gray, cursor: removing === m.id ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
                            onMouseOver={e => { if (removing !== m.id) { e.currentTarget.style.borderColor = T.danger; e.currentTarget.style.color = T.danger; } }}
                            onMouseOut={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.gray; }}
                          >
                            {removing === m.id ? '…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ── INVITE SIGNUP ─────────────────────────────────────────────────────────────
function InviteSignup() {
  const inviteSvc = useService();
  const { token: inviteToken } = useParams();
  const [gymName, setGymName] = useState('');
  const [validating, setValidating] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '', date_of_birth: '' });
  const [agree, setAgree] = useState(false);
  const legal = useLegal();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!inviteToken) { setInvalid(true); setValidating(false); return; }
    api(`/api/gym-invite/${inviteToken}`)
      .then(d => setGymName(d.gym_name))
      .catch(() => setInvalid(true))
      .finally(() => setValidating(false));
  }, [inviteToken]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!form.date_of_birth) { setError('Please enter your date of birth.'); return; }
    if (ageFromDob(form.date_of_birth) < 13) { setError(UNDER_13_TEXT); return; }
    if (legal.published && !agree) { setError('Please tick the box to agree to the Terms of Service and the Privacy Policy.'); return; }
    setLoading(true); setError('');
    try {
      const data = await api('/api/auth/signup-invite', 'POST', { name: form.name, email: form.email, password: form.password, token: inviteToken, date_of_birth: form.date_of_birth, agree_legal: legal.published ? agree : undefined });
      login(data.user, data.token, data.onboardingComplete);
      nav('/onboarding');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (validating) return <Spinner />;

  if (invalid) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg, padding: '80px 24px' }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <div style={{ fontSize: 64, marginBottom: 20 }}>🔗</div>
        <h2 style={{ fontFamily: 'Oswald', fontSize: 28, color: T.white, marginBottom: 12 }}>Invalid Invite Link</h2>
        <p style={{ color: T.gray, fontSize: 15, marginBottom: 28 }}>This invite link is not valid or has expired. Contact your gym administrator for a new one.</p>
        <Btn onClick={() => nav('/')}>Go Home</Btn>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg, padding: '80px 24px 40px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 30%, rgba(255,69,0,0.08) 0%, transparent 60%)' }} />
      <div style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: 460 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 52, height: 52, background: `linear-gradient(135deg, ${T.orange}, ${T.gold})`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, margin: '0 auto 12px' }}>🏋️</div>
          <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 28, letterSpacing: 3, color: T.white }}>{(inviteSvc?.name || 'HealthyBody').toUpperCase()}</h1>
          {gymName && <p style={{ color: T.orange, fontSize: 14, marginTop: 8 }}>You're joining: <strong>{gymName}</strong></p>}
        </div>
        <Card style={{ padding: 36 }}>
          <h2 style={{ fontFamily: 'Oswald', fontSize: 22, fontWeight: 600, marginBottom: 8 }}>Create Your Account</h2>
          <p style={{ color: T.gray, fontSize: 14, marginBottom: 28 }}>Your gym has invited you. Create your account to get started.</p>
          <form onSubmit={submit}>
            <Input label="Full Name" value={form.name} onChange={set('name')} placeholder="Your name" required />
            <Input label="Email Address" type="email" value={form.email} onChange={set('email')} placeholder="you@email.com" required />
            <Input label="Password" type="password" value={form.password} onChange={set('password')} placeholder="Min 8 characters" required />
            <Input label="Confirm Password" type="password" value={form.confirm} onChange={set('confirm')} placeholder="Confirm your password" required />
            <DobInput value={form.date_of_birth} onChange={set('date_of_birth')} />
            <LegalAgree checked={agree} onChange={setAgree} />
            {error && <div style={{ background: `${T.danger}15`, border: `1px solid ${T.danger}44`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: T.danger }}>{error}</div>}
            <Btn type="submit" fullWidth disabled={loading}>{loading ? 'Creating account…' : 'Create My Account →'}</Btn>
          </form>
        </Card>
      </div>
    </div>
  );
}

// ── COOK DASHBOARD ────────────────────────────────────────────────────────────
function CookDashboardPage() {
  const [clients, setClients] = useState([]);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', description: '', content: '', video_url: '', target_type: 'broadcast', client_id: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    const [c, cc] = await Promise.all([
      api('/api/cook/clients').catch(() => ({ clients: [] })),
      api('/api/cook/content').catch(() => ({ content: [] })),
    ]);
    setClients(c.clients || []);
    setContent(cc.content || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submitContent = async e => {
    e.preventDefault();
    if (!form.title.trim()) return;
    if (form.target_type === 'individual' && !form.client_id) { alert('Please select a Rebuilder.'); return; }
    setSubmitting(true);
    try {
      await api('/api/cook/content', 'POST', form);
      setForm({ title: '', description: '', content: '', video_url: '', target_type: 'broadcast', client_id: '' });
      load();
    } catch(err) { alert(err.message); }
    finally { setSubmitting(false); }
  };

  const deleteItem = async id => {
    if (!window.confirm('Delete this content?')) return;
    await api(`/api/cook/content/${id}`, 'DELETE');
    setContent(prev => prev.filter(c => c.id !== id));
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 32px 60px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, lineHeight: 1 }}>
          <span style={gradText}>MY KITCHEN</span>
        </h1>
        <p style={{ color: T.gray, fontSize: 15, marginTop: 4 }}>Post recipes, tips, and content for your Rebuilders.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        {/* Post Content Form */}
        <Card>
          <h2 style={{ fontFamily: 'Oswald', fontSize: 20, marginBottom: 20 }}>Post New Content</h2>
          <form onSubmit={submitContent}>
            <Input label="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Recipe name or content title" required />
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description…" rows={3}
                style={{ width: '100%', padding: '13px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Content Body (optional)</label>
              <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder="Full recipe steps, tips, additional details…" rows={5}
                style={{ width: '100%', padding: '13px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <Input label="Video URL (optional)" value={form.video_url} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))} placeholder="https://youtube.com/…" />
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Target Audience</label>
              <select value={form.target_type} onChange={e => setForm(f => ({ ...f, target_type: e.target.value, client_id: '' }))}
                style={{ width: '100%', padding: '13px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: T.white, fontSize: 14, fontFamily: 'Poppins', outline: 'none' }}>
                <option value="broadcast">Broadcast — all my Rebuilders</option>
                <option value="individual">Individual Rebuilder</option>
              </select>
            </div>
            {form.target_type === 'individual' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, color: T.grayLight, marginBottom: 6, fontWeight: 500 }}>Rebuilder <span style={{ color: T.orange }}>*</span></label>
                <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                  style={{ width: '100%', padding: '13px 16px', background: '#F5F7FA', border: `1px solid ${T.border}`, borderRadius: 8, color: form.client_id ? T.white : T.gray, fontSize: 14, fontFamily: 'Poppins', outline: 'none' }}>
                  <option value="">Select a Rebuilder…</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {clients.length === 0 && <p style={{ fontSize: 12, color: T.gray, marginTop: 6 }}>No Rebuilders assigned yet. Ask the admin to assign you Rebuilders.</p>}
              </div>
            )}
            <Btn type="submit" fullWidth disabled={submitting}>{submitting ? 'Posting…' : 'Post Content'}</Btn>
          </form>
        </Card>

        {/* Right: clients + posted content */}
        <div style={{ display: 'grid', gap: 24 }}>
          <Card>
            <h2 style={{ fontFamily: 'Oswald', fontSize: 20, marginBottom: 16 }}>My Rebuilders ({clients.length})</h2>
            {clients.length === 0
              ? <p style={{ color: T.gray, fontSize: 14 }}>No Rebuilders assigned yet. Contact the admin to get Rebuilders assigned to you.</p>
              : <div style={{ display: 'grid', gap: 8 }}>
                  {clients.map(c => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#F5F7FA', borderRadius: 8, border: `1px solid ${T.border}` }}>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{c.name}</p>
                        <p style={{ fontSize: 12, color: T.gray }}>{c.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </Card>

          <Card>
            <h2 style={{ fontFamily: 'Oswald', fontSize: 20, marginBottom: 16 }}>Posted Content ({content.length})</h2>
            {content.length === 0
              ? <p style={{ color: T.gray, fontSize: 14 }}>Nothing posted yet. Use the form to post your first recipe or tip.</p>
              : <div style={{ display: 'grid', gap: 10, maxHeight: 460, overflowY: 'auto' }}>
                  {content.map(c => (
                    <div key={c.id} style={{ padding: '12px 14px', background: '#F5F7FA', borderRadius: 8, border: `1px solid ${T.border}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                          <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{c.title}</p>
                          {c.description && <p style={{ fontSize: 12, color: T.gray, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.description}</p>}
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Badge color={c.target_type === 'individual' ? T.gold : T.orange}>{c.target_type === 'individual' ? 'Individual' : 'Broadcast'}</Badge>
                            <span style={{ fontSize: 11, color: T.gray }}>{new Date(c.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <button onClick={() => deleteItem(c.id)} style={{ background: `${T.danger}15`, border: `1px solid ${T.danger}44`, borderRadius: 6, color: T.danger, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontFamily: 'Poppins', flexShrink: 0 }}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </Card>
        </div>
      </div>

      <div style={{ marginTop: 32 }}>
        <h2 style={{ fontFamily: 'Oswald', fontSize: 20, marginBottom: 16 }}>Recipes for the meal planner</h2>
        <RS.MyRecipes />
      </div>
    </div>
  );
}

// ── SHARED PIECES FOR THE MODULE PAGES ───────────────────────────────────────
// ── CALISTHENICS ─────────────────────────────────────────────────────────────
function CalisthenicsPage() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(0);
  const A = '#1F6F8B';
  useEffect(() => { api('/api/calisthenics').then(setD).catch(err => setD({ locked: /not active|not on your plan/i.test(err.message || '') || err.status === 402, lockInfo: err.info, error: true })); }, []);
  if (!d) return <ModuleLoading />;
  if (d.locked) return <ModuleLocked info={d.lockInfo} />;
  const ladders = d.ladders || [];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 980, margin: '0 auto' }}>
      <ModuleHeader icon="🤸" title="CALISTHENICS" accent={A}
        sub="Five ladders. Each rung is earned with clean repetitions before the next one is offered." />
      <SafetyPanel data={d} accent={A} />

      {d.blocked ? null : (
        <>
          <p style={{ fontSize: 14.5, color: T.grayLight, lineHeight: 1.7, marginBottom: 10 }}>{d.premise}</p>
          <p style={{ fontSize: 13.5, color: T.gray, lineHeight: 1.65, marginBottom: 26 }}><strong>Equipment:</strong> {d.equipment}</p>

          {(d.notes || []).length > 0 && (
            <Card style={{ padding: 20, marginBottom: 26, borderLeft: `5px solid ${A}` }}>
              {(d.notes || []).map((n, i) => (
                <p key={i} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6, marginTop: i ? 10 : 0 }}>{n}</p>
              ))}
            </Card>
          )}

          <Card style={{ padding: 22, marginBottom: 30, borderLeft: `5px solid ${A}` }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>The rules that keep you in one piece</h3>
            <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(d.rules || []).map(r => <li key={r} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6 }}>{r}</li>)}
            </ul>
          </Card>

          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 6 }}>Your ladders</h2>
          <p style={{ fontSize: 13.5, color: T.gray, marginBottom: 16, lineHeight: 1.6 }}>
            Start where we have placed you, not where you think you should be. Each rung shows the test you must pass before the next one counts.
          </p>

          {ladders.map((l, idx) => (
            <Card key={l.key} style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <button type="button" onClick={() => setOpen(open === idx ? -1 : idx)} style={{
                width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit', background: 'transparent',
                border: 'none', padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              }}>
                <span>
                  <span style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600 }}>{l.name}</span>
                  <span style={{ display: 'block', fontSize: 13, color: T.gray, marginTop: 3 }}>{l.goal} · start at rung {l.startRung}</span>
                </span>
                <span style={{ fontSize: 20, color: A, flexShrink: 0 }}>{open === idx ? '−' : '+'}</span>
              </button>

              {open === idx && (
                <div style={{ borderTop: `1px solid ${T.border}` }}>
                  <p style={{ fontSize: 13, color: T.gray, padding: '14px 22px 0', fontStyle: 'italic' }}>{l.trains}</p>
                  {(l.rungs || []).map(r => {
                    const isStart = r.n === l.startRung;
                    const below = r.n < l.startRung;
                    return (
                      <div key={r.n} style={{
                        padding: '16px 22px', borderTop: `1px solid ${T.border}`,
                        background: isStart ? `${A}0E` : 'transparent', opacity: below ? 0.55 : 1,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'Oswald', fontSize: 13, fontWeight: 700, color: A, minWidth: 54 }}>RUNG {r.n}</span>
                          <span style={{ fontSize: 15, fontWeight: 600, color: T.white }}>{r.name}</span>
                          {isStart && <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#fff', background: A, padding: '3px 9px', borderRadius: 20 }}>START HERE</span>}
                        </div>
                        <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6, marginBottom: 6 }}>{r.how}</p>
                        <p style={{ fontSize: 13, color: T.success, lineHeight: 1.55, marginBottom: 4 }}><strong>Earn the next rung:</strong> {r.earn}</p>
                        <p style={{ fontSize: 13, color: '#8A5A00', lineHeight: 1.55 }}><strong>Common fault:</strong> {r.fault}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          ))}

          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, margin: '34px 0 14px' }}>Your week</h2>
          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 34 }}>
            {(d.weekly || []).map((w, i) => (
              <div key={w.day} style={{ display: 'flex', gap: 16, padding: '14px 20px', borderTop: i ? `1px solid ${T.border}` : 'none', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'Oswald', fontSize: 13, fontWeight: 600, color: A, minWidth: 82 }}>{w.day}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: T.white, minWidth: 110 }}>{w.theme}</span>
                <span style={{ fontSize: 13.5, color: T.grayLight, flex: '1 1 220px' }}>{w.practice}</span>
              </div>
            ))}
          </Card>

          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 6 }}>Looking after the joints this actually loads</h2>
          <p style={{ fontSize: 13.5, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>
            Almost every injury in this discipline lands in one of these four places, and almost all of them come from adding volume faster than tendon adapts.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 34 }}>
            {(d.jointCare || []).map(j => (
              <Card key={j.joint} style={{ padding: 20 }}>
                <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, color: A, marginBottom: 8 }}>{j.joint}</h3>
                <p style={{ fontSize: 13, color: T.grayLight, lineHeight: 1.6, marginBottom: 10 }}>{j.risk}</p>
                <ul style={{ paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {j.do.map(x => <li key={x} style={{ fontSize: 12.5, color: T.gray, lineHeight: 1.5 }}>{x}</li>)}
                </ul>
              </Card>
            ))}
          </div>

          <Card style={{ padding: 22, marginBottom: 22 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Test yourself, then test again in six weeks</h3>
            <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {(d.testYourself || []).map(x => <li key={x} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.55 }}>{x}</li>)}
            </ul>
          </Card>

          {d.escalate && (
            <Card style={{ padding: 22, borderLeft: '5px solid #B3261E' }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 10 }}>When this needs a physiotherapist</h3>
              <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6 }}>{d.escalate}</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ── WALKING AND HIKING ───────────────────────────────────────────────────────
function HikingPage() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(-1);
  const A = '#3F7D3F';
  useEffect(() => { api('/api/hiking').then(setD).catch(err => setD({ locked: /not active|not on your plan/i.test(err.message || '') || err.status === 402, lockInfo: err.info, error: true })); }, []);
  if (!d) return <ModuleLoading />;
  if (d.locked) return <ModuleLocked info={d.lockInfo} />;
  const stages = d.stages || [];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 980, margin: '0 auto' }}>
      <ModuleHeader icon="🥾" title="WALKING & HIKING" accent={A}
        sub="One ladder from ten minutes on a pavement to a long day on a trail." />
      <SafetyPanel data={d} accent={A} />

      {d.blocked ? null : (
        <>
          <p style={{ fontSize: 14.5, color: T.grayLight, lineHeight: 1.7, marginBottom: 22 }}>{d.premise}</p>

          {(d.notes || []).length > 0 && (
            <Card style={{ padding: 20, marginBottom: 26, borderLeft: `5px solid ${A}` }}>
              {(d.notes || []).map((n, i) => (
                <p key={i} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6, marginTop: i ? 10 : 0 }}>{n}</p>
              ))}
            </Card>
          )}

          {/* The warnings sit high on the page on purpose. Both of the first two
              present during walking and are routinely dismissed as being unfit. */}
          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 6 }}>Know these before your first walk</h2>
          <p style={{ fontSize: 13.5, color: T.gray, marginBottom: 14, lineHeight: 1.6 }}>
            Walking is where some conditions show themselves first. None of these mean you should not walk — they mean get it looked at rather than push through it.
          </p>
          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 34 }}>
            {(d.warnings || []).map((w, i) => (
              <div key={w.sign} style={{ padding: '16px 20px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: T.white, marginBottom: 6, lineHeight: 1.5 }}>{w.sign}</p>
                <p style={{ fontSize: 13.5, color: '#B3261E', lineHeight: 1.6 }}>{w.means}</p>
              </div>
            ))}
          </Card>

          <Card style={{ padding: 22, marginBottom: 30, borderLeft: `5px solid ${A}` }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 10 }}>How to set your pace</h3>
            <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.65 }}>{d.theTalkTest}</p>
          </Card>

          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 6 }}>The six stages</h2>
          <p style={{ fontSize: 13.5, color: T.gray, marginBottom: 16, lineHeight: 1.6 }}>
            You are starting at stage {d.startStage}. There is no requirement to reach the top — most of the benefit is in the first two.
          </p>
          {stages.map((st, idx) => {
            const current = st.n === d.startStage;
            return (
              <Card key={st.n} style={{ padding: 0, overflow: 'hidden', marginBottom: 14, borderLeft: current ? `5px solid ${A}` : 'none' }}>
                <button type="button" onClick={() => setOpen(open === idx ? -1 : idx)} style={{
                  width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit', background: current ? `${A}0E` : 'transparent',
                  border: 'none', padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                }}>
                  <span>
                    <span style={{ fontFamily: 'Oswald', fontSize: 13, fontWeight: 700, color: A, marginRight: 10 }}>STAGE {st.n}</span>
                    <span style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600 }}>{st.name}</span>
                    {current && <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#fff', background: A, padding: '3px 9px', borderRadius: 20, marginLeft: 10 }}>YOU ARE HERE</span>}
                    <span style={{ display: 'block', fontSize: 13, color: T.gray, marginTop: 4 }}>{st.goal} · weeks {st.weeks}</span>
                  </span>
                  <span style={{ fontSize: 20, color: A, flexShrink: 0 }}>{open === idx ? '−' : '+'}</span>
                </button>
                {open === idx && (
                  <div style={{ borderTop: `1px solid ${T.border}`, padding: '16px 22px' }}>
                    <p style={{ fontSize: 13.5, color: A, fontWeight: 600, marginBottom: 10 }}>Target: {st.target}</p>
                    <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                      {st.work.map(w => <li key={w} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.55 }}>{w}</li>)}
                    </ul>
                    <p style={{ fontSize: 13, color: T.gray, lineHeight: 1.6, fontStyle: 'italic' }}>{st.progress}</p>
                  </div>
                )}
              </Card>
            );
          })}

          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, margin: '34px 0 14px' }}>Why walking earns its place</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, marginBottom: 34 }}>
            {(d.whyItWorks || []).map(w => (
              <Card key={w} style={{ padding: 20 }}>
                <div style={{ width: 30, height: 3, background: A, borderRadius: 2, marginBottom: 12 }} />
                <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.65 }}>{w}</p>
              </Card>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 22 }}>
            <Card style={{ padding: 22 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 10 }}>Your feet</h3>
              <ul style={{ paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 7 }}>
                {(d.footCare || []).map(f => <li key={f} style={{ fontSize: 13, color: T.grayLight, lineHeight: 1.55 }}>{f}</li>)}
              </ul>
            </Card>
            <Card style={{ padding: 22 }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 10 }}>Pack and turnaround</h3>
              <p style={{ fontSize: 13, color: T.grayLight, lineHeight: 1.6, marginBottom: 10 }}>{d.packRule}</p>
              <p style={{ fontSize: 13, color: T.grayLight, lineHeight: 1.6 }}>{d.turnaroundRule}</p>
            </Card>
          </div>

          <Card style={{ padding: 22, marginBottom: 22, display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Somewhere to walk</h3>
              <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6 }}>Parks and trails near you, searchable by state.</p>
            </div>
            <Btn onClick={() => { window.location.href = `${process.env.PUBLIC_URL}/get-outside`; }}>Find trails →</Btn>
          </Card>

          {d.escalate && (
            <Card style={{ padding: 22, borderLeft: '5px solid #B3261E' }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 10 }}>When to stop and see a doctor</h3>
              <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6 }}>{d.escalate}</p>
              {d.disclaimer && <p style={{ fontSize: 12.5, color: T.gray, marginTop: 12, fontStyle: 'italic' }}>{d.disclaimer}</p>}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ── PHOTO CREDITS ────────────────────────────────────────────────────────────
// The six workout photos are CC BY 2.0, which legally requires visible credit:
// author, title, source and licence. This page is public on every front door
// and linked from the footer, which is what makes the licence satisfied.
function CreditsPage() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/image-credits.json`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('not found')))
      .then(setData)
      .catch(() => setFailed(true));
  }, []);

  const rows = (data?.images || []);
  const required = rows.filter(i => i.attributionRequired);
  const free = rows.filter(i => !i.attributionRequired);

  const Row = ({ i }) => (
    <div style={{ padding: '16px 0', borderTop: `1px solid ${T.border}` }}>
      <p style={{ fontSize: 14, fontWeight: 600, color: T.white, lineHeight: 1.5, marginBottom: 6 }}>
        {i.title || i.file}
      </p>
      <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.7 }}>
        {i.author ? <>by {i.authorUrl
          ? <a href={i.authorUrl} target="_blank" rel="noreferrer noopener" style={{ color: T.gold }}>{i.author}</a>
          : i.author}, </> : null}
        licensed <a href={i.licenseUrl} target="_blank" rel="noreferrer noopener" style={{ color: T.gold }}>{i.license}</a>
        {i.source && i.source.startsWith('http')
          ? <> · <a href={i.source} target="_blank" rel="noreferrer noopener" style={{ color: T.gold }}>source</a></>
          : <> · {i.source}</>}
      </p>
      <p style={{ fontSize: 12, color: T.gray, marginTop: 4, fontFamily: 'monospace' }}>{i.file}</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 860, margin: '0 auto' }}>
      <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(34px, 5vw, 54px)', letterSpacing: 2, marginBottom: 10 }}>PHOTO CREDITS</h1>
      <p style={{ color: T.grayLight, fontSize: 15, lineHeight: 1.7, marginBottom: 34 }}>
        Every photograph used on this site, who made it, and the licence it is used under.
      </p>

      {failed && <Card style={{ padding: 22 }}><p style={{ fontSize: 14, color: T.grayLight }}>The credits file could not be loaded. It lives at <code>/image-credits.json</code>.</p></Card>}

      {required.length > 0 && (
        <>
          <h2 style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Creative Commons Attribution</h2>
          <p style={{ fontSize: 13.5, color: T.gray, lineHeight: 1.7, marginBottom: 6 }}>
            Used under a licence that requires credit. Changes: some images have been cropped or resized to fit the layout.
          </p>
          <Card style={{ padding: '4px 22px 18px', marginBottom: 34 }}>
            {required.map(i => <Row key={i.file} i={i} />)}
          </Card>
        </>
      )}

      {free.length > 0 && (
        <>
          <h2 style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600, marginBottom: 10 }}>Public domain and CC0</h2>
          <p style={{ fontSize: 13.5, color: T.gray, lineHeight: 1.7, marginBottom: 6 }}>
            No credit is legally required for these. They are listed so the provenance of every photo on the platform is recorded in one place.
          </p>
          <Card style={{ padding: '4px 22px 18px', marginBottom: 34 }}>
            {free.map(i => <Row key={i.file} i={i} />)}
          </Card>
        </>
      )}

      <p style={{ fontSize: 12.5, color: T.gray, lineHeight: 1.7 }}>
        If you hold rights to an image here and believe it is credited wrongly or used wrongly, contact us and it will be corrected or removed.
      </p>
    </div>
  );
}

// ── PLAN & BILLING ── Stripe checkout and the Stripe billing portal (switched off until keys + prices exist)
// 30-day money-back on the first payment (2026-10-29): stated next to billing, one tap to ask.
function MoneyBack() {
  const [st, setSt] = useState(null), [reason, setReason] = useState(''), [open, setOpen] = useState(false), [msg, setMsg] = useState(''), [err, setErr] = useState('');
  const load = () => api('/api/refund-request').then(setSt).catch(() => {});
  useEffect(() => { load(); }, []);
  const ask = async () => { setErr(''); try { const r = await api('/api/refund-request', 'POST', { reason }); setMsg(r.message); setOpen(false); load(); } catch (e) { setErr(e.message); } };
  return (
    <Card style={{ padding: 18, marginBottom: 20 }}>
      <p style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600, marginBottom: 4 }}>30-day money-back promise</p>
      <p style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.6 }}>Not happy for any reason? Cancel within 30 days of your first payment and we refund it: your first payment, or the full amount on a plan paid in full. The one-time start-up fee is not refundable. No reason needed. After a refund your plan ends. After 30 days you are committed for the full term of your plan; on a plan with no commitment you can cancel any time before your next payment.</p>
      {msg && <p style={{ color: T.success, fontWeight: 600, marginTop: 8 }}>{msg}</p>}
      {st && !st.eligible && !msg && st.reason !== 'not_paid' && <p style={{ fontSize: 13.5, color: T.gray, marginTop: 8 }}>{st.message}</p>}
      {st && st.eligible && !open && <div style={{ marginTop: 10 }}><Btn variant="ghost" onClick={() => setOpen(true)}>Ask for my money back</Btn> <span style={{ fontSize: 13, color: T.gray }}>Until {new Date(st.until).toLocaleDateString()}</span></div>}
      {st && st.eligible && open && <div style={{ marginTop: 10 }}>
        <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Anything you want to tell us? (optional)" style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 14 }} aria-label="Optional reason" />
        {err && <p style={{ color: T.danger, fontSize: 13 }}>{err}</p>}
        <div style={{ marginTop: 8 }}><Btn onClick={ask}>{st.amount != null ? `Refund $${Number(st.amount).toFixed(2)} and end my plan` : st.cadence === 'pay_in_full' ? 'Refund the full amount and end my plan' : 'Refund my first payment and end my plan'}</Btn> <Btn variant="ghost" onClick={() => setOpen(false)}>Keep my plan</Btn></div>
      </div>}
    </Card>
  );
}

function BillingPage() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [sp] = useSearchParams();
  const base = window.location.origin + (process.env.PUBLIC_URL || '');
  const [my, setMy] = useState(null);
  useEffect(() => { api('/api/billing/status').then(setD).catch(e => setMsg(e.message)); api('/api/my/offer').then(setMy).catch(() => {}); }, []);
  // Inside the iPhone / Android app: no prices, no checkout, no plan changes (store rules, Mark 10-30).
  if (BF.isNativeApp()) return <BF.NativeNotice />;
  if (!d) return msg ? <div style={{ padding: 120, textAlign: 'center', color: T.gray }}>{msg}</div> : <Spinner />;
  const activeKeys = new Set(d.active.map(a => a.service_key));
  const hasAll = activeKeys.has('wellspring');
  const go = async (path, body) => {
    setBusy(true); setMsg('');
    try { const r = await api(path, 'POST', { ...body, base }); window.location.href = r.url; }
    catch (e) { setMsg(e.message); setBusy(false); }
  };
  const statusLabel = { active: 'Active', past_due: 'Payment failed: please update your card', canceled: 'Cancelled', suspended: 'Suspended', none: 'No paid plan yet' }[d.subscription_status] || d.subscription_status;
  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: 2, marginBottom: 6 }}>PLAN &amp; <span style={gradText}>BILLING</span></h1>
      {sp.get('paid') && <Card style={{ padding: 16, marginBottom: 16, border: `1px solid ${T.success}55` }}><p style={{ color: T.success, fontWeight: 600 }}>Payment received. Your programs are unlocking now; refresh in a moment if one still shows as locked.</p></Card>}
      {!d.enabled && <Card style={{ padding: 16, marginBottom: 16, border: `1px solid ${T.gold}55` }}><p style={{ color: T.grayLight }}>Payments are not switched on yet. Prices show as $xxx until they are set.</p></Card>}
      {d.subscription_status === 'past_due' && d.grace_period_ends_at && <Card style={{ padding: 16, marginBottom: 16, border: `1px solid ${T.danger}55` }}><p style={{ color: T.danger }}>Your last payment failed. Update your card before {String(d.grace_period_ends_at).slice(0, 10)} to keep your programs.</p></Card>}
      <Card style={{ padding: 22, marginBottom: 20 }}>
        <p style={{ fontSize: 13, color: T.gray }}>Status</p>
        <p style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600 }}>{statusLabel}</p>
        {my && my.offer && my.offer.name && <p style={{ fontSize: 13.5, color: T.grayLight, marginTop: 6 }}>Joined on: {my.offer.name}</p>}
        {my && my.trial_ends_at && d.subscription_status !== 'active' && <p style={{ fontSize: 13.5, color: new Date(my.trial_ends_at) > new Date() ? T.success : T.danger, marginTop: 6 }}>{new Date(my.trial_ends_at) > new Date() ? `Free trial until ${new Date(my.trial_ends_at).toLocaleDateString()}. Add your programs before then; your first charge waits for the trial to end.` : 'Your free trial has ended. Add your programs below to keep going.'}</p>}
        {my && my.initiation === 'waived' && <p style={{ fontSize: 13.5, color: T.success, marginTop: 6 }}>Initiation fee: <s style={{ color: T.gray }}>{my.initiation_fee}</s> $0 (waived)</p>}
        {my && my.initiation === 'due' && <p style={{ fontSize: 13.5, color: T.grayLight, marginTop: 6 }}>A one-time initiation fee of {my.initiation_fee} is added to your first payment.</p>}
        {d.can_manage && <div style={{ marginTop: 12 }}><Btn variant="ghost" disabled={busy} onClick={() => go('/api/billing/portal', {})}>Manage card or invoices →</Btn></div>}
      </Card>
      {/* Plan chooser (2026-10-30, BillingFlex.jsx): plan, then monthly / every 2 weeks / in full, with the terms. */}
      {d.active.length > 0 && <p style={{ fontSize: 14, color: T.grayLight, marginBottom: 14 }}>On your plan now: {hasAll ? 'every program' : d.programs.filter(p => activeKeys.has(p.key)).map(p => p.name).join(', ')}{!hasAll && d.food_included && !activeKeys.has('food') ? ', Cooking & Eating (included)' : ''}.</p>}
      <BF.PlanChooser />
      <p style={{ fontSize: 12, color: T.gray, margin: '-12px 0 20px' }}>Card details are entered on Stripe's own secure page. HealthyBody never sees or stores your card number.</p>
      <MoneyBack />
      {/* Cancelling goes through "Before you go" first: pause, easier plan, talk to the trainer (ClientCare.jsx). */}
      <CC.CancelFlow canManage={d.can_manage} onPortal={() => go('/api/billing/portal', {})} />
    </div>
  );
}

function ModuleLocked({ info }) {
  if (BF.isNativeApp()) return <BF.NativeNotice />;
  const svc = info || {};
  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '90px 24px', maxWidth: 620, margin: '0 auto' }}>
      <Card style={{ padding: 34, textAlign: 'center' }}>
        <div style={{ fontSize: 44, marginBottom: 14 }}>🔒</div>
        <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 34, letterSpacing: 1.5, marginBottom: 10 }}>
          {(svc.name || 'This module').toUpperCase()} IS NOT ON YOUR PLAN
        </h1>
        <p style={{ fontSize: 15, color: T.grayLight, lineHeight: 1.65, marginBottom: 22 }}>
          {`It is ${priceText(svc.price)} a month on its own, or included in the HealthyBody bundle with everything else.`}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {svc.host && <a href={`https://${svc.host}`} style={{
            display: 'inline-flex', alignItems: 'center', padding: '13px 24px', borderRadius: 9,
            background: T.orange, color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 15,
          }}>Read about it →</a>}
          <a href={`${process.env.PUBLIC_URL}/billing`} style={{
            display: 'inline-flex', alignItems: 'center', padding: '13px 24px', borderRadius: 9,
            border: `2px solid ${T.orange}`, color: T.orange, textDecoration: 'none', fontWeight: 700, fontSize: 15,
          }}>Add it to my plan</a>
          <a href={`${process.env.PUBLIC_URL}/`} style={{
            display: 'inline-flex', alignItems: 'center', padding: '13px 24px', borderRadius: 9,
            border: `2px solid ${T.border}`, color: T.grayLight, textDecoration: 'none', fontWeight: 600, fontSize: 15,
          }}>See all modules</a>
        </div>
      </Card>
    </div>
  );
}

function ModuleHeader({ icon, title, accent, sub }) {
  return (
    <div style={{ marginBottom: 34 }}>
      <h1 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(34px, 5vw, 58px)', letterSpacing: 2, marginBottom: 8 }}>
        <span style={{ marginRight: 12 }}>{icon}</span>{title}
      </h1>
      {sub && <p style={{ color: T.gray, fontSize: 15, lineHeight: 1.6, maxWidth: '68ch' }}>{sub}</p>}
      <div style={{ width: 54, height: 3, background: accent, borderRadius: 2, marginTop: 16 }} />
    </div>
  );
}

// Doctor clearance (2026-10-31, backend doctor-clearance.js): "Yes, cleared" asks for a photo or PDF of the
// doctor's note. The safety hold stays until the member's trainer approves it, or asks again with a reason.
function ClearanceNote() {
  const [st, setSt] = useState(null), [busy, setBusy] = useState(false), [err, setErr] = useState('');
  const load = () => api('/api/clearance-note').then(setSt).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = e => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setErr('');
    if (f.size > 6 * 1024 * 1024) { setErr('That file is too large. Please send a smaller photo or PDF (under about 6 MB).'); return; }
    setBusy(true);
    const rd = new FileReader();
    rd.onload = async () => { try { setSt(await api('/api/clearance-note', 'POST', { name: f.name, data: rd.result })); } catch (x) { setErr(x.message); } setBusy(false); };
    rd.onerror = () => { setErr('That file could not be read. Please try another photo or PDF.'); setBusy(false); };
    rd.readAsDataURL(f);
  };
  if (!st) return null;
  const box = { marginTop: 10, padding: '12px 14px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.glass, fontSize: 13.5, lineHeight: 1.6 };
  if (st.approved) return <div style={box}><b style={{ color: T.success }}>Your trainer checked your doctor's note. You are cleared.</b></div>;
  const n = st.note;
  return (
    <div style={box}>
      {n && n.status === 'sent' ? <p style={{ margin: 0 }}><b>Your note was sent; your trainer will check it.</b> Your exercise program stays on hold until then.</p> : <>
        {n && n.status === 'asked_again' && <p style={{ margin: '0 0 6px', color: T.danger }}><b>Your trainer asked for your doctor's note again:</b> {n.reason}</p>}
        <p style={{ margin: '0 0 8px' }}>Please add a photo or PDF of your doctor's note. Your trainer checks it, and then your exercise program opens.</p>
        <input type="file" accept="image/*,application/pdf" onChange={send} disabled={busy} aria-label="Photo or PDF of your doctor's note" />
        {busy && <span style={{ marginLeft: 8, color: T.gray }}>Sending...</span>}
      </>}
      {err && <p style={{ color: T.danger, margin: '6px 0 0' }}>{err}</p>}
    </div>
  );
}

// Shown at the top of any module page when the health screen flagged something.
function SafetyPanel({ data, accent }) {
  if (!data) return null;
  const { safety, blocked, safetyBriefing, stopSigns } = data;
  if (!safety) return null;
  const has = blocked || safety.stops?.length || safety.cautions?.length || safety.crisis;
  if (!has) return null;
  return (
    <div style={{
      background: blocked || safety.crisis ? '#FBEEED' : '#FFF8E8',
      border: `1px solid ${blocked || safety.crisis ? '#E4B9B5' : '#E8D5A8'}`,
      borderLeft: `5px solid ${blocked || safety.crisis ? '#B3261E' : '#8A5A00'}`,
      borderRadius: '0 12px 12px 0', padding: '20px 22px', marginBottom: 26,
    }}>
      <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 700, color: '#101418', marginBottom: 10 }}>
        {safety.crisis ? 'Please read this first' : blocked ? 'Your program is on hold' : 'Important for your program'}
      </h3>
      <p style={{ fontSize: 14.5, color: '#3F4750', lineHeight: 1.65, marginBottom: safetyBriefing ? 12 : 0 }}>{safety.summary}</p>
      {safetyBriefing && (
        <pre style={{
          whiteSpace: 'pre-wrap', fontFamily: 'Poppins', fontSize: 13.5, color: '#4A5158',
          lineHeight: 1.6, background: 'rgba(255,255,255,0.7)', padding: '14px 16px', borderRadius: 8, margin: 0,
        }}>{safetyBriefing}</pre>
      )}
      {stopSigns && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: '#B3261E' }}>
            Stop and get help if any of these happen
          </summary>
          <ul style={{ marginTop: 10, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {stopSigns.map(x => <li key={x} style={{ fontSize: 13.5, color: '#4A5158', lineHeight: 1.5 }}>{x}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

function ModuleLoading() {
  return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.gray }}>Loading your program…</div>;
}

// ── MIND ─────────────────────────────────────────────────────────────────────
function MindPage() {
  const [d, setD] = useState(null);
  const [mood, setMood] = useState(5);
  const [word, setWord] = useState('');
  const [moodHist, setMoodHist] = useState(null);
  const [moodSaving, setMoodSaving] = useState(false);
  const [moodSaved, setMoodSaved] = useState(false);
  const [moodErr, setMoodErr] = useState('');
  const A = '#4A5FC1';
  const today = new Date().toISOString().slice(0, 10);

  const loadMood = () => api('/api/mind/mood')
    .then(m => {
      setMoodHist(m);
      // If today is already logged, show what was logged rather than a blank 5.
      const t = (m.logs || []).find(r => r.date === today);
      if (t) { setMood(t.score); setWord(t.word || ''); }
    })
    .catch(() => setMoodHist(null));

  useEffect(() => {
    api('/api/mind')
      .then(res => { setD(res); if (!res.blocked) loadMood(); })
      .catch(err => setD({ locked: /not active|not on your plan/i.test(err.message || '') || err.status === 402, lockInfo: err.info, error: true }));
  }, []); // eslint-disable-line

  const saveMood = async () => {
    setMoodSaving(true); setMoodErr('');
    try {
      await api('/api/mind/mood', 'POST', { date: today, score: mood, word });
      setMoodSaved(true); setTimeout(() => setMoodSaved(false), 3000);
      await loadMood();
    } catch (err) { setMoodErr(err.message || 'Could not save that.'); }
    finally { setMoodSaving(false); }
  };
  if (!d) return <ModuleLoading />;
  if (d.locked) return <ModuleLocked info={d.lockInfo} />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 980, margin: '0 auto' }}>
      <ModuleHeader icon="🧠" title="MIND" accent={A}
        sub="Short, practical tools. Nothing here takes thirty minutes, because the ones that do are the ones you stop doing." />
      <SafetyPanel data={d} accent={A} />

      {d.blocked ? null : (
        <>
          {d.focus && (
            <p style={{ fontSize: 14, color: T.gray, marginBottom: 22 }}>
              Chosen for you based on what you told us: <strong style={{ color: A }}>{d.focus.join(' · ')}</strong>
            </p>
          )}

          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 14 }}>Today's practices</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginBottom: 36 }}>
            {(d.dailyPractices || []).map(p => (
              <Card key={p.key} style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600 }}>{p.name}</h3>
                  <span style={{ background: `${A}18`, color: A, fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>{p.minutes} min</span>
                </div>
                <p style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.6, marginBottom: 10 }}>{p.how}</p>
                <p style={{ fontSize: 12.5, color: T.gray, lineHeight: 1.55, fontStyle: 'italic' }}>{p.why}</p>
              </Card>
            ))}
          </div>

          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 14 }}>How today felt</h2>
          <Card style={{ padding: 22, marginBottom: 36 }}>
            <p style={{ fontSize: 13.5, color: T.gray, marginBottom: 14 }}>{d.moodTracking?.scale}</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <button key={n} type="button" onClick={() => setMood(n)} style={{
                  width: 42, height: 42, borderRadius: 10, cursor: 'pointer', font: 'inherit', fontWeight: 700,
                  border: `2px solid ${mood === n ? A : T.border}`,
                  background: mood === n ? A : '#fff', color: mood === n ? '#fff' : T.grayLight,
                }}>{n}</button>
              ))}
            </div>
            <input value={word} onChange={e => setWord(e.target.value)} placeholder="One word for why"
              style={{ width: '100%', padding: '11px 14px', borderRadius: 8, border: `1px solid ${T.border}`, fontFamily: 'Poppins', fontSize: 14, boxSizing: 'border-box' }} />
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <button type="button" onClick={saveMood} disabled={moodSaving} style={{
                font: 'inherit', fontWeight: 700, fontSize: 14, padding: '11px 22px', borderRadius: 8, border: 'none',
                background: A, color: '#fff', cursor: moodSaving ? 'default' : 'pointer', opacity: moodSaving ? 0.6 : 1,
              }}>{moodSaving ? 'Saving...' : 'Save today'}</button>
              {moodSaved && <span style={{ color: T.success, fontSize: 13, fontWeight: 600 }}>Saved</span>}
              {moodErr && <span style={{ color: '#B3261E', fontSize: 13 }}>{moodErr}</span>}
            </div>
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(d.moodTracking?.prompts || []).map(q => (
                <p key={q} style={{ fontSize: 13, color: T.gray }}>· {q}</p>
              ))}
            </div>

            {moodHist && (moodHist.logs || []).length > 0 && (
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${T.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                  <h3 style={{ fontFamily: 'Oswald', fontSize: 14, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: T.grayLight }}>The last two weeks</h3>
                  {moodHist.average != null && (
                    <span style={{ fontSize: 13, color: T.gray }}>Average this week <strong style={{ color: A }}>{moodHist.average}</strong> of 10</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 86, overflowX: 'auto' }}>
                  {(moodHist.logs || []).slice(0, 14).reverse().map(r => (
                    <div key={r.date} title={`${r.date}: ${r.score}/10${r.word ? ' - ' + r.word : ''}`}
                      style={{ flex: '0 0 auto', width: 30, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                      <div style={{ width: '100%', height: `${(r.score / 10) * 100}%`, minHeight: 4, borderRadius: '4px 4px 0 0', background: r.score <= 3 ? '#B3261E' : A }} />
                      <span style={{ fontSize: 9, color: T.gray, whiteSpace: 'nowrap' }}>{r.date.slice(5)}</span>
                    </div>
                  ))}
                </div>
                {moodHist.concern && d.escalation && (
                  <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 8, background: '#FBEEED', borderLeft: '4px solid #B3261E' }}>
                    <p style={{ fontSize: 13.5, color: '#4A5158', lineHeight: 1.6, marginBottom: 8 }}>
                      You have logged a 3 or lower on {moodHist.lowDays} of the last seven days. That is worth saying out loud to someone, not just to this page.
                    </p>
                    <p style={{ fontSize: 13.5, color: '#B3261E', fontWeight: 600, lineHeight: 1.6 }}>{d.escalation.crisisNow}</p>
                  </div>
                )}
              </div>
            )}
          </Card>

          <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600, marginBottom: 14 }}>Your week</h2>
          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 36 }}>
            {(d.weekly || []).map((w, i) => (
              <div key={w.day} style={{ display: 'flex', gap: 16, padding: '14px 20px', borderTop: i ? `1px solid ${T.border}` : 'none', alignItems: 'baseline' }}>
                <span style={{ fontFamily: 'Oswald', fontSize: 13, fontWeight: 600, color: A, minWidth: 82 }}>{w.day}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: T.white, minWidth: 92 }}>{w.theme}</span>
                <span style={{ fontSize: 13.5, color: T.grayLight }}>{w.practice}</span>
              </div>
            ))}
          </Card>

          {d.escalation && (
            <Card style={{ padding: 22, borderLeft: `5px solid ${A}` }}>
              <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>When to talk to someone</h3>
              <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 14 }}>
                {d.escalation.seeSomeone.map(x => <li key={x} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.55 }}>{x}</li>)}
              </ul>
              <p style={{ fontSize: 13.5, color: '#B3261E', fontWeight: 600, lineHeight: 1.6 }}>{d.escalation.crisisNow}</p>
              <p style={{ fontSize: 12.5, color: T.gray, marginTop: 10, fontStyle: 'italic' }}>{d.escalation.note}</p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ── MOBILITY & REHAB ─────────────────────────────────────────────────────────
function MobilityPage() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(0);
  const A = '#8A5A00';
  useEffect(() => { api('/api/mobility').then(setD).catch(err => setD({ locked: /not active|not on your plan/i.test(err.message || '') || err.status === 402, lockInfo: err.info, error: true })); }, []);
  if (!d) return <ModuleLoading />;
  if (d.locked) return <ModuleLocked info={d.lockInfo} />;
  const tracks = d.tracks || [];

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 980, margin: '0 auto' }}>
      <ModuleHeader icon="🦴" title="MOBILITY & REHAB" accent={A}
        sub="Ten minutes a day on the thing that actually hurts. Staged, not random." />
      <SafetyPanel data={d} accent={A} />

      {!d.blocked && (
        <>
          <Card style={{ padding: 20, marginBottom: 26, borderLeft: `5px solid ${A}` }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 15, fontWeight: 600, marginBottom: 10 }}>The rules</h3>
            <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(d.rules || []).map(r => <li key={r} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.55 }}>{r}</li>)}
            </ul>
          </Card>

          {tracks.map((t, idx) => (
            <Card key={t.key} style={{ padding: 0, overflow: 'hidden', marginBottom: 18 }}>
              <button type="button" onClick={() => setOpen(open === idx ? -1 : idx)} style={{
                width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit', background: 'transparent',
                border: 'none', padding: '18px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>
                  <span style={{ fontFamily: 'Oswald', fontSize: 18, fontWeight: 600 }}>{t.name}</span>
                  <span style={{ fontSize: 13, color: T.gray, marginLeft: 10 }}>{t.weeks} weeks · {t.stages.length} stages</span>
                </span>
                <span style={{ fontSize: 20, color: A }}>{open === idx ? '−' : '+'}</span>
              </button>
              {open === idx && (
                <div style={{ padding: '0 22px 22px' }}>
                  {t.stages.map(st => (
                    <div key={st.weeks} style={{ marginBottom: 18, paddingLeft: 14, borderLeft: `3px solid ${A}44` }}>
                      <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: A }}>
                        Weeks {st.weeks} — {st.goal}
                      </p>
                      <ul style={{ paddingLeft: 18, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {st.work.map(w => <li key={w} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.5 }}>{w}</li>)}
                      </ul>
                    </div>
                  ))}
                  <div style={{ background: '#FBEEED', border: '1px solid #E4B9B5', borderRadius: 10, padding: '14px 16px' }}>
                    <p style={{ fontSize: 12.5, fontWeight: 700, color: '#B3261E', marginBottom: 8 }}>Stop and get this looked at if:</p>
                    <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {t.redFlags.map(f => <li key={f} style={{ fontSize: 13, color: '#5A2320', lineHeight: 1.5 }}>{f}</li>)}
                    </ul>
                  </div>
                </div>
              )}
            </Card>
          ))}

          <p style={{ fontSize: 13.5, color: T.gray, lineHeight: 1.65, marginTop: 20, fontStyle: 'italic' }}>{d.seeAProfessional}</p>
        </>
      )}
    </div>
  );
}

// ── LIVING WELL (conditions) ─────────────────────────────────────────────────
function ConditionsPage() {
  const [d, setD] = useState(null);
  const A = '#B3261E';
  useEffect(() => { api('/api/conditions').then(setD).catch(err => setD({ locked: /not active|not on your plan/i.test(err.message || '') || err.status === 402, lockInfo: err.info, error: true })); }, []);
  if (!d) return <ModuleLoading />;
  if (d.locked) return <ModuleLocked info={d.lockInfo} />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 980, margin: '0 auto' }}>
      <ModuleHeader icon="❤️" title="LIVING WELL" accent={A}
        sub="Built around the limits your doctor set — not in spite of your diagnosis." />
      <SafetyPanel data={d} accent={A} />

      {!d.blocked && (
        <>
          {d.none && (
            <Card style={{ padding: 24, marginBottom: 24 }}>
              <p style={{ fontSize: 14.5, color: T.grayLight, lineHeight: 1.65 }}>
                You have not told us about a long-term condition, so there is no condition program to show.
                If that changes, update your health profile and this page fills in.
              </p>
            </Card>
          )}

          {(d.programs || []).map(p => (
            <Card key={p.key} style={{ padding: 24, marginBottom: 20 }}>
              <h2 style={{ fontFamily: 'Oswald', fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{p.name}</h2>
              <p style={{ fontSize: 13.5, color: A, fontWeight: 600, marginBottom: 18 }}>{p.accentGoal}</p>
              {[['Movement', p.movement], ['Eating', p.eating], ['What to monitor', p.monitoring]].map(([label, items]) => (
                <div key={label} style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: T.gray, marginBottom: 8 }}>{label}</p>
                  <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(items || []).map(x => <li key={x} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.55 }}>{x}</li>)}
                  </ul>
                </div>
              ))}
              <div style={{ background: `${A}0D`, borderRadius: 8, padding: '12px 14px' }}>
                <p style={{ fontSize: 13, color: '#5A2320', lineHeight: 1.55 }}><strong>Your care team:</strong> {p.careTeam}</p>
              </div>
            </Card>
          ))}

          <Card style={{ padding: 22 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>However your plan looks</h3>
            <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 14 }}>
              {(d.universal || []).map(x => <li key={x} style={{ fontSize: 13.5, color: T.grayLight, lineHeight: 1.55 }}>{x}</li>)}
            </ul>
            <p style={{ fontSize: 12.5, color: T.gray, lineHeight: 1.6, fontStyle: 'italic' }}>{d.boundary}</p>
          </Card>
        </>
      )}
    </div>
  );
}

// ── STRONG AT 65+ ────────────────────────────────────────────────────────────
function LongevityPage() {
  const [d, setD] = useState(null);
  const A = '#5B6470';
  useEffect(() => { api('/api/longevity').then(setD).catch(err => setD({ locked: /not active|not on your plan/i.test(err.message || '') || err.status === 402, lockInfo: err.info, error: true })); }, []);
  if (!d) return <ModuleLoading />;
  if (d.locked) return <ModuleLocked info={d.lockInfo} />;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, padding: '80px 24px 60px', maxWidth: 980, margin: '0 auto' }}>
      <ModuleHeader icon="🌳" title="STRONG AT 65+" accent={A} sub={d.premise} />
      <SafetyPanel data={d} accent={A} />

      {!d.blocked && (
        <>
          <Card style={{ padding: 20, marginBottom: 24, borderLeft: `5px solid ${A}` }}>
            <p style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.65 }}>{d.chairOption}</p>
          </Card>

          {(d.pillars || []).map((p, i) => (
            <Card key={p.key} style={{ padding: 24, marginBottom: 18 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontFamily: 'Bebas Neue', fontSize: 26, color: `${A}88` }}>{String(i + 1).padStart(2, '0')}</span>
                <h2 style={{ fontFamily: 'Oswald', fontSize: 19, fontWeight: 600 }}>{p.name}</h2>
              </div>
              <p style={{ fontSize: 13.5, color: T.gray, lineHeight: 1.6, marginBottom: 14, fontStyle: 'italic' }}>{p.why}</p>
              <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(p.work || []).map(w => <li key={w} style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.55 }}>{w}</li>)}
              </ul>
              {p.progress && (
                <p style={{ fontSize: 13, color: T.grayLight, marginTop: 12, background: `${A}0F`, padding: '10px 13px', borderRadius: 8, lineHeight: 1.55 }}>
                  <strong>Progress:</strong> {p.progress}
                </p>
              )}
            </Card>
          ))}

          <Card style={{ padding: 22, marginBottom: 18 }}>
            <h3 style={{ fontFamily: 'Oswald', fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Check in on these</h3>
            <ul style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {(d.checkIn || []).map(x => <li key={x} style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.55 }}>{x}</li>)}
            </ul>
          </Card>

          <Card style={{ padding: 20, marginBottom: 18 }}>
            <p style={{ fontSize: 14, color: T.grayLight, lineHeight: 1.65 }}><strong>Protein:</strong> {d.protein}</p>
          </Card>

          <div style={{ background: '#FFF8E8', border: '1px solid #E8D5A8', borderLeft: '5px solid #8A5A00', borderRadius: '0 12px 12px 0', padding: '18px 20px' }}>
            <p style={{ fontSize: 14, color: '#4A3A15', lineHeight: 1.65 }}>{d.escalate}</p>
          </div>
        </>
      )}
    </div>
  );
}

// ── APP ROUTER ────────────────────────────────────────────────────────────────
// Onboarding, check-ins, trainer chat and retention screens (ClientCare.jsx).
// Trainer tools + member progress screens (TrainerTools.jsx). CC looks them up through TT at render time.
const TT = {};
const CC = makeClientCare({ T, Btn, Card, Badge, Input, api, useAuth, gradText, resizeAndEncode, ext: TT });
Object.assign(TT, makeTrainerTools({ T, Btn, Card, Badge, api, useAuth, resizeAndEncode, ui: CC.ui }));
Object.assign(TT, makeMealPlanner({ T, Btn, Card, Badge, api, ui: CC.ui }));
Object.assign(TT, makeTraining({ T, Btn, Card, Badge, api, ui: CC.ui }));
// Build 3 (2026-10-30): hand portions, Say it, workout timer, Help desk, Admin Support (Build3.jsx).
const B3 = makeBuild3({ T, Btn, Card, Badge, api, ui: CC.ui, ext: TT });
// App Store requirements (2026-10-30): AI consent screen, Settings extras, chat report (AppStore.jsx).
const AS = makeAppStore({ T, Btn, Card, api, ui: CC.ui });
// Flexible billing (2026-10-30): plan chooser, Admin > Plans & billing options, trainer "My own clients",
// and no payments inside the phone apps (BillingFlex.jsx).
const BF = makeBillingFlex({ T, Btn, Card, Badge, api, ui: CC.ui });
{ const BaseTool = TT.TrainerTool; TT.TrainerTool = p => (p.tool === 'own_clients' ? <BF.TrainerBrought /> : <BaseTool {...p} />); }
// Add a recipe (trainers in their tool bar, home cooks in My Kitchen) and the admin review list (RecipeSubmissions.jsx, 2026-11-01).
const RS = makeRecipeSubmissions({ T, Btn, Card, Badge, api, ui: CC.ui });
{ const BaseTool = TT.TrainerTool; TT.TrainerTool = p => (p.tool === 'recipes' ? <RS.MyRecipes /> : <BaseTool {...p} />); }
// My alerts, my open times and my exercises for trainers; trainer logins and trainers' exercises for admins (TrainerGaps.jsx, 2026-11-01).
const TG = makeTrainerGaps({ T, Btn, Card, Badge, api, ui: CC.ui, enablePush: turnOnPushHere, payStore });
const ESV = makeExerciseSlotVideo({ T, Btn, api, ui: CC.ui });
{ const BaseTool = TT.TrainerTool; TT.TrainerTool = p => (p.tool === 'statement' ? <TG.MyPay /> : p.tool === 'coverage' ? <TG.MyCoverage /> : p.tool === 'alerts' ? <TG.MyAlerts /> : p.tool === 'my_exercises' ? <TG.MyExercises /> : p.tool === 'appointments' ? <><TG.MySchedule /><BaseTool {...p} /></> : <BaseTool {...p} />); }
Object.assign(TT, { ChatReport: AS.ChatReport, ReportExcerpt: AS.ReportExcerpt });
// Rebuilder progress for trainers + weekly progress reports (TrainerProgress.jsx, 2026-11-01): the roster and the
// Progress tab are looked up by ClientCare.jsx through TT; the member page and the admin page are routed below.
const TP = makeTrainerProgress({ T, Btn, Card, Badge, api, useAuth, ui: CC.ui });
Object.assign(TT, { ProgressRoster: TP.ProgressRoster, RebuilderProgress: TP.RebuilderProgress });
// Phone app screens (NativeFeatures.jsx, 2026-11-02): this week's plan, the reason screen for alerts, Apple Health / Health Connect.
const NF = makeNativeFeatures({ T, Btn, Card, api, ui: CC.ui });
Object.assign(TT, makeEasyLog({ T, Btn, Card, Badge, api, ui: CC.ui, b3: B3, as: AS, nf: NF }));
// The trainer's client page (More tab) also shows the client's hand-portion logging.
{ const BaseExtras = TT.ClientExtras; TT.ClientExtras = props => <><BaseExtras {...props} /><div style={{ marginTop: 12 }}><B3.HandPortionCoach clientId={props.clientId} /></div></>; }

export default function App() {
  return (
    <AuthProvider>
      <GlobalBackBar />
      <AS.AiConsentHost />
      <Navbar />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/signup" element={<AuthPage mode="signup" />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/onboarding" element={
          <PrivateOnboarding><Onboarding /></PrivateOnboarding>
        } />
        <Route path="/profile" element={<PrivateRoute><Onboarding editMode /></PrivateRoute>} />
        <Route path="/billing" element={<PrivateRoute><BillingPage /></PrivateRoute>} />
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/program" element={<PrivateRoute><ProgramPage /></PrivateRoute>} />
        <Route path="/workouts" element={<PrivateRoute><WorkoutsPage /></PrivateRoute>} />
        <Route path="/recipes" element={<PrivateRoute><RecipesPage /></PrivateRoute>} />
        <Route path="/progress" element={<PrivateRoute><ProgressPage /></PrivateRoute>} />
        <Route path="/food-diary" element={<PrivateRoute><FoodDiaryPage /></PrivateRoute>} />
        <Route path="/shopping" element={<PrivateRoute><ShoppingListPage /></PrivateRoute>} />
        <Route path="/cooking-shows" element={<PrivateRoute><CookingShowsPage /></PrivateRoute>} />
        <Route path="/coach" element={<CoachRoute><CC.TrainerPortalPage /></CoachRoute>} />
        <Route path="/cook-dashboard" element={<CookRoute><CookDashboardPage /></CookRoute>} />
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="/gym-admin" element={<GymAdminRoute><GymAdminPortal /></GymAdminRoute>} />
        <Route path="/signup-invite/:token" element={<InviteSignup />} />
        <Route path="/get-outside" element={<PrivateRoute><GetOutsidePage /></PrivateRoute>} />
        <Route path="/community" element={<PrivateRoute><CommunityPage /></PrivateRoute>} />
        <Route path="/sleep" element={<PrivateRoute><SleepPage /></PrivateRoute>} />
        <Route path="/mind" element={<PrivateRoute><MindPage /></PrivateRoute>} />
        <Route path="/mobility" element={<PrivateRoute><MobilityPage /></PrivateRoute>} />
        <Route path="/conditions" element={<PrivateRoute><ConditionsPage /></PrivateRoute>} />
        <Route path="/longevity" element={<PrivateRoute><LongevityPage /></PrivateRoute>} />
        <Route path="/supplements" element={<PrivateRoute><SupplementsPage /></PrivateRoute>} />
        <Route path="/measurements" element={<PrivateRoute><TT.MeasurementsPage /></PrivateRoute>} />
        <Route path="/meal-plan" element={<PrivateRoute><TT.MyMealPlanPage /></PrivateRoute>} />
        <Route path="/today" element={<PrivateRoute><TT.TodayPage /></PrivateRoute>} />
        <Route path="/hand-portions" element={<PrivateRoute><B3.HandPortionsPage /></PrivateRoute>} />
        <Route path="/timer" element={<PrivateRoute><B3.TimerPage /></PrivateRoute>} />
        <Route path="/help" element={<PrivateRoute><B3.HelpPage /></PrivateRoute>} />
        <Route path="/settings/notifications" element={<NotificationsRoute />} />
        <Route path="/nutrition-center" element={<PrivateRoute><TT.NutritionCenterPage /></PrivateRoute>} />
        <Route path="/challenges" element={<PrivateRoute><TT.ChallengesPage /></PrivateRoute>} />
        <Route path="/settings/account" element={<PrivateRoute><TT.AccountPage /></PrivateRoute>} />
        <Route path="/settings/phone-alerts" element={<SignedInRoute><NF.PhoneAlertsPage /></SignedInRoute>} />
        <Route path="/settings/health" element={<PrivateRoute><NF.HealthDataPage /></PrivateRoute>} />
        <Route path="/workout/week" element={<PrivateRoute><NF.WeekPlanPage /></PrivateRoute>} />
        <Route path="/my-lifts" element={<PrivateRoute><TT.MyLiftsPage /></PrivateRoute>} />
        <Route path="/my-weekly-reports" element={<PrivateRoute><TP.MyWeeklyReportsPage /></PrivateRoute>} />
        <Route path="/meal-plan-classic" element={<PrivateRoute><MealPlanPage /></PrivateRoute>} />
        <Route path="/contests" element={<PrivateRoute><ContestsPage /></PrivateRoute>} />
        <Route path="/referral" element={<PrivateRoute><ReferralPage /></PrivateRoute>} />
        <Route path="/book" element={<PrivateRoute><BookingPage /></PrivateRoute>} />
        <Route path="/ai-coach" element={<PrivateRoute><MemberAICoach /></PrivateRoute>} />
        <Route path="/portal" element={<PrivateRoute><MemberPortal /></PrivateRoute>} />
        <Route path="/calisthenics" element={<PrivateRoute><CalisthenicsPage /></PrivateRoute>} />
        <Route path="/hiking" element={<PrivateRoute><HikingPage /></PrivateRoute>} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/getting-started" element={<PrivateRoute><CC.GettingStartedPage /></PrivateRoute>} />
        <Route path="/intake/parq" element={<PrivateRoute><CC.ParqPage /></PrivateRoute>} />
        <Route path="/intake/lifestyle" element={<PrivateRoute><CC.LifestylePage /></PrivateRoute>} />
        <Route path="/intake/foods" element={<PrivateRoute><CC.FoodsPage /></PrivateRoute>} />
        <Route path="/intake/agreement" element={<PrivateRoute><CC.AgreementPage /></PrivateRoute>} />
        <Route path="/photos" element={<PrivateRoute><CC.PhotosPage /></PrivateRoute>} />
        <Route path="/messages" element={<PrivateRoute><CC.MessagesPage /></PrivateRoute>} />
        <Route path="/food-plan" element={<PrivateRoute><CC.FoodPlanPage /></PrivateRoute>} />
        <Route path="/insights" element={<PrivateRoute><CC.InsightsPage /></PrivateRoute>} />
        <Route path="/badges" element={<PrivateRoute><CC.BadgesPage /></PrivateRoute>} />
        <Route path="/workout/today" element={<PrivateRoute><CC.TodayWorkoutPage /></PrivateRoute>} />
        <Route path="/meal-photo" element={<PrivateRoute><CC.MealPhotoPage /></PrivateRoute>} />
        <Route path="/calendar" element={<PrivateRoute><TT.CalendarPage /></PrivateRoute>} />
        <Route path="/check-in" element={<PrivateRoute><TT.CheckInPage /></PrivateRoute>} />
        <Route path="/groups" element={<PrivateRoute><TT.GroupsPage /></PrivateRoute>} />
        <Route path="/goals" element={<PrivateRoute><TT.GoalsPage /></PrivateRoute>} />
        <Route path="/my-trainer" element={<PrivateRoute><TT.MyTrainerPage /></PrivateRoute>} />
        <Route path="/food-library" element={<PrivateRoute><TT.FoodLibraryPage /></PrivateRoute>} />
        <Route path="/trainer/:slug" element={<TT.PublicTrainerPage />} />
        <Route path="/trainer-apply" element={<TT.TrainerApplyPage />} />
        <Route path="/trainer-application" element={<PrivateOnboarding><TT.TrainerApplicationStatus /></PrivateOnboarding>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

function PrivateOnboarding({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}
